import React from "react";
import { connect } from "react-redux";

import inits from "../../engine/inits";
import musicEngine from "../../engine/music";
import versionWatch from "../../engine/versionWatch";
import { BUFF_TYPE } from "../../engine/general";
import RenderCharacters from "../../engine/renderCharacters";
import pvpChars from "../../config/pvpChars.json";
import StatsBox from "../../components/play/StatsBox";
import MapBox from "../../components/play/MapBox";
import InventoryBox from "../../components/play/InventoryBox";
import PartyPanel from "../../components/play/PartyPanel";
import GlobalPanel from "../../components/play/GlobalPanel";
import ClanPanel from "../../components/play/ClanPanel";
import TradeModal from "../../components/play/TradeModal";
import {
    KEY_CONFIG_VERSION,
    AUDIO_CONFIG_VERSION,
    DEFAULT_AUDIO,
    DEFAULT_KEY_BINDINGS,
    DEFAULT_MACROS,
    MACRO_COUNT,
    cloneDefaultBindings,
    cloneDefaultMacros
} from "../../engine/defaults";

import _ from "lodash";

// Helper: re-genera el map keyCodeMacros (keyCode → index del macro) a partir
// del array valueKeyMacro. Lo hacemos centralizado porque tanto el state
// initial, el hidratado de localStorage, y los cambios runtime (asignar
// tecla) tienen que mantener este map sincronizado.
function buildKeyCodeMacrosMap(valueKeyMacro) {
    const map = {};
    (valueKeyMacro || []).forEach((macro, idx) => {
        if (macro && macro.key) {
            map[macro.key] = idx;
        }
    });
    return map;
}

// Construye el texto del tooltip mostrado al hover sobre un slot del
// inventario. Para armas muestra "Nombre — minHit/maxHit", para
// armaduras/escudos/cascos "Nombre — minDef/maxDef". Para items
// sin combat stats (pociones, comida, pergaminos, etc) solo el nombre.
//
// NOTA: `inits.objs` (static cliente JSON `/static/init/objs.json`) es
// una versión shortened que NO trae `objType` — solo name, grhIndex,
// minHit/maxHit, minDef/maxDef, apu. Por eso detectamos el tipo de
// item por presencia de fields en vez de por objType.
function buildItemTooltip(objData) {
    if (!objData || !objData.name) return null;
    const parts = [objData.name];

    const hasHit =
        (objData.minHit > 0) || (objData.maxHit > 0);
    const hasDef =
        (objData.minDef > 0) || (objData.maxDef > 0);

    if (hasHit) {
        // Armas + flechas — daño
        parts.push((objData.minHit || 0) + "/" + (objData.maxHit || 0));
    } else if (hasDef) {
        // Armadura / escudo / casco — defensa física
        parts.push((objData.minDef || 0) + "/" + (objData.maxDef || 0));
    }

    return parts.join(" — ");
}

// Si `text` es un /w bien formado (con nombre + mensaje), devuelve el prefix
// para auto-rellenar la próxima apertura del chat (ej. `"/w Asuka "` o
// `"/w \"j la rata\" "`). Si no, devuelve null. Mirror del parser server-side
// en commands.js parseWhisperInput — mantener ambos sincronizados.
function extractWhisperPrefix(text) {
    if (!text) return null;
    const lower = text.toLowerCase();
    if (!lower.startsWith("/w ") && !lower.startsWith("/w\t")) return null;

    const afterCmd = text.slice(3).trim();
    if (!afterCmd) return null;

    let name, rest;
    if (afterCmd[0] === '"') {
        const endQuote = afterCmd.indexOf('"', 1);
        if (endQuote === -1) return null;
        name = afterCmd.slice(1, endQuote).trim();
        rest = afterCmd.slice(endQuote + 1).trim();
        if (!name || !rest) return null;
        return '/w "' + name + '" ';
    }

    const firstSpace = afterCmd.indexOf(" ");
    if (firstSpace === -1) return null;
    name = afterCmd.slice(0, firstSpace);
    rest = afterCmd.slice(firstSpace + 1).trim();
    if (!name || !rest) return null;
    return "/w " + name + " ";
}

import { fetchUrl, routerPush } from "../../config/utils";
import { nameClases, nameRazas } from "../../config/config";

import Header from "../../components/header/index";
import CreateCharacterForm from "../../components/createCharacter/index";

import style from "./style.scss";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faComment,
    faTimes,
    faVolumeUp,
    faVolumeMute,
    faSignOutAlt,
    faBolt,
    faCog,
    faShieldAlt,
    faSkull,
    faUserCircle,
    faTrash,
    faExclamationTriangle,
    faSearchPlus,
    faSearchMinus,
    faExpand,
    faCompress
} from "@fortawesome/free-solid-svg-icons";

// HEADER_FONTS / round-robin de tipografía vive en config/headerFonts.js.
// Aplica SOLO a CAJA 4 del HUD (nombre + nivel del char + barra exp) vía
// CSS var --header-font inline en .header. NO afecta al logo del navbar.
import {
    HEADER_FONTS,
    HEADER_FONT_STORAGE_KEY,
    headerFontFamilyFromIndex,
    readPersistedHeaderFontIndex
} from "../../config/headerFonts";

class Home extends React.Component {
    static async getInitialProps({ req }) {
        const userAgent = req
            ? req.headers["user-agent"]
            : typeof navigator !== "undefined"
            ? navigator.userAgent
            : "";
        return { userAgent };
    }

    constructor(props) {
        super(props);

        this.nameKeyCode = {
            flechaArriba: 0,
            flechaAbajo: 1,
            flechaIzquierda: 2,
            flechaDerecha: 3,
            usar: 4,
            atacar: 5,
            agarrar: 6,
            tirar: 7,
            equipar: 8,
            domar: 9,
            robar: 10,
            seguro: 11,
            meditar: 12,
            // Faltaba `ocultar` — el array de keysGrid lo usa (línea ~7291) y
            // generaba `key={undefined}` en el .map(), warning de React.
            ocultar: 13
        };

        // Header font index — restaurado de localStorage al montar el component.
        // Si no hay nada guardado, default a 0 (Doppio One).
        const initialHeaderFontIndex = readPersistedHeaderFontIndex();

        // Ticket #296: pixelPerfect (fix de líneas negras entre tiles al
        // caminar). Default ON — el fix es estable y mejora la mayoría de
        // setups. Si genera regresión visual rara en alguna GPU/zoom, el
        // user lo apaga del modal Configuración.
        let initialPixelPerfect = true;
        try {
            const stored = localStorage.getItem("argentum:pixelPerfect");
            if (stored !== null) initialPixelPerfect = stored !== "false";
        } catch (e) {}

        // Ticket #305: persistir tab del chat en localStorage. Sobrevive
        // reload + cambio de sesión del browser. Default "general".
        let initialChatTab = "general";
        try {
            const t = localStorage.getItem("argentum:chatTab");
            if (t === "chat" || t === "combat" || t === "general") {
                initialChatTab = t;
            }
        } catch (e) {}

        this.state = {
            showModalControlPanel: false,
            showInventary: true,
            showMacroConfig: false,
            loading: true,
            // Toggle de pantalla completa del documento. Lo sincroniza
            // _handlerFullscreenChange por si el user sale con Esc.
            isFullscreen: false,
            user: {},
            selectItem: 0,
            headerFontIndex: initialHeaderFontIndex,
            pixelPerfect: initialPixelPerfect,
            // Context menu del right-click sobre el canvas. Cuando el user
            // hace right-click sobre un NPC atacable, abre un menu en la
            // posición del mouse con la opción "Inspeccionar".
            contextMenu: null, // { x, y, npcId } o null si está cerrado
            // Context menu del right-click sobre una row del PartyPanel.
            // Solo el líder puede abrirlo; opción única "Promover a Líder".
            partyContextMenu: null, // { x, y, name } o null
            // Context menu del right-click sobre una row del GlobalPanel.
            // Opciones: Susurrar (pre-fill /w en chat), Invitar a party.
            globalContextMenu: null, // { x, y, name, isMe } o null
            // Context menu del right-click sobre una row del ClanPanel.
            // Solo el líder lo abre. Opción única: Expulsar del clan.
            clanContextMenu: null, // { x, y, name } o null
            // Data del NPC que estamos inspeccionando — llega del server
            // via packet npcInspect (engine/connection/messages.js).
            npcInspect: null,
            // Ídem para players. Se abre con right-click → Inspeccionar
            // sobre otro jugador. Payload en messages.js:userInspect().
            userInspect: null,
            // Auto-inspect (right-click sobre uno mismo). Modal separado,
            // posicionado en la MÁS DERECHA del par (side-by-side layout
            // en .render). Ambos pueden coexistir.
            selfInspect: null,
            // Herrería (#329) — modal de crafteo de lingotes. Se abre con
            // doble-click sobre el Martillo de Herrero equipado. Payload:
            // { cantHierro, cantPlata, cantOro } (conteo de minerales).
            herreriaModal: null,
            messagesConsole: [],
            // Ticket #300: buckets per-tab — antes el cap de 40 mensajes
            // total cortaba mensajes de chat cuando un burst de combat los
            // empujaba afuera. Ahora cada tab tiene su propio array con
            // cap 200. General = stream completo (todas las categorías).
            messagesByTab: {
                general: [],
                combat: [],
                chat: [],
                system: []
            },
            // Counter "↓ N mensajes nuevos" — incrementa cuando llega un
            // mensaje a la tab activa Y el user NO está pegado al fondo.
            // Reset al click del pill o al volver al fondo manualmente.
            chatScrollPending: 0,
            // Ticket #305: scroll position por tab. Cuando cambiás de tab y
            // volvés, recuperás la pos donde estabas (no te tira al fondo
            // si estabas leyendo arriba en esa tab). Default 0 = top; al
            // entrar la primera vez vamos al fondo (último mensaje).
            scrollByTab: {
                general: -1,  // -1 = "ir al fondo" (primera entrada)
                combat: -1,
                chat: -1,
                system: -1
            },

            // Ticket #283: tabs del chat. "general" muestra todo (default).
            // "chat" = mensajes de personajes (whispers, server, public,
            // click-name). "combat" = daños / hits / kills. "system" no
            // es tab — son los ambientales que solo se ven en general.
            //
            // Badge solo se incrementa para chat alert (whisper o
            // [Servidor]) o cualquier combat. El color del badge es el
            // color del último msg que lo disparó.
            chatTab: initialChatTab,
            unreadByTab: {
                chat: { count: 0, color: null },
                combat: { count: 0, color: null }
            },
            crosshair: false,
            nameMap: "",
            showFullMap: false,
            // Si se setea, el mapa abierto en showFullMap es ESTE en lugar
            // del mapa actual del char. Lo setea el click en una celda del
            // mapamundi para "navegar visualmente" a otro mapa.
            selectedMapToShow: null,
            // ── Lobby (pre-game) ─────────────────────────────────────
            // Si el user todavía no eligió char, mostramos el lobby
            // en vez de iniciar el engine. Una vez que clickea un slot,
            // setLocalStorage + initGame + lobbyMode=false.
            lobbyMode: true,
            lobbyTypeGame: "PvE",
            lobbyCharacters: [],
            // Ticket #213: state del drag & drop del lobby. `lobbyDraggingFrom`
            // es el índice del slot que está siendo arrastrado, `lobbyDraggingOver`
            // el slot destino del hover. Solo aplica al tab PvE — los chars
            // PvP son preset (no reordenables).
            lobbyDraggingFrom: -1,
            lobbyDraggingOver: -1,
            // Modal tirar item — reemplaza prompt() (bloqueado por Next.js)
            showDropItemModal: false,
            dropItemPos: 0,
            dropItemMaxCant: 1,
            dropItemValue: "1",
            // Modal borrar char (GitHub-style type-to-confirm)
            deleteCharTarget: null,
            deleteCharConfirmText: "",
            deleteCharSubmitting: false,
            deleteCharError: "",
            // Audio settings — master ON/OFF + 3 sliders independientes.
            // Cada volumen va de 0 a 100, se persiste en localStorage.
            //   musicVolume    → música de fondo (<audio> en loop)
            //   ambienceVolume → sonidos ambientales (viento, lluvia, etc.)
            //                    [no implementados aún, slot reservado]
            //   effectsVolume  → SFX de combate, hechizos, NPCs, pasos, etc.
            musicEnabled:
                typeof window !== "undefined"
                    ? localStorage.getItem("musicEnabled") !== "false"
                    : true,
            musicVolume:
                typeof window !== "undefined"
                    ? Number(
                          localStorage.getItem("musicVolume") ||
                              DEFAULT_AUDIO.musicVolume
                      )
                    : DEFAULT_AUDIO.musicVolume,
            // Label del track sonando — se actualiza vía suscripción al
            // music engine (onTrackChange).
            currentMusicTrack: "",
            // Modal de "nueva versión disponible". Trigger del engine
            // versionWatch (poll cada 5min + on tab focus).
            showVersionUpdateModal: false,
            ambienceVolume:
                typeof window !== "undefined"
                    ? Number(localStorage.getItem("ambienceVolume") || 50)
                    : 50,
            effectsVolume:
                typeof window !== "undefined"
                    ? Number(localStorage.getItem("effectsVolume") || 70)
                    : 70,
            // Buffs activos sobre el personaje (UI tipo WoW arriba-derecha
            // de la caja del juego). Cada entry tiene su propio campo de
            // state — por ahora solo invisibilidad. Cuando se agregan más
            // buffs (paralizar, meditar, etc.) conviene refactorizar a
            // un objeto state.activeBuffs = { id: { expiresAt, icon, ... } }.
            // invisibilityExpiresAt: timestamp ms en que termina, o null
            //   si no está activa. El server manda setInvisible packet con
            //   la duración y este state se setea desde messages.js.
            invisibilityExpiresAt: null,
            // Sombra Oscura de Morgolock (#49): buff persistente sin
            // expiry. Boolean (active true/false). Click sobre el buff icon
            // o re-cast del spell envía requestDeactivateSombraOscura al
            // server. No tiene timer porque dura indefinido.
            sombraOscuraActive: false,
            // Timestamp de expiración del buff Sombra Oscura cast-on-other
            // (30s). null = infinito (self-cast).
            sombraOscuraExpiresAt: null,
            // El Ojo del Demiurgo (#43) — mismo patrón que sombraOscura.
            ojoDemiurgoActive: false,
            ojoDemiurgoExpiresAt: null,
            // Sigilo de Atidorfa (#51) — Sombra Oscura mejorada.
            sigiloAtidorfaActive: false,
            sigiloAtidorfaExpiresAt: null,
            // Aceleración Arcana (#52) — buff stackeable de velocidad.
            // aceleracionStack: 1-5 (cap). Speed = 2^stack. Display = min(stack+1, 5).
            aceleracionActive: false,
            aceleracionExpiresAt: null,
            aceleracionStack: 0,
            // Modal de amnistía /PERDON. null = cerrado, objeto = abierto
            // con { cost, ciudadanosKilled }.
            perdonModal: null,
            // Modal de confirmación /criminal (#328). true = abierto.
            criminalModal: false,
            // Modal upgrade premium Magia (Morgolock Soul). null = cerrado.
            magiaUpgradeModal: null,
            // Skills modal — abrible desde el botón ⚡ junto al ⚙.
            // Visual-only por ahora; el server NO los lee (gameplay unchanged).
            //
            // Doble estado:
            //   - savedSkills: los valores LOCKEADOS (saved). NO se puede
            //     decrementar por debajo de estos — son la baseline permanente.
            //   - skills: working set (pendiente). El user adjusta acá; al
            //     hacer GUARDAR pasa a savedSkills y se persiste.
            // `-` button solo refunda lo que está pendiente (skills[k] > savedSkills[k]).
            showSkillsModal: false,
            skillPoints:
                typeof window !== "undefined" && window.localStorage
                    ? parseInt(localStorage.getItem("skillPoints") || "10", 10)
                    : 10,
            skills:
                typeof window !== "undefined" && window.localStorage
                    ? JSON.parse(localStorage.getItem("skills") || "{}")
                    : {},
            savedSkills:
                typeof window !== "undefined" && window.localStorage
                    ? JSON.parse(localStorage.getItem("savedSkills") || "{}")
                    : {},
            // UI skin — visual tema del HUD. "aoweb" = default actual.
            // "tierras-del-sur" = paleta más cálida tipo AO clásico.
            // Persistido en localStorage, no afecta gameplay.
            uiSkin:
                typeof window !== "undefined" && window.localStorage
                    ? localStorage.getItem("uiSkin") || "aoweb"
                    : "aoweb",
            // Flag explícito de Furia de Uhkrul (#50). Server lo broadcast
            // por el packet furiaState al castear/cancelar. Usado por
            // StatsBox para distinguir "ambos attrs en 38 por Furia"
            // (rojo pulse) de "ambos en 38 por dos pociones independientes"
            // (marrón + oliva).
            furiaActive: false,
            // Fuerza y Celeridad: independientes en cuanto a stat
            // (attrFuerza/attrAgilidad), pero los TIMERS están linkeados —
            // castearse cualquiera de los dos refresca el timer del otro
            // si está activo (lógica del server en game.js).
            fuerzaBuffExpiresAt: null,
            celeridadBuffExpiresAt: null,
            // Ticket #258: pets invocados activos. Array de
            // { id, npcIndex, expiresAt, totalMs }. Hasta 3 entradas.
            // El server manda petBuff packet en spawn (duration > 0) y al
            // despawn (duration = 0). Render en renderBuffs como cualquier
            // otro buff.
            activePetBuffs: [],
            // Trigger de re-render del countdown (cambia cada 200ms mientras
            // hay buffs activos para que el texto del timer se actualice).
            buffTick: 0,

            // Hechizos — selección en lista de texto + modal info.
            // selectedSpellSlot: idPos del hechizo highlighted en la lista
            // (sigue siendo idPos porque es state efímero del slot visible).
            // spellOrder: array de **idSpell** (id estable del hechizo) en
            // el orden en que se ven. Persistido en Mongo por personaje vía
            // WS, NO localStorage (idPos del server se reusa cuando se
            // olvidan/aprenden hechizos, lo cual causaba que un slot
            // mostrara el spell equivocado).
            selectedSpellSlot: 0,
            spellInfoTarget: null,
            spellsData: null,
            spellOrder: [],
            // Popover del botón de sort (un solo botón abre menú con
            // las dos opciones: alfabético y por mana). Cerrado por
            // default; toggle por click en el botón, cerrado por click
            // afuera (listener global mientras está abierto).
            sortMenuOpen: false,
            // Mapamundi (modal con grid de mapas adyacentes)
            showWorldMap: false,
            worldMapData: null,
            mapNames: null,
            showModalReconnect: false,
            // CRIT-67: modal que se muestra cuando el server kickea con
            // closeForce (dupe session en otra pestaña, anti-cheat, telep
            // corrupto). El user ve mensaje explicativo + botón para
            // volver al lobby en vez de pantalla negra silenciosa.
            showModalCloseForce: false,
            showInputText: false,
            textDialog: "",
            // Prefix de /w para auto-rellenar el chat la próxima vez que
            // se abra después de haber susurrado a alguien. Ej. después de
            // mandar `/w Asuka hola`, esto queda en `"/w Asuka "` para que
            // el siguiente Enter te abra el input listo para seguir whispeando.
            // Se limpia al mandar cualquier msg que no sea /w.
            lastWhisperTarget: null,
            // Ticket #313: sistema de clanes — payload del clan del user
            // (server lo pushea via clanInfo packet). Default { hasClan: false }.
            clan: { hasClan: false },
            // Posiciones live de miembros del clan online (server tick 1.5s).
            // Cliente filtra por mapa al renderear (full map muestra el mapa
            // abierto, minimap solo del actual).
            clanMembersPos: [],
            // Lista de clanes para unirse (server pushea via clanList packet).
            clanList: [],
            // Modales del clan (un solo flag — el modal decide qué subview
            // muestra según this.state.clan.hasClan).
            showClanModal: false,
            // Tab activa cuando NO tenés clan: "fundar" | "unirse".
            clanNoClanTab: "unirse",
            // Form state del modal de fundación.
            clanFoundName: "",
            clanFoundType: "mixto",
            // Confirmación de disolución (estilo GitHub — tipear el nombre).
            clanDissolveConfirm: "",
            showClanDissolveConfirm: false,
            // Posición del modal (draggable). Centrado por default (CSS).
            clanModalPos: null, // { x, y } o null = default centered
            // Ticket #302: nombre del último char que ME susurró. Usado
            // por el comando /r (reply) para auto-completar con /w <name>.
            // Se setea desde messages.js dialog handler cuando llega un
            // whisper entrante (color #DA6FD1 y sender ≠ self).
            lastWhisperFrom: null,
            // Ticket #306: history de mensajes enviados — flecha arriba
            // en el input del chat recupera el último (o más viejo, si
            // se sigue apretando). Array LIFO con cap. -1 = no estamos
            // navegando en el history (al apretar arriba va al [0] que es
            // el más reciente).
            chatHistory: [],
            chatHistoryIndex: -1,
            // Party state — sincronizado desde el server via packet
            // partyState. null = no estás en una party. Si estás:
            //   { iAmLeader: bool, members: [{name, level, idClase, isLeader}] }
            party: null,
            // Modal de invitación recibida — { inviterName, receivedAt }.
            // El user acepta/rechaza con botones que envían `/p aceptar` y
            // `/p rechazar` via chat. Null = sin invitación pendiente.
            partyInvite: null,
            // Modal de party UI (gestión: invitar, ver miembros, salir,
            // kick). Toggle con el botón "PARTY" debajo del inventario.
            showPartyModal: false,
            // Input del nombre a invitar dentro del modal de party.
            partyInviteName: "",
            // Panel GLOBAL del BOX 5 — toggle con el botón "GLOBAL".
            // Cuando se abre, se manda requestGlobalList al server.
            // Mientras está abierto, se refresca cada GLOBAL_REFRESH_MS.
            showGlobalPanel: false,
            // Lista cacheada — viene del server vía globalList packet.
            // Items: { name, level, idClase, idRaza, color }.
            globalList: [],
            // Panel CLAN del BOX 5 — mismo patrón. Datos vienen vía
            // state.clan (clanInfo packet). Refresh on demand al togglear.
            showClanPanel: false,
            clanInviteName: "",
            // Ticket #321: modal del Marinero (ferry NPC) — abre con
            // packet openFerry del server al hacer click en el NPC.
            showFerryModal: false,
            ferry: { npcName: "", npcHead: 0, routes: [] },
            showModalTrade: false,
            // Ticket #235: idPosTrade=-1 = "ningún slot del mercader
            // seleccionado". Antes era 0, lo que con la nueva UI causaba
            // que el slot 0 apareciera marcado al abrir.
            trade: {
                idPosTrade: -1,
                idPosInv: 0,
                itemsTrade: {},
                itemsUser: {}
            },
            cantTrade: 1,
            // Modal de banco — abre cuando server manda openBank (click
            // sobre NPC banquero). itemsBank es un dict por idPos del
            // banco. El inventario del user para mostrar al lado se lee
            // directo de this.engine.user.items (state vivo del HUD).
            showModalBank: false,
            modalBank: {
                itemsBank: {}
            },
            // Selección — slot activo de cada lado para los botones de
            // "Depositar/Retirar N". 0 = nada seleccionado.
            bankSelectedInvSlot: 0,
            bankSelectedBankSlot: 0,
            bankCantInv: 1,
            bankCantBank: 1,
            // Drag-and-drop temporal — { side: "inv"|"bank", idPos }.
            // null cuando no hay drag activo.
            bankDragging: null,
            mapasToLoad: 0,
            mapasCargados: 0,
            keyMacro: {
                indexMacro: "",
                idPosItem: "",
                // idItem canónico del item asignado. Resuelve el bug
                // de que el macro deja de funcionar si el item cambia
                // de slot en el inv (ej. swap potion con armadura).
                // triggerMacro busca el slot actual via findIdPosByIdItem.
                idItem: "",
                idSpell: "",
                idPosSpell: "",
                key: "",
                keyChar: "",
                img: "",
                // Picker tab activo en el modal: "item" | "spell".
                pickerMode: "item"
            },
            // Macros + bindings hidratados desde engine/defaults.js.
            // El componentDidMount aplica la migración por versión: si el
            // localStorage del user no matchea KEY_CONFIG_VERSION, fuerza
            // override con los defaults nuevos.
            valueKeyMacro: cloneDefaultMacros(),
            keyCodeMacros: buildKeyCodeMacrosMap(cloneDefaultMacros()),
            tmpKeyCodeDefault: {},
            keyCodeDefault: cloneDefaultBindings(),
            keyCodeDefaultReset: cloneDefaultBindings(),
            charKeyCodeDefault: {},

            // ── Spell keybindings (ticket #207) ────────────────────────
            // Dict { idSpell: keyCode } — cada hechizo conocido puede
            // tener una tecla custom asignada. Persistido per-char en
            // localStorage["spellKeys_<charName>"]. Hidratado al primer
            // packet con nameCharacter (initializePerCharSpellKeybindings).
            //
            // Validación de colisión: getKeyInUse() centraliza el scan
            // contra movement + action bindings + macros + otros spell
            // bindings + reservadas. Ver helper más abajo.
            spellKeybindings: {},
            // Right-click context menu sobre una row del listado de
            // hechizos. { idSpell, x, y } cuando abierto, null cerrado.
            spellContextMenu: null,
            // Si está capturando una tecla nueva para un hechizo (modal
            // "Presioná una tecla..."). El handler global de keyup mira
            // este flag y consume la próxima tecla. null cuando no capturando.
            capturingSpellKey: null, // { idSpell, spellName } o null
            // Feedback inline en el modal de captura — string con el
            // mensaje de colisión, o "" cuando todo OK.
            spellKeyCaptureError: "",

            // Ticket #214 — modal de confirmación al apretar ESC con nada
            // abierto. En lugar de salir directo del juego (behavior previo),
            // mostramos "¿Salir del juego?" con botones Salir / Cancelar.
            // Las OTRAS rutas a doLogout (navbar, /salir en chat) NO usan
            // este modal — son acciones explícitas que no necesitan confirm.
            showExitConfirmModal: false,

            // Ticket #216: tick counter cheap para forzar re-render del
            // minimapa + full map en idle (mientras el player no se mueve
            // pero los NPCs sí). Setear cada 500ms. Sin esto los dots de
            // enemigos quedan stale cuando estás parado. Cost: 2 setStates/s
            // sobre un primitivo number → irrelevante.

            // Ticket #218: toggle "Mostrar party en el mapa". 2 opciones:
            //   "none"    → no mostrar dots ni panel de party (default)
            //   "show"    → mostrar dots + panel "Party" en full map +
            //               markers en mapamundi
            // Persisted en localStorage. Ticket #312: default cambiado de
            // "none" a "show" — los miembros del party deberían verse en
            // el minimapa (caja 7 chico + full map) por default. Quien
            // tenga "none" guardado explícito conserva su opt-out.
            showPartyOnMap:
                typeof window !== "undefined"
                    ? (() => {
                          const stored = window.localStorage.getItem(
                              "showPartyOnMap"
                          );
                          if (stored === "none") return "none";
                          return "show";
                      })()
                    : "show",
            // Collapse state del panel Party (mismo patrón que enemy panel).
            showPartyPanelList: true,

            // ── Inventory item keybindings (ticket #208) ───────────────
            // Mismo patrón que spellKeybindings pero por item del inv.
            // Dict { idItem: keyCode }, persistido per-char en
            // localStorage["invKeys_<charName>"]. Bindings por idItem
            // canónico → si el user mueve el item a otro slot, la tecla
            // sigue funcionando (mismo patrón que macros).
            inventoryKeybindings: {},
            // Context menu sobre un slot del inventario. { slot, idItem, x, y }.
            inventoryContextMenu: null,

            // Ticket #280: mini menu del right-click en el ícono del buff de
            // pet (modo defensive ↔ offensive). { petId, x, y, currentMode }.
            petModeMenu: null,
            // Captura de tecla activa para un item. { idItem, itemName, slot }.
            capturingItemKey: null,
            // Feedback inline del modal de captura del inventario.
            itemKeyCaptureError: ""
        };

        this.macros = [];
        this.modalMacro = {};

        this.canvas = {
            background: {},
            techos: {},
            foreground: {},
            items: {},
            textos: {}
        };

        this.clickUse = 0;
        this.lastClickIdItem = 0;

        this.pkg = {};
        this.user = {};
        this.general = {};
        this.game = {};
        this.engine = {};
        this.messages = {};
        this.connection = {};
        this.config = {};
    }

    charKeyCodeDefault = () => {
        let { keyCodeDefault, charKeyCodeDefault } = this.state;

        Object.keys(keyCodeDefault).map(key => {
            const keyCode = keyCodeDefault[key];

            let fromChar = String.fromCharCode(keyCode);

            if (this.config.keyCodeMap[keyCode]) {
                fromChar = this.config.keyCodeMap[keyCode];
            }

            charKeyCodeDefault[key] = fromChar;
        });

        this.setState({
            charKeyCodeDefault: charKeyCodeDefault
        });
    };

    componentDidUpdate(prevProps, prevState) {
        // spellOrder viene del server (Mongo), populado por messages.js
        // cuando llega el packet getMyCharacter. No hay hidratado adicional.

        // Inicialización de macros PER-CHARACTER. Cuando llega el char
        // data del server (primer packet getMyCharacter), tenemos
        // nameCharacter. Ahí hidratamos los macros desde
        // localStorage["macros_${nameCharacter}"] y aplicamos autoBind
        // sobre lo que falte (pociones + dardo). Esto sustituye al viejo
        // global "macros" key — cada char tiene su propio set.
        const prevName =
            prevState.user && prevState.user.nameCharacter;
        const nowName =
            this.state.user && this.state.user.nameCharacter;
        if (!prevName && nowName) {
            this.initializePerCharMacros(nowName);
            // Spell keybindings (ticket #207) — hidratá per-char en el
            // mismo momento que los macros (primer packet con nameCharacter).
            this.initializePerCharSpellKeybindings(nowName);
            // Inventory keybindings (ticket #208) — mismo patrón.
            this.initializePerCharInventoryKeybindings(nowName);
            // Ticket #281: action bindings — pull del server + write-through
            // de localStorage (era global pre-#281, ahora per-char + DB).
            this.initializePerCharActionKeybindings(nowName);
        }

        // Buff ticker: arranca un setInterval que re-renderea cada 250ms
        // SOLO mientras hay buffs activos (regla de perf: cero trabajo en
        // idle). Cuando todos los buffs expiran el ticker se apaga solo.
        const hadAny =
            !!prevState.invisibilityExpiresAt ||
            !!prevState.fuerzaBuffExpiresAt ||
            !!prevState.celeridadBuffExpiresAt ||
            !!prevState.sombraOscuraExpiresAt ||
            !!prevState.ojoDemiurgoExpiresAt ||
            !!prevState.sigiloAtidorfaExpiresAt ||
            (prevState.activePetBuffs && prevState.activePetBuffs.length > 0);
        const hasAny =
            !!this.state.invisibilityExpiresAt ||
            !!this.state.fuerzaBuffExpiresAt ||
            !!this.state.celeridadBuffExpiresAt ||
            !!this.state.sombraOscuraExpiresAt ||
            !!this.state.ojoDemiurgoExpiresAt ||
            !!this.state.sigiloAtidorfaExpiresAt ||
            (this.state.activePetBuffs &&
                this.state.activePetBuffs.length > 0);
        if (hasAny && !hadAny) this.buffTickerStart();
        else if (!hasAny && hadAny) this.buffTickerStop();

        // Ticket #238: listener de outside-click attacheado SIEMPRE en
        // componentDidMount. Ver comentario allá. El handler tiene gates
        // internos para que solo trabaje cuando hay algo abierto.
    }

    // Re-renderea el componente cada 250ms mientras haya un buff con
    // countdown activo, así el contador se va actualizando visualmente.
    // Se auto-apaga cuando todos los timestamps de expire ya pasaron.
    buffTickerStart = () => {
        if (this._buffInterval) return;
        this._buffInterval = setInterval(() => {
            const now = Date.now();
            const {
                invisibilityExpiresAt,
                fuerzaBuffExpiresAt,
                celeridadBuffExpiresAt,
                sombraOscuraExpiresAt,
                ojoDemiurgoExpiresAt,
                sigiloAtidorfaExpiresAt
            } = this.state;
            const petBuffs = this.state.activePetBuffs || [];
            // Limpiar buffs expirados (server idealmente manda packet con
            // duration=0 al despawn, pero por timing edge cases hay que
            // limpiar local también — sino el icon queda colgado).
            const stillAlive = petBuffs.filter(p => p.expiresAt > now);
            if (stillAlive.length !== petBuffs.length) {
                this.setState({ activePetBuffs: stillAlive });
            }
            const anyActive =
                (invisibilityExpiresAt && now < invisibilityExpiresAt) ||
                (fuerzaBuffExpiresAt && now < fuerzaBuffExpiresAt) ||
                (celeridadBuffExpiresAt && now < celeridadBuffExpiresAt) ||
                (sombraOscuraExpiresAt && now < sombraOscuraExpiresAt) ||
                (ojoDemiurgoExpiresAt && now < ojoDemiurgoExpiresAt) ||
                (sigiloAtidorfaExpiresAt && now < sigiloAtidorfaExpiresAt) ||
                stillAlive.length > 0;
            if (!anyActive) {
                this.buffTickerStop();
                return;
            }
            this.setState({ buffTick: now });
        }, 500);
        // Perf 2026-07-07: 250 → 500ms. El setState({buffTick}) re-ejecuta
        // el render monolítico del HUD entero (~3260 líneas) — bajarlo a
        // la mitad = 50% menos re-renders/seg mientras hay buffs activos.
        // Lo visible NO cambia: el timerText muestra SEGUNDOS (cambia a
        // 1Hz, no a 4Hz) y el blink "por expirar" es CSS animation
        // (buffExpiringPulse infinite) que corre sola sin depender del tick.
        // El único costo: el progress bar da pasos de 0.5s en vez de 0.25s
        // — imperceptible.
    };

    buffTickerStop = () => {
        if (this._buffInterval) {
            clearInterval(this._buffInterval);
            this._buffInterval = null;
        }
    };

    componentWillUnmount() {
        this._unmounted = true;
        this.buffTickerStop();
        this.stopLobbyPoll();
        // Ticket #216: clear tick del minimapa.
        document.removeEventListener("mousedown", this.handleDocMouseDown);

        // HIGH-22 fix: remove de los 4 listeners agregados en
        // componentDidMount. Sin esto, cada logout/HMR cycle acumulaba
        // los handlers stuck en `document` → CPU spike + memoria leakeada.
        if (this._handlerMapDimKeyDown) {
            document.removeEventListener("keydown", this._handlerMapDimKeyDown);
        }
        if (this._handlerSpaceBlock) {
            document.removeEventListener("keydown", this._handlerSpaceBlock);
        }
        if (this._handlerArrowScrollBlock) {
            document.removeEventListener(
                "keydown",
                this._handlerArrowScrollBlock
            );
        }
        if (this._handlerFullscreenChange) {
            document.removeEventListener(
                "fullscreenchange",
                this._handlerFullscreenChange
            );
            document.removeEventListener(
                "webkitfullscreenchange",
                this._handlerFullscreenChange
            );
        }
        if (this._handlerBrowserShortcutBlock) {
            document.removeEventListener(
                "keydown",
                this._handlerBrowserShortcutBlock
            );
        }
        if (this._handlerBeforeUnload) {
            window.removeEventListener(
                "beforeunload",
                this._handlerBeforeUnload
            );
        }
        if (this._handlerMapDimKeyUp) {
            document.removeEventListener("keyup", this._handlerMapDimKeyUp);
        }
        if (this._handlerWindowBlur) {
            window.removeEventListener("blur", this._handlerWindowBlur);
        }

        // HIGH-22 fix: engine.destroy() libera los listeners globales del
        // engine (keydown / keyup / visibilitychange). Sin esto, cada
        // engine instance creada por logout/reconnect queda viva en
        // memoria respondiendo a todos los keystrokes.
        try {
            if (this.engine && typeof this.engine.destroy === "function") {
                this.engine.destroy();
            }
        } catch (e) {}

        // Restaurar el overflow del body al salir de /play (sino el lobby
        // queda con scroll lockeado).
        try {
            document.body.style.overflow = this._prevBodyOverflow || "";
            document.documentElement.style.overflow =
                this._prevHtmlOverflow || "";
        } catch (e) {}

        // Ticket #336 polish: cleanup defensivo del cache del PartyPanel.
        // Si Play se desmonta (logout, HMR cycle), liberamos las refs
        // cacheadas para que GC libere los elementos React + augParty
        // cloned. Marginal (~5KB) pero good practice.
        this._partyEl_cached = null;
        this._partyEl_lastParty = null;
        this._partyEl_lastInvite = null;
        this._partyEl_lastMyName = null;
        this._partyEl_lastMapNames = null;
        this._augBaseRef = null;
        this._augRangeFp = null;
        this._augParty = null;
        this._globalEl_cached = null;
        this._globalEl_lastList = null;
        if (this._globalPollInterval) {
            clearInterval(this._globalPollInterval);
            this._globalPollInterval = null;
        }
        this._clanEl_cached = null;
        this._clanEl_lastClan = null;
        this._clanEl_lastInvite = null;
        if (this._musicUnsub) {
            try { this._musicUnsub(); } catch (e) {}
            this._musicUnsub = null;
        }
        // v2026-06-24 leak fix: liberar el AudioContext + visibilitychange
        // listener + currentLoopTimeout del music engine. Sin esto, el
        // singleton conservaba el listener apuntando a una instance huérfana
        // tras un HMR cycle o logout, además de mantener ~50-300MB de
        // AudioBuffers decoded en bufferCache.
        try { musicEngine.destroy(); } catch (e) {}
        try { versionWatch.stop(); } catch (e) {}
        // Liberar Keyboard Lock + remove resize listener del HUD scale.
        try { this.unlockGameplayKeyboard(); } catch (e) {}
        if (this._handlerHudResize) {
            window.removeEventListener("resize", this._handlerHudResize);
            this._handlerHudResize = null;
        }
    }

    async componentDidMount() {
        this._unmounted = false;
        inits.setReact(this);

        // Preload del catálogo de spells (lazy en otros sites, pero acá
        // lo necesitamos para validar minSkill cuando el user castea).
        // Fire-and-forget — si no llega antes del primer cast, server
        // sigue validando (no anti-cheat, solo gate UX).
        if (!this.state.spellsData) {
            fetch("/static/init/spells.json")
                .then(r => r.json())
                .then(data => {
                    if (this._unmounted) return;
                    this.setState({ spellsData: data });
                })
                .catch(() => {});
        }

        // Init sistema de música — la inicialización del AudioContext
        // NO arranca audio (browsers requieren user gesture). Solo prepara
        // el grafo y carga el config. El primer track empieza cuando el
        // user hace click en algo (toggle de música, click en mapa).
        try {
            musicEngine.init().then(() => {
                const { musicEnabled, musicVolume } = this.state;
                musicEngine.setEnabled(!!musicEnabled);
                musicEngine.setVolume(
                    Math.max(0, Math.min(1, musicVolume / 100))
                );
            });
            // Suscripción al cambio de track para refrescar el label
            // "Música actual: ♪ Ullathorpe" del settings panel.
            this._musicUnsub = musicEngine.onTrackChange(name => {
                if (this._unmounted) return;
                this.setState({ currentMusicTrack: name });
            });
        } catch (e) {}

        // Detector de nueva versión: cada 5 min + on tab focus → polleea
        // /static/version.json. Si el buildId cambió desde el load, abre
        // el modal. El user decide cuándo recargar.
        try {
            versionWatch.start(() => {
                if (this._unmounted) return;
                this.setState({ showVersionUpdateModal: true });
            });
        } catch (e) {}

        // Responsive scaling del HUD. El HUD es fixed-size (1091×814 +
        // 132 padding-top + 16 padding-bottom = 1091×962 total). En
        // monitores chicos (laptops 1366×768) o fullscreen con aspect
        // ratio raro, no entra entero → user no podía scroll en
        // fullscreen y se le tapaba el chat / XP bar.
        //
        // Fix: calcular factor = min(vw / hudW, vh / hudH) y aplicar
        // transform: scale(factor) al wrapper. Si factor >= 1, no escala
        // (mantiene tamaño nativo para pixel-perfect en monitores grandes).
        this._handlerHudResize = () => this.recomputeHudScale();
        window.addEventListener("resize", this._handlerHudResize);
        // Initial compute después de un tick para que .outer haya
        // hecho display:flex y el browser tenga las dims correctas.
        setTimeout(() => this.recomputeHudScale(), 50);

        // Ticket #238: outside-click listener attacheado SIEMPRE. El
        // handler internamente checkea si hay algún popover / menu / modal
        // abierto antes de hacer trabajo — en idle es 4-5 comparaciones,
        // negligible. La alternativa de attach/detach en componentDidUpdate
        // dejaba ventanas donde el listener no estaba pegado al momento
        // del click (timing race con setState/render).
        document.addEventListener("mousedown", this.handleDocMouseDown);

        // El HUD del juego es fixed-size (1091×787) y se centra vía
        // table-cell. En pantallas chicas no entra entero → permitimos
        // scroll `auto`: el scrollbar aparece solo si la viewport es
        // menor que el HUD, sino queda invisible.
        try {
            this._prevBodyOverflow = document.body.style.overflow;
            this._prevHtmlOverflow =
                document.documentElement.style.overflow;
            document.body.style.overflow = "auto";
            document.documentElement.style.overflow = "auto";
        } catch (e) {}

        // Kill any leftover <audio> element from previous HMR cycles — Next.js
        // no siempre limpia los audio que estaban reproduciendo cuando el JSX
        // los remueve.
        try {
            document.querySelectorAll("audio").forEach(a => {
                a.pause();
                a.removeAttribute("src");
                a.load();
                if (a.parentNode) a.parentNode.removeChild(a);
            });
        } catch (e) {}

        // Hidratado de bindings + macros. Dos casos:
        //   1) versionMismatch (o sin versión guardada) → reset completo a
        //      los defaults de engine/defaults.js.
        //   2) versión OK → merge: stored overlay sobre defaults. Esto
        //      garantiza que cualquier acción nueva agregada después
        //      (ej: meditar) aparezca con su default aunque el localStorage
        //      del usuario no la tenga.
        const storedVersion = window.localStorage.getItem("keyConfigVersion");
        const versionMismatch = storedVersion !== KEY_CONFIG_VERSION;

        // ─── macros ─────────────────────────────────────────────────────
        // NO hidratamos macros acá — son PER-CHARACTER (key
        // "macros_${nameCharacter}" en localStorage), pero todavía no
        // sabemos el nombre del char (llega después en el packet
        // getMyCharacter). Inicializamos con defaults y la hidratación
        // per-char ocurre en componentDidUpdate cuando llega user data
        // (initializePerCharMacros). Ver tracking en CLAUDE.md → macros.
        const defaultMacros = cloneDefaultMacros();
        this.setState({
            valueKeyMacro: defaultMacros,
            keyCodeMacros: buildKeyCodeMacrosMap(defaultMacros)
        });

        // ─── bindings ───────────────────────────────────────────────────
        // SIEMPRE: defaults como base + stored overlay sobre los keys que
        // el usuario haya personalizado (no-cero). Esto es bidireccional —
        // el handler de keypress lee de state.keyCodeDefault, y el state
        // siempre tiene TODOS los keys (defaults rellenan lo que falta).
        // No hay branching por versión: la "migración" se hace por la
        // existencia o ausencia de keys, no por un string de versión.
        const defaultBindings = cloneDefaultBindings();
        let bindings = Object.assign({}, defaultBindings);
        if (!versionMismatch) {
            const defaultKeysRaw =
                window.localStorage.getItem("defaultKeys");
            const stored = defaultKeysRaw ? JSON.parse(defaultKeysRaw) : {};
            Object.keys(stored).forEach(k => {
                // Solo respetamos override del usuario si tiene un valor
                // válido (no 0, no undefined). Cero significa "limpiado",
                // así que vale el default.
                if (
                    stored[k] &&
                    defaultBindings.hasOwnProperty(k)
                ) {
                    bindings[k] = stored[k];
                }
            });
        }
        await this.setState({
            keyCodeDefault: _.cloneDeep(bindings),
            tmpKeyCodeDefault: _.cloneDeep(bindings)
        });
        try {
            window.localStorage.setItem(
                "defaultKeys",
                JSON.stringify(bindings)
            );
            window.localStorage.setItem(
                "keyConfigVersion",
                KEY_CONFIG_VERSION
            );
        } catch (e) {}

        // Audio: versión independiente de KEY_CONFIG_VERSION. Si bumpeás
        // AUDIO_CONFIG_VERSION, todos los usuarios con valores guardados
        // distintos vuelven a defaults (musicEnabled=true, volúmenes con
        // valores audibles). Sirve para des-mutear usuarios que se quedaron
        // en silencio y no recuerdan cómo reactivarlo.
        const audioVersion = window.localStorage.getItem("audioConfigVersion");
        if (audioVersion !== AUDIO_CONFIG_VERSION) {
            try {
                window.localStorage.setItem("musicEnabled", String(DEFAULT_AUDIO.musicEnabled));
                window.localStorage.setItem("musicVolume", String(DEFAULT_AUDIO.musicVolume));
                window.localStorage.setItem("ambienceVolume", String(DEFAULT_AUDIO.ambienceVolume));
                window.localStorage.setItem("effectsVolume", String(DEFAULT_AUDIO.effectsVolume));
                window.localStorage.setItem("audioConfigVersion", AUDIO_CONFIG_VERSION);
                // Refresh cache del AudioManager — los defaults nuevos
                // recién quedaron en localStorage y el AudioManager se
                // construyó al import del módulo con valores viejos.
                require("../../engine/audio").default.refreshSettings();
            } catch (e) {}
            this.setState({
                musicEnabled: DEFAULT_AUDIO.musicEnabled,
                musicVolume: DEFAULT_AUDIO.musicVolume,
                ambienceVolume: DEFAULT_AUDIO.ambienceVolume,
                effectsVolume: DEFAULT_AUDIO.effectsVolume
            });
        }

        // Asegurarnos que inits están cargados (assets, sprites, etc.)
        // para poder renderear los previews del lobby.
        await inits.initialize();
        this.props.dispatch &&
            this.props.dispatch({ type: "SET_INIT_LOADED", payload: true });

        // Si la cuenta está logueada, fetcheamos sus chars para el lobby
        // y arrancamos el polling para refrescar el badge "Conectado".
        const { account } = this.props;
        if (account && account.accountId) {
            const chars = await this.fetchLobbyCharacters();
            this.startLobbyPoll();
            // Si venimos de createCharacter (flag autoStartChar=1 seteado),
            // saltarse el lobby y entrar directo al juego con el char nuevo.
            this.maybeAutoStartFromFlag(chars);
        }
    }

    // Trae la lista de chars desde el cliente Express (que lee Mongo
    // incluyendo el flag `connected`). Llamado al mount y por el poll.
    // Retorna el array fetcheado para que el caller pueda usarlo
    // sincrónicamente sin esperar a que el setState propague.
    fetchLobbyCharacters = async () => {
        try {
            const chars = await fetchUrl("/characters");
            if (this._unmounted) return [];
            const list = chars || [];
            this.setState({ lobbyCharacters: list });
            return list;
        } catch (e) {
            if (this._unmounted) return [];
            // En error de red dejamos el state como estaba — mejor que
            // limpiar y mostrar "no hay chars" cuando solo se cayó la
            // request del poll.
            return [];
        }
    };

    // ── Lobby drag & drop reordering (ticket #213) ─────────────────
    // Permite arrastrar chars del grid PvE para reordenarlos. Optimistic
    // update local + POST a /api/characters/order para persistir. El
    // server guarda el array de IDs en account.characterOrder y el
    // próximo getCharactersByAccountId va a devolverlos en ese orden.
    onLobbyDragStart = (e, idx) => {
        const ch = (this.state.lobbyCharacters || [])[idx];
        if (!ch) {
            e.preventDefault();
            return;
        }
        e.dataTransfer.effectAllowed = "move";
        try {
            e.dataTransfer.setData("text/plain", String(idx));
        } catch (err) {}
        this.lobbyDragFromIdx = idx;
        this.setState({ lobbyDraggingFrom: idx });
    };

    onLobbyDragOver = (e, idx) => {
        if (
            this.lobbyDragFromIdx != null &&
            this.lobbyDragFromIdx !== idx
        ) {
            e.preventDefault();
            e.dataTransfer.dropEffect = "move";
            if (this.state.lobbyDraggingOver !== idx) {
                this.setState({ lobbyDraggingOver: idx });
            }
        }
    };

    onLobbyDragLeave = () => {
        if (this.state.lobbyDraggingOver !== -1) {
            this.setState({ lobbyDraggingOver: -1 });
        }
    };

    onLobbyDragEnd = () => {
        this.lobbyDragFromIdx = null;
        this.setState({ lobbyDraggingFrom: -1, lobbyDraggingOver: -1 });
    };

    onLobbyDrop = (e, toIdx) => {
        e.preventDefault();
        const fromIdx = this.lobbyDragFromIdx;
        this.lobbyDragFromIdx = null;
        this.setState({ lobbyDraggingFrom: -1, lobbyDraggingOver: -1 });
        if (fromIdx == null || fromIdx === toIdx) return;

        const chars = (this.state.lobbyCharacters || []).slice();
        if (!chars[fromIdx]) return; // sanity — empty drag

        // SWAP semantics (no insert/shift) — drop sobre un slot que ya
        // tiene char intercambia, drop sobre slot vacío mueve. Match con
        // el patrón del inventario para que la UX sea consistente.
        const a = chars[fromIdx];
        const b = chars[toIdx];
        chars[toIdx] = a;
        if (b) {
            chars[fromIdx] = b;
        } else {
            // toIdx era vacío — sacamos del origen sin reemplazar.
            chars[fromIdx] = undefined;
        }
        this.setState({ lobbyCharacters: chars });

        // Persist al server. Mandamos el array DENSO de IDs (sin huecos
        // vacíos) para que se guarde compacto. Los huecos del grid en
        // render se calculan implícitamente del orden.
        const order = chars
            .filter(c => c && c._id)
            .map(c => String(c._id));
        try {
            fetchUrl("/characters/order", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ order })
            });
        } catch (err) {
            // Si falla el POST, el state local sigue con el nuevo orden.
            // Al próximo poll del lobby, el server va a re-mandar el
            // orden VIEJO y se restaurará. Mejor que mostrar error
            // intrusivo en este flow ligero.
        }
    };

    // Auto-start: si llegamos a /play con el flag `autoStartChar=1` en
    // sessionStorage (seteado por createCharacter), entrar directo al juego
    // con el idCharacter persistido en lugar de mostrar el lobby selector.
    // El flag se consume (removeItem) para que no auto-arranque en navegaciones
    // posteriores.
    maybeAutoStartFromFlag = chars => {
        if (typeof window === "undefined") return;
        if (window.sessionStorage.getItem("autoStartChar") !== "1") return;
        window.sessionStorage.removeItem("autoStartChar");
        const idChar = window.sessionStorage.getItem("idCharacter");
        if (!idChar) return;
        const idx = (chars || []).findIndex(c => c && c._id === idChar);
        if (idx === -1) return;
        this.startGameFromLobby(chars[idx], idx);
    };

    // Polling del lobby — refresca chars + flag `connected` cada 5s
    // mientras estés en la pantalla de selección. Cero costo en gameplay
    // porque el interval se mata cuando lobbyMode pasa a false (ver
    // componentDidUpdate y stopLobbyPoll).
    startLobbyPoll = () => {
        if (this._lobbyPollInterval) return;
        this._lobbyPollInterval = setInterval(() => {
            if (!this.state.lobbyMode) {
                this.stopLobbyPoll();
                return;
            }
            this.fetchLobbyCharacters();
        }, 5000);
    };

    stopLobbyPoll = () => {
        if (this._lobbyPollInterval) {
            clearInterval(this._lobbyPollInterval);
            this._lobbyPollInterval = null;
        }
    };

    // Inicialización pesada del engine. Se llama cuando el user clickea
    // "Entrar al juego" en el lobby (no en mount, para no conectar WS
    // antes de que haya un char elegido).
    initGame = async () => {
        this.config = require("../../engine/config").default;
        const Engine = require("../../engine/engine").default;
        const General = require("../../engine/general").default;
        const Messages = require("../../engine/connection/messages").default;
        const Connection = require("../../engine/connection/connection")
            .default;
        const Game = require("../../engine/game").default;
        const User = require("../../engine/user").default;
        const Package = require("../../engine/connection/package").default;

        this.pkg = new Package();
        this.user = new User();

        this.general = new General(this.pkg, this.config);
        // La Guarida del Bardo — el motor de radio (singleton en engine/radio.js)
        // necesita reportar "trackEnded" al server desde el listener de YouTube.
        // Exponemos una ref global para no acoplar general.js al engine.
        if (typeof window !== "undefined") {
            window.__generalRef = this.general;
        }
        this.game = new Game(inits, this.user, this.pkg, this.config, this);
        this.engine = new Engine(
            inits,
            this.user,
            this.pkg,
            this.config,
            this.game,
            this.canvas,
            this
        );
        // Ticket #296: propagar el toggle del state (persisted en
        // localStorage) al engine antes de que arranque el render loop.
        // El engine lo lee por frame en drawGrhCapa para decidir si
        // floorear las coords; el imageSmoothing se setea adentro de
        // initCanvas según el flag.
        this.engine.pixelPerfect = this.state.pixelPerfect !== false;
        this.messages = new Messages(
            this.user,
            this.engine,
            inits,
            this.pkg,
            this.config,
            this.game,
            this
        );
        this.connection = new Connection(
            this.messages,
            this.pkg,
            this.game,
            this.engine,
            this.user,
            this.config,
            this
        );

        this.charKeyCodeDefault();
        await this.engine.initCanvas();
        this.connection.startWebSocket();

        this.setState({ loading: false });

        document.oncontextmenu = e => {
            e.stopPropagation();
            return false;
        };

        // Cuando un mapa (mundi o actual) está abierto y el char se mueve,
        // bajamos opacidad del overlay para que se vea el canvas detrás.
        //
        // Trackeamos teclas de movimiento sostenidas via keydown + keyup
        // (NO confiar en auto-repeat de keydown — el browser espera ~500ms
        // antes de empezar a repetir, y eso causa flicker con un debounce
        // corto). Mientras haya AL MENOS UNA tecla de movimiento sostenida,
        // dim ON. Cuando todas se liberan, debounce de 250ms para quitar
        // el dim (el debounce smoothea cambios de dirección rápidos).
        //
        // Las movement keys en nameKeyCode son flechaArriba/Abajo/Izquierda/
        // Derecha (configurables; defaults W/S/A/D — ver engine/defaults.js).
        // Imperativo via ref + classList — sin setState per-keypress para no
        // triggerear re-render del play monolítico (regla 0).
        this.heldMoveKeys = new Set();
        const isMoveKey = kc => {
            const kcd = this.state.keyCodeDefault || {};
            const nameKC = (this.config && this.config.nameKeyCode) || {};
            return (
                kc === kcd[nameKC.flechaArriba] ||
                kc === kcd[nameKC.flechaAbajo] ||
                kc === kcd[nameKC.flechaIzquierda] ||
                kc === kcd[nameKC.flechaDerecha]
            );
        };
        const isMapOpen = () =>
            this.state.showWorldMap || this.state.showFullMap;
        const applyDim = () => {
            const el = this.mapOverlayRef;
            if (!el) return;
            el.classList.add(style.mapOverlayDimmed);
            if (this.mapMoveDebounce) {
                clearTimeout(this.mapMoveDebounce);
                this.mapMoveDebounce = null;
            }
        };
        const scheduleDimRelease = () => {
            if (this.mapMoveDebounce) clearTimeout(this.mapMoveDebounce);
            this.mapMoveDebounce = setTimeout(() => {
                if (this.mapOverlayRef) {
                    this.mapOverlayRef.classList.remove(
                        style.mapOverlayDimmed
                    );
                }
                this.mapMoveDebounce = null;
            }, 250);
        };
        // HIGH-22 fix: guardamos los handlers como `this._foo` para poder
        // removerlos en `componentWillUnmount`. Antes eran anónimos y
        // quedaban stuck en `document` después de cada logout/HMR cycle,
        // acumulando varias copias por keypress → CPU spike + memoria
        // que no se libera. Las helpers `isMoveKey`/`isMapOpen`/etc viven
        // en el closure de `componentDidMount` y quedan capturadas por los
        // handlers — siguen funcionando igual que antes.
        this._handlerMapDimKeyDown = e => {
            if (!isMapOpen()) return;
            if (this.state.showInputText) return;
            if (!isMoveKey(e.keyCode)) return;
            // Si ya estaba en el set (auto-repeat), nada nuevo que hacer.
            if (this.heldMoveKeys.has(e.keyCode)) return;
            this.heldMoveKeys.add(e.keyCode);
            applyDim();
        };
        document.addEventListener("keydown", this._handlerMapDimKeyDown);

        // Bloquear el scroll default de Space en /play. Si el user tiene
        // foco en un elemento scrolleable (ej. la lista de hechizos), el
        // browser por default scrollea esa lista al apretar Space. Como
        // Space no está bindeada a ninguna acción del juego, prevenirla
        // siempre (excepto cuando se está tipeando en el chat).
        this._handlerSpaceBlock = e => {
            if (e.keyCode !== 32) return;
            if (this.state.showInputText) return;
            // Si el target es un input/textarea, dejar pasar (chat externo,
            // search box, etc. — aunque hoy no hay otros).
            const tag = e.target && e.target.tagName;
            if (tag === "INPUT" || tag === "TEXTAREA") return;
            e.preventDefault();
        };
        document.addEventListener("keydown", this._handlerSpaceBlock);

        // Mismo problema con ↑/↓: el browser las usa para scrollear la
        // página. Como el HUD a veces es más alto que el viewport (modales,
        // tooltips, etc.), apretar ↑/↓ para caminar también desplazaba la
        // página entera. Las flechas SÍ están bindeadas a movimiento por
        // default, pero preventDefault solo bloquea la acción default del
        // browser — el game key handler igual procesa la tecla.
        this._handlerArrowScrollBlock = e => {
            // Ticket #329: bloquear las 4 flechas (37 left, 38 up, 39 right,
            // 40 down). En fullscreen el viewport puede ser más chico que el
            // HUD y horizontal scroll también desplaza la pantalla.
            if (e.keyCode < 37 || e.keyCode > 40) return;
            if (this.state.showInputText) return;
            const tag = e.target && e.target.tagName;
            if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return;
            e.preventDefault();
        };
        document.addEventListener("keydown", this._handlerArrowScrollBlock);

        // Fullscreen sync: el user puede salir con Esc (no pasa por nuestro
        // toggle), así que escuchamos el evento del browser para mantener
        // el estado consistente con el ícono del botón.
        this._handlerFullscreenChange = () => {
            const fs = !!(
                document.fullscreenElement ||
                document.webkitFullscreenElement ||
                document.msFullscreenElement
            );
            // Lockear/unlockear shortcuts del browser según el estado FS.
            // Cubre el caso "user sale con Esc/F11 sin pasar por toggleFullscreen".
            if (fs) {
                this.lockGameplayKeyboard();
            } else {
                this.unlockGameplayKeyboard();
            }
            // Recompute HUD scale — fullscreen cambia el viewport disponible.
            this.recomputeHudScale();
            if (this.state.isFullscreen !== fs) {
                this.setState({ isFullscreen: fs });
            }
        };
        document.addEventListener(
            "fullscreenchange",
            this._handlerFullscreenChange
        );
        document.addEventListener(
            "webkitfullscreenchange",
            this._handlerFullscreenChange
        );

        // Bloquear shortcuts del browser que interfieren con el gameplay.
        // El user puede tener Ctrl bindeado a "atacar" (default), así que
        // Ctrl+W para atacar+caminar arriba dispara accidentalmente
        // "cerrar tab" en el browser. Mismo problema con Ctrl+1..9
        // (cambiar de tab) que choca con los macros 1..9.
        //
        // Caveat: Ctrl+W, Ctrl+T, Ctrl+N son INBLOCKABLES en navegadores
        // modernos por política de seguridad — el browser los intercepta
        // antes que el JS. Para esos sólo queda beforeunload (abajo).
        this._handlerBrowserShortcutBlock = e => {
            // Si está tipeando en chat o en cualquier input, dejar pasar
            // (Ctrl+C/V/X para copy/paste tiene que andar).
            if (this.state.showInputText) return;
            const tag = e.target && e.target.tagName;
            if (tag === "INPUT" || tag === "TEXTAREA") return;

            const kc = e.keyCode;
            // Ctrl + número (1..9, 0): el browser cambia de tab — choca
            // con macros 1..9. Bloqueamos. Ctrl+0 también porque resetea
            // zoom y choca con el macro 0 si lo agregamos a futuro.
            if (e.ctrlKey && !e.altKey && kc >= 48 && kc <= 57) {
                e.preventDefault();
                return;
            }
            // F5 / Ctrl + R / Ctrl + Shift + R: refresh. NO bloqueamos —
            // a veces el user legítimamente quiere refrescar (post-deploy,
            // debugging, etc.). El beforeunload de abajo igual le pide
            // confirmación si está jugando.
            // Ctrl + S: save page. Dialog molesto en medio del juego.
            if (e.ctrlKey && kc === 83) {
                e.preventDefault();
                return;
            }
            // Ctrl + P: print. Mismo.
            if (e.ctrlKey && kc === 80) {
                e.preventDefault();
                return;
            }
            // Ctrl + F: find. Abre la búsqueda del browser sobre el HUD.
            if (e.ctrlKey && kc === 70) {
                e.preventDefault();
                return;
            }
            // F1: help del browser.
            if (kc === 112) {
                e.preventDefault();
                return;
            }
            // Backspace fuera de input: navegar atrás en algunos browsers.
            if (kc === 8) {
                e.preventDefault();
                return;
            }
        };
        document.addEventListener("keydown", this._handlerBrowserShortcutBlock);

        // beforeunload: red de seguridad para Ctrl+W / Ctrl+T / Ctrl+N
        // que no podemos preventDefault. Muestra el prompt del browser
        // pidiendo confirmación antes de cerrar la tab. Solo se muestra
        // si el char está conectado (this.user.id != null) — no molesta
        // en el lobby.
        this._handlerBeforeUnload = e => {
            if (!this.user || !this.user.id) return;
            // Ticket #259: garantizar persist de macros antes de cerrar tab.
            // Un POST normal puede no completarse antes del unload (browser
            // cancela). `sendBeacon` es la API estándar para esto — POST
            // síncrono "fire and forget" que el browser garantiza enviar
            // aunque la página se cierre. Sin esto, si el user configura
            // macros y cierra el tab rápido, el POST async no llega a Mongo
            // y los macros se pierden.
            try {
                const idCharacter =
                    window.sessionStorage.getItem("idCharacter");
                if (
                    idCharacter &&
                    Array.isArray(this.state.valueKeyMacro) &&
                    typeof navigator !== "undefined" &&
                    navigator.sendBeacon
                ) {
                    const payload = JSON.stringify({
                        macros: this.state.valueKeyMacro
                    });
                    const blob = new Blob([payload], {
                        type: "application/json"
                    });
                    navigator.sendBeacon(
                        "/api/character/" +
                            encodeURIComponent(idCharacter) +
                            "/macros",
                        blob
                    );
                }
            } catch (e) {}
            // Modern browsers ignoran el mensaje custom — muestran su
            // propio prompt genérico — pero igual el returnValue activa
            // el dialog.
            e.preventDefault();
            e.returnValue = "";
            return "";
        };
        window.addEventListener("beforeunload", this._handlerBeforeUnload);

        this._handlerMapDimKeyUp = e => {
            if (!this.heldMoveKeys.has(e.keyCode)) return;
            this.heldMoveKeys.delete(e.keyCode);
            // Si todavía hay otra tecla de movimiento sostenida (cambio de
            // dirección sin soltar la anterior, o WASD diagonal), dim sigue
            // ON. Solo cuando todas se liberan, arrancamos el debounce.
            if (this.heldMoveKeys.size === 0) scheduleDimRelease();
        };
        document.addEventListener("keyup", this._handlerMapDimKeyUp);

        // Si el window pierde foco (Alt+Tab, click afuera), keyup puede no
        // dispararse y las teclas quedarían "sostenidas" infinito → mapa
        // dimeado permanente. Limpiamos el set al perder foco.
        this._handlerWindowBlur = () => {
            if (this.heldMoveKeys.size === 0) return;
            this.heldMoveKeys.clear();
            scheduleDimRelease();
        };
        window.addEventListener("blur", this._handlerWindowBlur);

        document.onkeyup = e => {
            const {
                selectItem,
                showInputText,
                textDialog,
                showMacroConfig,
                valueKeyMacro,
                keyCodeMacros
            } = this.state;
            const keyCode = e.keyCode;

            // Si el foco está en un input/textarea/contenteditable que NO
            // sea el chat del juego, skipear el handler entero. Sino las
            // teclas mientras tipeás (ej. en el modal de party invite) se
            // interpretan como acciones del juego (T tira, W camina, etc).
            //
            // El chat del juego ya tiene gating por `showInputText` en cada
            // action — lo detectamos por `data-game-key="true"` en el input
            // y NO early-returnamos para que Enter siga funcionando.
            const ae = document.activeElement;
            if (
                ae &&
                (ae.tagName === "INPUT" ||
                    ae.tagName === "TEXTAREA" ||
                    ae.isContentEditable)
            ) {
                const isGameChat =
                    ae.dataset && ae.dataset.gameKey === "true";
                if (!isGameChat) {
                    return;
                }
            }

            // ── Spell key capture (ticket #207) ────────────────────
            // Ticket #273: ahora ROBA la tecla en lugar de rechazar. Si
            // colisiona con acción/macro/item/otro spell, el slot anterior
            // queda limpio. El último bind gana — convención unificada.
            if (this.state.capturingSpellKey && keyCode !== 27) {
                const cap = this.state.capturingSpellKey;
                const used = this.getKeyInUse(keyCode, {
                    excludeSpellId: cap.idSpell
                });
                if (used) {
                    this.stealKeyFromAllSlots(keyCode, {
                        spellId: cap.idSpell,
                        includeAction: true
                    });
                }
                this.setSpellKeybinding(cap.idSpell, keyCode);
                this.setState({
                    capturingSpellKey: null,
                    spellKeyCaptureError: ""
                });
                return;
            }

            // ── Inventory key capture (ticket #208) ────────────────
            // Ticket #273: ahora ROBA la tecla de cualquier source (antes
            // sólo robaba de OTRO item, rechazaba el resto).
            if (this.state.capturingItemKey && keyCode !== 27) {
                const cap = this.state.capturingItemKey;
                const used = this.getKeyInUse(keyCode, {
                    excludeItemId: cap.idItem
                });
                if (used) {
                    this.stealKeyFromAllSlots(keyCode, {
                        itemId: cap.idItem,
                        includeAction: true
                    });
                }
                this.setInventoryKeybinding(cap.idItem, keyCode);
                this.setState({
                    capturingItemKey: null,
                    itemKeyCaptureError: ""
                });
                return;
            }

            // CRIT-26: ESC cascade. Antes hacía `doLogout()` directo desde
            // CUALQUIER estado — abrir banco / trade / macro modal y
            // apretar ESC te tiraba del juego. Ahora cierra el más-efímero
            // primero (aim mode → context menu → modales → maps → chat
            // input) y solo si NADA está abierto, recién entonces logout.
            // Equivalente al back button del browser.
            if (keyCode === 27) {
                // Spell key capture modal (ticket #207) — ESC cancela
                // antes que cualquier otro modal abierto.
                if (this.state.capturingSpellKey) {
                    this.cancelSpellKeyCapture();
                    return;
                }
                if (this.state.spellContextMenu) {
                    this.setState({ spellContextMenu: null });
                    return;
                }
                // Inventory key capture (ticket #208).
                if (this.state.capturingItemKey) {
                    this.cancelItemKeyCapture();
                    return;
                }
                if (this.state.inventoryContextMenu) {
                    this.setState({ inventoryContextMenu: null });
                    return;
                }
                if (this.state.petModeMenu) {
                    this.setState({ petModeMenu: null });
                    return;
                }
                // Aim mode (spell o item seleccionado para usar)
                if (this.config.hechizoIdSpell || this.config.itemSelected) {
                    this.config.hechizoIdSpell = 0;
                    this.config.itemSelected = 0;
                    this.setState({ crosshair: false });
                    return;
                }
                if (this.state.contextMenu) {
                    this.setState({ contextMenu: null });
                    return;
                }
                if (this.state.partyContextMenu) {
                    this.setState({ partyContextMenu: null });
                    return;
                }
                if (this.state.globalContextMenu) {
                    this.setState({ globalContextMenu: null });
                    return;
                }
                if (this.state.clanContextMenu) {
                    this.setState({ clanContextMenu: null });
                    return;
                }
                if (this.state.npcInspect) {
                    this.setState({ npcInspect: null });
                    return;
                }
                if (this.state.userInspect) {
                    this.closeUserInspect();
                    return;
                }
                if (this.state.selfInspect) {
                    this.closeSelfInspect();
                    return;
                }
                if (this.state.showModalBank) {
                    this.closeModalBank();
                    return;
                }
                if (this.state.showModalTrade) {
                    this.closeModalTrade();
                    return;
                }
                if (this.state.showModalControlPanel) {
                    this.setState({ showModalControlPanel: false });
                    return;
                }
                if (this.state.showMacroConfig) {
                    // Bug fix: antes reseteaba `valueKeyMacro: ""` (el
                    // ARRAY de macros), lo que rompía renderBoxMacros al
                    // siguiente render porque iteraba sobre undefined.
                    // Lo correcto es resetear `keyMacro` (la edición en
                    // progreso) o simplemente cerrar el modal sin tocar
                    // nada — el state de la edición se descarta por la
                    // próxima apertura del modal.
                    this.setState({
                        showMacroConfig: false
                    });
                    return;
                }
                if (this.state.showWorldMap) {
                    this.setState({ showWorldMap: false });
                    return;
                }
                if (this.state.showFullMap) {
                    this.setState({ showFullMap: false });
                    return;
                }
                if (this.state.showInputText) {
                    // Chat input — cancelar sin enviar
                    this.setState({
                        textDialog: "",
                        showInputText: false
                    });
                    return;
                }
                // Ticket #214: si el modal de confirmación de salida ya
                // está abierto, ESC lo cancela (no re-dispara, no logout).
                if (this.state.showExitConfirmModal) {
                    this.setState({ showExitConfirmModal: false });
                    return;
                }
                // Nada abierto → abrir confirm modal en vez de logout
                // directo. Antes ESC tiraba al lobby instant; ahora
                // requiere confirmación explícita (ticket #214).
                this.setState({ showExitConfirmModal: true });
                return;
            }

            if (showMacroConfig) return;

            if (!showInputText && !isNaN(parseInt(keyCodeMacros[keyCode]))) {
                this.triggerMacro(keyCodeMacros[keyCode]);
                return;
            }

            // ── Spell keybindings (ticket #207) ────────────────────
            // Si esta tecla está asignada a un hechizo via spellKeybindings,
            // disparar el cast (mismo flow que doble-click en la spell row:
            // selectSpell hace que el próximo click sobre canvas castee
            // sobre ese target). Resolvemos idPos por idSpell canónico —
            // si el slot driftéo por reorder, igual encuentra el spell.
            if (!showInputText) {
                const bindings = this.state.spellKeybindings || {};
                let castIdSpell = null;
                for (const idSpell of Object.keys(bindings)) {
                    if (Number(bindings[idSpell]) === keyCode) {
                        castIdSpell = idSpell;
                        break;
                    }
                }
                if (castIdSpell) {
                    const currentIdPos = this.findIdPosByIdSpell(
                        Number(castIdSpell)
                    );
                    if (currentIdPos) {
                        this.selectSpell(currentIdPos);
                    }
                    return;
                }
            }

            // ── Inventory keybindings (ticket #208) ────────────────
            // Si la tecla está asignada a un idItem del inv, resolver el
            // slot actual (idItem canónico) y delegar a useOrEquipItem
            // que dispatcha use/equip según objType.
            if (!showInputText) {
                const invBindings = this.state.inventoryKeybindings || {};
                let useIdItem = null;
                for (const idItem of Object.keys(invBindings)) {
                    if (Number(invBindings[idItem]) === keyCode) {
                        useIdItem = Number(idItem);
                        break;
                    }
                }
                if (useIdItem) {
                    const slot = this.findIdPosByIdItem(useIdItem);
                    if (slot) {
                        this.useOrEquipItem(slot);
                    }
                    return;
                }
            }

            //Usar
            if (
                keyCode ==
                    this.state.keyCodeDefault[this.config.nameKeyCode.usar] &&
                !showInputText
            ) {
                if (selectItem) this.game.useItem(selectItem);
            }

            //Equipar
            if (
                keyCode ==
                    this.state.keyCodeDefault[
                        this.config.nameKeyCode.equipar
                    ] &&
                !showInputText
            ) {
                const item = this.user.items[selectItem];

                if (selectItem && item)
                    this.game.equiparItem(selectItem, item.idItem);
            }

            //Agarrar
            if (
                keyCode ==
                    this.state.keyCodeDefault[
                        this.config.nameKeyCode.agarrar
                    ] &&
                !showInputText
            ) {
                this.pkg.setPackageID(this.pkg.serverPacketID.agarrarItem);
                this.config.ws.send(this.pkg.dataSend());
            }

            //Tirar
            if (
                keyCode ==
                    this.state.keyCodeDefault[this.config.nameKeyCode.tirar] &&
                !showInputText
            ) {
                if (selectItem) {
                    const item = this.user.items[selectItem];
                    const maxCant = item ? item.cant || 1 : 1;
                    // Reemplazo de prompt() (Next.js lo bloquea en dev).
                    // Abrimos modal moderno con input numérico.
                    this.setState({
                        showDropItemModal: true,
                        dropItemPos: selectItem,
                        dropItemMaxCant: maxCant,
                        dropItemValue: String(maxCant)
                    });
                } else {
                    // Sin item seleccionado: comportamiento histórico — tirar 1.
                    this.pkg.setPackageID(this.pkg.serverPacketID.tirarItem);
                    this.pkg.writeInt(0);
                    this.pkg.writeShort(1);
                    this.config.ws.send(this.pkg.dataSend());
                }
            }

            //Enter
            if (keyCode == 13) {
                if (showInputText) {
                    // ── CLOSING (mandando el msg) ────────────────────────
                    // Interceptar comandos locales (no se envían al server).
                    const cmd = (textDialog || "").trim().toLowerCase();
                    if (cmd === "/salir" || cmd === "/quit" || cmd === "/exit") {
                        this.setState({
                            textDialog: "",
                            showInputText: false
                        });
                        this.doLogout();
                        return;
                    }
                    // La Guarida del Bardo — toggle personal de recepción.
                    // "/radio" solo (sin args) = client-side toggle.
                    // "/radio <url>" (con args) va al server (GM check).
                    if (cmd === "/radio") {
                        try {
                            const radioEngine = require("../../engine/radio").default;
                            radioEngine.toggleEnabled().then((enabled) => {
                                if (enabled) {
                                    if (radioEngine.hasTrack()) {
                                        this.game.writeConsole(
                                            "♫ Sintonizado a La Guarida del Bardo. La música del mapa se silencia.",
                                            "#c88a4a"
                                        );
                                    } else {
                                        this.game.writeConsole(
                                            "♫ Sintonía activada — La Guarida del Bardo está en silencio. Esperá que un GM pase algo.",
                                            "#c88a4a"
                                        );
                                    }
                                } else {
                                    this.game.writeConsole(
                                        "♫ Sintonía cortada. Volvés a escuchar la música del mapa.",
                                        "#888"
                                    );
                                }
                            });
                        } catch (e) {}
                        this.setState({
                            textDialog: "",
                            showInputText: false
                        });
                        return;
                    }

                    // Ticket #302: comando /r (reply) — autocompleta a
                    // `/w <lastWhisperFrom> <resto>` al enviar, SIN mostrar
                    // jamás `/w <name>` en el input del user. El user solo
                    // tipea `/r texto` y se manda al sender del último
                    // whisper recibido. `/r` solo (sin texto) → ignorado
                    // (cierra el chat, no envía ni pre-rellena nada — el
                    // user no quiere ver autocomplete visible).
                    let dialogToSend = textDialog;
                    const trimmed = (textDialog || "").trim();
                    const isReplyOnly = trimmed.toLowerCase() === "/r";
                    const isReplyWithText = /^\/r\s+(.*)$/i.test(trimmed);
                    if (isReplyOnly) {
                        // Cerrar input sin enviar. Sin feedback ruidoso.
                        this.setState({
                            textDialog: "",
                            showInputText: false
                        });
                        return;
                    }
                    if (isReplyWithText) {
                        const target = this.state.lastWhisperFrom;
                        if (!target) {
                            if (
                                this.game &&
                                typeof this.game.writeConsole === "function"
                            ) {
                                this.game.writeConsole(
                                    "Aún no recibiste ningún susurro al cual responder.",
                                    "#FF6464",
                                    0,
                                    0
                                );
                            }
                            this.setState({
                                textDialog: "",
                                showInputText: false
                            });
                            return;
                        }
                        const hasSpaces = /\s/.test(target);
                        const namePart = hasSpaces
                            ? '"' + target + '"'
                            : target;
                        const rest = trimmed.match(/^\/r\s+(.*)$/i)[1];
                        dialogToSend = "/w " + namePart + " " + rest;
                    }

                    this.general.sendDialog(dialogToSend);

                    // Si el msg que mando era un /w válido, guardo el prefix
                    // para auto-rellenar la próxima vez. Sino limpio el state
                    // (vuelve a abrir vacío).
                    const nextPrefix = extractWhisperPrefix(dialogToSend);

                    // Ticket #306: push al history para que flecha arriba
                    // lo recupere. Guardamos el textDialog ORIGINAL (lo que
                    // el user tipeó, no el dialogToSend autocompletado de
                    // /r) — al apretar arriba, el user ve exactamente lo
                    // que escribió. Dedup si es igual al [0] anterior +
                    // cap de 30 (suficiente sin gastar memoria).
                    const rawSent = textDialog;
                    let nextHistory = this.state.chatHistory || [];
                    if (rawSent && rawSent.trim()) {
                        if (nextHistory[0] !== rawSent) {
                            nextHistory = [rawSent, ...nextHistory].slice(0, 30);
                        }
                    }

                    this.setState({
                        textDialog: "",
                        lastWhisperTarget: nextPrefix,
                        showInputText: false,
                        chatHistory: nextHistory,
                        chatHistoryIndex: -1
                    });
                } else {
                    // ── OPENING ──────────────────────────────────────────
                    // Pre-rellena solo con lastWhisperTarget (vos mandaste
                    // un /w por última vez → conversación activa). Para
                    // responder a alguien que TE susurró está el comando
                    // /r — no auto-rellenamos con `/w <sender>` porque
                    // eso es invasivo si querés hablar público después
                    // de recibir un susurro (#306b).
                    const prefix = this.state.lastWhisperTarget || "";
                    this.setState({
                        textDialog: prefix,
                        showInputText: true
                    });
                }
            }

            // Meditar — usa el binding configurable. Default C, cambiable
            // desde el modal de configurar teclas como cualquier otra acción.
            // Fallback a DEFAULT_KEY_BINDINGS si por algún motivo el state
            // del usuario quedó sin meditar (migración rota, cache, etc) —
            // así NUNCA queda mudo: si no hay binding, vale el default.
            const meditarKey =
                this.state.keyCodeDefault[
                    this.config.nameKeyCode.meditar
                ] ||
                DEFAULT_KEY_BINDINGS[this.config.nameKeyCode.meditar];
            if (!showInputText && keyCode == meditarKey) {
                this.general.sendDialog("/meditar");
            }

            // Ocultarse (#239) — skill manual con chance roll. Default O.
            // Spameable: el server enforça min interval interno (600ms).
            const ocultarKey =
                this.state.keyCodeDefault[
                    this.config.nameKeyCode.ocultar
                ] ||
                DEFAULT_KEY_BINDINGS[this.config.nameKeyCode.ocultar];
            if (!showInputText && keyCode == ocultarKey) {
                this.general.sendDialog("/ocultar");
            }

            // Ticket #287: refresh position — resync del sprite del char
            // con la pos canonical del server. Útil cuando el sprite se
            // bugea visualmente (predictive move rechazado, packet perdido).
            // Default P (80). Throttle local 500ms para evitar spam si el
            // user pisa la tecla.
            const refreshKey =
                this.state.keyCodeDefault[
                    this.config.nameKeyCode.refreshPosition
                ] ||
                DEFAULT_KEY_BINDINGS[this.config.nameKeyCode.refreshPosition];
            if (!showInputText && keyCode == refreshKey) {
                const now = Date.now();
                if (!this._lastRefreshPos || now - this._lastRefreshPos > 500) {
                    this._lastRefreshPos = now;
                    this.sendRefreshPosition();
                }
            }

            if (
                keyCode ==
                    this.state.keyCodeDefault[this.config.nameKeyCode.seguro] &&
                !showInputText
            ) {
                if (this.config.seguroActivado) {
                    this.config.seguroActivado = false;
                } else {
                    this.config.seguroActivado = true;
                }

                this.pkg.setPackageID(this.pkg.serverPacketID.changeSeguro);
                this.config.ws.send(this.pkg.dataSend());
            }

            // Tecla M — schema simplificado de 3 estados:
            //   estado 0 (nada abierto)               → estado 1 (mapamundi)
            //   estado 1 (mapamundi)                  → estado 0 (cierra)
            //   estado 2 (mapa específico seleccionado del mapamundi)
            //                                          → estado 0 (cierra)
            //
            // Transiciones:
            //   - Estado 0 + M       → Estado 1 (abre mapamundi)
            //   - Estado 1 + click   → Estado 2 (mapa específico)
            //   - Estado 1 + M       → Estado 0 (cierra)
            //   - Estado 2 + M       → Estado 0 (cierra)
            //   - Estado 2 + right   → Estado 1 (vuelve al mapamundi) — handler aparte
            //
            // Gateado por showInputText para no triggerear cuando escribís.
            if (keyCode === 77 && !showInputText) {
                const { showWorldMap, showFullMap } = this.state;
                if (showWorldMap || showFullMap) {
                    // Cualquier estado abierto (1 o 2) → 0 (cierra todo).
                    this.setState({
                        showWorldMap: false,
                        showFullMap: false,
                        selectedMapToShow: null
                    });
                } else {
                    // Estado 0 → 1 (abre mapamundi).
                    this.openWorldMap();
                }
            }
            if (
                keyCode ==
                this.state.keyCodeDefault[this.config.nameKeyCode.atacar]
            ) {
                if (
                    +Date.now() - this.config.timeHitStart >
                    this.config.intervalHit
                ) {
                    this.engine.hit();
                }
            }

            event = event || window.event;
            if (event.ctrlKey) {
                const c = event.which || keyCode;
                switch (c) {
                    case 83:
                    case 87:
                    case 68:
                        event.preventDefault();
                        event.stopPropagation();
                        break;
                }
            }
        };
    }

    // Ticket #283 + #305: tabs del chat — 3 botones encima del console.
    //   - Cambia tab activa y resetea su unread badge.
    //   - Persiste la tab elegida en localStorage (sobrevive reload).
    //   - Recuerda la scroll position por tab — al volver a una tab donde
    //     estabas scrolleando arriba, se restaura la pos (no te tira al
    //     fondo). Primera entrada de cada tab → fondo (default -1).
    setChatTab = tab => {
        if (tab !== "general" && tab !== "chat" && tab !== "combat") return;
        // Guardar la scroll position de la tab ACTUAL antes de cambiar.
        let savedScroll = null;
        try {
            const el = this.refs && this.refs.console;
            if (el) savedScroll = el.scrollTop;
        } catch (e) {}
        const fromTab = this.state.chatTab;

        this.setState(prev => {
            const empty = { chat: { count: 0, color: null }, combat: { count: 0, color: null } };
            const nextUnread = { ...(prev.unreadByTab || empty) };
            if (tab !== "general") {
                nextUnread[tab] = { count: 0, color: null };
            }
            const nextScroll = { ...(prev.scrollByTab || {}) };
            if (savedScroll !== null && fromTab) {
                nextScroll[fromTab] = savedScroll;
            }
            return {
                chatTab: tab,
                unreadByTab: nextUnread,
                chatScrollPending: 0,
                scrollByTab: nextScroll
            };
        }, () => {
            // Persist en localStorage (sobrevive reload).
            try {
                localStorage.setItem("argentum:chatTab", tab);
            } catch (e) {}
            // Restaurar scroll de la tab NUEVA. Si nunca la visitamos
            // (valor -1), arrancamos al fondo (último msg).
            try {
                const el = this.refs && this.refs.console;
                if (!el) return;
                const sb = this.state.scrollByTab || {};
                const stored = sb[tab];
                if (stored == null || stored === -1) {
                    el.scrollTop = el.scrollHeight;
                } else {
                    // Clamp para no quedar fuera del rango si el contenido
                    // cambió de tamaño desde la última visita.
                    const max = el.scrollHeight - el.clientHeight;
                    el.scrollTop = Math.max(0, Math.min(stored, max));
                }
            } catch (e) {}
        });
    };

    // Ticket #300: lee del bucket per-tab directamente. Cada tab tiene
    // su propio array — sin filter en cada render. General = stream
    // completo (todas las categorías incluyendo system).
    getFilteredConsoleMsgs = () => {
        const tab = this.state.chatTab || "general";
        const buckets = this.state.messagesByTab;
        if (buckets && buckets[tab]) {
            return buckets[tab].map(m =>
                m && m.content !== undefined ? m.content : m
            );
        }
        // Fallback al state viejo (writeConsole legacy o tests sin
        // messagesByTab inicializado).
        const msgs = this.state.messagesConsole || [];
        return msgs.map(m => (m && m.content !== undefined ? m.content : m));
    };

    // Ticket #300: handler del scroll del console. Cuando el user
    // scrollea hasta el fondo manualmente, resetea el counter de
    // "↓ N mensajes nuevos". Throttled vía rAF para no spammear setState.
    onChatScroll = () => {
        if (this._onChatScrollFrame) return;
        this._onChatScrollFrame = requestAnimationFrame(() => {
            this._onChatScrollFrame = 0;
            const el = this.refs && this.refs.console;
            if (!el) return;
            const distFromBottom =
                el.scrollHeight - el.clientHeight - el.scrollTop;
            if (distFromBottom <= 28 && this.state.chatScrollPending > 0) {
                this.setState({ chatScrollPending: 0 });
            }
        });
    };

    // Click del pill "↓ N nuevos" → forzar scroll al fondo + reset counter.
    scrollChatToBottom = () => {
        const el = this.refs && this.refs.console;
        if (!el) return;
        el.scrollTop = el.scrollHeight - el.clientHeight;
        if (this.state.chatScrollPending > 0) {
            this.setState({ chatScrollPending: 0 });
        }
    };

    // Header de tabs: 3 pills minimalistas con badges. Estilo Atrium —
    // tab activa con underline dorado, inactivas tono stone apagado.
    // Chat con accent cálido (#ec9a44) para invitar a leer + ser social.
    renderChatTabs = () => {
        const tab = this.state.chatTab || "general";
        const emptyUnread = { chat: { count: 0, color: null }, combat: { count: 0, color: null } };
        const unread = this.state.unreadByTab || emptyUnread;
        // Picker de texto blanco / negro según luma del background del
        // badge. Threshold ~140 — colores cálidos claros (ámbar, lila)
        // van con texto negro, oscuros (verde GM, rojos combat) con
        // blanco. Asegura legibilidad sin ojo manual.
        const pickTextOn = bg => {
            let s = String(bg || "").replace("#", "").toLowerCase();
            if (s === "red") return "#fff";
            if (s.length === 3) s = s.split("").map(x => x + x).join("");
            if (s.length !== 6) return "#fff";
            const r = parseInt(s.slice(0, 2), 16);
            const g = parseInt(s.slice(2, 4), 16);
            const b = parseInt(s.slice(4, 6), 16);
            const luma = 0.299 * r + 0.587 * g + 0.114 * b;
            return luma > 140 ? "#0d0d0d" : "#fff";
        };
        const Tab = ({ id, label, accent }) => {
            // Backward-compat: si el state cached era shape viejo (number)
            // lo lifteamos a {count, color: null} antes de leer .count.
            let bucket = null;
            if (id !== "general") {
                const raw = unread[id];
                if (typeof raw === "object" && raw) bucket = raw;
                else bucket = { count: raw || 0, color: null };
            }
            // Ticket #283: el tab Combate NO muestra badge — el flujo de
            // daños es continuo y la burbuja roja resultaba ruidosa. Chat
            // sigue mostrando badge porque whisper / GM / [Servidor] sí
            // son notificaciones que el user puede perderse.
            const showBadge = id !== "combat" && bucket && bucket.count > 0 && tab !== id;
            // Color del badge = el del último msg que lo disparó. Si no
            // hay color (caso edge), cae al gradient default de la clase.
            const badgeStyle = showBadge && bucket.color
                ? { background: bucket.color, color: pickTextOn(bucket.color) }
                : undefined;
            return (
                <button
                    type="button"
                    className={`${style.chatTab} ${tab === id ? style.chatTabActive : ""} ${accent ? style[accent] : ""}`}
                    onClick={() => this.setChatTab(id)}
                    title={
                        id === "general" ? "Todos los mensajes" :
                        id === "chat" ? "Mensajes de personajes (whispers, server, público)" :
                        "Daños, hits y kills"
                    }
                >
                    <span className={style.chatTabLabel}>{label}</span>
                    {showBadge && (
                        <span className={style.chatTabBadge} style={badgeStyle}>
                            {bucket.count > 99 ? "99+" : bucket.count}
                        </span>
                    )}
                </button>
            );
        };
        return (
            <div className={style.chatTabs}>
                <Tab id="general" label="General" />
                <Tab id="combat" label="Combate" accent="chatTabCombatAccent" />
                <Tab id="chat" label="Chat" accent="chatTabChatAccent" />
            </div>
        );
    };

    // Llamado desde writeConsole cuando el user clickea un [Name] en el chat.
    // Abre el input con `/w <name> ` pre-rellenado (o `/w "name con espacios" `
    // si el nombre tiene espacios). El nombre viene del display name visible
    // entre [...] en el msg de chat.
    openWhisperTo = name => {
        if (!name || typeof name !== "string") return;
        const trimmed = name.trim();
        if (!trimmed) return;

        // Si el nombre tiene espacios, ponerlo entre comillas para que el
        // parser server-side lo trate como un solo target.
        const hasSpaces = /\s/.test(trimmed);
        const prefix = hasSpaces ? '/w "' + trimmed + '" ' : "/w " + trimmed + " ";

        this.setState({
            textDialog: prefix,
            showInputText: true,
            lastWhisperTarget: prefix
        });
    };

    closeModalTrade = () => {
        // Ticket #235: reset de selección + cantidad al cerrar — siguiente
        // openTrade arranca limpio (mismo patrón que closeModalBank).
        this.setState(prev => ({
            showModalTrade: false,
            cantTrade: 1,
            trade: {
                ...prev.trade,
                idPosTrade: -1,
                idPosInv: 0
            }
        }));
    };

    closeModalBank = () => {
        this.setState({
            showModalBank: false,
            bankSelectedInvSlot: 0,
            bankSelectedBankSlot: 0,
            bankCantInv: 1,
            bankCantBank: 1,
            bankDragging: null
        });
    };

    // Click sobre un slot del modal banco. Side="bank" → selecciona slot
    // del banco. Side="inv" → selecciona slot del inv (a menos que esté
    // equipped, en cuyo caso muestra mensaje en chat y NO selecciona).
    onBankSlotClick = (side, idPos) => {
        if (side === "inv") {
            const item =
                this.engine &&
                this.engine.user &&
                this.engine.user.items &&
                this.engine.user.items[idPos];
            if (item && item.equipped) {
                this.bankChatMsg(
                    `Desequipá "${item.nameItem ||
                        ""}" antes de depositarlo en el banco.`
                );
                return;
            }
            this.setState({
                bankSelectedInvSlot: idPos,
                bankSelectedBankSlot: 0,
                bankCantInv: 1
            });
        } else {
            this.setState({
                bankSelectedBankSlot: idPos,
                bankSelectedInvSlot: 0,
                bankCantBank: 1
            });
        }
    };

    // Mensaje informativo al chat del juego — reusa el sistema de mensajes
    // que ya muestra los "Te encuentras muy lejos para comerciar", etc.
    // Color naranja Atrium para que destaque sin ser alarmante.
    bankChatMsg = msg => {
        if (this.game && typeof this.game.writeConsole === "function") {
            this.game.writeConsole(msg, "#ec9a44", true, false);
        }
    };

    // Inicio de drag — guardamos el origen (side + idPos) en state.
    // dataTransfer es para evitar el "no permitido" cursor del browser.
    onBankDragStart = (e, side, idPos) => {
        const item =
            side === "inv"
                ? this.engine && this.engine.user && this.engine.user.items
                    ? this.engine.user.items[idPos]
                    : null
                : (this.state.modalBank &&
                      this.state.modalBank.itemsBank[idPos]) ||
                  null;
        if (!item) {
            e.preventDefault();
            return;
        }
        if (side === "inv" && item.equipped) {
            // Visual: igualmente cancelamos el drag (browser muestra
            // cursor de "drag not allowed"). Chat msg también.
            e.preventDefault();
            this.bankChatMsg(
                `Desequipá "${item.nameItem || ""}" antes de depositarlo.`
            );
            return;
        }
        e.dataTransfer.effectAllowed = "move";
        e.dataTransfer.setData("text/plain", `${side}:${idPos}`);
        this.setState({
            bankDragging: { side, idPos }
        });
    };

    onBankDragEnd = () => {
        this.setState({ bankDragging: null });
    };

    // dragover requiere preventDefault para que el drop sea aceptado.
    // Solo aceptamos drop si el destino es del lado opuesto al origen
    // (no tiene sentido drag dentro de la misma columna).
    onBankDragOver = (e, targetSide) => {
        const dragging = this.state.bankDragging;
        if (!dragging || dragging.side === targetSide) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = "move";
    };

    // Drop: si origen=inv y destino=bank → depositar todo. Si origen=bank
    // y destino=inv → retirar todo. Misma puerta que los botones "todo"
    // pero accionada por gesto visual.
    onBankDrop = (e, targetSide) => {
        e.preventDefault();
        const dragging = this.state.bankDragging;
        if (!dragging) return;
        const { side, idPos } = dragging;
        this.setState({ bankDragging: null });

        if (side === "inv" && targetSide === "bank") {
            this.sendDeposit(idPos, 0); // 0 = todo
        } else if (side === "bank" && targetSide === "inv") {
            this.sendWithdraw(idPos, 0); // 0 = todo
        }
    };

    // Botón "Depositar N" — usa el slot seleccionado del inv + la cant
    // que el user puso en el input.
    depositN = () => {
        const { bankSelectedInvSlot, bankCantInv } = this.state;
        if (!bankSelectedInvSlot) return;
        const cant = Math.max(1, parseInt(bankCantInv) || 1);
        this.sendDeposit(bankSelectedInvSlot, cant);
    };

    depositAll = () => {
        const { bankSelectedInvSlot } = this.state;
        if (!bankSelectedInvSlot) return;
        this.sendDeposit(bankSelectedInvSlot, 0);
    };

    withdrawN = () => {
        const { bankSelectedBankSlot, bankCantBank } = this.state;
        if (!bankSelectedBankSlot) return;
        const cant = Math.max(1, parseInt(bankCantBank) || 1);
        this.sendWithdraw(bankSelectedBankSlot, cant);
    };

    withdrawAll = () => {
        const { bankSelectedBankSlot } = this.state;
        if (!bankSelectedBankSlot) return;
        this.sendWithdraw(bankSelectedBankSlot, 0);
    };

    // Wire helpers — wire format: serverPacketID + idPos (byte) + cant (short).
    // cant=0 significa "todo el stack" (mismo handling server-side).
    sendDeposit = (idPos, cant) => {
        try {
            this.pkg.setPackageID(this.pkg.serverPacketID.depositItem);
            this.pkg.writeByte(idPos);
            this.pkg.writeShort(cant);
            this.config.ws.send(this.pkg.dataSend());
        } catch (err) {
            console.log("sendDeposit error:", err && err.message);
        }
    };

    sendWithdraw = (idPos, cant) => {
        try {
            this.pkg.setPackageID(this.pkg.serverPacketID.withdrawItem);
            this.pkg.writeByte(idPos);
            this.pkg.writeShort(cant);
            this.config.ws.send(this.pkg.dataSend());
        } catch (err) {
            console.log("sendWithdraw error:", err && err.message);
        }
    };

    // ── Drag-and-drop dentro de la misma columna (reordenar) ──────────
    // Diferenciamos del cross-column (deposit/withdraw) por la `side`
    // del origen vs `targetSide` del slot/grid.
    //
    // Behavior por slot:
    //   - Mismo side y mismo slot que origen → no-op
    //   - Mismo side, slot distinto → swap optimistic + packet
    //   - Side distinto → preventDefault NO se llama acá, el evento
    //     bubblea al grid handler (que hace deposit/withdraw todo)

    onBankSlotDragOver = (e, targetSide, targetIdPos) => {
        const dragging = this.state.bankDragging;
        if (!dragging) return;
        if (dragging.side !== targetSide) return; // cross-side: bubble al grid
        if (dragging.idPos === targetIdPos) return; // mismo slot: no-op
        e.preventDefault();
        e.stopPropagation();
        e.dataTransfer.dropEffect = "move";
    };

    onBankSlotDrop = (e, targetSide, targetIdPos) => {
        const dragging = this.state.bankDragging;
        if (!dragging) return;
        if (dragging.side !== targetSide) return; // cross-side: bubble al grid
        e.preventDefault();
        e.stopPropagation();
        const { side, idPos } = dragging;
        this.setState({ bankDragging: null });
        if (idPos === targetIdPos) return;

        if (side === "bank") {
            this.swapBankSlotsOptimistic(idPos, targetIdPos);
        } else {
            this.swapInvSlotsOptimistic(idPos, targetIdPos);
        }
    };

    // Swap optimistic en state.modalBank.itemsBank + packet swapBankSlots
    // al server (que actualiza user.bankInv del lado server).
    swapBankSlotsOptimistic = (fromPos, toPos) => {
        const modalBank = this.state.modalBank;
        if (!modalBank) return;
        const items = { ...modalBank.itemsBank };
        const a = items[fromPos];
        const b = items[toPos];
        if (a) items[toPos] = a;
        else delete items[toPos];
        if (b) items[fromPos] = b;
        else delete items[fromPos];

        // Reasignar selección si el slot seleccionado cambió de posición.
        let newSel = this.state.bankSelectedBankSlot;
        if (newSel === fromPos) newSel = toPos;
        else if (newSel === toPos) newSel = fromPos;

        this.setState({
            modalBank: { ...modalBank, itemsBank: items },
            bankSelectedBankSlot: newSel
        });

        try {
            this.pkg.setPackageID(this.pkg.serverPacketID.swapBankSlots);
            this.pkg.writeByte(fromPos);
            this.pkg.writeByte(toPos);
            this.config.ws.send(this.pkg.dataSend());
        } catch (err) {
            console.log("swapBankSlots send error:", err && err.message);
        }
    };

    // Swap optimistic en this.user.items + packet swapItems al server.
    // Reusa el mismo packet que el HUD principal usa para drag-and-drop
    // del inventario. También sincroniza punteros de equipo (idItemBody
    // etc.) si un item equipado cambió de posición.
    swapInvSlotsOptimistic = (fromPos, toPos) => {
        if (!this.user || !this.user.items) return;
        const items = this.user.items;
        const a = items[fromPos];
        const b = items[toPos];
        if (a) items[toPos] = a;
        else delete items[toPos];
        if (b) items[fromPos] = b;
        else delete items[fromPos];

        const equipKeys = [
            "idItemBody",
            "idItemWeapon",
            "idItemShield",
            "idItemHelmet",
            "idItemArrow"
        ];
        equipKeys.forEach(k => {
            if (this.user[k] === fromPos) this.user[k] = toPos;
            else if (this.user[k] === toPos) this.user[k] = fromPos;
        });

        // Sincronizar también selectItem y bankSelectedInvSlot si el
        // slot seleccionado se movió (consistencia del HUD + modal).
        let newSelInv = this.state.bankSelectedInvSlot;
        if (newSelInv === fromPos) newSelInv = toPos;
        else if (newSelInv === toPos) newSelInv = fromPos;

        let newSelHud = this.state.selectItem;
        if (newSelHud === fromPos) newSelHud = toPos;
        else if (newSelHud === toPos) newSelHud = fromPos;

        this.setState({
            user: this.user,
            bankSelectedInvSlot: newSelInv,
            selectItem: newSelHud
        });

        try {
            this.pkg.setPackageID(this.pkg.serverPacketID.swapItems);
            this.pkg.writeInt(fromPos);
            this.pkg.writeInt(toPos);
            this.config.ws.send(this.pkg.dataSend());
        } catch (err) {
            console.log("swapItems send error:", err && err.message);
        }
    };

    selectItem = i => {
        this.setState({
            selectItem: i
        });
    };

    // Doble-click en slot del inventario:
    //   - Si el item es equipable (armas/armaduras/escudos/cascos/anillos/
    //     instrumentos) → toggle equip/desequip (el server decide según
    //     `equipped` actual del item).
    //   - Si no (comida/pociones/bebidas/etc) → useItem.
    // Reemplaza el viejo contador `clickUse` que requería triple-click y solo
    // hacía useItem nunca equipar.
    EQUIPABLE_OBJ_TYPES = new Set([
        2, // armas
        3, // armaduras
        16, // escudos
        17, // cascos
        18, // anillos
        26, // instrumentos musicales
        32, // flechas (proyectiles para arco — server case `objType.flechas`
        //  setea user.idItemArrow al equipar)
        37, // caña de pescar (#325) — equipable; doble-click estando
        //  equipada togglea la pesca automática (server equiparItem)
        38, // piquete de minero (#326) — ídem caña, togglea minería
        39, // hacha de leñador (#327) — ídem caña/pico, togglea talado
        40 // martillo de herrero (#329) — ídem; doble-click abre Herrería
    ]);

    useOrEquipItem = i => {
        const { user } = this.state;
        const item = user.items && user.items[i];
        if (!item || !item.idItem) return;

        // objType viene en la packet del server (engine/connection/messages.js
        // línea 118 y 796 — pkg.getByte). Leemos directo del item, no via
        // inits.objs (que en este cliente no se loadea como dict global).
        if (this.EQUIPABLE_OBJ_TYPES.has(item.objType)) {
            this.game.equiparItem(i, item.idItem);
        } else {
            this.game.useItem(i);
        }
    };

    // Pair-click detection manual para items del inventario. El `onDoubleClick`
    // nativo del browser es flaky bajo click-spam (la ventana interna de
    // 500ms se resetea de forma errática y muchos pares de clicks se pierden).
    // Trackeamos lastSlot + lastTime nosotros: si el siguiente click es sobre
    // el MISMO slot dentro de PAIR_WINDOW_MS, dispara la acción y reseta.
    // Resultado: spam → cada segundo click sobre un slot fire acción de
    // forma confiable.
    _itemLastClick = { slot: null, time: 0 };
    PAIR_WINDOW_MS = 400;

    handleItemSlotClick = i => {
        const now = Date.now();
        const last = this._itemLastClick;
        if (last.slot === i && now - last.time <= this.PAIR_WINDOW_MS) {
            // Segundo click rápido sobre el mismo slot → acción.
            this._itemLastClick = { slot: null, time: 0 };
            this.useOrEquipItem(i);
        } else {
            // Primer click (o click en otro slot) → solo seleccionar.
            this._itemLastClick = { slot: i, time: now };
            this.selectItem(i);
        }
    };

    selectSpell = i => {
        const spell = this.user.spells[i];
        if (!spell) return;

        if (this.user.maxMana > 0) {
            // Guardamos el idSpell CANÓNICO (no el slot). Hace el cast
            // inmune a reordenamientos y desyncs de slot. Ver
            // engine.js → clickCanvas para el resolve server-side.
            this.config.hechizoIdSpell = spell.idSpell;
            this.setState({
                crosshair: true
            });
            this.game.writeConsole("Haz click sobre el objetivo...", "gray");
        }
    };

    clickCanvas = e => {
        try {
            if (
                !this.user ||
                !this.user.pos ||
                typeof this.user.pos.x !== "number" ||
                typeof this.user.pos.y !== "number"
            ) {
                return;
            }

            // Si el context menu está abierto, un left-click lo cierra
            // (en lugar de gatillar la acción del canvas).
            if (this.state.contextMenu) {
                this.setState({ contextMenu: null });
                return;
            }

            let xCanvas = e.nativeEvent.offsetX;
            let yCanvas = e.nativeEvent.offsetY;

            // Posición canvas-tile del char local. LINKEADA a viewportRadius:
            // el render itera desde tileY-(r-1), así que el char queda a
            // step (r-1) desde el borde superior del canvas → pixel
            // (r-1)*32. Tile center a +16 px → (r - 1.5) * 32.
            // Para r=12 (default histórico) esto da 10.5 (= 672/64), igual
            // al magic number viejo. Para r=13 da 11.5, etc.
            const userCanvasTile = this.config.viewportRadius - 1.5;
            const posX = Math.round(this.user.pos.x + xCanvas / 32 - userCanvasTile);
            const posY = Math.round(this.user.pos.y + yCanvas / 32 - userCanvasTile);

            if (!Number.isFinite(posX) || !Number.isFinite(posY)) return;

            this.engine.clickCanvas({ x: posX, y: posY });
        } catch (err) {
            console.log("clickCanvas (page) error:", err && err.message);
        }
    };

    // Right-click sobre el canvas → si la tile clickeada tiene un NPC
    // atacable, abre el context menu en la posición del mouse con la
    // opción "Inspeccionar". Si no hay NPC ahí, no pasa nada (devuelve
    // el menu nativo del browser, que el preventDefault luego suprime).
    contextMenuCanvas = e => {
        try {
            e.preventDefault();
            if (
                !this.user ||
                !this.user.pos ||
                typeof this.user.pos.x !== "number"
            ) {
                return;
            }

            const xCanvas = e.nativeEvent.offsetX;
            const yCanvas = e.nativeEvent.offsetY;
            // Ver clickCanvas (l. ~1329) para detalle del derivado.
            const userCanvasTile = this.config.viewportRadius - 1.5;
            const posX = Math.round(
                this.user.pos.x + xCanvas / 32 - userCanvasTile
            );
            const posY = Math.round(
                this.user.pos.y + yCanvas / 32 - userCanvasTile
            );

            // Busca qué entity está en esa tile via el mapData del engine.
            // Si no hay nada (id=0) o no es un NPC → no menu.
            const map =
                inits.mapData && inits.mapData[this.config.mapNumber];
            if (!map || !map[posY] || !map[posY][posX]) return;
            let id = map[posY][posX].id;
            // Si la tile clickeada está vacía, probá la de abajo (mismo
            // patrón que clickCanvas — los sprites se anclan a la tile
            // del pie del personaje).
            if (!id && map[posY + 1] && map[posY + 1][posX]) {
                id = map[posY + 1][posX].id;
            }
            if (!id) return;

            const entity = this.engine && this.engine.personajes[id];
            if (!entity) return;
            const isSelf = this.user && String(id) === String(this.user.id);

            // Click es page-relative (clientX/Y) para posicionar el menu
            // como overlay fixed (no se mueve si el viewport scrollea).
            if (entity.isNpc) {
                this.setState({
                    contextMenu: {
                        x: e.clientX,
                        y: e.clientY,
                        npcId: id,
                        npcName: entity.nameCharacter || "",
                        npcType: entity.npcType || 0
                    }
                });
            } else {
                // Player-target (incluye a uno mismo: `isSelf` cambia el
                // label del menu a "Auto-inspeccionar"). Mismo shape que
                // NPC, con userId en lugar de npcId.
                this.setState({
                    contextMenu: {
                        x: e.clientX,
                        y: e.clientY,
                        userId: id,
                        userName: entity.nameCharacter || "",
                        isSelf: !!isSelf
                    }
                });
            }
        } catch (err) {
            console.log("contextMenuCanvas error:", err && err.message);
        }
    };

    // Manda el packet inspectUser (one-shot). El polling dinámico se
    // removió — estaba metiendo lag en el WS (pociones lentas). Si en
    // el futuro se quiere HP dinámico, hacerlo con un packet PUSH
    // server-side (updateHP broadcast al observador) en lugar de polling.
    inspectUser = (userId) => {
        try {
            this.pkg.setPackageID(this.pkg.serverPacketID.inspectUser);
            this.pkg.writeInt(userId);
            this.config.ws.send(this.pkg.dataSend());
        } catch (err) {
            console.log("inspectUser send error:", err && err.message);
        }
        this.setState({ contextMenu: null });
    };

    closeUserInspect = () => {
        this.setState({ userInspect: null });
    };

    closeSelfInspect = () => {
        this.setState({ selfInspect: null });
    };

    // Herrería (#329) — pide forjar un lingote. El server valida martillo
    // + 100 minerales, consume, entrega el lingote y reenvía openHerreria
    // (que refresca el conteo del modal in-place).
    crearLingote = idLingote => {
        try {
            this.pkg.setPackageID(this.pkg.serverPacketID.crearLingote);
            this.pkg.writeInt(idLingote);
            this.config.ws.send(this.pkg.dataSend());
        } catch (err) {
            console.log("crearLingote send error:", err && err.message);
        }
    };

    closeHerreria = () => {
        this.setState({ herreriaModal: null });
    };

    // Item del context menu — manda el packet inspectNpc y cierra el menu.
    // La respuesta del server llena state.npcInspect que abre el modal
    // (renderNpcInspectModal).
    inspectNpc = npcId => {
        try {
            this.pkg.setPackageID(this.pkg.serverPacketID.inspectNpc);
            // Ticket #227: ids u32.
            this.pkg.writeInt(npcId);
            this.config.ws.send(this.pkg.dataSend());
        } catch (err) {
            console.log("inspectNpc send error:", err && err.message);
        }
        this.setState({ contextMenu: null });
    };

    // Item del context menu — manda el packet setHogarFromNpc al server
    // pidiendo setear el hogar a la posición actual del char (validando
    // que el NPC es un Gobernador y está adyacente). Server contesta con
    // un console message (éxito o rechazo).
    // Ticket #287: cliente pide al server resync de su pos canonical.
    // Server responde con actPositionServer + setNewAreas → snap del
    // sprite + re-fetch del viewport. Útil cuando el char se ve
    // visualmente desincronizado del state real.
    sendRefreshPosition = () => {
        try {
            this.pkg.setPackageID(this.pkg.serverPacketID.refreshPosition);
            this.config.ws.send(this.pkg.dataSend());
            if (this.game && typeof this.game.writeConsole === "function") {
                this.game.writeConsole("Posición actualizada.", "gray");
            }
        } catch (err) {
            console.log("refreshPosition send error:", err && err.message);
        }
    };

    setHogarFromNpc = npcId => {
        try {
            this.pkg.setPackageID(this.pkg.serverPacketID.setHogarFromNpc);
            // Ticket #227: ids u32.
            this.pkg.writeInt(npcId);
            this.config.ws.send(this.pkg.dataSend());
        } catch (err) {
            console.log("setHogarFromNpc send error:", err && err.message);
        }
        this.setState({ contextMenu: null });
    };

    // Ticket #280: click en el ícono del buff de una pet → despawn esa pet.
    // Útil para liberar un slot del cap de 3 e invocar otra. Optimistic
    // remove del state — el server eventualmente manda el petBuff con
    // durationMs=0 que termina de limpiar.
    sendDespawnPet = idPet => {
        try {
            this.pkg.setPackageID(this.pkg.serverPacketID.despawnPet);
            this.pkg.writeInt(idPet);
            this.config.ws.send(this.pkg.dataSend());
        } catch (err) {
            console.log("despawnPet send error:", err && err.message);
        }
    };

    // Ticket #280: right-click en el ícono del buff → toggle entre modo
    // defensivo (default) y ofensivo. Optimistic local update — el server
    // confirma con console message + mantiene la state authoritative.
    sendSetPetMode = (idPet, mode) => {
        try {
            this.pkg.setPackageID(this.pkg.serverPacketID.setPetMode);
            this.pkg.writeInt(idPet);
            this.pkg.writeByte(mode === "offensive" ? 1 : 0);
            this.config.ws.send(this.pkg.dataSend());
        } catch (err) {
            console.log("setPetMode send error:", err && err.message);
        }
        // Update local optimistic.
        this.setState(s => ({
            activePetBuffs: (s.activePetBuffs || []).map(p =>
                p.id === idPet ? { ...p, mode: mode } : p
            )
        }));
    };

    // Ticket #280 polish: el right-click abre un mini menu en vez de
    // togglear directo. UX más explícito + permite cancelar.
    openPetModeMenu = (e, idPet) => {
        const current = (this.state.activePetBuffs || []).find(p => p.id === idPet);
        const currentMode = (current && current.mode) || "defensive";
        this.setState({
            petModeMenu: {
                petId: idPet,
                x: e.clientX,
                y: e.clientY,
                currentMode
            }
        });
    };

    closePetModeMenu = () => {
        if (this.state.petModeMenu) this.setState({ petModeMenu: null });
    };

    pickPetMode = (idPet, mode) => {
        this.sendSetPetMode(idPet, mode);
        this.setState({ petModeMenu: null });
    };

    closeNpcInspect = () => {
        this.setState({ npcInspect: null });
    };

    // Render del context menu del NPC. Position: fixed en clientX/Y del
    // click. Se clipea contra el viewport por si el right-click cae cerca
    // del borde derecho/abajo.
    renderNpcContextMenu = () => {
        const cm = this.state.contextMenu;
        if (!cm) return null;
        // Si el menu se sale por la derecha o abajo, lo ofseteamos. 180/80
        // son aprox las dimensiones del menu chico.
        const maxX =
            typeof window !== "undefined"
                ? window.innerWidth - 180
                : cm.x;
        const maxY =
            typeof window !== "undefined"
                ? window.innerHeight - 80
                : cm.y;
        const left = Math.min(cm.x, Math.max(0, maxX));
        const top = Math.min(cm.y, Math.max(0, maxY));
        // Fork: si el context menu es de un player (tiene userId) mostramos
        // el menu de player; sino asumimos NPC (tiene npcId).
        if (cm.userId) {
            return (
                <div
                    className={style.npcContextMenu}
                    style={{ left: `${left}px`, top: `${top}px` }}
                    onContextMenu={e => e.preventDefault()}
                >
                    <div className={style.npcContextMenuHeader}>
                        {cm.isSelf ? "Vos" : (cm.userName || "Jugador")}
                    </div>
                    <button
                        type="button"
                        className={style.npcContextMenuItem}
                        onClick={() => this.inspectUser(cm.userId)}
                    >
                        {cm.isSelf ? "Auto-inspeccionar" : "Inspeccionar"}
                    </button>
                </div>
            );
        }
        // npcType=11 → Gobernador, habilita la opción "Hogar".
        const isGobernador = cm.npcType === 11;
        return (
            <div
                className={style.npcContextMenu}
                style={{ left: `${left}px`, top: `${top}px` }}
                onContextMenu={e => e.preventDefault()}
            >
                <div className={style.npcContextMenuHeader}>
                    {cm.npcName || "NPC"}
                </div>
                <button
                    type="button"
                    className={style.npcContextMenuItem}
                    onClick={() => this.inspectNpc(cm.npcId)}
                >
                    Inspeccionar
                </button>
                {isGobernador && (
                    <button
                        type="button"
                        className={style.npcContextMenuItem}
                        onClick={() => this.setHogarFromNpc(cm.npcId)}
                    >
                        Hogar
                    </button>
                )}
            </div>
        );
    };

    // Context menu del PartyPanel — right-click sobre la row de un miembro.
    // Solo abierto si el viewer es el líder y target NO es el líder + está
    // online. Acciones: "Promover a Líder".
    openPartyContextMenu = (e, memberName) => {
        e.preventDefault();
        this.setState({
            partyContextMenu: {
                x: e.clientX,
                y: e.clientY,
                name: memberName
            }
        });
    };

    closePartyContextMenu = () => {
        this.setState({ partyContextMenu: null });
    };

    promoteFromContextMenu = () => {
        const cm = this.state.partyContextMenu;
        if (!cm || !cm.name) return;
        const cmd = /\s/.test(cm.name)
            ? '/p promover "' + cm.name + '"'
            : "/p promover " + cm.name;
        this.general.sendDialog(cmd);
        this.closePartyContextMenu();
    };

    renderPartyContextMenu = () => {
        const cm = this.state.partyContextMenu;
        if (!cm) return null;
        const maxX =
            typeof window !== "undefined" ? window.innerWidth - 200 : cm.x;
        const maxY =
            typeof window !== "undefined" ? window.innerHeight - 90 : cm.y;
        const left = Math.min(cm.x, Math.max(0, maxX));
        const top = Math.min(cm.y, Math.max(0, maxY));
        return (
            <div
                className={style.npcContextMenu}
                style={{ left: left + "px", top: top + "px" }}
                onContextMenu={e => e.preventDefault()}
                ref={ref => (this._partyContextMenuRef = ref)}
            >
                <div className={style.npcContextMenuHeader}>{cm.name}</div>
                <button
                    type="button"
                    className={style.npcContextMenuItem}
                    onClick={this.promoteFromContextMenu}
                >
                    Promover a Líder
                </button>
            </div>
        );
    };

    // Context menu del GlobalPanel — right-click sobre una row de char
    // online. Opciones:
    //   - Susurrar → abre el input de chat con `/w "Name" ` precargado.
    //   - Invitar a party → dispara `/p invitar Name` (mismo flow que
    //     el botón del modal del party). El cap + dupes los maneja el
    //     server (no inflar la lógica acá).
    openGlobalContextMenu = (e, memberName) => {
        e.preventDefault();
        const myName = this.user && this.user.nameCharacter;
        this.setState({
            globalContextMenu: {
                x: e.clientX,
                y: e.clientY,
                name: memberName,
                isMe: !!(myName && memberName === myName)
            }
        });
    };

    closeGlobalContextMenu = () => {
        this.setState({ globalContextMenu: null });
    };

    whisperFromGlobalContextMenu = () => {
        const cm = this.state.globalContextMenu;
        if (!cm || !cm.name) return;
        // Quoting si tiene espacios — el server parsea `/w "Name con espacio" mensaje`.
        const prefix = /\s/.test(cm.name)
            ? '/w "' + cm.name + '" '
            : "/w " + cm.name + " ";
        // Abre el input de chat (mismo flag que Enter) y lo pre-llena.
        // El input se monta con autoFocus + selectionRange al final
        // (ya implementado para ticket #305).
        this.setState({
            showInputText: true,
            textDialog: prefix
        });
        this.closeGlobalContextMenu();
    };

    inviteFromGlobalContextMenu = () => {
        const cm = this.state.globalContextMenu;
        if (!cm || !cm.name) return;
        const cmd = /\s/.test(cm.name)
            ? '/p invitar "' + cm.name + '"'
            : "/p invitar " + cm.name;
        this.general.sendDialog(cmd);
        this.closeGlobalContextMenu();
    };

    // Context menu del ClanPanel — right-click sobre miembro (líder only).
    // Opción única: Expulsar del clan. El kick lo procesa el server.
    openClanContextMenu = (e, memberName) => {
        e.preventDefault();
        this.setState({
            clanContextMenu: {
                x: e.clientX,
                y: e.clientY,
                name: memberName
            }
        });
    };

    closeClanContextMenu = () => {
        this.setState({ clanContextMenu: null });
    };

    kickFromClanContextMenu = () => {
        const cm = this.state.clanContextMenu;
        if (!cm || !cm.name) return;
        try {
            this.general.sendDialog("/clan expulsar " + cm.name);
        } catch (e) {}
        this.closeClanContextMenu();
    };

    renderClanContextMenu = () => {
        const cm = this.state.clanContextMenu;
        if (!cm) return null;
        const maxX = typeof window !== "undefined" ? window.innerWidth - 200 : cm.x;
        const maxY = typeof window !== "undefined" ? window.innerHeight - 90 : cm.y;
        const left = Math.min(cm.x, Math.max(0, maxX));
        const top = Math.min(cm.y, Math.max(0, maxY));
        return (
            <div
                className={style.npcContextMenu}
                style={{ left: left + "px", top: top + "px" }}
                onContextMenu={e => e.preventDefault()}
                ref={ref => (this._clanContextMenuRef = ref)}
            >
                <div className={style.npcContextMenuHeader}>{cm.name}</div>
                <button
                    type="button"
                    className={style.npcContextMenuItem}
                    onClick={this.kickFromClanContextMenu}
                >
                    Expulsar del Clan
                </button>
            </div>
        );
    };

    renderGlobalContextMenu = () => {
        const cm = this.state.globalContextMenu;
        if (!cm) return null;
        const maxX =
            typeof window !== "undefined" ? window.innerWidth - 200 : cm.x;
        const maxY =
            typeof window !== "undefined" ? window.innerHeight - 120 : cm.y;
        const left = Math.min(cm.x, Math.max(0, maxX));
        const top = Math.min(cm.y, Math.max(0, maxY));
        return (
            <div
                className={style.npcContextMenu}
                style={{ left: left + "px", top: top + "px" }}
                onContextMenu={e => e.preventDefault()}
                ref={ref => (this._globalContextMenuRef = ref)}
            >
                <div className={style.npcContextMenuHeader}>{cm.name}</div>
                {!cm.isMe && (
                    <button
                        type="button"
                        className={style.npcContextMenuItem}
                        onClick={this.whisperFromGlobalContextMenu}
                    >
                        Susurrar
                    </button>
                )}
                {!cm.isMe && (
                    <button
                        type="button"
                        className={style.npcContextMenuItem}
                        onClick={this.inviteFromGlobalContextMenu}
                    >
                        Invitar a party
                    </button>
                )}
                {cm.isMe && (
                    <div className={style.npcContextMenuItem} style={{ opacity: 0.5, cursor: "default" }}>
                        (sos vos)
                    </div>
                )}
            </div>
        );
    };

    // Context menu del right-click sobre una row del listado de hechizos
    // (ticket #207). Opciones: "Asignar tecla" (siempre) + "Quitar tecla"
    // (si ya tiene una asignada). Position fixed en clientX/Y.
    renderSpellContextMenu = () => {
        const cm = this.state.spellContextMenu;
        if (!cm) return null;
        const spells = (this.state.user && this.state.user.spells) || {};
        // Resolver nombre del spell por idSpell canónico.
        let spellName = "Hechizo";
        for (const slot of Object.keys(spells)) {
            if (
                spells[slot] &&
                String(spells[slot].idSpell) === String(cm.idSpell)
            ) {
                spellName = spells[slot].name || spellName;
                break;
            }
        }
        const hasBinding = !!(this.state.spellKeybindings || {})[cm.idSpell];
        const boundKey = this.state.spellKeybindings[cm.idSpell];
        const boundChar = boundKey ? this.keyCodeToChar(boundKey) : "";

        const maxX =
            typeof window !== "undefined"
                ? window.innerWidth - 200
                : cm.x;
        const maxY =
            typeof window !== "undefined"
                ? window.innerHeight - 100
                : cm.y;
        const left = Math.min(cm.x, Math.max(0, maxX));
        const top = Math.min(cm.y, Math.max(0, maxY));

        return (
            <div
                className={style.npcContextMenu}
                style={{ left: `${left}px`, top: `${top}px` }}
                onContextMenu={e => e.preventDefault()}
                ref={ref => (this._spellContextMenuRef = ref)}
            >
                <div className={style.npcContextMenuHeader}>{spellName}</div>
                <button
                    type="button"
                    className={style.npcContextMenuItem}
                    onClick={() =>
                        this.startSpellKeyCapture(cm.idSpell, spellName)
                    }
                >
                    {hasBinding
                        ? "Cambiar tecla (" + boundChar + ")"
                        : "Asignar tecla"}
                </button>
                {hasBinding && (
                    <button
                        type="button"
                        className={style.npcContextMenuItem}
                        onClick={() => this.clearSpellKeybinding(cm.idSpell)}
                    >
                        Quitar tecla
                    </button>
                )}
            </div>
        );
    };

    // Ticket #280: mini menu del right-click sobre el ícono de buff de
    // pet. 2 opciones: Defensivo / Ofensivo. Posicionado en clientX/Y,
    // estilo minimal (header + 2 buttons) consistente con los demás
    // context menus del HUD.
    renderPetModeMenu = () => {
        const cm = this.state.petModeMenu;
        if (!cm) return null;
        const maxX = typeof window !== "undefined" ? window.innerWidth - 180 : cm.x;
        const maxY = typeof window !== "undefined" ? window.innerHeight - 110 : cm.y;
        const left = Math.min(cm.x, Math.max(0, maxX));
        const top = Math.min(cm.y, Math.max(0, maxY));
        return (
            <div
                className={style.npcContextMenu}
                style={{ left: `${left}px`, top: `${top}px` }}
                onContextMenu={e => e.preventDefault()}
                ref={ref => (this._petModeMenuRef = ref)}
            >
                <div className={style.npcContextMenuHeader}>Modo de mascota</div>
                <button
                    type="button"
                    className={style.npcContextMenuItem}
                    onClick={() => this.pickPetMode(cm.petId, "defensive")}
                >
                    {cm.currentMode === "defensive" ? "✓ " : ""}Defensivo
                </button>
                <button
                    type="button"
                    className={style.npcContextMenuItem}
                    onClick={() => this.pickPetMode(cm.petId, "offensive")}
                >
                    {cm.currentMode === "offensive" ? "✓ " : ""}Ofensivo
                </button>
            </div>
        );
    };

    // Context menu del right-click sobre un slot del inventario
    // (ticket #208). Mismo patrón que el de spell — "Asignar tecla" o
    // "Cambiar tecla (X)" + "Quitar tecla" si ya bindeada.
    renderInventoryContextMenu = () => {
        const cm = this.state.inventoryContextMenu;
        if (!cm) return null;
        // Nombre del item via inits.objs (static catalog).
        let itemName = "Item";
        if (inits.objs && inits.objs[cm.idItem]) {
            itemName = inits.objs[cm.idItem].name || itemName;
        }
        const bindings = this.state.inventoryKeybindings || {};
        const hasBinding = !!bindings[cm.idItem];
        const boundKey = bindings[cm.idItem];
        const boundChar = boundKey ? this.keyCodeToChar(boundKey) : "";

        const maxX =
            typeof window !== "undefined"
                ? window.innerWidth - 200
                : cm.x;
        const maxY =
            typeof window !== "undefined"
                ? window.innerHeight - 100
                : cm.y;
        const left = Math.min(cm.x, Math.max(0, maxX));
        const top = Math.min(cm.y, Math.max(0, maxY));

        return (
            <div
                className={style.npcContextMenu}
                style={{ left: `${left}px`, top: `${top}px` }}
                onContextMenu={e => e.preventDefault()}
                ref={ref => (this._inventoryContextMenuRef = ref)}
            >
                <div className={style.npcContextMenuHeader}>{itemName}</div>
                <button
                    type="button"
                    className={style.npcContextMenuItem}
                    onClick={() =>
                        this.startItemKeyCapture(cm.idItem, itemName, cm.slot)
                    }
                >
                    {hasBinding
                        ? "Cambiar tecla (" + boundChar + ")"
                        : "Asignar tecla"}
                </button>
                {hasBinding && (
                    <button
                        type="button"
                        className={style.npcContextMenuItem}
                        onClick={() =>
                            this.clearInventoryKeybinding(cm.idItem)
                        }
                    >
                        Quitar tecla
                    </button>
                )}
            </div>
        );
    };

    // Modal de captura de tecla para un item (ticket #208). Reusa los
    // mismos estilos `.modalSpellKeyCapture*` que el modal del #207 —
    // la UX es idéntica, solo cambia el texto.
    renderItemKeyCaptureModal = () => {
        const cap = this.state.capturingItemKey;
        if (!cap) return null;
        const err = this.state.itemKeyCaptureError;
        return (
            <div className={style.modalSpellKeyCaptureBackdrop}>
                {/* Ticket #238: el handleDocMouseDown global checkea si
                    el click cayó fuera de este card y cierra el modal. */}
                <div
                    className={style.modalSpellKeyCaptureCard}
                    ref={ref => (this._itemKeyCaptureCardRef = ref)}
                >
                    <div className={style.modalSpellKeyCaptureTitle}>
                        Asignar tecla
                    </div>
                    <div className={style.modalSpellKeyCaptureBody}>
                        Presioná una tecla para usar
                        <strong> {cap.itemName}</strong>.
                    </div>
                    {err && (
                        <div className={style.modalSpellKeyCaptureError}>
                            {err}
                        </div>
                    )}
                    <div className={style.modalSpellKeyCaptureFooter}>
                        <button
                            type="button"
                            className={style.modalSpellKeyCaptureCancel}
                            onClick={this.cancelItemKeyCapture}
                        >
                            Cancelar
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    // Modal de confirmación al salir del juego (ticket #214). Se abre
    // cuando el user aprieta ESC con NADA más abierto (aim mode, modales,
    // captura de teclas, etc., todos caen primero del cascade). Replaceó
    // el behavior previo de logout-directo que causaba salidas accidentales.
    renderExitConfirmModal = () => {
        if (!this.state.showExitConfirmModal) return null;
        const cancel = () => {
            this.setState({ showExitConfirmModal: false });
        };
        const confirmExit = () => {
            this.setState({ showExitConfirmModal: false });
            this.doLogout();
        };
        return (
            <div
                className={style.modalExitConfirmBackdrop}
                onClick={cancel}
            >
                <div
                    className={style.modalExitConfirmCard}
                    onClick={e => e.stopPropagation()}
                >
                    <div className={style.modalExitConfirmTitle}>
                        Salir del juego
                    </div>
                    <div className={style.modalExitConfirmBody}>
                        Vas a volver al lobby. Tu personaje se desconecta
                        del mundo.
                    </div>
                    <div className={style.modalExitConfirmFooter}>
                        <button
                            type="button"
                            className={style.modalExitConfirmExit}
                            onClick={confirmExit}
                        >
                            Salir
                        </button>
                        <button
                            type="button"
                            className={style.modalExitConfirmCancel}
                            onClick={cancel}
                            autoFocus
                        >
                            Cancelar
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    // Modal de captura — se abre tras click en "Asignar tecla". Está
    // listo para que el siguiente keydown lo consuma (handler global lee
    // capturingSpellKey y rutea). El user ve un mensaje claro de qué
    // hechizo está bindeando + feedback de colisión si pasa.
    renderSpellKeyCaptureModal = () => {
        const cap = this.state.capturingSpellKey;
        if (!cap) return null;
        const err = this.state.spellKeyCaptureError;
        return (
            <div className={style.modalSpellKeyCaptureBackdrop}>
                {/* Ticket #238: ver comentario en renderItemKeyCaptureModal. */}
                <div
                    className={style.modalSpellKeyCaptureCard}
                    ref={ref => (this._spellKeyCaptureCardRef = ref)}
                >
                    <div className={style.modalSpellKeyCaptureTitle}>
                        Asignar tecla
                    </div>
                    <div className={style.modalSpellKeyCaptureBody}>
                        Presioná una tecla para lanzar
                        <strong> {cap.spellName}</strong>.
                    </div>
                    {err && (
                        <div className={style.modalSpellKeyCaptureError}>
                            {err}
                        </div>
                    )}
                    <div className={style.modalSpellKeyCaptureFooter}>
                        <button
                            type="button"
                            className={style.modalSpellKeyCaptureCancel}
                            onClick={this.cancelSpellKeyCapture}
                        >
                            Cancelar
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    // Modal de inspección de NPC. Muestra nombre + sprite (head/body
    // composite, mismo patrón que el ranking) + stats de combate +
    // drop table. Click en el backdrop o en × cierra.
    // Modal del banco — abre con `showModalBank`, llega vía openBank.
    // UX:
    //   - Click slot del lado izq (banco) → selecciona ese slot del banco
    //   - Click slot del lado der (inv) → selecciona, salvo equipped que
    //     emite chat msg "Desequipá antes de depositar"
    //   - Drag slot inv → grid banco = depositar TODO
    //   - Drag slot banco → grid inv = retirar TODO
    //   - Input cant + botones "Depositar N / todo" del lado del inv
    //   - Input cant + botones "Retirar N / todo" del lado del banco
    // Slot seleccionado: border dorado vivo + glow tenue.
    renderModalBank = () => {
        const {
            showModalBank,
            modalBank,
            bankSelectedInvSlot,
            bankSelectedBankSlot,
            bankCantInv,
            bankCantBank
        } = this.state;
        if (!showModalBank) return null;

        const SLOTS = 24;
        const itemsBank = (modalBank && modalBank.itemsBank) || {};
        const itemsInv =
            (this.engine && this.engine.user && this.engine.user.items) || {};

        // Render un slot. `side` = "bank" o "inv". `idPos` = posición 1..24.
        // No renderea badges visuales sobre equipped — el chat msg en click
        // y el cursor disabled comunican la restricción.
        const renderSlot = (item, idPos, side) => {
            const hasItem = !!item;
            const isEquipped = !!(hasItem && side === "inv" && item.equipped);
            const isSelected =
                side === "inv"
                    ? bankSelectedInvSlot === idPos
                    : bankSelectedBankSlot === idPos;

            const slotClass = [
                style.bankSlot,
                isSelected ? style.bankSlotSelected : "",
                isEquipped ? style.bankSlotDisabled : ""
            ]
                .filter(Boolean)
                .join(" ");

            return (
                <div
                    key={`${side}-${idPos}`}
                    className={slotClass}
                    onClick={() => this.onBankSlotClick(side, idPos)}
                    draggable={hasItem && !isEquipped}
                    onDragStart={e => this.onBankDragStart(e, side, idPos)}
                    onDragEnd={this.onBankDragEnd}
                    onDragOver={e =>
                        this.onBankSlotDragOver(e, side, idPos)
                    }
                    onDrop={e => this.onBankSlotDrop(e, side, idPos)}
                    title={
                        hasItem
                            ? `${item.name ||
                                  item.nameItem ||
                                  ""} × ${item.cant}${
                                  isEquipped ? " (equipado)" : ""
                              }`
                            : ""
                    }
                >
                    {hasItem && (
                        <>
                            <div
                                className={style.bankSlotImg}
                                style={{
                                    backgroundImage: `url("${
                                        item.imgItem ||
                                        (item.grhIndex
                                            ? `/static/graficos/${
                                                  inits.graphics[item.grhIndex]
                                                      .numFile
                                              }.png`
                                            : "")
                                    }")`
                                }}
                            />
                            {item.cant > 1 ? (
                                <span className={style.bankSlotCant}>
                                    {item.cant}
                                </span>
                            ) : null}
                            {/* Badge "E" sobre items equipados del lado del
                                inv. `isEquipped` ya viene coerced a Boolean
                                con `!!` arriba; el ternario explícito acá es
                                defensa extra contra el bug viejo del "0"
                                (que pasaba por `{0 && <span/>}` → React
                                renderea "0" como texto literal). */}
                            {isEquipped ? (
                                <span className={style.bankSlotEquipped}>
                                    E
                                </span>
                            ) : null}
                        </>
                    )}
                </div>
            );
        };

        const slotsBank = [];
        const slotsInv = [];
        for (let i = 1; i <= SLOTS; i++) {
            slotsBank.push(renderSlot(itemsBank[i], i, "bank"));
            slotsInv.push(renderSlot(itemsInv[i], i, "inv"));
        }

        const selectedBankItem = bankSelectedBankSlot
            ? itemsBank[bankSelectedBankSlot]
            : null;
        const selectedInvItem = bankSelectedInvSlot
            ? itemsInv[bankSelectedInvSlot]
            : null;

        return (
            <div className={style.modalBankOverlay}>
                <div className={style.modalBank}>
                    <div className={style.bankHeader}>
                        <div className={style.bankTitle}>Banco Goliath</div>
                        <div
                            className={style.bankClose}
                            onClick={this.closeModalBank}
                        >
                            ×
                        </div>
                    </div>
                    <div className={style.bankBody}>
                        {/* Banco — drop zone para items que vienen del inv. */}
                        <div
                            className={style.bankColumn}
                            onDragOver={e => this.onBankDragOver(e, "bank")}
                            onDrop={e => this.onBankDrop(e, "bank")}
                        >
                            <div className={style.bankColumnTitle}>
                                Banco {selectedBankItem
                                    ? `· ${selectedBankItem.name}`
                                    : ""}
                            </div>
                            <div className={style.bankGrid}>{slotsBank}</div>
                            <div className={style.bankActions}>
                                <input
                                    type="number"
                                    min="1"
                                    className={style.bankCantInput}
                                    value={bankCantBank || 1}
                                    onChange={e =>
                                        this.setState({
                                            bankCantBank: Math.max(
                                                1,
                                                parseInt(e.target.value) || 1
                                            )
                                        })
                                    }
                                    disabled={!selectedBankItem}
                                />
                                <button
                                    className={style.bankBtn}
                                    onClick={this.withdrawN}
                                    disabled={!selectedBankItem}
                                >
                                    Retirar
                                </button>
                                <button
                                    className={`${style.bankBtn} ${style.bankBtnPrimary}`}
                                    onClick={this.withdrawAll}
                                    disabled={!selectedBankItem}
                                >
                                    Retirar todo
                                </button>
                            </div>
                            <div className={style.bankHint}>
                                Click para seleccionar · arrastrá al inv para retirar todo
                            </div>
                        </div>

                        {/* Inventario — drop zone para items que vienen del banco. */}
                        <div
                            className={style.bankColumn}
                            onDragOver={e => this.onBankDragOver(e, "inv")}
                            onDrop={e => this.onBankDrop(e, "inv")}
                        >
                            <div className={style.bankColumnTitle}>
                                Inventario {selectedInvItem
                                    ? `· ${selectedInvItem.name ||
                                          selectedInvItem.nameItem ||
                                          ""}`
                                    : ""}
                            </div>
                            <div className={style.bankGrid}>{slotsInv}</div>
                            <div className={style.bankActions}>
                                <input
                                    type="number"
                                    min="1"
                                    className={style.bankCantInput}
                                    value={bankCantInv || 1}
                                    onChange={e =>
                                        this.setState({
                                            bankCantInv: Math.max(
                                                1,
                                                parseInt(e.target.value) || 1
                                            )
                                        })
                                    }
                                    disabled={!selectedInvItem}
                                />
                                <button
                                    className={style.bankBtn}
                                    onClick={this.depositN}
                                    disabled={!selectedInvItem}
                                >
                                    Depositar
                                </button>
                                <button
                                    className={`${style.bankBtn} ${style.bankBtnPrimary}`}
                                    onClick={this.depositAll}
                                    disabled={!selectedInvItem}
                                >
                                    Depositar todo
                                </button>
                            </div>
                            <div className={style.bankHint}>
                                Click para seleccionar · arrastrá al banco para depositar todo
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    // Herrería (#329) — modal de crafteo de lingotes. Se abre con doble-
    // click sobre el Martillo de Herrero equipado. Muestra las 3 recetas
    // (Hierro/Plata/Oro): cada lingote cuesta 100 minerales del mismo
    // metal. El botón "Forjar" se habilita cuando hay ≥100. El server
    // reenvía openHerreria tras cada forja → el conteo se refresca solo.
    // Chrome Argentum Frame (madera cálida — ver CLAUDE.md).
    renderModalHerreria = () => {
        const { herreriaModal } = this.state;
        if (!herreriaModal) return null;

        const COSTO = 100;
        const RECETAS = [
            {
                idLingote: 386,
                grhIndex: "724",
                nombre: "Lingote de Hierro",
                mineral: "Minerales de Hierro",
                cant: herreriaModal.cantHierro || 0
            },
            {
                idLingote: 387,
                grhIndex: "725",
                nombre: "Lingote de Plata",
                mineral: "Minerales de Plata",
                cant: herreriaModal.cantPlata || 0
            },
            {
                idLingote: 388,
                grhIndex: "723",
                nombre: "Lingote de Oro",
                mineral: "Minerales de Oro",
                cant: herreriaModal.cantOro || 0
            }
        ];

        const imgFor = grhIndex => {
            const g = inits.graphics && inits.graphics[grhIndex];
            return g ? `/static/graficos/${g.numFile}.png` : "";
        };

        return (
            <div className={style.modalHerreriaOverlay}>
                <div className={style.modalHerreria}>
                    <div className={style.herreriaHeader}>
                        <div className={style.herreriaTitle}>Herrería</div>
                        <div
                            className={style.herreriaClose}
                            onClick={this.closeHerreria}
                        >
                            ×
                        </div>
                    </div>
                    <div className={style.herreriaSub}>
                        Forjá lingotes fundiendo {COSTO} minerales del mismo metal.
                    </div>
                    <div className={style.herreriaBody}>
                        {RECETAS.map(r => {
                            const puede = Math.floor(r.cant / COSTO);
                            const canForge = puede >= 1;
                            const stockClass = `${style.herreriaStockNum} ${
                                canForge
                                    ? style.herreriaStockOk
                                    : style.herreriaStockLow
                            }`;
                            return (
                                <div
                                    className={style.herreriaRecipe}
                                    key={r.idLingote}
                                >
                                    <div
                                        className={style.herreriaIcon}
                                        style={{
                                            backgroundImage: `url("${imgFor(
                                                r.grhIndex
                                            )}")`
                                        }}
                                    />
                                    <div className={style.herreriaInfo}>
                                        <div className={style.herreriaName}>
                                            {r.nombre}
                                        </div>
                                        <div className={style.herreriaStock}>
                                            <span className={stockClass}>
                                                {r.cant}
                                            </span>
                                            <span
                                                className={
                                                    style.herreriaStockSep
                                                }
                                            >
                                                {" "}
                                                / {COSTO} {r.mineral}
                                            </span>
                                        </div>
                                    </div>
                                    <button
                                        className={style.herreriaForgeBtn}
                                        disabled={!canForge}
                                        onClick={() =>
                                            this.crearLingote(r.idLingote)
                                        }
                                        title={
                                            canForge
                                                ? `Podés forjar ${puede}`
                                                : `Te faltan ${COSTO -
                                                      r.cant} minerales`
                                        }
                                    >
                                        Forjar
                                    </button>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
        );
    };

    // Modal de inspect. Compartido entre auto-inspeccionar (variant "self")
    // e inspect a otro (variant "other"). Ambos viven en el stack
    // `.inspectStack` (abajo-derecha del render): other arriba, self abajo.
    // Diseño minimalista — solo números, sin barras.
    _renderInspectModal = (u, onClose, variant) => {
        if (!u) return null;

        // Mirror server vars.nameClases/nameRazas.
        const CLASE_NAMES = [
            null, "Mago", "Clérigo", "Guerrero", "Asesino", "Ladrón",
            "Bardo", "Druida", "Paladín", "Cazador", "Trabajador", "Pirata"
        ];
        const RAZA_NAMES = [
            null, "Humano", "Elfo", "Elfo Drow", "Enano", "Gnomo"
        ];
        const claseName = CLASE_NAMES[u.idClase] || "?";
        const razaName = RAZA_NAMES[u.idRaza] || "?";

        let nameColor = "#9FC7FF";
        let roleBadge = null;
        if (u.privileges === 99) {
            nameColor = "#7CFC8B";
            roleBadge = { text: "GM MAX", color: "#7CFC8B" };
        } else if (u.privileges === 1) {
            nameColor = "#7CFC8B";
            roleBadge = { text: "GM · Nivel 1", color: "#7CFC8B" };
        } else if (u.criminal) {
            nameColor = "#FF6464";
        }

        return (
            <div
                className={`${style.userInspectCard} ${variant === "self" ? style.userInspectSelf : ""}`}
                onClick={e => e.stopPropagation()}
                onContextMenu={e => e.preventDefault()}
            >
                <div className={style.userInspectHeader}>
                    <span className={style.userInspectName} style={{ color: nameColor }}>
                        {u.name}
                    </span>
                    {u.clan ? (
                        <span className={style.userInspectClan}>&lt;{u.clan}&gt;</span>
                    ) : null}
                    <button
                        className={style.userInspectClose}
                        onClick={onClose}
                        aria-label="Cerrar"
                        title="Cerrar (Esc)"
                    >×</button>
                </div>

                <div className={style.userInspectSub}>
                    Nivel {u.level} · {claseName} · {razaName}
                </div>

                <div className={style.userInspectRows}>
                    <div className={style.userInspectStat}>
                        <span className={style.userInspectStatLabel}>Vida</span>
                        <span className={style.userInspectStatNum}>
                            <span className={style.userInspectStatCur}>{u.hp}</span>/{u.maxHp}
                        </span>
                    </div>
                    {u.maxMana > 0 && (
                        <div className={style.userInspectStat}>
                            <span className={style.userInspectStatLabel}>Maná</span>
                            <span className={style.userInspectStatNum}>
                                <span className={style.userInspectStatCur}>{u.mana}</span>/{u.maxMana}
                            </span>
                        </div>
                    )}
                    <div className={style.userInspectDivider} />
                    <div className={style.userInspectStat}>
                        <span className={style.userInspectStatLabel}>
                            <span className={style.userInspectKillIcon}>🐺</span> Criaturas
                        </span>
                        <span className={style.userInspectStatNum}>{u.npcsMuertos}</span>
                    </div>
                    <div className={style.userInspectStat}>
                        <span className={style.userInspectStatLabel}>
                            <span className={style.userInspectKillIcon}>⚔</span> Matados
                        </span>
                        <span className={style.userInspectStatNum}>{u.usersMuertos}</span>
                    </div>
                    <div className={style.userInspectSubStat}>
                        <span>Ciudadanos</span>
                        <span>{u.ciudadanosMatados}</span>
                    </div>
                    <div className={style.userInspectSubStat}>
                        <span>Criminales</span>
                        <span>{u.criminalesMatados}</span>
                    </div>
                </div>

                <div className={style.userInspectFooter}>
                    <span style={{ color: u.criminal ? "#ff6464" : "#9FC7FF" }}>
                        {u.criminal ? "Criminal" : "Ciudadano"}
                    </span>
                    {roleBadge && (
                        <span style={{ color: roleBadge.color, fontWeight: "bold" }}>
                            {roleBadge.text}
                        </span>
                    )}
                </div>
            </div>
        );
    };

    // Modal de otro-player: apilado ARRIBA del de auto-inspección.
    renderUserInspectModal = () =>
        this._renderInspectModal(
            this.state.userInspect,
            this.closeUserInspect,
            "other"
        );

    // Modal de auto-inspección: abajo del stack (abajo-derecha del render).
    renderSelfInspectModal = () =>
        this._renderInspectModal(
            this.state.selfInspect,
            this.closeSelfInspect,
            "self"
        );

    renderNpcInspectModal = () => {
        const npc = this.state.npcInspect;
        if (!npc) return null;

        // Sprite del head — usa el mismo lookup que renderRanking sprites.
        const headGrh =
            npc.idHead && inits.heads && inits.heads[npc.idHead]
                ? inits.heads[npc.idHead][2] // direction 2 = front
                : null;
        const headGraphic =
            headGrh && inits.graphics && inits.graphics[headGrh]
                ? inits.graphics[headGrh]
                : null;
        const headStyle = headGraphic
            ? {
                  backgroundImage: `url("/static/graficos/${headGraphic.numFile}.png")`,
                  backgroundPosition: `-${headGraphic.sX || 0}px -${headGraphic
                      .sY || 0}px`,
                  width: `${headGraphic.width || 16}px`,
                  height: `${headGraphic.height || 16}px`
              }
            : null;

        // Body sprite (frame 1, direction 2).
        const bodyEntry =
            npc.idBody && inits.bodies && inits.bodies[npc.idBody]
                ? inits.bodies[npc.idBody]
                : null;
        const bodyGrh = bodyEntry ? bodyEntry[2] : 0;
        const bodyAnim =
            bodyGrh && inits.graphics ? inits.graphics[bodyGrh] : null;
        const bodyFrame1 =
            bodyAnim && bodyAnim.frames ? bodyAnim.frames[1] : null;
        const bodyGraphic =
            bodyFrame1 && inits.graphics && inits.graphics[bodyFrame1]
                ? inits.graphics[bodyFrame1]
                : null;
        // Ticket #298: tint del NPC desde el packet inspect. Mismos casos
        // que en _filterForTint del engine — replicado acá porque el modal
        // usa background-image en lugar de canvas drawImage.
        const inspectTintFilters = {
            white: "grayscale(1) brightness(1.8) contrast(0.7)",
            // Ticket #299: black — replicado del engine para coherencia.
            black: "grayscale(1) brightness(0.35) contrast(1.5)",
            blue: "hue-rotate(-80deg) saturate(1.4) brightness(0.95)",
            green: "hue-rotate(180deg) saturate(1.6) brightness(0.95)",
            red: "hue-rotate(50deg) saturate(2) brightness(0.55) contrast(1.2)"
        };
        const tintFilter = inspectTintFilters[npc.tint] || "none";

        const bodyStyle = bodyGraphic
            ? {
                  backgroundImage: `url("/static/graficos/${bodyGraphic.numFile}.png")`,
                  backgroundPosition: `-${bodyGraphic.sX || 0}px -${bodyGraphic
                      .sY || 0}px`,
                  width: `${bodyGraphic.width || 25}px`,
                  height: `${bodyGraphic.height || 16}px`,
                  filter: tintFilter
              }
            : null;

        return (
            <div
                className={style.npcInspectBackdrop}
                onClick={e => {
                    if (e.target === e.currentTarget) this.closeNpcInspect();
                }}
            >
                <div className={style.npcInspectCard}>
                    <div className={style.npcInspectHeader}>
                        <div className={style.npcInspectTitle}>
                            {npc.name}
                        </div>
                        <button
                            type="button"
                            className={style.npcInspectClose}
                            onClick={this.closeNpcInspect}
                            title="Cerrar"
                        >
                            ×
                        </button>
                    </div>

                    <div className={style.npcInspectBody}>
                        {/* Sprite — composite head + body, mismo layout
                            que el avatar del ranking pero un poco más
                            grande para que se vea como una "ficha". */}
                        <div className={style.npcInspectSprite}>
                            {bodyStyle ? (
                                <div
                                    className={style.npcInspectSpriteBody}
                                    style={bodyStyle}
                                />
                            ) : null}
                            {headStyle ? (
                                <div
                                    className={style.npcInspectSpriteHead}
                                    style={headStyle}
                                />
                            ) : null}
                        </div>

                        {/* Stats grid */}
                        <div className={style.npcInspectStats}>
                            <div className={style.npcInspectStat}>
                                <span className={style.npcInspectStatLabel}>
                                    HP
                                </span>
                                <span className={style.npcInspectStatValue}>
                                    {npc.maxHp || npc.hp}
                                </span>
                            </div>
                            <div className={style.npcInspectStat}>
                                <span className={style.npcInspectStatLabel}>
                                    Daño
                                </span>
                                <span className={style.npcInspectStatValue}>
                                    {npc.minHit}-{npc.maxHit}
                                </span>
                            </div>
                            <div className={style.npcInspectStat}>
                                <span className={style.npcInspectStatLabel}>
                                    Evasión
                                </span>
                                <span className={style.npcInspectStatValue}>
                                    {npc.poderEvasion}
                                </span>
                            </div>
                            <div className={style.npcInspectStat}>
                                <span className={style.npcInspectStatLabel}>
                                    Ataque
                                </span>
                                <span className={style.npcInspectStatValue}>
                                    {npc.poderAtaque}
                                </span>
                            </div>
                            <div className={style.npcInspectStat}>
                                <span className={style.npcInspectStatLabel}>
                                    Defensa
                                </span>
                                <span className={style.npcInspectStatValue}>
                                    {npc.def}
                                </span>
                            </div>
                            <div className={style.npcInspectStat}>
                                <span className={style.npcInspectStatLabel}>
                                    EXP
                                </span>
                                <span className={style.npcInspectStatValue}>
                                    {(npc.exp || 0).toLocaleString()}
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* Bonus XP por kill — full-width abajo del body. Grid
                        horizontal de 5 cells (party 1..5). Refactor 2026-06:
                        antes estaba dentro del body como 3er flex child y
                        overflowed el card. */}
                    {npc.bonusKillExp > 0 && (
                        <div className={style.npcInspectBonus}>
                            <div className={style.npcInspectBonusHeader}>
                                Bonus EXP por kill
                            </div>
                            <div className={style.npcInspectBonusGrid}>
                                {[1, 2, 3, 4, 5].map(n => (
                                    <div
                                        key={n}
                                        className={style.npcInspectBonusRow}
                                    >
                                        <span
                                            className={
                                                style.npcInspectBonusEmojis
                                            }
                                            aria-label={
                                                n === 1
                                                    ? "1 jugador"
                                                    : `${n} jugadores`
                                            }
                                        >
                                            {"👤".repeat(n)}
                                        </span>
                                        <span
                                            className={
                                                style.npcInspectBonusPct
                                            }
                                        >
                                            {n * 10}%
                                        </span>
                                        <span
                                            className={
                                                style.npcInspectBonusValue
                                            }
                                        >
                                            +{(npc.bonusKillExp * n).toLocaleString()}
                                        </span>
                                        {n > 1 && (
                                            <span
                                                className={
                                                    style.npcInspectBonusPerPerson
                                                }
                                            >
                                                {npc.bonusKillExp.toLocaleString()} c/u
                                            </span>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {npc.desc ? (
                        <div className={style.npcInspectDesc}>
                            {npc.desc}
                        </div>
                    ) : null}

                    {/* Drop table */}
                    <div className={style.npcInspectDrops}>
                        <div className={style.npcInspectDropsHeader}>
                            <span>Loot</span>
                            <span className={style.npcInspectDropsCount}>
                                {npc.drops.length} item
                                {npc.drops.length === 1 ? "" : "s"}
                            </span>
                        </div>
                        {npc.drops.length === 0 ? (
                            <div className={style.npcInspectEmpty}>
                                Sin drops conocidos.
                            </div>
                        ) : (
                            <div className={style.npcInspectDropsList}>
                                {npc.drops.map((d, i) => {
                                    const grhData =
                                        d.grhIndex &&
                                        inits.graphics &&
                                        inits.graphics[d.grhIndex]
                                            ? inits.graphics[d.grhIndex]
                                            : null;
                                    const itemImg = grhData
                                        ? `/static/graficos/${grhData.numFile}.png`
                                        : "";
                                    // Format chance bps → % string.
                                    // 9000 → "90%", 1000 → "10%",
                                    // 100  → "1%",  10 → "0.1%",
                                    // 1    → "0.01%".
                                    const bps = d.chanceBps || 0;
                                    const pct = bps / 100;
                                    let pctLabel;
                                    if (bps === 0) pctLabel = "—";
                                    else if (pct >= 1)
                                        pctLabel = pct.toFixed(0) + "%";
                                    else if (pct >= 0.1)
                                        pctLabel = pct.toFixed(1) + "%";
                                    else pctLabel = pct.toFixed(2) + "%";
                                    // Tier visual del chance — cambia el
                                    // color del badge: high (>=10%) verde,
                                    // mid (>=1%) amarillo, low (<1%) rojo.
                                    let tierClass = style.npcDropChanceLow;
                                    if (pct >= 10) tierClass = style.npcDropChanceHigh;
                                    else if (pct >= 1) tierClass = style.npcDropChanceMid;
                                    return (
                                        <div
                                            key={i}
                                            className={
                                                style.npcInspectDropItem
                                            }
                                        >
                                            <div
                                                className={
                                                    style.npcInspectDropImg
                                                }
                                                style={
                                                    itemImg
                                                        ? {
                                                              backgroundImage: `url("${itemImg}")`
                                                          }
                                                        : null
                                                }
                                            />
                                            <span
                                                className={
                                                    style.npcInspectDropName
                                                }
                                            >
                                                {d.name}
                                            </span>
                                            <span
                                                className={
                                                    style.npcInspectDropCant
                                                }
                                            >
                                                ×{d.cant}
                                            </span>
                                            <span
                                                className={`${style.npcInspectDropChance} ${tierClass}`}
                                                title="Probabilidad de drop"
                                            >
                                                {pctLabel}
                                            </span>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>

                    {npc.gold > 0 ? (
                        <div className={style.npcInspectGold}>
                            <span>Oro estimado</span>
                            <span className={style.npcInspectGoldValue}>
                                {npc.gold}
                            </span>
                        </div>
                    ) : null}
                </div>
            </div>
        );
    };

    // Computa el daño y defensa actuales del char a partir de los items
    // EQUIPADOS en su inventario. Itera todos los slots equipped:true y
    // suma minHit/maxHit (arma) y minDef/maxDef (armadura/escudo/casco).
    //
    // Usa la misma heurística de tooltip (presencia de minHit vs minDef)
    // porque inits.objs (static cliente) no trae objType.
    //
    // Retorna { dmg: {min,max} | null, def: {min,max} | null }.
    // `null` cuando el char no tiene nada equipado de ese tipo.
    computeCombatStats = () => {
        const { user } = this.state;
        const items = (user && user.items) || {};
        const objs = inits.objs || {};

        let dmgMin = 0, dmgMax = 0, hasDmg = false;
        let defMin = 0, defMax = 0, hasDef = false;

        for (const idPos in items) {
            const item = items[idPos];
            if (!item || !item.equipped) continue;
            const obj = objs[item.idItem];
            if (!obj) continue;

            if (obj.minHit > 0 || obj.maxHit > 0) {
                dmgMin += obj.minHit || 0;
                dmgMax += obj.maxHit || 0;
                hasDmg = true;
            } else if (obj.minDef > 0 || obj.maxDef > 0) {
                defMin += obj.minDef || 0;
                defMax += obj.maxDef || 0;
                hasDef = true;
            }
        }

        return {
            dmg: hasDmg ? { min: dmgMin, max: dmgMax } : null,
            def: hasDef ? { min: defMin, max: defMax } : null
        };
    };

    // Ticket #336 v2: callbacks estables (bound una sola vez en el field
    // declaration). PartyPanel los recibe como props sin que cambie la
    // referencia entre renders del parent → React.memo no se rompe.
    _onPartyInviteNameChange = (val) => {
        this.setState({ partyInviteName: val });
    };

    _onPartySendDialog = (cmd) => {
        this.general.sendDialog(cmd);
    };

    // Ticket #336 v2: cache manual del elemento <PartyPanel /> en la
    // instancia. Cuando el parent re-renderea (e.g. tu HP cambia, una
    // posición se actualiza, llega un packet), si los inputs relevantes
    // del panel (party ref, invite name, my name, mapNames ref) no
    // cambiaron, devolvemos el elemento cacheado. InventoryBox recibe la
    // MISMA referencia → su shouldComponentUpdate hace bail → no
    // re-renderea ni se invalidan los 24 slots del inv.
    //
    // Cache hit típico: ~95%+ durante combate (party se broadcastea
    // cada 1500ms, el parent re-renderea 5-15x/seg). En cache hit, costo
    // es 4 ref compares — O(1) trivial.
    _getCachedPartyPanelEl = () => {
        const baseParty = this.state.party;
        const inviteName = this.state.partyInviteName;
        const myName = this.user && this.user.nameCharacter;
        const mapNames = this.state.mapNames;
        // BUG FIX: el mapa del propio user vive en `this.config.mapNumber`,
        // no en `this.user.map`. Antes inXpRange quedaba undefined porque
        // myMap era undefined → emojis nunca cambiaban a gris para "fuera
        // de rango". (Disconnected sí se veía gris vía el row-level filter,
        // confundiendo el debug.)
        const myMap = this.config && this.config.mapNumber;
        const myX = this.user && this.user.pos && this.user.pos.x;
        const myY = this.user && this.user.pos && this.user.pos.y;
        const r = (this.config && this.config.viewportRadius) || 13;

        // Ticket #336 polish: augmenta party.members con `inXpRange` —
        // bool calculado contra mi posición. Cache invalidation por
        // fingerprint (baseParty ref + my pos snap a tile). Si crossing
        // de boundary del viewport, fp cambia → rebuild. Si solo te
        // movés dentro del viewport sin cambiar rangos, NO invalidamos
        // por mi pos (ahorra renders durante walking en grupo).
        let party = baseParty;
        if (baseParty && myMap !== undefined && myX !== undefined && myY !== undefined) {
            // El fp incluye solo INFO RELEVANTE para el render:
            // - baseParty ref (cambia en cada packet partyState)
            // - in/out-of-range por miembro (depende de mi pos vs su pos)
            // BUG FIX: el user actual SIEMPRE está en rango consigo mismo.
            // Al cambiar de mapa, this.config.mapNumber se actualiza al
            // instante pero el partyState packet con tu nueva pos tarda
            // hasta 1500ms en llegar. Sin este shortcut tu propio 👤 se
            // pone gris durante la transición.
            const rangeFp = baseParty.members.map(m => {
                if (!m.connected) return "d";
                if (m.hp <= 0 && m.maxHp > 0) return "x";
                if (myName && m.name === myName) return "i"; // self always in range
                if (m.map !== myMap) return "o";
                if (Math.abs(m.x - myX) > r || Math.abs(m.y - myY) > r) return "o";
                return "i";
            }).join("");
            // BUG FIX: cache invalidation. Antes el fp era `baseParty + "|"
            // + rangeFp` lo cual hacía string-coerce de baseParty a
            // "[object Object]" constante → el fp NO cambiaba cuando
            // baseParty era una ref nueva pero rangeFp se mantenía igual
            // (caso típico: cambio de color por /gm, kick, etc). Ahora
            // chequeamos baseParty por REF y rangeFp por valor.
            if (this._augBaseRef !== baseParty || this._augRangeFp !== rangeFp) {
                this._augBaseRef = baseParty;
                this._augRangeFp = rangeFp;
                this._augParty = Object.assign({}, baseParty, {
                    members: baseParty.members.map(m => Object.assign({}, m, {
                        inXpRange:
                            m.connected
                            && (m.hp > 0 || m.maxHp === 0)
                            && (
                                (myName && m.name === myName)
                                || (
                                    m.map === myMap
                                    && Math.abs(m.x - myX) <= r
                                    && Math.abs(m.y - myY) <= r
                                )
                            )
                    }))
                });
            }
            party = this._augParty;
        }

        if (
            this._partyEl_lastParty === party &&
            this._partyEl_lastInvite === inviteName &&
            this._partyEl_lastMyName === myName &&
            this._partyEl_lastMapNames === mapNames &&
            this._partyEl_cached
        ) {
            return this._partyEl_cached;
        }
        this._partyEl_lastParty = party;
        this._partyEl_lastInvite = inviteName;
        this._partyEl_lastMyName = myName;
        this._partyEl_lastMapNames = mapNames;
        this._partyEl_cached = (
            <PartyPanel
                style={style}
                party={party}
                partyInviteName={inviteName}
                myName={myName}
                mapNames={mapNames}
                onInviteNameChange={this._onPartyInviteNameChange}
                onSendDialog={this._onPartySendDialog}
                onMemberContextMenu={this.openPartyContextMenu}
            />
        );
        return this._partyEl_cached;
    };

    // Toggles MUTUAMENTE EXCLUSIVOS — PARTY y GLOBAL son dos vistas del
    // mismo slot del BOX 5. Solo una puede estar abierta a la vez. Al
    // abrir GLOBAL: PARTY se cierra. Al abrir PARTY: GLOBAL se cierra
    // (y su polling también para no gastar packets innecesarios).
    _stopGlobalPolling = () => {
        if (this._globalPollInterval) {
            clearInterval(this._globalPollInterval);
            this._globalPollInterval = null;
        }
    };

    togglePartyPanel = () => {
        this.setState(prev => {
            const next = !prev.showPartyModal;
            if (next) {
                const updates = { showPartyModal: true };
                if (prev.showGlobalPanel) {
                    this._stopGlobalPolling();
                    updates.showGlobalPanel = false;
                }
                if (prev.showClanPanel) updates.showClanPanel = false;
                return updates;
            }
            return { showPartyModal: next };
        });
    };

    // Toggle del panel CLAN. Mutual exclusion vs PARTY + GLOBAL. Al
    // abrirlo, refresca `/clan info` Y `/clan list` para tener data
    // fresh disponible:
    //   - info → renderea el panel inline (members o empty state)
    //   - list → precarga la lista de clanes disponibles ANTES que el
    //     user clickee "Fundar / Ver clanes" del empty state. Sin esto
    //     el modal legacy abre con state.clanList=[] y aparece "no hay
    //     clanes" durante el round-trip async.
    toggleClanPanel = () => {
        this.setState(prev => {
            const next = !prev.showClanPanel;
            if (next) {
                try {
                    this.general.sendDialog("/clan info");
                    this.general.sendDialog("/clan list");
                } catch (e) {}
                const updates = { showClanPanel: true };
                if (prev.showPartyModal) updates.showPartyModal = false;
                if (prev.showGlobalPanel) {
                    this._stopGlobalPolling();
                    updates.showGlobalPanel = false;
                }
                return updates;
            }
            return { showClanPanel: next };
        });
    };

    _onClanInviteNameChange = val => {
        this.setState({ clanInviteName: val });
    };

    _onClanSendDialog = cmd => {
        try {
            this.general.sendDialog(cmd);
        } catch (e) {}
    };

    _getCachedClanPanelEl = () => {
        const clan = this.state.clan;
        const inviteName = this.state.clanInviteName;
        if (
            this._clanEl_lastClan === clan &&
            this._clanEl_lastInvite === inviteName &&
            this._clanEl_cached
        ) {
            return this._clanEl_cached;
        }
        this._clanEl_lastClan = clan;
        this._clanEl_lastInvite = inviteName;
        this._clanEl_cached = (
            <ClanPanel
                style={style}
                clan={clan}
                inviteName={inviteName}
                onInviteNameChange={this._onClanInviteNameChange}
                onSendDialog={this._onClanSendDialog}
                onOpenLegacyModal={this.openClanModal}
                onMemberContextMenu={this.openClanContextMenu}
            />
        );
        return this._clanEl_cached;
    };

    // Toggle del panel GLOBAL. Al abrirlo pide refresh inmediato + cierra
    // los otros paneles del slot (PARTY/CLAN). Al cerrarlo limpia el polling.
    toggleGlobalPanel = () => {
        this.setState(prev => {
            const next = !prev.showGlobalPanel;
            if (next) {
                if (this.general && this.general.sendRequestGlobalList) {
                    this.general.sendRequestGlobalList();
                }
                if (this._globalPollInterval) clearInterval(this._globalPollInterval);
                this._globalPollInterval = setInterval(() => {
                    if (this.general && this.general.sendRequestGlobalList) {
                        this.general.sendRequestGlobalList();
                    }
                }, 5000);
                const updates = { showGlobalPanel: true };
                if (prev.showPartyModal) updates.showPartyModal = false;
                if (prev.showClanPanel) updates.showClanPanel = false;
                return updates;
            } else {
                this._stopGlobalPolling();
            }
            return { showGlobalPanel: next };
        });
    };

    _getCachedGlobalPanelEl = () => {
        const list = this.state.globalList;
        // Cache invalidation por ref. El server siempre manda un array
        // nuevo → cualquier login/logout dispara reseteo del cache.
        if (this._globalEl_lastList === list && this._globalEl_cached) {
            return this._globalEl_cached;
        }
        this._globalEl_lastList = list;
        this._globalEl_cached = (
            <GlobalPanel
                style={style}
                list={list}
                onRowContextMenu={this.openGlobalContextMenu}
            />
        );
        return this._globalEl_cached;
    };

    renderBoxItems = () => {
        const { user, selectItem, invDraggingFrom, invDraggingOver, inventoryKeybindings } = this.state;
        const items = user.items || {};
        const bindings = inventoryKeybindings || {};

        let html = [];

        // 24 slots (4 × 6 grid — match con el banco). Server swapItems
        // valida 1..24 ahora.
        for (let i = 1; i < 25; i++) {
            const item = items[i];
            const isDragging = invDraggingFrom === i;
            const isDropTarget = invDraggingOver === i && invDraggingFrom;
            const objData =
                item && inits.objs ? inits.objs[item.idItem] : null;
            const tooltipText = buildItemTooltip(objData);
            // Badge (ticket #208) — si este item tiene tecla bindeada,
            // mostrar el char arriba a la izquierda del slot. Lookup por
            // idItem canónico → si el slot driftéo, el badge sigue al item.
            const boundKey = item && bindings[item.idItem];
            const boundChar = boundKey ? this.keyCodeToChar(boundKey) : "";

            html.push(
                <div
                    className={`${style.slot_inv} ${
                        selectItem === i ? style.item_selected : ""
                    } ${isDragging ? style.slot_dragging : ""} ${
                        isDropTarget ? style.slot_drop_target : ""
                    }`}
                    key={i}
                    draggable={!!item}
                    data-tooltip={tooltipText || undefined}
                    onClick={() => this.handleItemSlotClick(i)}
                    onContextMenu={e =>
                        this.openInventoryContextMenu(
                            e,
                            i,
                            item ? item.idItem : null
                        )
                    }
                    onDragStart={e => this.onInvDragStart(e, i)}
                    onDragOver={e => this.onInvDragOver(e, i)}
                    onDragLeave={this.onInvDragLeave}
                    onDragEnd={this.onInvDragEnd}
                    onDrop={e => this.onInvDrop(e, i)}
                >
                    <div
                        className={`${style.img_item} ${
                            item && !item.validUser ? style.itemNotValid : ""
                        }`}
                        style={{
                            backgroundImage:
                                item &&
                                inits.graphics &&
                                inits.graphics[item.grhIndex]
                                    ? `url("/static/graficos/${
                                          inits.graphics[item.grhIndex].numFile
                                      }.png")`
                                    : "none"
                        }}
                    />
                    <div className={style.amount}>{item ? item.cant : ""}</div>

                    {item && item.equipped ? (
                        <div className={style.equipped}>E</div>
                    ) : null}

                    {boundChar && (
                        <div
                            className={style.invSlotKey}
                            title={"Tecla asignada: " + boundChar}
                        >
                            {boundChar}
                        </div>
                    )}
                </div>
            );
        }

        return html;
    };

    // Selecciona un hechizo de la lista (sin lanzarlo todavía).
    selectSpellInList = i => {
        const spell =
            this.state.user.spells && this.state.user.spells[i];
        if (!spell) return;
        this.setState({ selectedSpellSlot: i });
    };

    // Resuelve qué hechizo es el "activo" para lanzar — el seleccionado en
    // la lista del tab Hechizos. Devuelve idPos o 0.
    getActiveSpellSlot = () => {
        return this.state.selectedSpellSlot || 0;
    };

    // Lanzar el hechizo "activo" (dispone según el tab actual).
    castSelectedSpell = () => {
        const { user } = this.state;
        const slot = this.getActiveSpellSlot();
        if (!slot) return;
        const spell = user.spells && user.spells[slot];
        if (!spell) return;
        if (!(this.user && this.user.maxMana > 0)) return;

        // Guardamos el idSpell CANÓNICO (no el slot). Ver engine.js.
        this.config.hechizoIdSpell = spell.idSpell;
        this.setState({ crosshair: true });
        this.game.writeConsole("Haz click sobre el objetivo...", "gray");
    };

    // Mover el hechizo seleccionado N posiciones (la lista del tab
    // Hechizos). Delegado a moveSelectedSpell.
    moveActiveSpell = delta => {
        this.moveSelectedSpell(delta);
    };

    // Popover del botón de sort. Un solo botón abre/cierra; cada item
    // dispara el sort y cierra el menú.
    toggleSortMenu = () => {
        this.setState(s => ({ sortMenuOpen: !s.sortMenuOpen }));
    };

    closeSortMenu = () => {
        if (this.state.sortMenuOpen) {
            this.setState({ sortMenuOpen: false });
        }
    };

    pickSort = mode => {
        this.sortActiveSpells(mode);
        this.setState({ sortMenuOpen: false });
    };

    // Listener global de mousedown para cerrar popovers / context menus
    // / modales con click outside. Solo trabaja si alguno está abierto.
    handleDocMouseDown = e => {
        // Ticket #238: modal "Asignar tecla" (spell/item key capture).
        if (this.state.capturingSpellKey) {
            const card = this._spellKeyCaptureCardRef;
            if (card && !card.contains(e.target)) {
                this.cancelSpellKeyCapture();
                return;
            }
        }
        if (this.state.capturingItemKey) {
            const card = this._itemKeyCaptureCardRef;
            if (card && !card.contains(e.target)) {
                this.cancelItemKeyCapture();
                return;
            }
        }

        // Ticket #238: context menus de spell / inventario (right-click).
        // Click fuera del menu lo cierra como ESC.
        if (this.state.spellContextMenu) {
            const menu = this._spellContextMenuRef;
            if (menu && !menu.contains(e.target)) {
                this.setState({ spellContextMenu: null });
                return;
            }
        }
        if (this.state.inventoryContextMenu) {
            const menu = this._inventoryContextMenuRef;
            if (menu && !menu.contains(e.target)) {
                this.setState({ inventoryContextMenu: null });
                return;
            }
        }
        if (this.state.petModeMenu) {
            const menu = this._petModeMenuRef;
            if (menu && !menu.contains(e.target)) {
                this.setState({ petModeMenu: null });
                return;
            }
        }
        // Context menu del PartyPanel (right-click sobre miembro). Click
        // afuera lo cierra.
        if (this.state.partyContextMenu) {
            const menu = this._partyContextMenuRef;
            if (menu && !menu.contains(e.target)) {
                this.setState({ partyContextMenu: null });
                return;
            }
        }
        // Context menu del GlobalPanel (right-click sobre char online).
        // Click afuera lo cierra.
        if (this.state.globalContextMenu) {
            const menu = this._globalContextMenuRef;
            if (menu && !menu.contains(e.target)) {
                this.setState({ globalContextMenu: null });
                return;
            }
        }
        // Context menu del ClanPanel (right-click sobre miembro, líder only).
        if (this.state.clanContextMenu) {
            const menu = this._clanContextMenuRef;
            if (menu && !menu.contains(e.target)) {
                this.setState({ clanContextMenu: null });
                return;
            }
        }

        if (!this.state.sortMenuOpen) return;
        const wrap = this._sortMenuRef;
        if (wrap && !wrap.contains(e.target)) {
            this.setState({ sortMenuOpen: false });
        }
    };

    // Sort de la lista de hechizos por nombre o costo de mana. Resultado
    // se persiste vía syncSpellOrder (array de idSpell estable).
    sortActiveSpells = mode => {
        const { user } = this.state;
        const spells = (user && user.spells) || {};

        // Índice idSpell → entry (para evitar O(n²) en el comparador).
        const idSpellToEntry = {};
        Object.keys(spells).forEach(k => {
            const sp = spells[k];
            if (sp && sp.idSpell) {
                idSpellToEntry[sp.idSpell] = sp;
            }
        });
        const cmp = (idSpellA, idSpellB) => {
            const sa = idSpellToEntry[idSpellA] || {};
            const sb = idSpellToEntry[idSpellB] || {};
            if (mode === "mana") {
                return (sa.manaRequired || 0) - (sb.manaRequired || 0);
            }
            return (sa.name || "").localeCompare(sb.name || "", "es");
        };

        const sorted = Object.keys(spells)
            .map(k => spells[k] && spells[k].idSpell)
            .filter(Boolean)
            .sort(cmp);
        this.setState(
            { spellOrder: sorted, selectedSpellSlot: 0 },
            () => this.syncSpellOrder(sorted)
        );
    };

    // Carga lazy del catálogo completo de hechizos (spells.json del server)
    // para mostrar la ficha de info. Se cachea en state.spellsData.
    openSpellInfo = async () => {
        const { user } = this.state;
        const activeSlot = this.getActiveSpellSlot();
        if (!activeSlot) return;
        const spell = user.spells && user.spells[activeSlot];
        if (!spell) return;

        let data = this.state.spellsData;
        if (!data) {
            try {
                data = await fetch("/static/init/spells.json").then(r =>
                    r.json()
                );
                if (this._unmounted) return;
                this.setState({ spellsData: data });
            } catch (e) {
                data = {};
            }
        }
        this.setState({ spellInfoTarget: spell.idSpell });
    };

    closeSpellInfo = () => {
        this.setState({ spellInfoTarget: null });
    };

    // Devuelve el idSpell que actualmente ocupa el slot idPos, o null si
    // el slot está vacío. Helper para convertir entre idPos (efímero, slot
    // visible) e idSpell (estable, id real del hechizo).
    getIdSpellAtPos = idPos => {
        const spells = (this.state.user && this.state.user.spells) || {};
        return spells[idPos] ? spells[idPos].idSpell : null;
    };

    // Devuelve el idPos donde está hoy el hechizo con ese idSpell, o null.
    findIdPosByIdSpell = idSpell => {
        const spells = (this.state.user && this.state.user.spells) || {};
        for (const k of Object.keys(spells)) {
            if (spells[k] && spells[k].idSpell === idSpell) {
                return parseInt(k, 10);
            }
        }
        return null;
    };

    // Devuelve el idPos donde está hoy el item con ese idItem, o null.
    // Espejo de findIdPosByIdSpell — usado por triggerMacro para resolver
    // la posición ACTUAL del item ante swaps del inventario. Sin esto,
    // los macros guardaban idPos (slot) y al swappear items el macro
    // disparaba sobre lo que quedó en ese slot (ej. armadura en lugar
    // de la poción que estaba antes).
    findIdPosByIdItem = idItem => {
        const items = (this.state.user && this.state.user.items) || {};
        for (const k of Object.keys(items)) {
            if (items[k] && items[k].idItem === idItem) {
                return parseInt(k, 10);
            }
        }
        return null;
    };

    // Convierte el state.spellOrder (array de idSpell, source of truth desde
    // el server) a una lista de idPos para renderizar. idSpell que ya no
    // están en user.spells (olvidados) se filtran. Hechizos en user.spells
    // que no están en spellOrder (recién aprendidos) se appendean al final
    // en orden de idPos ascendente.
    getEffectiveSpellOrder = () => {
        const { user, spellOrder } = this.state;
        const spells = (user && user.spells) || {};
        const idSpellToIdPos = {};
        for (const k of Object.keys(spells)) {
            const sp = spells[k];
            if (sp && sp.idSpell) {
                idSpellToIdPos[sp.idSpell] = parseInt(k, 10);
            }
        }

        const ordered = [];
        const seenIdSpell = new Set();
        for (const idSpell of spellOrder || []) {
            const idPos = idSpellToIdPos[idSpell];
            if (idPos && !seenIdSpell.has(idSpell)) {
                ordered.push(idPos);
                seenIdSpell.add(idSpell);
            }
        }
        const newSlots = Object.keys(spells)
            .map(k => parseInt(k, 10))
            .filter(
                n =>
                    Number.isFinite(n) &&
                    spells[n] &&
                    !seenIdSpell.has(spells[n].idSpell)
            )
            .sort((a, b) => a - b);
        return ordered.concat(newSlots);
    };

    // Envía el spellOrder actual (array de idSpell) al server vía WS.
    // El server lo guarda en memoria y persiste en el próximo save a Mongo.
    syncSpellOrder = idSpellArr => {
        if (this.game && this.game.updateSpellOrder) {
            this.game.updateSpellOrder(idSpellArr);
        }
    };

    // Mover el hechizo seleccionado N posiciones (positivo = abajo, negativo = arriba).
    // Internamente trabaja sobre el array de idSpell (state.spellOrder).
    moveSelectedSpell = delta => {
        const { selectedSpellSlot } = this.state;
        if (!selectedSpellSlot || !delta) return;
        const idSpell = this.getIdSpellAtPos(selectedSpellSlot);
        if (!idSpell) return;

        // Computar la lista de idSpell efectiva (refleja lo que está
        // visible, incluyendo los appendeados al final).
        const orderByPos = this.getEffectiveSpellOrder();
        const idSpellOrdered = orderByPos
            .map(p => this.getIdSpellAtPos(p))
            .filter(Boolean);

        const idx = idSpellOrdered.indexOf(idSpell);
        if (idx < 0) return;
        const target = idx + delta;
        if (target < 0 || target >= idSpellOrdered.length) return;

        const next = idSpellOrdered.slice();
        const tmp = next[idx];
        next[idx] = next[target];
        next[target] = tmp;
        this.setState({ spellOrder: next }, () => this.syncSpellOrder(next));
    };

    renderBoxSpells = () => {
        const { user, selectedSpellSlot } = this.state;
        const spells = (user && user.spells) || {};
        const hasSelection =
            selectedSpellSlot && spells[selectedSpellSlot];

        // Orden efectivo: respeta el reorder manual del usuario, agrega
        // nuevos hechizos al final. NUNCA filtra spells que existan en
        // user.spells — si están en el dict, se ven en la lista.
        const slots = this.getEffectiveSpellOrder();

        // Posición actual del seleccionado en la lista, para deshabilitar
        // botones de mover cuando estamos en los extremos.
        const selIdx = selectedSpellSlot
            ? slots.indexOf(selectedSpellSlot)
            : -1;
        const canMoveUp = selIdx > 0;
        const canMoveDown = selIdx >= 0 && selIdx < slots.length - 1;

        const rows = [];
        const spellKeybindings = this.state.spellKeybindings || {};
        for (const i of slots) {
            const spell = spells[i];
            if (!spell) continue;
            const isSelected = i === selectedSpellSlot;
            // Si este hechizo tiene una tecla asignada, mostramos badge.
            // Lookup por idSpell canónico (el slot puede haber driftéo
            // por reorder, pero el binding sigue el idSpell).
            const boundKeyCode = spellKeybindings[spell.idSpell];
            const boundKeyChar = boundKeyCode
                ? this.keyCodeToChar(boundKeyCode)
                : "";
            rows.push(
                <li
                    key={i}
                    className={`${style.spellRow} ${
                        isSelected ? style.spellRowSelected : ""
                    }`}
                    onClick={() => this.selectSpellInList(i)}
                    onDoubleClick={() => {
                        this.setState(
                            { selectedSpellSlot: i },
                            this.castSelectedSpell
                        );
                    }}
                    onContextMenu={e =>
                        this.openSpellContextMenu(e, spell.idSpell)
                    }
                    title={spell.name + " (click derecho para asignar tecla)"}
                >
                    <span className={style.spellRowName}>{spell.name}</span>
                    {boundKeyChar && (
                        <span
                            className={style.spellRowKey}
                            title={"Tecla asignada: " + boundKeyChar}
                        >
                            {boundKeyChar}
                        </span>
                    )}
                    <span className={style.spellRowMana}>
                        {spell.manaRequired}
                    </span>
                </li>
            );
        }

        return (
            <div className={style.spellListWrap}>
                <ul className={style.spellList}>
                    {rows.length === 0 ? (
                        <li className={style.spellListEmpty}>
                            No conocés ningún hechizo.
                        </li>
                    ) : (
                        rows
                    )}
                </ul>
            </div>
        );
    };

    // Action row del tab Hechizos: Lanzar + info + mover up/down + sort.
    renderSpellActionRow = () => {
        const { selectedSpellSlot, user, sortMenuOpen } = this.state;
        const spells = (user && user.spells) || {};
        const activeSlot = this.getActiveSpellSlot();
        const hasActive = !!(activeSlot && spells[activeSlot]);

        const slots = this.getEffectiveSpellOrder();
        const idx = selectedSpellSlot ? slots.indexOf(selectedSpellSlot) : -1;
        const canMoveUp = idx > 0;
        const canMoveDown = idx >= 0 && idx < slots.length - 1;
        const canSort = slots.length > 1;

        return (
            <div className={style.spellActions}>
                <button
                    type="button"
                    className={style.spellCastBtn}
                    disabled={!hasActive}
                    onClick={this.castSelectedSpell}
                    title="Lanzar el hechizo seleccionado"
                >
                    Lanzar
                </button>
                <button
                    type="button"
                    className={style.spellInfoBtn}
                    disabled={!hasActive}
                    onClick={this.openSpellInfo}
                    title="Información del hechizo"
                >
                    i
                </button>
                <button
                    type="button"
                    className={style.spellMoveBtn}
                    disabled={!canMoveUp}
                    onClick={() => this.moveActiveSpell(-1)}
                    title="Mover hacia arriba"
                >
                    ↑
                </button>
                <button
                    type="button"
                    className={style.spellMoveBtn}
                    disabled={!canMoveDown}
                    onClick={() => this.moveActiveSpell(1)}
                    title="Mover hacia abajo"
                >
                    ↓
                </button>
                <div
                    className={style.spellSortWrap}
                    ref={ref => (this._sortMenuRef = ref)}
                >
                    <button
                        type="button"
                        className={`${style.spellSortBtn} ${
                            sortMenuOpen ? style.spellSortBtnActive : ""
                        }`}
                        disabled={!canSort}
                        onClick={this.toggleSortMenu}
                        title="Ordenar hechizos"
                        aria-expanded={sortMenuOpen}
                    >
                        <svg
                            viewBox="0 0 24 24"
                            className={style.spellSortIcon}
                            aria-hidden="true"
                        >
                            <path
                                d="M4 6h16M6 12h12M9 18h6"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                fill="none"
                            />
                        </svg>
                    </button>
                    {sortMenuOpen && (
                        <div className={style.spellSortMenu} role="menu">
                            <button
                                type="button"
                                className={style.spellSortMenuItem}
                                onClick={() => this.pickSort("name")}
                                role="menuitem"
                            >
                                <span className={style.spellSortMenuGlyph}>
                                    A↓
                                </span>
                                <span className={style.spellSortMenuLabel}>
                                    Alfabético
                                </span>
                            </button>
                            <button
                                type="button"
                                className={style.spellSortMenuItem}
                                onClick={() => this.pickSort("mana")}
                                role="menuitem"
                            >
                                <span className={style.spellSortMenuGlyph}>
                                    ⚡
                                </span>
                                <span className={style.spellSortMenuLabel}>
                                    Por mana
                                </span>
                            </button>
                        </div>
                    )}
                </div>
            </div>
        );
    };


    // Buffs activos del personaje. Posicionado top-right dentro de la
    // caja del juego (.render), absoluto. Cada buff tiene su ícono cuadrado,
    // un overlay de progreso (decrece de arriba a abajo a medida que se
    // agota) y un contador numérico de segundos restantes.
    //
    // Por ahora solo invisibilidad. Para agregar más buffs:
    //   1) Agregar campo de state con el timestamp de expiración (o un
    //      objeto si necesitás más metadata).
    //   2) Agregar push a `buffs[]` acá con su ícono SVG y duración total.
    //   3) Si querés un ticker más rápido o más lento, ajustar buffTickerStart.
    renderBuffs = () => {
        const {
            invisibilityExpiresAt,
            sombraOscuraActive,
            ojoDemiurgoActive,
            sigiloAtidorfaActive,
            fuerzaBuffExpiresAt,
            celeridadBuffExpiresAt
        } = this.state;
        const now = Date.now();
        const buffs = [];

        // Helpers locales — declarados una sola vez por render para
        // que las 3-4 sites que computan timer/progress no repitan código.
        const fmt = ms => {
            const totalSec = Math.max(1, Math.ceil(ms / 1000));
            if (totalSec < 60) return totalSec + "s";
            const m = Math.floor(totalSec / 60);
            const s = totalSec % 60;
            return m + ":" + (s < 10 ? "0" + s : s);
        };
        // Para buffs tipo "sigilo" (Sombra/Ojo/Sigilo) — self-cast ∞,
        // cast-on-other countdown 30s. Si expiresAt es null/0 → ∞.
        const sigiloProgress = expiresAt => {
            if (!expiresAt) return { timerText: "∞", progress: 1 };
            const remMs = expiresAt - now;
            if (remMs <= 0) return { timerText: "∞", progress: 1 };
            const sec = Math.max(1, Math.ceil(remMs / 1000));
            const timerText = sec < 60 ? sec + "s"
                : Math.floor(sec / 60) + ":" + (sec % 60 < 10 ? "0" : "") + (sec % 60);
            return { timerText, progress: Math.max(0, Math.min(1, remMs / 30000)) };
        };

        // ── ORDEN VISUAL: 1) Invisibilidad → 2) Ojo Demiurgo →
        //    3) Sombra Oscura → 4) Sigilo Atidorfa → 5) Fuerza →
        //    6) Celeridad → 7) Pets (al final). Para reordenar, mover
        //    los push() de lugar — son self-contained.

        // 1. Invisibilidad (#14)
        if (invisibilityExpiresAt && invisibilityExpiresAt > now) {
            const remainingMs = invisibilityExpiresAt - now;
            buffs.push({
                key: "invisibility",
                label: "Invisibilidad",
                timerText: fmt(remainingMs),
                progress: Math.max(0, Math.min(1, remainingMs / 13000)),
                tone: "violet",
                onClick: () => {
                    if (this.general && this.general.sendEndBuff) {
                        this.general.sendEndBuff(BUFF_TYPE.INVISIBILIDAD);
                    }
                },
                icon: (
                    <svg viewBox="0 0 24 24" className={style.buffIconSvg} aria-hidden="true">
                        <path d="M12 5c-5 0-9.2 3-11 7 1.8 4 6 7 11 7s9.2-3 11-7c-1.8-4-6-7-11-7zm0 11.5a4.5 4.5 0 1 1 0-9 4.5 4.5 0 0 1 0 9zm0-7a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5z" fill="currentColor"/>
                        <line x1="4" y1="4" x2="20" y2="20" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"/>
                    </svg>
                )
            });
        }

        // 2. Ojo del Demiurgo (#43)
        if (ojoDemiurgoActive) {
            const p = sigiloProgress(this.state.ojoDemiurgoExpiresAt);
            buffs.push({
                key: "ojoDemiurgo",
                label: "Ojo del Demiurgo",
                timerText: p.timerText,
                progress: p.progress,
                tone: "violet",
                onClick: () => {
                    if (this.general && this.general.sendDeactivateOjoDemiurgo) {
                        this.general.sendDeactivateOjoDemiurgo();
                    }
                },
                icon: (
                    <svg viewBox="0 0 24 24" className={style.buffIconSvg} aria-hidden="true">
                        <path d="M2 12s4-7 10-7 10 7 10 7-4 7-10 7S2 12 2 12z" fill="currentColor" opacity="0.7"/>
                        <circle cx="12" cy="12" r="4" fill="#ffe599"/>
                        <circle cx="12" cy="12" r="1.5" fill="#000"/>
                        <line x1="12" y1="3" x2="12" y2="5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
                        <line x1="12" y1="19" x2="12" y2="21" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
                    </svg>
                )
            });
        }

        // 3. Sombra Oscura de Morgolock (#49)
        if (sombraOscuraActive) {
            const p = sigiloProgress(this.state.sombraOscuraExpiresAt);
            buffs.push({
                key: "sombraOscura",
                label: "Sombra Oscura",
                timerText: p.timerText,
                progress: p.progress,
                tone: "violet",
                onClick: () => {
                    if (this.general && this.general.sendDeactivateSombraOscura) {
                        this.general.sendDeactivateSombraOscura();
                    }
                },
                icon: (
                    <svg viewBox="0 0 24 24" className={style.buffIconSvg} aria-hidden="true">
                        <path d="M12 2c-3 0-5 2.5-5 5.5 0 2 .8 3.4 1.6 4.5L7 14c-1.2 1-2 2.6-2 4.4V22h14v-3.6c0-1.8-.8-3.4-2-4.4l-1.6-2c.8-1.1 1.6-2.5 1.6-4.5C17 4.5 15 2 12 2z" fill="currentColor"/>
                        <circle cx="9.5" cy="9" r="0.9" fill="#000"/>
                        <circle cx="14.5" cy="9" r="0.9" fill="#000"/>
                    </svg>
                )
            });
        }

        // 4. Sigilo de Atidorfa (#51) — el sigilo más poderoso
        if (sigiloAtidorfaActive) {
            const p = sigiloProgress(this.state.sigiloAtidorfaExpiresAt);
            buffs.push({
                key: "sigiloAtidorfa",
                label: "Sigilo de Atidorfa",
                timerText: p.timerText,
                progress: p.progress,
                tone: "violet",
                onClick: () => {
                    if (this.general && this.general.sendDeactivateSigiloAtidorfa) {
                        this.general.sendDeactivateSigiloAtidorfa();
                    }
                },
                icon: (
                    // Pluma minimalista — silencio + ingrávido.
                    <svg viewBox="0 0 24 24" className={style.buffIconSvg} aria-hidden="true">
                        <path d="M17 4 C 13 5, 9 9, 7 14 C 6 16.5, 6.5 18, 8 19 L 10 18.5 C 11 18, 12 17, 13 15.5 C 15 12.5, 17 8.5, 17 4 Z" fill="currentColor" opacity="0.7"/>
                        <path d="M17 4 C 14 7, 11 11, 9 15 C 8 17, 8 18.5, 8.5 19" stroke="#000" strokeWidth="0.9" fill="none" opacity="0.55" strokeLinecap="round"/>
                        <path d="M13.5 7 L 15 6 M 12 10 L 13.8 9 M 10.5 13 L 12.5 12.2 M 9.5 16 L 11 15.3" stroke="currentColor" strokeWidth="0.65" fill="none" opacity="0.85" strokeLinecap="round"/>
                        <line x1="8.3" y1="19.2" x2="7" y2="20.6" stroke="currentColor" strokeWidth="0.7" opacity="0.5" strokeLinecap="round"/>
                    </svg>
                )
            });
        }

        // 5. Fuerza
        if (fuerzaBuffExpiresAt && fuerzaBuffExpiresAt > now) {
            const remainingMs = fuerzaBuffExpiresAt - now;
            const totalMs = 89000;
            buffs.push({
                key: "fuerza",
                label: "Fuerza",
                timerText: fmt(remainingMs),
                progress: Math.max(0, Math.min(1, remainingMs / totalMs)),
                tone: "crimson",
                onClick: () => {
                    if (this.general && this.general.sendEndBuff) {
                        this.general.sendEndBuff(BUFF_TYPE.FUERZA);
                    }
                },
                icon: (
                    // Brazo flexionado estilizado (bíceps).
                    <svg
                        viewBox="0 0 24 24"
                        className={style.buffIconSvg}
                        aria-hidden="true"
                    >
                        <path
                            d="M3 17c0-3 2-5 5-5 1 0 1.6.2 2.4.6.5-2.4 2.4-4.6 5.6-4.6 2.8 0 5 2.2 5 5 0 1.6-.7 3-1.8 4-1 .9-2.2 1-3.4.6L13 16l-2 5H4l-1-4z"
                            fill="currentColor"
                        />
                        <circle
                            cx="16"
                            cy="9"
                            r="1.4"
                            fill="rgba(0,0,0,0.35)"
                        />
                    </svg>
                )
            });
        }

        if (celeridadBuffExpiresAt && celeridadBuffExpiresAt > now) {
            const remainingMs = celeridadBuffExpiresAt - now;
            const totalMs = 89000;
            buffs.push({
                key: "celeridad",
                label: "Celeridad",
                timerText: fmt(remainingMs),
                progress: Math.max(0, Math.min(1, remainingMs / totalMs)),
                tone: "lime",
                onClick: () => {
                    if (this.general && this.general.sendEndBuff) {
                        this.general.sendEndBuff(BUFF_TYPE.CELERIDAD);
                    }
                },
                icon: (
                    // Rayo de velocidad.
                    <svg
                        viewBox="0 0 24 24"
                        className={style.buffIconSvg}
                        aria-hidden="true"
                    >
                        <path
                            d="M13 2 4 14h6l-1 8 9-12h-6l1-8z"
                            fill="currentColor"
                        />
                    </svg>
                )
            });
        }

        // 7. Aceleración Arcana (#52) — buff stackeable de velocidad.
        //    Display value = min(stack+1, 5) → x2/x3/x4/x5. Speed = 2^stack.
        //    Cada cast refresca duración a 13s. Click cancela.
        if (this.state.aceleracionActive && this.state.aceleracionExpiresAt && this.state.aceleracionExpiresAt > now) {
            const remainingMs = this.state.aceleracionExpiresAt - now;
            const totalMs = 13000;
            const stack = this.state.aceleracionStack || 1;
            const displayNum = Math.min(stack + 1, 5);
            buffs.push({
                key: "aceleracionArcana",
                label: "Aceleración Arcana ×" + displayNum,
                timerText: fmt(remainingMs),
                stackText: "×" + displayNum,
                progress: Math.max(0, Math.min(1, remainingMs / totalMs)),
                tone: "azure",
                onClick: () => {
                    if (this.general && this.general.sendEndBuff) {
                        this.general.sendEndBuff(4);
                    }
                },
                icon: (
                    <svg viewBox="0 0 24 24" className={style.buffIconSvg} aria-hidden="true">
                        <path d="M4 8 L10 12 L4 16" stroke="currentColor" strokeWidth="2.4" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M11 8 L17 12 L11 16" stroke="currentColor" strokeWidth="2.4" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
                        <line x1="19" y1="9" x2="21" y2="9" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" opacity="0.7"/>
                        <line x1="19" y1="12" x2="21" y2="12" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" opacity="0.7"/>
                        <line x1="19" y1="15" x2="21" y2="15" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" opacity="0.7"/>
                    </svg>
                )
            });
        }

        // Ticket #258: pets activos (Elemental de Agua/Fuego/Tierra). Hasta 3
        // entradas. Cada una tiene su contador propio (3 min cada una, pero
        // el server las puede invocar en momentos distintos así que cada
        // buff tiene su expiresAt independiente).
        const petBuffs = this.state.activePetBuffs || [];
        // Ticket #280: extendido el catálogo de pet icons. Cada pet renderea
        // un SVG procedural (forma + paleta distinta) para que el user las
        // distinga a golpe de vista. Default fallback = gota azul.
        const PET_INFO = {
            92: { label: "Elemental de Agua",   tone: "azure",   colorMain: "#4ec1ff", colorAccent: "#1a6da8", shape: "drop" },
            93: { label: "Elemental de Fuego",  tone: "crimson", colorMain: "#ff7847", colorAccent: "#a83e1a", shape: "drop" },
            94: { label: "Elemental de Tierra", tone: "earth",   colorMain: "#a6885a", colorAccent: "#5a4a30", shape: "drop" },
            501: { label: "Lobo",         tone: "stone",   colorMain: "#a8a294", colorAccent: "#54514a", shape: "wolf" },
            502: { label: "Zombie",       tone: "necro",   colorMain: "#7ea36e", colorAccent: "#3a4d33", shape: "zombie" },
            509: { label: "Dragón Rojo",  tone: "crimson", colorMain: "#d8442a", colorAccent: "#5a1208", shape: "dragon" },
            // Ticket #298: Dragón Blanco — paleta blanco-hielo con accent
            // azul gris frío, para que se distinga del Dragón Rojo (crimson).
            909: { label: "Dragón Blanco", tone: "stone", colorMain: "#e8f1ff", colorAccent: "#85a3d1", shape: "dragon" },
            // Ticket #299: Dragón Negro — paleta noche con accent gris frío,
            // contrapartida visual del Blanco. Tone "necro" coincide con
            // Zombie pero shape es dragon — paleta única diferencia.
            910: { label: "Dragón Negro", tone: "necro", colorMain: "#2a2a32", colorAccent: "#6b6b78", shape: "dragon" }
        };
        const renderPetIcon = (info) => {
            switch (info.shape) {
                case "wolf":
                    return (
                        <svg viewBox="0 0 24 24" className={style.buffIconSvg} aria-hidden="true">
                            <path d="M4 14 L6 8 L9 10 L12 6 L15 10 L18 8 L20 14 L18 18 L15 16 L12 18 L9 16 L6 18 Z"
                                fill={info.colorMain} stroke={info.colorAccent} strokeWidth="1.2"/>
                            <circle cx="9.5" cy="12.5" r="1.2" fill={info.colorAccent}/>
                            <circle cx="14.5" cy="12.5" r="1.2" fill={info.colorAccent}/>
                        </svg>
                    );
                case "zombie":
                    return (
                        <svg viewBox="0 0 24 24" className={style.buffIconSvg} aria-hidden="true">
                            <rect x="6" y="5" width="12" height="14" rx="2"
                                fill={info.colorMain} stroke={info.colorAccent} strokeWidth="1.2"/>
                            <circle cx="9.5" cy="10" r="1.5" fill={info.colorAccent}/>
                            <circle cx="14.5" cy="10" r="1.5" fill={info.colorAccent}/>
                            <path d="M8 15 L10 14 L12 15 L14 14 L16 15" stroke={info.colorAccent}
                                strokeWidth="1.2" fill="none"/>
                        </svg>
                    );
                case "dragon":
                    return (
                        <svg viewBox="0 0 24 24" className={style.buffIconSvg} aria-hidden="true">
                            <path d="M4 16 L8 8 L12 12 L16 6 L20 16 L18 18 L14 14 L10 18 L6 14 Z"
                                fill={info.colorMain} stroke={info.colorAccent} strokeWidth="1.2"/>
                            <circle cx="12" cy="13" r="1.8" fill="#FFD700" stroke={info.colorAccent} strokeWidth="0.8"/>
                        </svg>
                    );
                default: // "drop"
                    return (
                        <svg viewBox="0 0 24 24" className={style.buffIconSvg} aria-hidden="true">
                            <path d="M12 2 C 7 8, 5 12, 5 16 a 7 7 0 0 0 14 0 c 0 -4, -2 -8, -7 -14 z"
                                fill={info.colorMain} stroke={info.colorAccent} strokeWidth="1.5"/>
                            <circle cx="12" cy="16" r="2.5" fill={info.colorAccent} opacity="0.6"/>
                        </svg>
                    );
            }
        };
        petBuffs.forEach(p => {
            if (!p || !p.expiresAt || p.expiresAt <= now) return;
            const info = PET_INFO[p.npcIndex] || {
                label: "Mascota",
                tone: "azure",
                colorMain: "#4ec1ff",
                colorAccent: "#1a6da8",
                shape: "drop"
            };
            const remainingMs = p.expiresAt - now;
            const totalMs = p.totalMs || 180000;
            const mode = p.mode || "defensive";
            buffs.push({
                key: "pet-" + p.id,
                label: info.label,
                timerText: fmt(remainingMs),
                progress: Math.max(0, Math.min(1, remainingMs / totalMs)),
                tone: info.tone,
                icon: renderPetIcon(info),
                // Ticket #280: pet-specific interactivity.
                petId: p.id,
                petMode: mode,
                petTitle: `${info.label} (${mode === "offensive" ? "ofensivo" : "defensivo"}) — ${fmt(remainingMs)}\nClick: despawn. Click derecho: cambiar modo.`
            });
        });

        if (!buffs.length) return null;

        return (
            <div className={style.buffs}>
                {buffs.map(b => {
                    const isPet = b.petId != null;
                    const hasCustomClick = typeof b.onClick === "function";
                    const onClick = isPet
                        ? (e => { e.preventDefault(); this.sendDespawnPet(b.petId); })
                        : hasCustomClick
                            ? (e => { e.preventDefault(); b.onClick(); })
                            : undefined;
                    const onContextMenu = isPet
                        ? (e => { e.preventDefault(); this.openPetModeMenu(e, b.petId); })
                        : undefined;
                    const isOffensive = isPet && b.petMode === "offensive";
                    const modeClass = isPet
                        ? (isOffensive ? style.buffPetOffensive : style.buffPetDefensive)
                        : "";
                    const isClickable = isPet || hasCustomClick;
                    // Aviso visual: cuando queda menos del 16.67% (1/6) de
                    // la duración, el buff entra en estado "por expirar"
                    // y titila para llamar la atención del jugador. No se
                    // aplica a buffs sin progreso real (sombraOscura, pets
                    // sin timer) — éstos vienen con progress=1 fijo.
                    const isExpiring =
                        typeof b.progress === "number" &&
                        b.progress > 0 &&
                        b.progress < 0.1667 &&
                        !isPet;
                    return (
                        <div
                            key={b.key}
                            className={`${style.buff} ${style["buff_" + b.tone] || ""} ${modeClass} ${isClickable ? style.buffClickable : ""} ${isExpiring ? style.buffExpiring : ""}`}
                            title={b.petTitle || `${b.label} — ${b.timerText}` + (hasCustomClick ? " (click para desactivar)" : "")}
                            onClick={onClick}
                            onContextMenu={onContextMenu}
                        >
                            <div
                                className={style.buffProgress}
                                style={{ height: `${b.progress * 100}%` }}
                            />
                            <div className={style.buffIcon}>{b.icon}</div>
                            <div className={style.buffTimer}>{b.timerText}</div>
                            {b.stackText && (
                                <div className={style.buffStackBadge}>{b.stackText}</div>
                            )}
                            {isPet && (
                                <div
                                    className={style.buffPetBadge}
                                    aria-label={isOffensive ? "Ofensivo" : "Defensivo"}
                                    title={isOffensive ? "Ofensivo" : "Defensivo"}
                                >
                                    {isOffensive ? (
                                        // Espadita — guard cruzada + hoja delgada.
                                        <svg viewBox="0 0 12 12" width="9" height="9" aria-hidden="true">
                                            <path
                                                d="M9.5 1 L11 2.5 L5.5 8 L4 6.5 z M3 7 L5 9 M2.5 9.5 L1.5 10.5 M3.5 8.5 L4.5 9.5"
                                                stroke="#e6e0cd"
                                                strokeWidth="1"
                                                fill="none"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                            />
                                        </svg>
                                    ) : (
                                        // Escudito — silueta heráldica simple.
                                        <svg viewBox="0 0 12 12" width="9" height="9" aria-hidden="true">
                                            <path
                                                d="M6 1.5 L10 2.5 L9.5 7 C 9.5 9, 7.5 10.2, 6 10.8 C 4.5 10.2, 2.5 9, 2.5 7 L 2 2.5 z"
                                                stroke="#e6e0cd"
                                                strokeWidth="1"
                                                fill="none"
                                                strokeLinejoin="round"
                                            />
                                        </svg>
                                    )}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        );
    };

    // Modal con ficha completa del hechizo (sprite + nombre + desc +
    // mana + daño/cura + efectos + palabras mágicas).
    renderSpellInfoModal = () => {
        const { spellInfoTarget, spellsData } = this.state;
        if (!spellInfoTarget) return null;
        const data = (spellsData && spellsData[String(spellInfoTarget)]) || {};

        const minHp = Number(data.minHp) || 0;
        const maxHp = Number(data.maxHp) || 0;

        // Hechizo ofensivo: hace daño, paraliza, inmoviliza, o es debuff de
        // stat (subeAg=2 → Torpeza, subeFz=2 → Debilidad). Estos solo se
        // tiran a enemigos.
        const isOffensive =
            data.paraliza === 1 ||
            data.inmoviliza === 1 ||
            data.subeAg === 2 ||
            data.subeFz === 2 ||
            minHp < 0 ||
            maxHp < 0;

        // Hechizo beneficioso: cura, buffea stats, resucita, vuelve invisi,
        // remueve parálisis. Se puede tirar a uno mismo o a un aliado.
        const isBeneficial =
            data.subeHp === 1 ||
            data.subeAg === 1 ||
            data.subeFz === 1 ||
            data.invisibilidad === 1 ||
            data.revivir === 1 ||
            data.removerParalisis === 1 ||
            minHp > 0 ||
            maxHp > 0;

        const facts = [];
        facts.push({ label: "Mana", value: data.manaRequired || 0 });
        if (data.minSkill) {
            facts.push({ label: "Skill mín.", value: data.minSkill });
        }
        if (minHp || maxHp) {
            // Sign del HP determina Cura vs Daño. Los datos originales
            // del juego usan el mismo campo para ambos casos.
            const isHeal = minHp >= 0 && maxHp >= 0;
            const abs = n => Math.abs(n);
            const r =
                abs(minHp) === abs(maxHp)
                    ? `${abs(maxHp)}`
                    : `${abs(minHp)} - ${abs(maxHp)}`;
            facts.push({
                label: isHeal ? "Cura" : "Daño",
                value: r
            });
        }
        if (data.minAg || data.maxAg) {
            facts.push({
                label: "Agilidad",
                value: `${data.minAg || 0} - ${data.maxAg || 0}`
            });
        }
        if (data.minFz || data.maxFz) {
            facts.push({
                label: "Fuerza",
                value: `${data.minFz || 0} - ${data.maxFz || 0}`
            });
        }

        // Objetivo: derivado de los efectos del hechizo, NO del campo `target`
        // de spells.json (que es ambiguo — Celeridad tiene target=1 pero se
        // puede tirar a aliados). Solo cuando los efectos no son concluyentes
        // (e.g. summons, antídoto sin flag estructurado) caemos al fallback
        // del campo `target`.
        const targetLabel = (() => {
            if (isOffensive) return "Enemigo";
            if (isBeneficial) return "Aliado";
            switch (Number(data.target)) {
                case 1:
                    return "Uno mismo";
                case 2:
                    return "Aliado";
                case 3:
                    return "Enemigo";
                default:
                    return "—";
            }
        })();

        return (
            <div
                className={style.modalSpellInfo}
                onClick={this.closeSpellInfo}
            >
                <div
                    className={style.modalSpellInfoInner}
                    onClick={e => e.stopPropagation()}
                >
                    <div className={style.modalSpellInfoHeader}>
                        <span>{data.name || `Hechizo ${spellInfoTarget}`}</span>
                        <span
                            className={style.modalSpellInfoClose}
                            onClick={this.closeSpellInfo}
                            title="Cerrar"
                        >
                            <FontAwesomeIcon icon={faTimes} />
                        </span>
                    </div>
                    <div className={style.modalSpellInfoBody}>
                        <div className={style.modalSpellInfoTop}>
                            <div
                                className={style.modalSpellInfoImg}
                                style={{
                                    backgroundImage: `url('/static/spells/${spellInfoTarget}.png')`
                                }}
                            />
                            <div className={style.modalSpellInfoFacts}>
                                {facts.map((f, i) => (
                                    <div
                                        key={i}
                                        className={style.modalSpellInfoFact}
                                    >
                                        <span
                                            className={
                                                style.modalSpellInfoFactLabel
                                            }
                                        >
                                            {f.label}
                                        </span>
                                        <span
                                            className={
                                                style.modalSpellInfoFactValue
                                            }
                                        >
                                            {f.value}
                                        </span>
                                    </div>
                                ))}
                                <div className={style.modalSpellInfoFact}>
                                    <span
                                        className={
                                            style.modalSpellInfoFactLabel
                                        }
                                    >
                                        Objetivo
                                    </span>
                                    <span
                                        className={
                                            style.modalSpellInfoFactValue
                                        }
                                    >
                                        {targetLabel}
                                    </span>
                                </div>
                            </div>
                        </div>
                        {data.desc && (
                            <p className={style.modalSpellInfoDesc}>
                                {data.desc}
                            </p>
                        )}
                        {data.palabrasMagicas && (
                            <div className={style.modalSpellInfoWords}>
                                <span
                                    className={
                                        style.modalSpellInfoWordsLabel
                                    }
                                >
                                    Palabras mágicas
                                </span>
                                <em>{data.palabrasMagicas}</em>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    // Ejecuta el macro del slot dado. Llamado desde:
    //   - keypress handler (cuando el user aprieta la tecla bindeada)
    //   - onClick del slot en la macroBar (mismo efecto)
    // Si tiene item asignado → game.useItem. Si tiene spell → resolve
    // idSpell → idPos actual (por si el slot driftéo) y selectSpell.
    triggerMacro = idx => {
        const { valueKeyMacro, showMacroConfig } = this.state;
        if (showMacroConfig) return;
        const macro = valueKeyMacro && valueKeyMacro[idx];
        if (!macro) return;

        if (macro.idPosItem !== "" && macro.idPosItem !== undefined) {
            // Si tenemos idItem canónico (macros nuevos), resolvemos el
            // slot actual buscando por idItem — robusto a swaps del inv.
            // Si no (macros viejos en localStorage), fallback al idPosItem
            // guardado. Después del primer save post-fix, todos los
            // macros tienen idItem y este fallback no se ejecuta.
            let currentIdPos = macro.idPosItem;
            if (macro.idItem) {
                const resolved = this.findIdPosByIdItem(macro.idItem);
                if (resolved) {
                    currentIdPos = resolved;
                } else {
                    // El item ya no está en el inv — silenciar el trigger
                    // (antes ejecutaba sobre lo que quedó en idPosItem,
                    // ej. armadura en vez de poción que tiraste).
                    return;
                }
            }
            this.game.useItem(currentIdPos);
            return;
        }
        if (macro.idSpell !== "" && macro.idSpell !== undefined) {
            // idPosSpell guardado puede haber driftéo — resolvemos por
            // idSpell canónico (más confiable).
            const currentIdPos = this.findIdPosByIdSpell(macro.idSpell);
            if (currentIdPos) {
                this.selectSpell(currentIdPos);
            }
        }
    };

    renderBoxMacros = () => {
        const { valueKeyMacro, keyCodeDefault, user } = this.state;
        const userItems = (user && user.items) || {};

        let html = [];

        // Slots 1..9: macros regulares configurables (ver MACRO_COUNT en defaults.js).
        // Guard: si valueKeyMacro está corrupto (no array), no iterar.
        if (!Array.isArray(valueKeyMacro)) return html;

        // Perf 2026-07-07: precalcular idItem → total UNA vez (un solo scan
        // de userItems), en vez del loop anidado O(macros × items) que
        // re-escaneaba todo el inv por CADA macro. Con 9 macros y 24 slots
        // eran hasta 216 iteraciones/render → ahora 24 + 9 lookups O(1).
        const itemTotals = {};
        for (const k in userItems) {
            const it = userItems[k];
            if (it && it.idItem) {
                itemTotals[it.idItem] = (itemTotals[it.idItem] || 0) + (it.cant || 0);
            }
        }

        for (let i = 0; i < MACRO_COUNT; i++) {
            const macro = valueKeyMacro[i];
            // Slot vacío (puede pasar si el state quedó con menos entradas
            // que MACRO_COUNT por carrera de migración). Renderear placeholder.
            if (!macro) continue;

            // Ticket #215: contar items del macro bindeado en tiempo real.
            // Lookup por idItem canónico (no por idPosItem, que puede haber
            // driftéo). Suma TODOS los stacks del mismo idItem por si el
            // user splitteó el stack en varios slots — el HUD muestra el
            // total real disponible para usar. Update live: cada vez que
            // un item se consume del inv, este render se redibuja con el
            // nuevo cant.
            let macroItemCant = null;
            if (macro && macro.idItem) {
                const total = itemTotals[macro.idItem] || 0;
                if (total > 0) macroItemCant = total;
            }

            html.push(
                <div
                    key={i}
                    className={style.macro}
                    onClick={() => this.triggerMacro(i)}
                    onContextMenu={e => this.showMacroConfig(e, i)}
                    ref={ref => {
                        this.macros[i] = ref;
                    }}
                >
                    {macro.idPosItem !== "" && macro.img ? (
                        <div
                            className={style.item}
                            style={{
                                backgroundImage: `url("${macro.img}")`
                            }}
                        />
                    ) : null}

                    {macro.idSpell !== "" && macro.img ? (
                        <div
                            className={style.spell}
                            style={{
                                backgroundImage: `url("${macro.img}")`
                            }}
                        />
                    ) : null}

                    {macroItemCant !== null ? (
                        <div className={style.macroCant}>{macroItemCant}</div>
                    ) : null}

                    {macro.keyChar !== "" ? (
                        <div className={style.key}>{macro.keyChar}</div>
                    ) : null}
                </div>
            );
        }

        return html;
    };

    // Ticket #235: simplified selection — TradeModal computa el preview
    // (img/name/info/precio) internamente desde tradeItems[idPosTrade] o
    // userItems[idPosInv]. El padre solo trackea qué slot está marcado.
    selectItemUserTrade = i => {
        this.setState(prev => ({
            trade: { ...prev.trade, idPosInv: i }
        }));
    };

    selectItemTrade = i => {
        this.setState(prev => ({
            trade: { ...prev.trade, idPosTrade: i }
        }));
    };

    buyTrade = () => {
        const { trade, cantTrade } = this.state;

        // Validar item existente — chequeamos que HAY un item en el slot,
        // no que el index sea truthy (idPosTrade=0 es slot válido del
        // comerciante).
        if (trade.itemsTrade[trade.idPosTrade]) {
            this.pkg.setPackageID(this.pkg.serverPacketID.buyItem);
            this.pkg.writeByte(trade.idPosTrade);
            this.pkg.writeShort(cantTrade);
            this.config.ws.send(this.pkg.dataSend());
        }
    };

    sellTrade = () => {
        const { trade, cantTrade } = this.state;

        // Ticket #235: validar item existente + no equipado. Antes
        // sellTrade mandaba el packet incluso si el slot del user
        // estaba vacío o el item estaba equipado.
        const item = trade.idPosInv ? trade.itemsUser[trade.idPosInv] : null;
        if (item && !item.equipped) {
            this.pkg.setPackageID(this.pkg.serverPacketID.sellItem);
            this.pkg.writeByte(trade.idPosInv);
            this.pkg.writeShort(cantTrade);
            this.config.ws.send(this.pkg.dataSend());
        }
    };

    // Ticket #235: setter del input de cantidad (botones +/-, edit
    // directo). Cap en 1 para abajo; sin cap arriba (server clampea).
    setCantTrade = n => {
        this.setState({ cantTrade: Math.max(1, parseInt(n, 10) || 1) });
    };

    // Sistema de música inmersiva — Web Audio API en engine/music.js.
    // El antiguo syncMusicEl manejaba un <audio> nativo que nunca fue
    // implementado. Ahora delega al engine: setVolume + setEnabled.
    // El engine maneja crossfade entre mapas, silencio entre loops,
    // ducking en eventos (muerte/level-up/whisper).
    syncMusicEl = () => {
        const { musicEnabled, musicVolume } = this.state;
        // Radio y música del mapa son MUTUAMENTE EXCLUYENTES. Si la radio
        // está sonando: (a) el toggle de música NO revive el mapa — queda
        // aplicado recién cuando la radio se apague; (b) el slider de
        // volumen controla la RADIO. El estado del toggle/slider se
        // persiste igual (localStorage), así la radio al apagarse
        // restaura lo que el user eligió.
        let radioOn = false;
        try {
            const radioEngine = require("../../engine/radio").default;
            radioOn = radioEngine.isEnabled();
            // Slider 0-100 → YT player 0-100 (misma escala).
            radioEngine.setVolume(musicVolume);
        } catch (e) {}
        musicEngine.setEnabled(!!musicEnabled && !radioOn);
        musicEngine.setVolume(Math.max(0, Math.min(1, musicVolume / 100)));
    };

    // Llamado cuando cambia el mapa del char (telep o respawn). Notifica
    // al engine que dispara el crossfade. Idempotente si el track resultado
    // es el mismo (e.g. dentro del mismo bioma de bosque).
    onMapChangedMusic = mapNumber => {
        try {
            musicEngine.setMap(mapNumber);
        } catch (e) {}
    };

    openWorldMap = async () => {
        // Lazy-load del worldmap.json + mapnames.json en el primer open.
        // 13KB + ~30KB → cacheado por el browser de ahí en más.
        if (!this.state.worldMapData) {
            try {
                const [wm, names] = await Promise.all([
                    fetch("/static/init/worldmap.json").then(r => r.json()),
                    fetch("/static/init/mapnames.json").then(r => r.json())
                ]);
                if (this._unmounted) return;
                this.setState({
                    worldMapData: wm,
                    mapNames: names,
                    showWorldMap: true
                });
            } catch (e) {
                this.setState({ showWorldMap: true });
            }
        } else {
            this.setState({ showWorldMap: true });
        }
    };

    closeWorldMap = () => {
        this.setState({ showWorldMap: false });
    };

    findMapInWorlds = (mapNum) => {
        // Devuelve { worldIndex, row, col } o null si no se encuentra.
        const wm = this.state.worldMapData;
        if (!wm || !wm.worlds || !mapNum) return null;
        for (const w of wm.worlds) {
            const i = w.cells.indexOf(mapNum);
            if (i >= 0) {
                return {
                    worldIndex: w.index,
                    row: Math.floor(i / w.ancho),
                    col: i % w.ancho
                };
            }
        }
        return null;
    };

    // Ref callback compartido entre ambos modales (mundi + full). Además de
    // guardar el ref, re-aplica .mapOverlayDimmed si hay teclas de movimiento
    // sostenidas en este momento (transición de estado mid-walk debe mantener
    // la transparencia hasta que el user pare).
    attachMapOverlayRef = ref => {
        this.mapOverlayRef = ref;
        if (ref && this.heldMoveKeys && this.heldMoveKeys.size > 0) {
            ref.classList.add(style.mapOverlayDimmed);
        }
    };

    // Click derecho sobre el overlay del mapa full (estado 1 = mapa actual,
    // estado 3 = mapa seleccionado desde mapamundi) → vuelve al mapamundi
    // (estado 2). Limpia selectedMapToShow así si el user vuelve a estado 1
    // ve el mapa actual del char (no el último seleccionado).
    onFullMapRightClick = e => {
        if (e && e.preventDefault) e.preventDefault();
        this.openWorldMap();
        this.setState({ showFullMap: false, selectedMapToShow: null });
    };

    // Click derecho en el mapamundi (estado 2) → cierra al estado 0.
    // Atajo simétrico al right-click del full map.
    onWorldMapRightClick = e => {
        if (e && e.preventDefault) e.preventDefault();
        this.setState({ showWorldMap: false, selectedMapToShow: null });
    };

    // Render del overlay del mapa actual (o seleccionado desde mapamundi).
    // Mounted dentro de .render, ocupa 672x672 absolute inset:0.

    // Ticket #218: setter del toggle "mostrar party en el mapa".
    setShowPartyOnMap = mode => {
        if (mode !== "none" && mode !== "show") return;
        this.setState({ showPartyOnMap: mode });
        try {
            window.localStorage.setItem("showPartyOnMap", mode);
        } catch (e) {}
    };

    togglePartyPanelList = () => {
        this.setState(s => ({ showPartyPanelList: !s.showPartyPanelList }));
    };

    // Ticket #218 polish: resolve dot color class por facción del entity.
    // Prioridad: GM > criminal > ciudadano. Devuelve sufijo de class para
    // composing (`Gm`, `Criminal`, `Citizen`) — el caller usa template
    // string `${baseClass}${suffix}`.
    // ── Ticket #313: handlers del modal de clan ───────────────────────
    openClanModal = () => {
        this.setState({ showClanModal: true });
        // Refresh info + lista on demand.
        try {
            this.general.sendDialog("/clan info");
            this.general.sendDialog("/clan list");
        } catch (e) {}
    };

    closeClanModals = () => {
        this.setState({
            showClanModal: false,
            clanFoundName: "",
            clanFoundType: "mixto",
            clanDissolveConfirm: "",
            showClanDissolveConfirm: false
        });
    };

    submitClanFound = () => {
        const name = (this.state.clanFoundName || "").trim();
        const type = this.state.clanFoundType || "mixto";
        if (name.length < 3 || name.length > 20) {
            if (this.game && this.game.writeConsole) {
                this.game.writeConsole(
                    "El nombre del clan debe tener entre 3 y 20 caracteres.",
                    "#FF6464", 0, 0
                );
            }
            return;
        }
        try {
            this.general.sendDialog("/clan fundar " + type + " " + name);
        } catch (e) {}
        this.closeClanModals();
    };

    // Pide unirse a un clan por su id.
    requestJoinClan = clanId => {
        if (!clanId) return;
        try {
            this.general.sendDialog("/clan solicitar " + clanId);
        } catch (e) {}
    };

    // Líder acepta/rechaza una solicitud.
    acceptJoinRequest = requesterId => {
        if (!requesterId) return;
        try {
            this.general.sendDialog("/clan aceptarsolicitud " + requesterId);
        } catch (e) {}
    };

    rejectJoinRequest = requesterId => {
        if (!requesterId) return;
        try {
            this.general.sendDialog("/clan rechazarsolicitud " + requesterId);
        } catch (e) {}
    };

    // Salir del clan (no líder) o iniciar la confirmación de disolución
    // (líder + último miembro).
    submitClanLeave = () => {
        const c = this.state.clan || {};
        const members = c.members || [];
        // Si sos líder y hay otros members → no podés salir hasta kickearlos.
        // El server lo rechazará; mostramos el mensaje desde el HUD igual.
        if (c.isLeader && members.length > 1) {
            if (this.game && this.game.writeConsole) {
                this.game.writeConsole(
                    "Como líder, tenés que expulsar a los demás miembros antes de salir.",
                    "#FF6464", 0, 0
                );
            }
            return;
        }
        // Si es disolución (líder único), pedir confirmación tipeando el nombre.
        if (c.isLeader && members.length === 1) {
            this.setState({
                showClanDissolveConfirm: true,
                clanDissolveConfirm: ""
            });
            return;
        }
        // Caso normal: salir sin más.
        try {
            this.general.sendDialog("/clan salir");
        } catch (e) {}
        this.closeClanModals();
    };

    submitClanDissolve = () => {
        const c = this.state.clan || {};
        const expected = (c.name || "").trim();
        const typed = (this.state.clanDissolveConfirm || "").trim();
        if (typed !== expected) {
            if (this.game && this.game.writeConsole) {
                this.game.writeConsole(
                    "El nombre tipeado no coincide.",
                    "#FF6464", 0, 0
                );
            }
            return;
        }
        try {
            this.general.sendDialog("/clan salir");
        } catch (e) {}
        this.closeClanModals();
    };

    promptClanInvite = () => {
        // Simple: usar el input del chat con /clan invitar pre-rellenado.
        this.setState({
            textDialog: "/clan invitar ",
            showInputText: true,
            showClanModal: false
        });
    };

    promptClanKick = name => {
        try {
            this.general.sendDialog("/clan kick " + name);
        } catch (e) {}
    };

    // Drag handlers del modal del clan.
    onClanModalMouseDown = e => {
        // Solo arrastrar si el target es el header (data-clan-drag-handle).
        if (!e.target || !e.target.closest("[data-clan-drag-handle]")) return;
        e.preventDefault();
        const startX = e.clientX;
        const startY = e.clientY;
        const startPos = this.state.clanModalPos || { x: 0, y: 0 };
        const onMove = ev => {
            this.setState({
                clanModalPos: {
                    x: startPos.x + (ev.clientX - startX),
                    y: startPos.y + (ev.clientY - startY)
                }
            });
        };
        const onUp = () => {
            window.removeEventListener("mousemove", onMove);
            window.removeEventListener("mouseup", onUp);
        };
        window.addEventListener("mousemove", onMove);
        window.addEventListener("mouseup", onUp);
    };

    factionClassSuffix = entity => {
        if (!entity) return "Citizen";
        // Ticket #312: el GM solo cuenta como "Gm" si su modo GM está
        // ACTIVO (clan === "<AOW Staff>"). El cliente no recibe el flag
        // gmEnabled directo, pero el server setea clan="<AOW Staff>"
        // cuando GM activo y "" cuando civil (login.js + gmToggle). Sin
        // este check el GM se veía verde aún tras /gm civil.
        if (
            entity.privileges &&
            entity.privileges > 0 &&
            entity.clan === "<AOW Staff>"
        ) {
            return "Gm";
        }
        // Ticket #312 polish: deducir criminal/ciudadano desde el COLOR
        // (que el server sincroniza vía sendCharacter), no desde el flag
        // entity.criminal — ese bool no llega en el packet sendCharacter
        // y queda stale en this.user cuando matás un civil in-game. El
        // color sí refleja el estado real (red / #8B0000 Caos / azul /
        // cian Real).
        var c = String(entity.color || "").toLowerCase();
        if (c === "red" || c === "#ff0000" || c === "#8b0000") {
            return "Criminal";
        }
        if (entity.criminal) return "Criminal"; // fallback legacy
        return "Citizen";
    };

    // Devuelve los miembros de la party (excluyendo al self) que están
    // en el mapa indicado. Si mapNum no se pasa → todos los del party.
    // Usado por el render de party en el mapa state 1 (mismo mapa) y
    // mapamundi state 2 (todos los maps).
    getPartyMembersForMap = mapNum => {
        const party = this.state.party;
        if (!party || !Array.isArray(party.members)) return [];
        const myName = this.user && this.user.nameCharacter;
        const out = [];
        for (const m of party.members) {
            if (!m) continue;
            if (myName && m.name === myName) continue; // skip self
            if (!m.map || !m.x || !m.y) continue;       // sin pos válida
            if (mapNum != null && m.map !== mapNum) continue;
            out.push(m);
        }
        return out;
    };

    // Resolve sprite (numFile + crop region) para un hostile entry. Prefiere
    // idBody (mob completo), cae a idHead si el body no resolvió. Returns
    // null si no hay sprite resolvable.
    //
    // Importante: los grhIndex en bodies.json/heads.json suelen apuntar a
    // entries ANIMADAS (numFrames > 1) que NO tienen numFile/sX/sY directo
    // sino un dict `frames: { 1: subGrhIdx, 2: subGrhIdx, ... }`. Hay que
    // dereferenciar el subGrh del primer frame para obtener el tile real.
    _resolveHostileSprite = entry => {
        if (!entry || !inits.graphics) return null;
        let grhIndex = 0;
        if (entry.idBody && inits.bodies && inits.bodies[entry.idBody]) {
            grhIndex = inits.bodies[entry.idBody]["2"];
        }
        if (!grhIndex && entry.idHead && inits.heads && inits.heads[entry.idHead]) {
            grhIndex = inits.heads[entry.idHead]["2"];
        }
        if (!grhIndex) return null;
        let g = inits.graphics[grhIndex];
        if (!g) return null;
        // Si es animada, resolver al sub-grh del primer frame (entry estática
        // con numFile/sX/sY). Bail si la cadena no resuelve a algo con numFile.
        if (g.numFrames > 1 && g.frames) {
            const subGrh = parseInt(g.frames["1"], 10);
            const sub = subGrh && inits.graphics[subGrh];
            if (sub && sub.numFile) g = sub;
        }
        if (!g.numFile) return null;
        return {
            numFile: g.numFile,
            sX: g.sX || 0,
            sY: g.sY || 0,
            width: g.width || 32,
            height: g.height || 32
        };
    };

    // Lista agrupada de NPCs hostiles que aparecen en un mapa. Lee data
    // estática (hostileSpawns + hostileCatalog cargadas en inits). Sin
    // round-trip al server. Sorted por count desc, name asc.
    getHostilesForMap = mapNum => {
        const spawns = inits.hostileSpawns && inits.hostileSpawns[String(mapNum)];
        if (!Array.isArray(spawns)) return [];
        const counts = {};
        for (const tuple of spawns) {
            const npcIdx = tuple[2];
            counts[npcIdx] = (counts[npcIdx] || 0) + 1;
        }
        const catalog = inits.hostileCatalog || {};
        const out = [];
        for (const idx in counts) {
            const entry = catalog[idx];
            if (!entry) continue;
            out.push({
                npcIndex: parseInt(idx, 10),
                name: entry.name,
                count: counts[idx],
                sprite: this._resolveHostileSprite(entry)
            });
        }
        out.sort(
            (a, b) =>
                b.count - a.count || (a.name || "").localeCompare(b.name || "")
        );
        return out;
    };

    renderFullMapModal = () => {
        const { user } = this.state;
        const mapToRender =
            this.state.selectedMapToShow || this.config.mapNumber;
        const isCurrentMap = mapToRender === this.config.mapNumber;
        // mapLabel solo desde fuentes "reales" — mapNames y nameMap. Si el
        // mapa no tiene nombre registrado, mapLabel queda null y NO
        // renderizamos el plaque superior (en lugar de mostrar "Tierras
        // desconocidas" o algo genérico).
        const rawLabel =
            (this.state.mapNames &&
                this.state.mapNames[String(mapToRender)]) ||
            (isCurrentMap ? this.state.nameMap : "") ||
            "";
        const mapLabel = rawLabel && rawLabel.trim() ? rawLabel : null;
        return (
            <div
                className={style.modalFullMap}
                ref={this.attachMapOverlayRef}
                onContextMenu={this.onFullMapRightClick}
            >
                <div
                    className={style.modalFullMapImg}
                    style={{
                        backgroundImage: mapToRender
                            ? `url('/static/imgs_mapas/${mapToRender}.png')`
                            : "none"
                    }}
                >
                    {/* Punto del char solo si estás viendo tu mapa actual.
                        Ticket #218: color por facción — verde GM > rojo
                        criminal > azul ciudadano. CSS clases con !important
                        para sobreescribir el rojo legacy del
                        .modalFullMapPoint. */}
                    {isCurrentMap && user.pos && (
                        <div
                            className={`${style.modalFullMapPoint} ${
                                style[
                                    "modalFullMapPoint" +
                                        this.factionClassSuffix(user)
                                ]
                            }`}
                            style={{
                                top: `${user.pos.y - 1}%`,
                                left: `${user.pos.x - 1}%`
                            }}
                        />
                    )}
                    {/* Ticket #218: dots de party en el full map. Mostramos
                        tanto en estado 1 (mapa actual) como en estado 3
                        (mapa seleccionado del mapamundi) — si hay miembros
                        en ESE mapa que estás visualizando, los mostramos. */}
                    {this.state.showPartyOnMap === "show" &&
                        this.getPartyMembersForMap(mapToRender).map(m => (
                            <div
                                key={"party_" + m.name}
                                className={`${style.modalFullMapPartyDot} ${
                                    style[
                                        "modalFullMapPartyDot" +
                                            this.factionClassSuffix(m)
                                    ]
                                }`}
                                style={{
                                    top: `${m.y - 1}%`,
                                    left: `${m.x - 1}%`
                                }}
                                title={m.name + " (party)"}
                            />
                        ))}
                    {/* Ticket #313: dots de miembros del clan SIEMPRE
                        visibles en el full map (estados 1, 2 mapamundi y 3),
                        independiente del toggle de party. */}
                    {(this.state.clanMembersPos || [])
                        .filter(m => m.map === mapToRender)
                        .map(m => (
                            <div
                                key={"clan_" + m.name}
                                className={style.modalFullMapClanDot}
                                style={{
                                    top: `${m.y - 1}%`,
                                    left: `${m.x - 1}%`
                                }}
                                title={m.name + " (clan)"}
                            />
                        ))}
                </div>
                {/* Ticket #218: panel Party (bottom-left del full map para
                    no chocar con el panel de enemigos). Aparece tanto en
                    state 1 (mapa actual) como en state 3 (mapa visualizado
                    desde mapamundi) — siempre que haya miembros en ese
                    mapa específico. */}
                {this.state.showPartyOnMap === "show" &&
                    (() => {
                        const members = this.getPartyMembersForMap(mapToRender);
                        if (members.length === 0) return null;
                        const collapsed = !this.state.showPartyPanelList;
                        return (
                            <div className={style.modalFullMapPartyPanel}>
                                <div
                                    className={`${
                                        style.modalFullMapPartyPanelHeader
                                    } ${
                                        collapsed
                                            ? style.modalFullMapPartyPanelHeaderCollapsed
                                            : ""
                                    }`}
                                    onClick={this.togglePartyPanelList}
                                    title={
                                        collapsed
                                            ? "Mostrar party"
                                            : "Ocultar lista de party"
                                    }
                                >
                                    <span>Party · {members.length}</span>
                                    <span
                                        className={
                                            style.modalFullMapPartyPanelChevron
                                        }
                                    >
                                        {collapsed ? "▾" : "▴"}
                                    </span>
                                </div>
                                {!collapsed && (
                                    <ul
                                        className={
                                            style.modalFullMapPartyPanelList
                                        }
                                    >
                                        {members.map(m => (
                                            <li
                                                key={"partyrow_" + m.name}
                                                className={
                                                    style.modalFullMapPartyPanelItem
                                                }
                                            >
                                                <span
                                                    className={`${
                                                        style.modalFullMapPartyPanelDot
                                                    } ${
                                                        style[
                                                            "modalFullMapPartyPanelDot" +
                                                                this.factionClassSuffix(
                                                                    m
                                                                )
                                                        ]
                                                    }`}
                                                />
                                                <span
                                                    className={
                                                        style.modalFullMapPartyPanelName
                                                    }
                                                >
                                                    {m.name}
                                                </span>
                                                <span
                                                    className={
                                                        style.modalFullMapPartyPanelPos
                                                    }
                                                >
                                                    {m.x},{m.y}
                                                </span>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                        );
                    })()}
                {/* Panel de enemigos (bottom-right, balanceando el party
                    panel a bottom-left). Lista agrupada con sprite + count
                    de los NPCs hostiles que aparecen en este mapa. Lee
                    data estática desde inits.hostileSpawns/Catalog — sin
                    round-trip al server. */}
                {(() => {
                    const hostiles = this.getHostilesForMap(mapToRender);
                    if (hostiles.length === 0) return null;
                    const totalCount = hostiles.reduce(
                        (s, h) => s + h.count,
                        0
                    );
                    return (
                        <div className={style.modalFullMapEnemiesPanel}>
                            <div className={style.modalFullMapEnemiesHeader}>
                                <span>Enemigos · {totalCount}</span>
                            </div>
                            <ul className={style.modalFullMapEnemiesList}>
                                {hostiles.map(h => (
                                    <li
                                        key={"enemy_" + h.npcIndex}
                                        className={
                                            style.modalFullMapEnemyItem
                                        }
                                    >
                                        <div
                                            className={
                                                style.modalFullMapEnemySpriteBox
                                            }
                                        >
                                            {h.sprite && (() => {
                                                // Scale-to-fit en la box 32×32. Si el sprite
                                                // ya es <= 32 en ambas dims, no escala (mantiene
                                                // pixel-perfect). Si excede, escala proporcional
                                                // para que el lado mayor entre y se centra.
                                                const sBox = 32;
                                                const scale = Math.min(
                                                    sBox / h.sprite.width,
                                                    sBox / h.sprite.height,
                                                    1
                                                );
                                                return (
                                                    <div
                                                        className={
                                                            style.modalFullMapEnemySprite
                                                        }
                                                        style={{
                                                            backgroundImage: `url('/static/graficos/${h.sprite.numFile}.png')`,
                                                            backgroundPosition: `-${h.sprite.sX}px -${h.sprite.sY}px`,
                                                            width:
                                                                h.sprite.width +
                                                                "px",
                                                            height:
                                                                h.sprite.height +
                                                                "px",
                                                            transform:
                                                                scale < 1
                                                                    ? `scale(${scale.toFixed(3)})`
                                                                    : undefined
                                                        }}
                                                    />
                                                );
                                            })()}
                                        </div>
                                        <span
                                            className={
                                                style.modalFullMapEnemyName
                                            }
                                            title={h.name}
                                        >
                                            {h.name}
                                        </span>
                                        <span
                                            className={
                                                style.modalFullMapEnemyCount
                                            }
                                        >
                                            × {h.count}
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    );
                })()}

                {/* Plaque del mapa — siempre presente con la meta (Mapa N -
                    x,y). El título (nombre del lugar) solo se muestra si el
                    mapa tiene nombre registrado. Mapas sin nombre = plaque
                    con solo la meta. Aplica a estados 1 y 3 por igual. */}
                <div className={style.modalFullMapName}>
                    {mapLabel && (
                        <span className={style.modalFullMapNameTitle}>
                            {mapLabel}
                        </span>
                    )}
                    <span className={style.modalFullMapNameMeta}>
                        Mapa {mapToRender}
                        {isCurrentMap && user.pos
                            ? ` - ${user.pos.x},${user.pos.y}`
                            : ""}
                    </span>
                </div>
            </div>
        );
    };

    // Click en una celda del mapamundi: cierra el world map y abre el full
    // map mostrando ESE mapa (no el mapa actual). Permite "navegar" el mundo
    // visualmente desde el mapamundi.
    onWorldMapCellClick = mapId => {
        if (!mapId) return;
        const inRange = mapId >= 1 && mapId <= 290;
        if (!inRange) return;
        this.setState({
            showWorldMap: false,
            showFullMap: true,
            selectedMapToShow: mapId
        });
    };

    renderWorldMapModal = () => {
        const { worldMapData, mapNames } = this.state;
        const currentMap = this.config.mapNumber;
        const here = this.findMapInWorlds(currentMap);
        const worldIndex = (here && here.worldIndex) || 1;
        const world =
            worldMapData &&
            worldMapData.worlds &&
            worldMapData.worlds.find(w => w.index === worldIndex);

        // Ticket #218: mapa pre-computado de "miembros de party por mapId".
        // Si showPartyOnMap === "show", para cada celda del mapamundi
        // mostramos un badge con la count de miembros que están ahí.
        const partyByMap = {};
        if (this.state.showPartyOnMap === "show") {
            const all = this.getPartyMembersForMap(null);
            for (const m of all) {
                if (!partyByMap[m.map]) partyByMap[m.map] = [];
                partyByMap[m.map].push(m);
            }
        }

        return (
            <div
                className={style.modalWorldMap}
                ref={this.attachMapOverlayRef}
                onContextMenu={this.onWorldMapRightClick}
            >
                <div className={style.modalWorldMapBody}>
                    {!world && (
                        <div className={style.modalWorldMapEmpty}>
                            Cargando mapa del mundo...
                        </div>
                    )}
                    {world && (
                        <div
                            className={style.modalWorldMapGrid}
                            style={{
                                gridTemplateColumns: `repeat(${world.ancho}, 1fr)`,
                                gridTemplateRows: `repeat(${world.alto || Math.ceil(world.cells.length / world.ancho)}, 1fr)`
                            }}
                        >
                            {world.cells.map((mapId, idx) => {
                                if (!mapId) {
                                    return (
                                        <div
                                            key={idx}
                                            className={
                                                style.worldMapCellEmpty
                                            }
                                        />
                                    );
                                }
                                const isHere = mapId === currentMap;
                                const inRange = mapId >= 1 && mapId <= 290;
                                const name =
                                    mapNames && mapNames[String(mapId)];
                                return (
                                    <div
                                        key={idx}
                                        className={`${style.worldMapCell} ${
                                            isHere
                                                ? style.worldMapCellHere
                                                : ""
                                        } ${
                                            !inRange
                                                ? style.worldMapCellMissing
                                                : ""
                                        }`}
                                        style={{
                                            backgroundImage: inRange
                                                ? `url('/static/imgs_mapas/${mapId}.png')`
                                                : "none"
                                        }}
                                        onClick={() =>
                                            this.onWorldMapCellClick(mapId)
                                        }
                                        title={`${name || "Mapa sin nombre"} (#${mapId})`}
                                    >
                                        <span
                                            className={style.worldMapCellNum}
                                        >
                                            {mapId}
                                        </span>
                                        {isHere && (
                                            <span
                                                className={
                                                    style.worldMapCellMarker
                                                }
                                            />
                                        )}
                                        {/* Ticket #218: badge de party en
                                            esta celda. Muestra el count
                                            de miembros ahí; color del más
                                            reciente miembro (mix tolerado
                                            — el badge es indicativo, no
                                            granular). */}
                                        {partyByMap[mapId] &&
                                            partyByMap[mapId].length > 0 &&
                                            (() => {
                                                // Color por prioridad:
                                                //   GM > criminal > ciudadano.
                                                // Si hay cualquier GM en la
                                                // celda, badge verde. Sino
                                                // si hay criminal, rojo. Else
                                                // azul ciudadano.
                                                const list = partyByMap[mapId];
                                                let suffix = "Citizen";
                                                if (
                                                    list.some(
                                                        m =>
                                                            m.privileges &&
                                                            m.privileges > 0
                                                    )
                                                ) {
                                                    suffix = "Gm";
                                                } else if (
                                                    list.some(m => m.criminal)
                                                ) {
                                                    suffix = "Criminal";
                                                }
                                                return (
                                                    <span
                                                        className={`${
                                                            style.worldMapCellPartyBadge
                                                        } ${
                                                            style[
                                                                "worldMapCellPartyBadge" +
                                                                    suffix
                                                            ]
                                                        }`}
                                                        title={list
                                                            .map(m => m.name)
                                                            .join(", ")}
                                                    >
                                                        {list.length}
                                                    </span>
                                                );
                                            })()}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>
        );
    };

    // Handlers del modal "nueva versión disponible".
    // Recargar: cache-buster duro al URL así no agarra HTML cacheado.
    handleVersionReload = () => {
        try {
            const u = window.location.href.split("?")[0];
            window.location.href = u + "?v=" + Date.now();
        } catch (e) {
            window.location.reload();
        }
    };

    // Después: snooze 5 min — el engine no vuelve a abrir el modal hasta
    // que pase ese tiempo. Si el deploy es muy frecuente y el user dice
    // "después" cada vez, eventualmente recarga cuando quiera.
    handleVersionSnooze = () => {
        try { versionWatch.snooze(); } catch (e) {}
        this.setState({ showVersionUpdateModal: false });
    };

    // Recomputa el scale del HUD para que entre en el viewport actual.
    // Aplica `transform: scale(X)` al wrapper `.outer` con origin top center.
    // Si la viewport es lo suficientemente grande, factor=1 (no escala —
    // pixel-perfect). Caso típico:
    //   - Laptop 1366×768 (no FS): factor ~0.80 — HUD entra entero
    //   - 1920×1080 (no FS): factor 1.0 — sin scale
    //   - Fullscreen 1920×1080: factor depende del aspect ratio del HUD
    recomputeHudScale = () => {
        try {
            if (this._unmounted) return;
            // hudW × hudH = .outer total (content + paddings).
            // CONTENT = 1091×814, paddings = 132 top + 16 bottom + 16 horiz
            const HUD_W = 1091 + 32;
            const HUD_H = 814 + 132 + 16;
            const vw = window.innerWidth;
            const vh = window.innerHeight;
            const factor = Math.min(vw / HUD_W, vh / HUD_H, 1);
            // No tocar si el cambio es <1% — evita layout thrash on resize.
            const prev = this._hudScale || 1;
            if (Math.abs(factor - prev) < 0.01) return;
            this._hudScale = factor;
            // Aplicar via CSS variable — el style.scss la consume.
            document.documentElement.style.setProperty(
                "--hud-scale",
                String(factor)
            );
        } catch (e) {}
    };

    // Keyboard Lock API — bloquea Ctrl+W, Ctrl+T, Ctrl+1..9, etc. mientras
    // estamos en fullscreen. Solo Chrome/Edge desktop. En Firefox/Safari la
    // API no existe → fallback silencioso al blocker manual del
    // _handlerBrowserShortcutBlock (que cubre Ctrl+S/P/F/H/J/D/U/L/E/K/B + F1).
    //
    // Importante: NO lockeamos Escape ni F11 — son los escape hatches del
    // user para salir de fullscreen. El browser también provee Hold-Esc-2s
    // como anti-malware ininhibible.
    lockGameplayKeyboard = async () => {
        try {
            if (
                typeof navigator === "undefined" ||
                !navigator.keyboard ||
                !navigator.keyboard.lock
            ) {
                return false;
            }
            const isFs =
                document.fullscreenElement ||
                document.webkitFullscreenElement ||
                document.msFullscreenElement;
            if (!isFs) return false;

            const letterCodes = "ABCDEFGHIJKLMNOPQRSTUVWXYZ"
                .split("")
                .map(ch => "Key" + ch);
            const digitCodes = "0123456789"
                .split("")
                .map(ch => "Digit" + ch);
            const numpadCodes = "0123456789"
                .split("")
                .map(ch => "Numpad" + ch);

            await navigator.keyboard.lock([
                ...letterCodes,
                ...digitCodes,
                ...numpadCodes,
                "ControlLeft", "ControlRight",
                "ShiftLeft", "ShiftRight",
                "AltLeft", "AltRight",
                "MetaLeft", "MetaRight",          // Mac Cmd+W, etc.
                "ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight",
                "Space", "Backspace", "Tab",
                "F1", "F5"
                // NO: "Escape", "F11" — escape hatches para salir de fullscreen
            ]);
            this._keyboardLockActive = true;
            return true;
        } catch (e) {
            this._keyboardLockActive = false;
            return false;
        }
    };

    unlockGameplayKeyboard = () => {
        try {
            if (
                typeof navigator !== "undefined" &&
                navigator.keyboard &&
                navigator.keyboard.unlock
            ) {
                navigator.keyboard.unlock();
            }
        } catch (e) {}
        this._keyboardLockActive = false;
    };

    // Toggle pantalla completa. Best-effort: si el browser no soporta
    // requestFullscreen (iOS Safari pre-16.4), swallow silently. Al entrar
    // a fullscreen disparamos también el Keyboard Lock para bloquear los
    // shortcuts reservados del browser.
    toggleFullscreen = async () => {
        try {
            const docEl = document.documentElement;
            const isFs =
                document.fullscreenElement ||
                document.webkitFullscreenElement ||
                document.msFullscreenElement;
            if (!isFs) {
                const fn =
                    docEl.requestFullscreen ||
                    docEl.webkitRequestFullscreen ||
                    docEl.msRequestFullscreen;
                if (fn) {
                    const ret = fn.call(docEl);
                    if (ret && typeof ret.then === "function") await ret;
                    await this.lockGameplayKeyboard();
                }
            } else {
                this.unlockGameplayKeyboard();
                const fn =
                    document.exitFullscreen ||
                    document.webkitExitFullscreen ||
                    document.msExitFullscreen;
                if (fn) {
                    const ret = fn.call(document);
                    if (ret && typeof ret.then === "function") await ret;
                }
            }
        } catch (e) {}
    };

    doLogout = () => {
        // Ticket #259: garantizar persist de macros via sendBeacon ANTES
        // de limpiar el sessionStorage. Sin esto el ws close + reload
        // pueden ganar la race al POST normal y los macros se pierden.
        try {
            const idCharacter =
                window.sessionStorage.getItem("idCharacter");
            if (
                idCharacter &&
                Array.isArray(this.state.valueKeyMacro) &&
                typeof navigator !== "undefined" &&
                navigator.sendBeacon
            ) {
                const payload = JSON.stringify({
                    macros: this.state.valueKeyMacro
                });
                const blob = new Blob([payload], {
                    type: "application/json"
                });
                navigator.sendBeacon(
                    "/api/character/" +
                        encodeURIComponent(idCharacter) +
                        "/macros",
                    blob
                );
            }
        } catch (e) {}

        try {
            if (this.config && this.config.ws && this.config.ws.close) {
                this.config.varCloseForce = true;
                this.config.ws.close();
            }
        } catch (e) {}
        // Limpia el char seleccionado de este tab (per-tab session) para
        // que /play no auto-rejoinee con el mismo char.
        try {
            sessionStorage.removeItem("idCharacter");
        } catch (e) {}
        // Volver al lobby (/play). El WS quedó cerrado, el server detectará
        // disconnect. Desde el lobby el user puede elegir otro char o crear uno.
        window.location.href = "/play";
    };

    // ── Borrar personaje (lobby PvE) ──────────────────────────────
    openDeleteCharModal = (character, e) => {
        if (e) e.stopPropagation();
        this.setState({
            deleteCharTarget: character,
            deleteCharConfirmText: "",
            deleteCharError: ""
        });
    };

    closeDeleteCharModal = () => {
        if (this.state.deleteCharSubmitting) return;
        this.setState({
            deleteCharTarget: null,
            deleteCharConfirmText: "",
            deleteCharError: ""
        });
    };

    onDeleteCharInput = e => {
        this.setState({
            deleteCharConfirmText: e.target.value,
            deleteCharError: ""
        });
    };

    confirmDeleteChar = async () => {
        const { deleteCharTarget, deleteCharConfirmText } = this.state;
        if (!deleteCharTarget) return;
        if (deleteCharConfirmText.trim() !== deleteCharTarget.name) {
            this.setState({
                deleteCharError: "El nombre no coincide."
            });
            return;
        }
        this.setState({ deleteCharSubmitting: true, deleteCharError: "" });
        try {
            const result = await fetchUrl(
                `/character/${deleteCharTarget._id}`,
                {
                    method: "DELETE",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                        confirmName: deleteCharTarget.name
                    }),
                    credentials: "include"
                }
            );
            if (result && result.error) {
                this.setState({
                    deleteCharSubmitting: false,
                    deleteCharError: result.message || "Error al borrar"
                });
                return;
            }
            // OK: refrescar lista de chars sacando el borrado.
            const newList = (this.state.lobbyCharacters || []).filter(
                c => c && c._id !== deleteCharTarget._id
            );
            if (this._unmounted) return;
            this.setState({
                lobbyCharacters: newList,
                deleteCharTarget: null,
                deleteCharConfirmText: "",
                deleteCharSubmitting: false,
                deleteCharError: ""
            });
        } catch (err) {
            if (this._unmounted) return;
            this.setState({
                deleteCharSubmitting: false,
                deleteCharError: "Error de red."
            });
        }
    };

    // ── Lobby (pre-game) helpers ──────────────────────────────────

    // Callback que dispara CreateCharacterForm al crear un char exitosamente.
    // Refresca la lista del lobby (para que el char nuevo aparezca en el grid)
    // y entra directo al juego con él — análogo a clickear su slot.
    handleCharacterCreated = async character => {
        if (!character || !character._id) return;
        const chars = await this.fetchLobbyCharacters();
        if (this._unmounted) return;
        const idx = (chars || []).findIndex(c => c && c._id === character._id);
        if (idx === -1) {
            // Si por alguna razón Mongo no replicó a tiempo, igual lo
            // intentamos con el doc devuelto del POST.
            this.startGameFromLobby(character, chars.length);
            return;
        }
        this.startGameFromLobby(chars[idx], idx);
    };

    startGameFromLobby = async (character, slotIdx) => {
        // Guard: evita doble-init si el user clickea 2 veces rápido.
        // Sin esto, dos initGame() → dos WS connect → estado corrupto.
        if (this.gameInitStarted) return;
        this.gameInitStarted = true;

        const { account } = this.props;
        const { lobbyTypeGame } = this.state;
        if (!account || !account.accountId) {
            this.gameInitStarted = false;
            // La cuenta se crea desde el dropdown del navbar (path /register
            // ya no existe). Volvemos al home; ahí el usuario puede loguearse
            // o registrarse desde el botón Login.
            routerPush("/");
            return;
        }

        // Si no hay char, abortamos — el form de creación está embebido más
        // abajo en el lobby, no hay route a la que mandar al user.
        if (!character) {
            this.gameInitStarted = false;
            return;
        }

        // Persistir credenciales. idAccount/email = account-wide
        // (localStorage). idCharacter/idChar/typeGame = per-tab
        // (sessionStorage), permite multi-char simultáneo en tabs.
        window.localStorage.setItem("idAccount", account.accountId);
        window.localStorage.setItem("email", account.email);
        if (lobbyTypeGame === "PvE") {
            window.sessionStorage.setItem("idCharacter", character._id);
        } else {
            window.sessionStorage.setItem("idChar", String(slotIdx));
        }
        window.sessionStorage.setItem(
            "typeGame",
            lobbyTypeGame === "PvE" ? "1" : "2"
        );

        // Esperar a que React renderee el JSX del juego (donde están los
        // canvases con refs) ANTES de inicializar el engine. Sin esto,
        // engine.initCanvas() falla porque refCanvas.background es undefined.
        this.setState({ lobbyMode: false, loading: true }, () => {
            // Doble RAF para asegurarnos que el DOM está montado y los refs
            // asignados (setState callback corre después de render pero a veces
            // los refs callback corren un tick después en modo development).
            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    this.initGame();
                });
            });
        });
    };

    renderDeleteCharModal = () => {
        const {
            deleteCharTarget: char,
            deleteCharConfirmText,
            deleteCharSubmitting,
            deleteCharError
        } = this.state;
        if (!char) return null;
        const matches = deleteCharConfirmText.trim() === char.name;

        return (
            <div
                className={style.modalDeleteChar}
                onClick={this.closeDeleteCharModal}
            >
                <div
                    className={style.modalDeleteInner}
                    onClick={e => e.stopPropagation()}
                >
                    <div className={style.modalDeleteHeader}>
                        <FontAwesomeIcon icon={faExclamationTriangle} />
                        <span>Eliminar personaje</span>
                    </div>
                    <div className={style.modalDeleteBody}>
                        <p className={style.modalDeleteWarn}>
                            Esta acción <strong>no se puede deshacer</strong>.
                            El personaje{" "}
                            <strong className={style.modalDeleteCharName}>
                                {char.name}
                            </strong>{" "}
                            y todos sus items, oro y progreso se perderán
                            permanentemente.
                        </p>
                        <label className={style.modalDeleteLabel}>
                            Para confirmar, escribí el nombre del personaje
                            abajo:
                        </label>
                        <input
                            type="text"
                            className={style.modalDeleteInput}
                            value={deleteCharConfirmText}
                            onChange={this.onDeleteCharInput}
                            placeholder={char.name}
                            autoFocus
                            autoComplete="off"
                            disabled={deleteCharSubmitting}
                        />
                        {deleteCharError && (
                            <div className={style.modalDeleteError}>
                                {deleteCharError}
                            </div>
                        )}
                    </div>
                    <div className={style.modalDeleteFooter}>
                        <button
                            type="button"
                            className={style.modalDeleteCancel}
                            onClick={this.closeDeleteCharModal}
                            disabled={deleteCharSubmitting}
                        >
                            Cancelar
                        </button>
                        <button
                            type="button"
                            className={style.modalDeleteConfirm}
                            onClick={this.confirmDeleteChar}
                            disabled={!matches || deleteCharSubmitting}
                        >
                            {deleteCharSubmitting
                                ? "Eliminando..."
                                : `Eliminar ${char.name}`}
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    renderLobbyCanvas = (character, idx) => {
        if (!character || !this.props.initsLoaded) return null;
        const canvas = this.lobbyCanvas && this.lobbyCanvas[idx];
        if (!canvas) return null;
        const ctx = canvas.getContext("2d");
        try {
            const rndChar = new RenderCharacters(
                inits,
                ctx,
                character,
                24,
                60
            );
            rndChar.drawChar();
        } catch (e) {}
    };

    // ── Drag-and-drop inventario ─────────────────────────────────
    onInvDragStart = (e, idPos) => {
        const item = this.user.items && this.user.items[idPos];
        if (!item) {
            e.preventDefault();
            return;
        }
        e.dataTransfer.effectAllowed = "move";
        // Algunos browsers necesitan data en el transfer para que funcione.
        try {
            e.dataTransfer.setData("text/plain", String(idPos));
        } catch (err) {}
        this.dragFromPos = idPos;
        this.setState({ invDraggingFrom: idPos });
    };

    onInvDragOver = (e, idPos) => {
        if (this.dragFromPos && this.dragFromPos !== idPos) {
            e.preventDefault();
            e.dataTransfer.dropEffect = "move";
            if (this.state.invDraggingOver !== idPos) {
                this.setState({ invDraggingOver: idPos });
            }
        }
    };

    onInvDragLeave = () => {
        if (this.state.invDraggingOver) {
            this.setState({ invDraggingOver: 0 });
        }
    };

    onInvDragEnd = () => {
        this.dragFromPos = 0;
        this.setState({ invDraggingFrom: 0, invDraggingOver: 0 });
    };

    onInvDrop = (e, toPos) => {
        e.preventDefault();
        const fromPos = this.dragFromPos;
        this.dragFromPos = 0;
        this.setState({ invDraggingFrom: 0, invDraggingOver: 0 });
        if (!fromPos || fromPos === toPos) return;
        if (!this.user || !this.user.items) return;

        // Optimistic swap en estado local (sincronizado con this.user).
        const items = this.user.items;
        const a = items[fromPos];
        const b = items[toPos];
        if (a) items[toPos] = a;
        else delete items[toPos];
        if (b) items[fromPos] = b;
        else delete items[fromPos];

        // Actualizar punteros de items equipados (idItemBody, idItemWeapon, ...).
        const equipKeys = [
            "idItemBody",
            "idItemWeapon",
            "idItemShield",
            "idItemHelmet",
            "idItemArrow"
        ];
        equipKeys.forEach(k => {
            if (this.user[k] === fromPos) this.user[k] = toPos;
            else if (this.user[k] === toPos) this.user[k] = fromPos;
        });

        // Si el slot seleccionado se mueve, mover la selección con él.
        const sel = this.state.selectItem;
        let newSel = sel;
        if (sel === fromPos) newSel = toPos;
        else if (sel === toPos) newSel = fromPos;

        this.setState({ user: this.user, selectItem: newSel });

        // Notificar al server para que actualice user.inv en su lado.
        try {
            this.pkg.setPackageID(this.pkg.serverPacketID.swapItems);
            this.pkg.writeInt(fromPos);
            this.pkg.writeInt(toPos);
            this.config.ws.send(this.pkg.dataSend());
        } catch (err) {}
    };

    // ── Drop item modal ──────────────────────────────────────────
    cancelDropItem = () => {
        this.setState({ showDropItemModal: false });
    };

    confirmDropItem = () => {
        const { dropItemPos, dropItemMaxCant, dropItemValue } = this.state;
        const cant = Math.max(
            1,
            Math.min(dropItemMaxCant, parseInt(dropItemValue, 10) || 1)
        );
        if (!dropItemPos || cant <= 0) {
            this.setState({ showDropItemModal: false });
            return;
        }
        try {
            this.pkg.setPackageID(this.pkg.serverPacketID.tirarItem);
            this.pkg.writeInt(dropItemPos);
            this.pkg.writeShort(cant);
            this.config.ws.send(this.pkg.dataSend());
        } catch (e) {}
        this.setState({ showDropItemModal: false });
    };

    onDropItemInput = e => {
        // Permitimos vacío temporal mientras tipea (clampea en confirm).
        const v = e.target.value.replace(/[^0-9]/g, "");
        this.setState({ dropItemValue: v });
    };

    onDropItemKey = e => {
        if (e.key === "Enter") this.confirmDropItem();
        else if (e.key === "Escape") this.cancelDropItem();
    };

    toggleMusic = () => {
        const next = !this.state.musicEnabled;
        this.setState({ musicEnabled: next }, this.syncMusicEl);
        try {
            localStorage.setItem("musicEnabled", String(next));
            // Invalidar cache de AudioManager. Antes leía localStorage en
            // cada SFX (50ms/seg de jank); ahora cachea y refresca acá.
            require("../../engine/audio").default.refreshSettings();
        } catch (e) {}
    };

    // Ticket #296: toggle del fix "bordes nítidos de tiles". Aplica al
    // engine en runtime — el flag se lee por frame en drawGrhCapa (decide
    // si floor las coords) y acá (decide imageSmoothing por context).
    // Si ya hay engine montado, ajustamos imageSmoothing en vivo para
    // que no haga falta restart visual.
    togglePixelPerfect = () => {
        const next = !this.state.pixelPerfect;
        this.setState({ pixelPerfect: next }, () => {
            try {
                localStorage.setItem("argentum:pixelPerfect", String(next));
            } catch (e) {}
            if (this.engine) {
                this.engine.pixelPerfect = next;
                const canvas = this.engine.canvas || {};
                for (const k of Object.keys(canvas)) {
                    const ctx = canvas[k] && canvas[k].ctx;
                    if (ctx) {
                        ctx.imageSmoothingEnabled = !next;
                        ctx.mozImageSmoothingEnabled = !next;
                        ctx.webkitImageSmoothingEnabled = !next;
                        ctx.msImageSmoothingEnabled = !next;
                    }
                }
            }
        });
    };

    // Round-robin de fuentes para CAJA 4 (nombre + nivel + barra). Aplica
    // en tiempo real via CSS variable (--header-font) sobre el div .header
    // del play (scope local), sin re-render del play monolítico. Persiste
    // en localStorage. Decisión: NO propagar a :root — el logo del navbar
    // queda fijo en Doppio One como identidad de marca.
    cycleHeaderFont = () => {
        const next =
            (this.state.headerFontIndex + 1) % HEADER_FONTS.length;
        this.setState({ headerFontIndex: next });
        try {
            localStorage.setItem(HEADER_FONT_STORAGE_KEY, String(next));
        } catch (e) {}
    };

    setMusicVolume = e => {
        const v = Math.max(0, Math.min(100, Number(e.target.value)));
        this.setState({ musicVolume: v }, this.syncMusicEl);
        try {
            localStorage.setItem("musicVolume", String(v));
        } catch (err) {}
    };

    setAmbienceVolume = e => {
        const v = Math.max(0, Math.min(100, Number(e.target.value)));
        this.setState({ ambienceVolume: v });
        try {
            localStorage.setItem("ambienceVolume", String(v));
            require("../../engine/audio").default.refreshSettings();
        } catch (err) {}
        // TODO: AmbienceManager.setVolume(v / 100) cuando se implementen
        // sonidos de viento/lluvia/etc.
    };

    setEffectsVolume = e => {
        const v = Math.max(0, Math.min(100, Number(e.target.value)));
        this.setState({ effectsVolume: v });
        try {
            localStorage.setItem("effectsVolume", String(v));
            // AudioManager cachea este valor — refreshSettings() invalida
            // la cache así el próximo SFX usa el nuevo volumen.
            require("../../engine/audio").default.refreshSettings();
        } catch (err) {}
    };

    showInventary = () => {
        this.setState({
            showInventary: true
        });
    };

    showSpells = () => {
        this.setState({
            showInventary: false
        });
    };

    showMacroConfig = (e, key) => {
        e.preventDefault();

        // Pre-llenamos keyMacro con el macro existente (si el usuario está
        // editando uno ya configurado en lugar de armar uno desde cero).
        // Esto hace que el modal arranque mostrando lo asignado actualmente.
        const existing = (this.state.valueKeyMacro || [])[key] || {};
        const pickerMode = existing.idSpell ? "spell" : "item";

        const keyMacro = {
            indexMacro: key,
            idPosItem: existing.idPosItem || "",
            idItem: existing.idItem || "",
            idSpell: existing.idSpell || "",
            idPosSpell: existing.idPosSpell || "",
            key: existing.key || "",
            keyChar: existing.keyChar || "",
            img: existing.img || "",
            pickerMode
        };

        this.setState({
            showMacroConfig: true,
            keyMacro,
            macroKeyStealHint: ""
        });
    };

    // Cambia el tab del picker entre "item" (inventario) y "spell" (hechizos).
    // Al cambiar limpia la asignación previa del tipo opuesto para que el
    // user no quede con un item Y un spell "asignados" simultáneamente.
    setMacroPickerMode = mode => {
        const { keyMacro } = this.state;
        const next = { ...keyMacro, pickerMode: mode };
        if (mode === "item") {
            next.idSpell = "";
            next.idPosSpell = "";
        } else {
            next.idPosItem = "";
            next.idItem = "";
        }
        // Si cambia de tipo, también vaciamos la img — se actualizará al
        // elegir un item/spell del picker.
        if (
            (mode === "item" && !next.idPosItem) ||
            (mode === "spell" && !next.idSpell)
        ) {
            next.img = "";
        }
        this.setState({ keyMacro: next });
    };

    // Click en un item del picker del modal de macro → asigna ese item.
    // GUARDA idItem canónico además del idPosItem (slot actual). El
    // triggerMacro resuelve el slot vía idItem para que sea robusto a
    // swaps del inventario. Sin esto, mover la poción a otro slot
    // hacía que el macro disparara sobre lo que quedaba en el slot
    // original (ej. armadura).
    pickMacroItem = idPos => {
        const { keyMacro, user } = this.state;
        const item = user.items && user.items[idPos];
        if (!item) return;

        const grhData =
            inits.graphics && inits.graphics[item.grhIndex]
                ? inits.graphics[item.grhIndex]
                : null;
        const img = grhData
            ? `/static/graficos/${grhData.numFile}.png`
            : "";

        this.setState({
            keyMacro: {
                ...keyMacro,
                idPosItem: idPos,
                idItem: item.idItem,
                idSpell: "",
                idPosSpell: "",
                img,
                pickerMode: "item"
            }
        });
    };

    // Click en un hechizo del picker del modal de macro → asigna ese spell.
    pickMacroSpell = idPos => {
        const { keyMacro, user } = this.state;
        const spell = user.spells && user.spells[idPos];
        if (!spell) return;

        this.setState({
            keyMacro: {
                ...keyMacro,
                idPosItem: "",
                idItem: "",
                idSpell: spell.idSpell,
                idPosSpell: idPos,
                img: `/static/spells/${spell.idSpell}.png`,
                pickerMode: "spell"
            }
        });
    };

    // "Limpiar" del modal — vacía la asignación pero mantiene la tecla y el
    // pickerMode actual para que el user pueda elegir algo nuevo sin reabrir.
    clearMacroAssignment = () => {
        const { keyMacro } = this.state;
        this.setState({
            keyMacro: {
                ...keyMacro,
                idPosItem: "",
                idItem: "",
                idSpell: "",
                idPosSpell: "",
                img: ""
            }
        });
    };

    handleKeyMacro = e => {
        const { keyMacro } = this.state;
        const keyCode = e.keyCode;
        // Ticket #273: NO rechazar si la tecla está en uso. Solo capturar
        // en el preview del modal — el robo a otros slots ocurre al confirmar
        // el bind (`saveMacro`), no acá. Esto deja al user cancelar el
        // modal sin afectar bindings existentes.
        // Detectar colisión para mostrar hint inline (no bloquea).
        const used = this.getKeyInUse(keyCode, {
            macroIdx: keyMacro.indexMacro
        });

        let fromChar = String.fromCharCode(keyCode);
        if (this.config.keyCodeMap[keyCode]) {
            fromChar = this.config.keyCodeMap[keyCode];
        }

        keyMacro.key = keyCode;
        keyMacro.keyChar = fromChar;

        this.setState({
            keyMacro: keyMacro,
            macroKeyStealHint: used
                ? "Al guardar, esta tecla se liberará de: " + used.label
                : ""
        });
    };

    saveMacro = () => {
        const { keyMacro } = this.state;

        // Ticket #273: robar la tecla de cualquier otro slot que la tenga
        // (acción / spell / item / otros macros). El último bind gana.
        // `includeAction: true` toca también el live keyCodeDefault.
        if (keyMacro.key) {
            this.stealKeyFromAllSlots(keyMacro.key, {
                macroIdx: keyMacro.indexMacro,
                includeAction: true
            });
        }

        // Ticket #273 follow-up: usar setState(prev => ...) para componer
        // sobre el state YA limpiado por stealKeyFromAllSlots. Si hago un
        // setState directo con valueKeyMacro mutado de this.state, React
        // batchea y el último gana — pisaría el clear del helper (otro
        // macro queda con la misma tecla y aparece doble binding).
        this.setState(
            prev => {
                const baseMacros = Array.isArray(prev.valueKeyMacro)
                    ? prev.valueKeyMacro.slice()
                    : [];
                baseMacros[keyMacro.indexMacro] = {
                    idPosItem: keyMacro.idPosItem,
                    // idItem canónico — usado por triggerMacro para resolver
                    // el slot actual incluso si el item fue swappeado en inv.
                    idItem: keyMacro.idItem || "",
                    idSpell: keyMacro.idSpell,
                    idPosSpell: keyMacro.idPosSpell,
                    img: keyMacro.img,
                    key: keyMacro.key,
                    keyChar: keyMacro.keyChar
                };
                return {
                    valueKeyMacro: baseMacros,
                    keyCodeMacros: buildKeyCodeMacrosMap(baseMacros),
                    showMacroConfig: false,
                    macroKeyStealHint: ""
                };
            },
            () => {
                // Ticket #215: persistir a localStorage + server (canonical).
                this.persistMacrosBoth(this.state.valueKeyMacro);
            }
        );
    };

    // ── Defaults de macros para chars de nivel 1 ──────────────────
    // idItems de las pociones del starter kit (ver
    // server/controllers/CharacterController.js → STARTER_UNIVERSAL):
    //   857 = Poción Roja Newbie (vida)
    //   856 = Poción Azul Newbie (maná)
    // El slot dentro del inventario varía según la clase (depende de cuántos
    // items de equipo se acumularon antes), por eso scaneamos user.items en
    // runtime en lugar de hardcodear slot.
    DEFAULT_MACRO_POCION_ROJA_IDITEM = 857;
    DEFAULT_MACRO_POCION_AZUL_IDITEM = 856;
    DEFAULT_MACRO_DARDO_IDSPELL = 2;

    // Helper — devuelve la key de localStorage donde viven los macros de
    // este char en particular. Cada char tiene su propio set (antes era
    // global "macros" → mezclaba bindings entre chars del mismo browser).
    //
    // Ticket #215: ahora localStorage es CACHE de resiliencia (offline /
    // falla server), no fuente de verdad. La canonical vive en
    // characters.macros en Mongo. Ver `persistMacrosBoth` abajo.
    getMacrosStorageKey = nameCharacter => {
        if (!nameCharacter) return null;
        return "macros_" + nameCharacter;
    };

    // Ticket #215: persiste los macros a localStorage (cache) + servidor.
    // Llamado desde saveMacro / initializePerCharMacros / restoreDefaultKeys.
    // El POST es fire-and-forget — si falla, los macros viven en localStorage
    // hasta el próximo save exitoso (eventual consistency).
    persistMacrosBoth = macros => {
        // 1) localStorage cache (siempre, instant)
        const key = this.getMacrosStorageKey(
            this.state.user && this.state.user.nameCharacter
        );
        if (key) {
            try {
                window.localStorage.setItem(key, JSON.stringify(macros));
            } catch (e) {}
        }
        // 2) Server (canonical, async). Solo si es char PvE — los PvP son
        // sintéticos y no se persisten (sessionStorage.idCharacter solo
        // se setea para PvE; ver lobby startGame).
        if (typeof window === "undefined") return;
        const idCharacter = window.sessionStorage.getItem("idCharacter");
        if (!idCharacter) return;
        try {
            fetchUrl("/character/" + encodeURIComponent(idCharacter) + "/macros", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ macros: macros })
            });
        } catch (e) {}
    };

    // Storage key para los spell keybindings de este char (ticket #207).
    // Convive con macros_<char> y la clave global de defaults — viven en
    // namespaces distintos a propósito así un user puede tener bindings
    // diferentes por personaje sin migrar nada manualmente.
    getSpellKeybindingsStorageKey = nameCharacter => {
        if (!nameCharacter) return null;
        return "spellKeys_" + nameCharacter;
    };

    // Hidratá spell keybindings desde localStorage al primer mount del
    // char. Server canonical + localStorage cache.
    initializePerCharSpellKeybindings = nameCharacter => {
        this.initializePerCharKeybindings(
            nameCharacter,
            "spell",
            this.getSpellKeybindingsStorageKey(nameCharacter),
            bindings => this.setState({ spellKeybindings: bindings })
        );
    };

    // Ticket #281: helper genérico que hidratá un set de keybindings.
    // Fetch del server primero (canonical, sobrevive clearStorage + cross-
    // browser). Si server tiene data → state usa eso + localStorage se
    // sobrescribe. Si server vacío → fallback localStorage cache. Si hay
    // localStorage → write-through migration (push al server).
    //
    // Args:
    //   nameCharacter:  string del char (para storage key per-char).
    //   kind:           "spell" | "inventory" | "action" (path del API).
    //   storageKey:     localStorage key (per-char).
    //   apply:          callback(bindings) — aplica al state del componente.
    initializePerCharKeybindings = (nameCharacter, kind, storageKey, apply) => {
        if (!nameCharacter || !storageKey || !apply) return;
        const parseFromLocal = () => {
            try {
                const raw = window.localStorage.getItem(storageKey);
                if (!raw) return {};
                const parsed = JSON.parse(raw);
                if (!parsed || typeof parsed !== "object") return {};
                const out = {};
                Object.keys(parsed).forEach(k => {
                    const kc = parseInt(parsed[k]);
                    // Ticket #288: aceptar kc=0 (action limpiada por
                    // #273 steal). Para spell/inventory los callers
                    // (set*Keybinding) hacen `delete` cuando es null/0
                    // — nunca llegan 0s spurious a localStorage.
                    if (Number.isFinite(kc) && kc >= 0) out[k] = kc;
                });
                return out;
            } catch (e) {
                return {};
            }
        };
        const writeLocal = bindings => {
            try {
                window.localStorage.setItem(storageKey, JSON.stringify(bindings));
            } catch (e) {}
        };

        // PvE only (los chars PvP son sintéticos, sin id en sessionStorage).
        const idCharacter =
            typeof window !== "undefined"
                ? window.sessionStorage.getItem("idCharacter")
                : null;

        // Sin id → fallback localStorage solo (chars PvP).
        if (!idCharacter) {
            apply(parseFromLocal());
            return;
        }

        // Pull del server. La default (404 / sin data) se trata como vacío.
        fetchUrl(
            "/character/" +
                encodeURIComponent(idCharacter) +
                "/keybindings/" +
                kind
        )
            .then(r => (r && r.json ? r.json() : r))
            .then(data => {
                const serverBindings =
                    data && data.bindings && typeof data.bindings === "object"
                        ? data.bindings
                        : {};
                const hasServer = Object.keys(serverBindings).length > 0;
                if (hasServer) {
                    // Server wins. Sync localStorage al valor server.
                    apply(serverBindings);
                    writeLocal(serverBindings);
                    return;
                }
                // Server vacío — migrá desde localStorage (write-through).
                const local = parseFromLocal();
                apply(local);
                if (Object.keys(local).length > 0) {
                    try {
                        fetchUrl(
                            "/character/" +
                                encodeURIComponent(idCharacter) +
                                "/keybindings/" +
                                kind,
                            {
                                method: "POST",
                                headers: { "Content-Type": "application/json" },
                                body: JSON.stringify({ bindings: local })
                            }
                        );
                    } catch (e) {}
                }
            })
            .catch(() => {
                // Server unreachable / error → fallback localStorage solo.
                apply(parseFromLocal());
            });
    };

    // Ticket #281: persiste un dict de bindings a localStorage + server
    // (canonical). Fire-and-forget al server — si falla, localStorage
    // mantiene la cache para el próximo init.
    persistKeybindingsBoth = (kind, storageKey, bindings) => {
        // 1) localStorage (siempre, instant).
        if (storageKey) {
            try {
                window.localStorage.setItem(
                    storageKey,
                    JSON.stringify(bindings)
                );
            } catch (e) {}
        }
        // 2) Server (PvE only).
        if (typeof window === "undefined") return;
        const idCharacter = window.sessionStorage.getItem("idCharacter");
        if (!idCharacter) return;
        try {
            fetchUrl(
                "/character/" +
                    encodeURIComponent(idCharacter) +
                    "/keybindings/" +
                    kind,
                {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ bindings: bindings })
                }
            );
        } catch (e) {}
    };

    // ── Control abstraction (ticket #207, extendido en #208) ───────
    // Devuelve qué binding está usando ESTA tecla, o null si está libre.
    // Centraliza el scan de las 5 fuentes para evitar reimplementarlo en
    // cada feature nueva que agregue bindings:
    //   1) Movement + action bindings (state.keyCodeDefault)
    //   2) Macros regulares (state.valueKeyMacro slots 0-5)
    //   3) Spell keybindings (state.spellKeybindings)
    //   4) Inventory item keybindings (state.inventoryKeybindings)
    //   5) Teclas reservadas hardcoded (Enter, Esc, etc.)
    //
    // options.excludeSpellId: rebind del mismo spell, no es colisión.
    // options.excludeItemId: rebind del mismo item, no es colisión.
    //
    // Devuelve: { kind: 'action'|'macro'|'spell'|'item'|'reserved',
    //             label: string descriptivo para mostrar al user }
    // o null si la tecla está libre.
    getKeyInUse = (keyCode, options) => {
        const opts = options || {};
        const excludeSpellId = opts.excludeSpellId;
        const excludeItemId = opts.excludeItemId;
        const {
            keyCodeDefault,
            valueKeyMacro,
            spellKeybindings,
            inventoryKeybindings,
            user
        } = this.state;

        // 1) Action bindings (movement + acciones)
        const nameKC = (this.config && this.config.nameKeyCode) || {};
        // Mapa inverso: keyCode → label legible
        const actionLabels = {
            [nameKC.flechaArriba]: "Mover Arriba",
            [nameKC.flechaAbajo]: "Mover Abajo",
            [nameKC.flechaIzquierda]: "Mover Izquierda",
            [nameKC.flechaDerecha]: "Mover Derecha",
            [nameKC.usar]: "Usar",
            [nameKC.atacar]: "Atacar",
            [nameKC.agarrar]: "Agarrar",
            [nameKC.tirar]: "Tirar",
            [nameKC.equipar]: "Equipar",
            [nameKC.domar]: "Domar",
            [nameKC.robar]: "Robar",
            [nameKC.seguro]: "Seguro",
            [nameKC.meditar]: "Meditar"
        };
        for (const actionId of Object.keys(keyCodeDefault)) {
            if (Number(keyCodeDefault[actionId]) === keyCode) {
                return {
                    kind: "action",
                    label: actionLabels[actionId] || "Acción " + actionId
                };
            }
        }

        // 2) Macros regulares (slots 0..MACRO_COUNT-1 con teclas configurables)
        if (Array.isArray(valueKeyMacro)) {
            for (let i = 0; i < MACRO_COUNT; i++) {
                const m = valueKeyMacro[i];
                if (m && Number(m.key) === keyCode) {
                    return {
                        kind: "macro",
                        label: "Macro " + (i + 1)
                    };
                }
            }
        }

        // 3) Spell keybindings (excluyendo el que estamos rebindeando)
        for (const idSpell of Object.keys(spellKeybindings)) {
            if (excludeSpellId && String(idSpell) === String(excludeSpellId)) {
                continue;
            }
            if (Number(spellKeybindings[idSpell]) === keyCode) {
                // Resolver nombre del spell via user.spells (puede no estar
                // si el char olvidó el hechizo — caso edge, mostramos id).
                let spellName = "Hechizo " + idSpell;
                if (user && user.spells) {
                    for (const slot of Object.keys(user.spells)) {
                        if (
                            user.spells[slot] &&
                            String(user.spells[slot].idSpell) === String(idSpell)
                        ) {
                            spellName = user.spells[slot].name || spellName;
                            break;
                        }
                    }
                }
                return {
                    kind: "spell",
                    label: spellName
                };
            }
        }

        // 4) Inventory keybindings (ticket #208)
        if (inventoryKeybindings) {
            for (const idItem of Object.keys(inventoryKeybindings)) {
                if (excludeItemId && String(idItem) === String(excludeItemId)) {
                    continue;
                }
                if (Number(inventoryKeybindings[idItem]) === keyCode) {
                    // Resolver nombre via inits.objs (static catalog del
                    // cliente). Si el item ya no está en el inv del char,
                    // igual mostramos el nombre canónico del catálogo.
                    let itemName = "Item " + idItem;
                    if (inits.objs && inits.objs[idItem]) {
                        itemName = inits.objs[idItem].name || itemName;
                    }
                    return {
                        kind: "item",
                        label: itemName
                    };
                }
            }
        }

        // 5) Reservadas hardcoded
        if (keyCode === 13) {
            return { kind: "reserved", label: "Chat (Enter)" };
        }
        if (keyCode === 27) {
            return { kind: "reserved", label: "Esc (cerrar modales)" };
        }

        return null;
    };

    // ── Steal key (ticket #273) ────────────────────────────────────
    // Cuando se asigna una tecla a un slot (acción, macro, spell, item),
    // libera la misma tecla de TODOS los demás slots — la convención del
    // sistema es "1 tecla = 1 binding". El último bind gana.
    //
    // Diferente a `getKeyInUse` (que reporta) — este MUTA state y persiste.
    // Excepción `exclude` permite que el caller no se robe a sí mismo
    // (ej: re-bindeando el mismo macro idx).
    //
    // exclude shape (todos opcionales — pass el campo del caller):
    //   { actionId, macroIdx, spellId, itemId, includeAction }
    // Por default NO toca `keyCodeDefault` (las acciones se gestionan
    // en su propio flow tmp/save). Para tocarlas, pasar `includeAction: true`
    // (usado desde `saveChangesKeys` post-confirm del modal).
    stealKeyFromAllSlots = (keyCode, exclude) => {
        const e = exclude || {};
        if (!keyCode) return;

        this.setState(
            prev => {
                const updates = {};

                // 1) Acciones (solo si includeAction=true — sino, el modal
                //    de keybinds las maneja en su tmp/save flow).
                if (e.includeAction) {
                    const nextKCD = Object.assign({}, prev.keyCodeDefault);
                    const nextChar = Object.assign(
                        {},
                        prev.charKeyCodeDefault
                    );
                    let changed = false;
                    Object.keys(nextKCD).forEach(k => {
                        if (
                            Number(nextKCD[k]) === keyCode &&
                            Number(k) !== Number(e.actionId)
                        ) {
                            nextKCD[k] = 0;
                            nextChar[k] = "";
                            changed = true;
                        }
                    });
                    if (changed) {
                        updates.keyCodeDefault = nextKCD;
                        updates.charKeyCodeDefault = nextChar;
                    }
                }

                // 2) Macros — limpiar key del slot que tenga este keyCode.
                if (Array.isArray(prev.valueKeyMacro)) {
                    let macroChanged = false;
                    const nextMacros = prev.valueKeyMacro.map((m, i) => {
                        if (
                            m &&
                            Number(m.key) === keyCode &&
                            i !== Number(e.macroIdx)
                        ) {
                            macroChanged = true;
                            return Object.assign({}, m, {
                                key: "",
                                keyChar: ""
                            });
                        }
                        return m;
                    });
                    if (macroChanged) {
                        updates.valueKeyMacro = nextMacros;
                        updates.keyCodeMacros = buildKeyCodeMacrosMap(
                            nextMacros
                        );
                    }
                }

                // 3) Spell keybindings — delete del idSpell que use esta tecla.
                if (prev.spellKeybindings) {
                    const nextSpell = Object.assign({}, prev.spellKeybindings);
                    let spellChanged = false;
                    Object.keys(nextSpell).forEach(idS => {
                        if (
                            Number(nextSpell[idS]) === keyCode &&
                            String(idS) !== String(e.spellId)
                        ) {
                            delete nextSpell[idS];
                            spellChanged = true;
                        }
                    });
                    if (spellChanged) updates.spellKeybindings = nextSpell;
                }

                // 4) Inventory keybindings — delete del idItem que use esta.
                if (prev.inventoryKeybindings) {
                    const nextInv = Object.assign(
                        {},
                        prev.inventoryKeybindings
                    );
                    let invChanged = false;
                    Object.keys(nextInv).forEach(idI => {
                        if (
                            Number(nextInv[idI]) === keyCode &&
                            String(idI) !== String(e.itemId)
                        ) {
                            delete nextInv[idI];
                            invChanged = true;
                        }
                    });
                    if (invChanged) updates.inventoryKeybindings = nextInv;
                }

                return updates;
            },
            () => {
                // Persistir las fuentes que pueden haber cambiado. Cheap
                // check: cada save inspecciona si su slice está vacío.
                // Macros: localStorage cache + server canonical.
                if (this.state.valueKeyMacro) {
                    this.persistMacrosBoth(this.state.valueKeyMacro);
                }
                const nameCh = this.state.user && this.state.user.nameCharacter;
                // Ticket #281: las 3 keybindings persisten a localStorage + DB.
                // Acciones (solo si includeAction): per-char + global fallback.
                if (e.includeAction) {
                    try {
                        window.localStorage.setItem(
                            "defaultKeys",
                            JSON.stringify(this.state.keyCodeDefault)
                        );
                    } catch (err) {}
                    const aKey = this.getActionKeybindingsStorageKey(nameCh);
                    if (aKey) {
                        this.persistKeybindingsBoth("action", aKey, this.state.keyCodeDefault);
                    }
                }
                // Spells.
                const sKey = this.getSpellKeybindingsStorageKey(nameCh);
                if (sKey) {
                    this.persistKeybindingsBoth("spell", sKey, this.state.spellKeybindings);
                }
                // Items.
                const iKey = this.getInventoryKeybindingsStorageKey(nameCh);
                if (iKey) {
                    this.persistKeybindingsBoth("inventory", iKey, this.state.inventoryKeybindings);
                }
            }
        );
    };

    // Helper de display — devuelve la char/label legible para un keyCode.
    // Reusa config.keyCodeMap para teclas especiales (Ctrl, F1, etc.) y
    // String.fromCharCode para letras/números normales.
    keyCodeToChar = keyCode => {
        if (!keyCode) return "";
        if (this.config && this.config.keyCodeMap && this.config.keyCodeMap[keyCode]) {
            return this.config.keyCodeMap[keyCode];
        }
        return String.fromCharCode(keyCode);
    };

    // ── Spell keybinding handlers (ticket #207) ─────────────────────
    // Right-click sobre la row de un hechizo → abre context menu en
    // la posición del mouse con opciones "Asignar tecla" / "Quitar tecla".
    openSpellContextMenu = (e, idSpell) => {
        e.preventDefault();
        e.stopPropagation();
        this.setState({
            spellContextMenu: {
                idSpell: idSpell,
                x: e.clientX,
                y: e.clientY
            }
        });
    };

    closeSpellContextMenu = () => {
        if (this.state.spellContextMenu) {
            this.setState({ spellContextMenu: null });
        }
    };

    // Click en "Asignar tecla" del context menu → abre modal de captura.
    startSpellKeyCapture = (idSpell, spellName) => {
        this.setState({
            spellContextMenu: null,
            capturingSpellKey: { idSpell: idSpell, spellName: spellName },
            spellKeyCaptureError: ""
        });
    };

    cancelSpellKeyCapture = () => {
        this.setState({
            capturingSpellKey: null,
            spellKeyCaptureError: ""
        });
    };

    // Aplica el binding al state + persiste a localStorage + server.
    // Ticket #281: server canonical (DB), localStorage cache.
    setSpellKeybinding = (idSpell, keyCode) => {
        this.setState(
            prev => {
                const next = Object.assign({}, prev.spellKeybindings);
                if (keyCode) {
                    next[idSpell] = keyCode;
                } else {
                    delete next[idSpell];
                }
                return { spellKeybindings: next };
            },
            () => {
                const key = this.getSpellKeybindingsStorageKey(
                    this.state.user && this.state.user.nameCharacter
                );
                this.persistKeybindingsBoth(
                    "spell",
                    key,
                    this.state.spellKeybindings
                );
            }
        );
    };

    // Click en "Quitar tecla" del context menu → borra el binding.
    clearSpellKeybinding = idSpell => {
        this.setSpellKeybinding(idSpell, null);
        this.setState({ spellContextMenu: null });
    };

    // ── Inventory keybinding handlers (ticket #208) ─────────────────
    // Mismo patrón que los de spell pero por idItem canónico.
    getInventoryKeybindingsStorageKey = nameCharacter => {
        if (!nameCharacter) return null;
        return "invKeys_" + nameCharacter;
    };

    initializePerCharInventoryKeybindings = nameCharacter => {
        this.initializePerCharKeybindings(
            nameCharacter,
            "inventory",
            this.getInventoryKeybindingsStorageKey(nameCharacter),
            bindings => this.setState({ inventoryKeybindings: bindings })
        );
    };

    // Ticket #281: action bindings (modal de Configuración) ahora per-char
    // + persisted en DB. Antes vivían en localStorage GLOBAL ("defaultKeys")
    // → un char heredaba los de otro, y clearStorage los borraba. Ahora:
    //   - DB canonical (sobrevive cross-browser y clearStorage).
    //   - localStorage cache per-char ("actionKeys_<char>").
    //   - El global "defaultKeys" sigue siendo el fallback para chars sin
    //     DB record + para boot-time del play page (antes de tener el nombre
    //     del char). En el primer save por char ya se pisa con el DB.
    getActionKeybindingsStorageKey = nameCharacter => {
        if (!nameCharacter) return null;
        return "actionKeys_" + nameCharacter;
    };

    initializePerCharActionKeybindings = nameCharacter => {
        this.initializePerCharKeybindings(
            nameCharacter,
            "action",
            this.getActionKeybindingsStorageKey(nameCharacter),
            bindings => {
                // Si server / localStorage per-char tiene contenido, lo
                // aplicamos. Si vacío, mantenemos el state actual
                // (cargado del global "defaultKeys" en el constructor) —
                // así chars nuevos no pierden los defaults.
                const hasData = Object.keys(bindings).length > 0;
                if (!hasData) return;
                // Coerce keys a int (cliente espera int en keyCodeDefault).
                const next = {};
                Object.keys(bindings).forEach(k => {
                    next[parseInt(k, 10)] = parseInt(bindings[k], 10);
                });
                this.setState(prev => ({
                    keyCodeDefault: { ...prev.keyCodeDefault, ...next },
                    tmpKeyCodeDefault: { ...prev.keyCodeDefault, ...next }
                }), () => {
                    if (typeof this.charKeyCodeDefault === "function") {
                        this.charKeyCodeDefault();
                    }
                });
            }
        );
    };

    openInventoryContextMenu = (e, slot, idItem) => {
        e.preventDefault();
        e.stopPropagation();
        if (!idItem) return; // slot vacío — no abrir menu
        this.setState({
            inventoryContextMenu: {
                slot: slot,
                idItem: idItem,
                x: e.clientX,
                y: e.clientY
            }
        });
    };

    closeInventoryContextMenu = () => {
        if (this.state.inventoryContextMenu) {
            this.setState({ inventoryContextMenu: null });
        }
    };

    startItemKeyCapture = (idItem, itemName, slot) => {
        this.setState({
            inventoryContextMenu: null,
            capturingItemKey: { idItem: idItem, itemName: itemName, slot: slot },
            itemKeyCaptureError: ""
        });
    };

    cancelItemKeyCapture = () => {
        this.setState({
            capturingItemKey: null,
            itemKeyCaptureError: ""
        });
    };

    setInventoryKeybinding = (idItem, keyCode) => {
        this.setState(
            prev => {
                const next = Object.assign({}, prev.inventoryKeybindings);
                if (keyCode) {
                    next[idItem] = keyCode;
                } else {
                    delete next[idItem];
                }
                return { inventoryKeybindings: next };
            },
            () => {
                const key = this.getInventoryKeybindingsStorageKey(
                    this.state.user && this.state.user.nameCharacter
                );
                this.persistKeybindingsBoth(
                    "inventory",
                    key,
                    this.state.inventoryKeybindings
                );
            }
        );
    };

    clearInventoryKeybinding = idItem => {
        this.setInventoryKeybinding(idItem, null);
        this.setState({ inventoryContextMenu: null });
    };

    // Inicializa los macros para un char dado (llamado en
    // componentDidUpdate cuando llega el primer packet con nameCharacter).
    //
    // Ticket #215 — flow nuevo:
    //   1) Fetch del SERVER (canonical, cross-browser). Si tiene macros
    //      válidos, usar esos.
    //   2) Si server vacío Y localStorage tiene macros (legacy/offline)
    //      → migrar: usar los de localStorage y pushearlos al server.
    //   3) Si todo vacío → defaults clonados de engine/defaults.js.
    //   4) AutoBind (slot 1 poción roja, slot 2 azul, slot 3 dardo).
    //   5) setState + persist a ambos.
    //
    // Si el fetch del server falla (red), fallback a localStorage para
    // no romper la UI — el próximo save eventual restaurará sync.
    initializePerCharMacros = async nameCharacter => {
        const key = this.getMacrosStorageKey(nameCharacter);
        if (!key) return;

        let serverMacros = null;
        let serverFetched = false;

        // 1) Try server fetch (PvE only — PvP no tiene idCharacter).
        const idCharacter =
            typeof window !== "undefined"
                ? window.sessionStorage.getItem("idCharacter")
                : null;
        if (idCharacter) {
            try {
                const resp = await fetchUrl(
                    "/character/" +
                        encodeURIComponent(idCharacter) +
                        "/macros"
                );
                if (resp && Array.isArray(resp.macros)) {
                    serverMacros = resp.macros;
                    serverFetched = true;
                }
            } catch (e) {}
        }

        let macros = null;

        // 2) Server tiene macros válidos → usar esos.
        if (
            serverMacros &&
            serverMacros.length >= 6 &&
            serverMacros.some(m => m && (m.idItem || m.idSpell))
        ) {
            macros = serverMacros;
        } else {
            // 3) Server vacío o sin fetch → probar localStorage (cache o legacy).
            try {
                const raw = window.localStorage.getItem(key);
                if (raw) {
                    const parsed = JSON.parse(raw);
                    if (Array.isArray(parsed) && parsed.length >= 6) {
                        macros = parsed;
                    }
                }
            } catch (e) {}
        }

        // 4) Si todo vacío → defaults.
        if (!macros) {
            macros = cloneDefaultMacros();
        }

        // 4.5) Padding retroactivo para chars existentes: si el array
        // tiene MENOS de MACRO_COUNT entradas (ej. 6 viejas), agregamos
        // los slots faltantes con la default vacía (key + keyChar pre-set).
        // Sin esto, renderBoxMacros crashea al hacer macro.idPosItem en
        // undefined cuando itera hasta MACRO_COUNT.
        if (macros.length < MACRO_COUNT) {
            const defaults = cloneDefaultMacros();
            for (let i = macros.length; i < MACRO_COUNT; i++) {
                macros.push(defaults[i]);
            }
        }

        // 5) AutoBind (slot 1/2/3 pociones + dardo) sobre el set actual.
        macros = this.applyMacroAutoBind(macros);

        // 6) setState.
        this.setState({
            valueKeyMacro: macros,
            keyCodeMacros: buildKeyCodeMacrosMap(macros)
        });

        // 7) Persist:
        //   - localStorage siempre (cache instant).
        //   - Server SI server estaba vacío Y ahora tenemos macros con contenido
        //     → migración seamless de localStorage → DB.
        //   - Server siempre (incluso si serverFetched=false) — backfill defensivo.
        try {
            window.localStorage.setItem(key, JSON.stringify(macros));
        } catch (e) {}

        // Push to server si:
        //   (a) no fetcheamos y hay idCharacter (write-through de defaults).
        //   (b) fetcheamos y server estaba vacío pero ahora hay contenido
        //       (migración localStorage → DB).
        //   (c) fetcheamos y el array del server tiene MENOS slots que
        //       MACRO_COUNT (chars con macros legacy de 6 slots cuando
        //       ahora son 9). Sin esto, el padding retroactivo se hacía
        //       solo en memoria — al re-loguear se perdían los slots
        //       agregados porque el server seguía devolviendo 6.
        const hasContent = macros.some(m => m && (m.idItem || m.idSpell));
        const serverShorterThanCurrent =
            serverFetched &&
            serverMacros &&
            serverMacros.length < macros.length;
        const shouldPushToServer =
            idCharacter &&
            (!serverFetched ||
                (serverFetched && !serverMacros.length && hasContent) ||
                (serverFetched && serverMacros.length === 0 && hasContent) ||
                serverShorterThanCurrent);
        if (shouldPushToServer) {
            try {
                fetchUrl(
                    "/character/" +
                        encodeURIComponent(idCharacter) +
                        "/macros",
                    {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({ macros: macros })
                    }
                );
            } catch (e) {}
        }
    };

    // Función pura — recibe el array de macros actual + lee user del state,
    // y devuelve un array nuevo con los slots vacíos auto-bindeados a
    // (Slot 1) Poción Roja, (Slot 2) Poción Azul, (Slot 3) Dardo Mágico.
    // Si ya estaban configurados, no los toca.
    applyMacroAutoBind = macrosInput => {
        const { user } = this.state;
        if (!user || !user.items) return macrosInput;

        const macros = [...macrosInput];

        // Migración retroactiva — macros viejos del localStorage que
        // tienen `idPosItem` pero NO `idItem` (creados antes del fix de
        // resolver-por-canonico). Resolvemos el idItem mirando qué item
        // hay HOY en ese slot. Si el slot está vacío o ya no es el item
        // original, el macro probablemente está bug — pero al menos
        // capturamos el idItem actual y de ahí en más sigue trackeando
        // ese item canónicamente.
        for (let i = 0; i < macros.length; i++) {
            const m = macros[i];
            if (m && m.idPosItem && !m.idItem) {
                const slotItem = user.items[m.idPosItem];
                if (slotItem && slotItem.idItem) {
                    macros[i] = { ...m, idItem: slotItem.idItem };
                }
            }
        }

        const findItemSlot = idItem => {
            for (const k in user.items) {
                if (user.items[k] && user.items[k].idItem === idItem) {
                    return parseInt(k, 10);
                }
            }
            return null;
        };

        const findSpellSlot = idSpell => {
            const spells = user.spells || {};
            for (const k in spells) {
                if (spells[k] && spells[k].idSpell === idSpell) {
                    return parseInt(k, 10);
                }
            }
            return null;
        };

        const itemImg = item => {
            if (!item || !inits.graphics) return "";
            const grh = inits.graphics[item.grhIndex];
            return grh ? `/static/graficos/${grh.numFile}.png` : "";
        };

        const isMacroEmpty = m => m && !m.idPosItem && !m.idSpell;

        // Slot 1 → Poción Roja
        // Guardamos idItem canónico además del slot — el slot es solo
        // sugerencia inicial, triggerMacro resuelve via idItem para
        // ser robusto a swaps del inv.
        if (macros[0] && isMacroEmpty(macros[0])) {
            const slot = findItemSlot(this.DEFAULT_MACRO_POCION_ROJA_IDITEM);
            if (slot) {
                macros[0] = {
                    ...macros[0],
                    idPosItem: slot,
                    idItem: this.DEFAULT_MACRO_POCION_ROJA_IDITEM,
                    idSpell: "",
                    idPosSpell: "",
                    img: itemImg(user.items[slot])
                };
            }
        }

        // Slot 2 → Poción Azul
        if (macros[1] && isMacroEmpty(macros[1])) {
            const slot = findItemSlot(this.DEFAULT_MACRO_POCION_AZUL_IDITEM);
            if (slot) {
                macros[1] = {
                    ...macros[1],
                    idPosItem: slot,
                    idItem: this.DEFAULT_MACRO_POCION_AZUL_IDITEM,
                    idSpell: "",
                    idPosSpell: "",
                    img: itemImg(user.items[slot])
                };
            }
        }

        // Slot 3 → Dardo Mágico (solo si el char lo conoce)
        if (macros[2] && isMacroEmpty(macros[2])) {
            const slot = findSpellSlot(this.DEFAULT_MACRO_DARDO_IDSPELL);
            if (slot) {
                macros[2] = {
                    ...macros[2],
                    idPosItem: "",
                    idSpell: this.DEFAULT_MACRO_DARDO_IDSPELL,
                    idPosSpell: slot,
                    img: `/static/spells/${this.DEFAULT_MACRO_DARDO_IDSPELL}.png`
                };
            }
        }

        return macros;
    };

    // Modal de configuración de macro — versión rework. Centered modal con
    // Rogue Frame chrome. Permite setear:
    //   1) Tecla (input que captura keyup)
    //   2) Tipo: Objeto o Hechizo (tabs)
    //   3) Picker grid del tipo elegido (items del inventario o hechizos)
    //   4) Preview de lo asignado
    //   5) Limpiar / Guardar
    // Reemplaza el viejo modal sprite-based que requería clickear lejos en
    // el inventario para asignar (UX confusa).
    renderMacroConfigModal = () => {
        const { keyMacro, user } = this.state;
        const mode = keyMacro.pickerMode || "item";
        const macroIndex = keyMacro.indexMacro;
        // Slot number visible (1-indexed) — los macros son slot 0..5 en
        // valueKeyMacro pero queremos mostrar 1..6 al usuario.
        const slotLabel =
            macroIndex !== "" && macroIndex !== undefined
                ? parseInt(macroIndex, 10) + 1
                : "?";

        const items = (user && user.items) || {};
        const spells = (user && user.spells) || {};

        return (
            <div
                className={style.macroConfigBackdrop}
                onClick={e => {
                    // Click en el backdrop (fuera del card) cierra el modal.
                    if (e.target === e.currentTarget) {
                        this.setState({ showMacroConfig: false });
                    }
                }}
            >
                <div className={style.macroConfigCard}>
                    <div className={style.macroConfigHeader}>
                        <div className={style.macroConfigTitle}>
                            Macro <span>#{slotLabel}</span>
                        </div>
                        <button
                            type="button"
                            className={style.macroConfigClose}
                            onClick={() =>
                                this.setState({ showMacroConfig: false })
                            }
                            title="Cerrar"
                        >
                            ×
                        </button>
                    </div>

                    <div className={style.macroConfigBody}>
                        {/* Tecla */}
                        <div className={style.macroConfigSection}>
                            <label className={style.macroConfigLabel}>
                                Tecla
                            </label>
                            <input
                                type="text"
                                onKeyUp={this.handleKeyMacro}
                                className={style.macroConfigKey}
                                value={keyMacro.keyChar || ""}
                                onChange={() => {}}
                                placeholder="Presioná una tecla..."
                                readOnly
                            />
                            {this.state.macroKeyStealHint && (
                                <div
                                    style={{
                                        color: "#f5af50",
                                        fontSize: "12px",
                                        marginTop: "4px"
                                    }}
                                >
                                    {this.state.macroKeyStealHint}
                                </div>
                            )}
                        </div>

                        {/* Tipo (tabs) */}
                        <div className={style.macroConfigSection}>
                            <label className={style.macroConfigLabel}>
                                Tipo
                            </label>
                            <div className={style.macroConfigTabs}>
                                <button
                                    type="button"
                                    className={`${style.macroConfigTab} ${
                                        mode === "item"
                                            ? style.macroConfigTabActive
                                            : ""
                                    }`}
                                    onClick={() =>
                                        this.setMacroPickerMode("item")
                                    }
                                >
                                    Objeto
                                </button>
                                <button
                                    type="button"
                                    className={`${style.macroConfigTab} ${
                                        mode === "spell"
                                            ? style.macroConfigTabActive
                                            : ""
                                    }`}
                                    onClick={() =>
                                        this.setMacroPickerMode("spell")
                                    }
                                >
                                    Hechizo
                                </button>
                            </div>
                        </div>

                        {/* Picker grid */}
                        <div className={style.macroConfigSection}>
                            <label className={style.macroConfigLabel}>
                                {mode === "item"
                                    ? "Elegí un objeto"
                                    : "Elegí un hechizo"}
                            </label>
                            <div className={style.macroConfigPicker}>
                                {mode === "item"
                                    ? this.renderMacroItemPicker(items)
                                    : this.renderMacroSpellPicker(spells)}
                            </div>
                        </div>

                        {/* Preview de la asignación actual */}
                        <div className={style.macroConfigPreview}>
                            <div className={style.macroConfigPreviewLabel}>
                                Asignado
                            </div>
                            <div className={style.macroConfigPreviewBox}>
                                {keyMacro.img ? (
                                    <div
                                        className={
                                            style.macroConfigPreviewImg
                                        }
                                        style={{
                                            backgroundImage: `url("${keyMacro.img}")`
                                        }}
                                    />
                                ) : (
                                    <span
                                        className={
                                            style.macroConfigPreviewEmpty
                                        }
                                    >
                                        Sin asignar
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className={style.macroConfigFooter}>
                        <button
                            type="button"
                            className={style.macroConfigBtnSecondary}
                            onClick={this.clearMacroAssignment}
                        >
                            Limpiar
                        </button>
                        <button
                            type="button"
                            className={style.macroConfigBtnPrimary}
                            onClick={this.saveMacro}
                        >
                            Guardar
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    renderMacroItemPicker = items => {
        const { keyMacro } = this.state;
        const tiles = [];
        // Itera los 24 slots del inventario (`vars.maxInvSize = 24` en
        // server/vars.js). Antes el loop iba hasta 21 — los slots 22, 23 y
        // 24 quedaban invisibles al asignar macros aunque el usuario
        // tuviera items ahí.
        for (let i = 1; i <= 24; i++) {
            const item = items[i];
            if (!item || !item.idItem) {
                tiles.push(
                    <div
                        key={`empty-${i}`}
                        className={style.macroConfigTileEmpty}
                    />
                );
                continue;
            }
            const grhData =
                inits.graphics && inits.graphics[item.grhIndex]
                    ? inits.graphics[item.grhIndex]
                    : null;
            const img = grhData
                ? `/static/graficos/${grhData.numFile}.png`
                : "";
            const selected = keyMacro.idPosItem === i;
            tiles.push(
                <div
                    key={`item-${i}`}
                    className={`${style.macroConfigTile} ${
                        selected ? style.macroConfigTileSelected : ""
                    }`}
                    onClick={() => this.pickMacroItem(i)}
                    title={item.nameItem || ""}
                >
                    {img ? (
                        <div
                            className={style.macroConfigTileImg}
                            style={{ backgroundImage: `url("${img}")` }}
                        />
                    ) : null}
                    {item.cant > 1 ? (
                        <div className={style.macroConfigTileCant}>
                            {item.cant}
                        </div>
                    ) : null}
                </div>
            );
        }
        return tiles;
    };

    renderMacroSpellPicker = spells => {
        const { keyMacro } = this.state;
        const tiles = [];
        // Ordenamos por idPos para consistencia visual.
        const keys = Object.keys(spells)
            .map(k => parseInt(k, 10))
            .filter(k => spells[k])
            .sort((a, b) => a - b);

        if (keys.length === 0) {
            return (
                <div className={style.macroConfigEmpty}>
                    No conocés hechizos todavía.
                </div>
            );
        }

        keys.forEach(idPos => {
            const spell = spells[idPos];
            const img = `/static/spells/${spell.idSpell}.png`;
            const selected = keyMacro.idPosSpell === idPos;
            tiles.push(
                <div
                    key={`spell-${idPos}`}
                    className={`${style.macroConfigTile} ${
                        selected ? style.macroConfigTileSelected : ""
                    }`}
                    onClick={() => this.pickMacroSpell(idPos)}
                    title={`${spell.name || ""} (${spell.manaRequired || 0} maná)`}
                >
                    <div
                        className={style.macroConfigTileImg}
                        style={{ backgroundImage: `url("${img}")` }}
                    />
                </div>
            );
        });
        return tiles;
    };

    handleKeyDefault = (e, keyType) => {
        const keyCode = e.keyCode;
        const fromChar =
            (this.config &&
                this.config.keyCodeMap &&
                this.config.keyCodeMap[keyCode]) ||
            String.fromCharCode(keyCode);
        const keyTypeNum = Number(keyType);

        this.setState(prev => {
            const nextTmp = Object.assign({}, prev.tmpKeyCodeDefault);
            const nextChar = Object.assign({}, prev.charKeyCodeDefault);

            // Si la tecla estaba en OTRA acción, se la sacamos (la dejamos
            // sin asignar). La acción actual la recibe.
            Object.keys(nextTmp).forEach(k => {
                if (
                    Number(nextTmp[k]) === keyCode &&
                    Number(k) !== keyTypeNum
                ) {
                    nextTmp[k] = 0;
                    nextChar[k] = "";
                }
            });

            nextTmp[keyType] = keyCode;
            nextChar[keyType] = fromChar;

            return {
                tmpKeyCodeDefault: nextTmp,
                charKeyCodeDefault: nextChar
            };
        });
    };

    restoreDefaultKeys = async () => {
        // Restaura DESDE engine/defaults.js (no del state cache) — así si en
        // el futuro alguien bumpea DEFAULT_KEY_BINDINGS, el botón restaurar
        // siempre devuelve a los defaults más nuevos sin importar lo que el
        // user haya guardado antes.
        const freshBindings = cloneDefaultBindings();
        const freshMacrosBase = cloneDefaultMacros();
        // Aplicá el autoBind también acá (pociones/dardo) — así "restaurar"
        // deja el set listo para usar, no completamente vacío.
        const freshMacros = this.applyMacroAutoBind(freshMacrosBase);

        window.localStorage.setItem(
            "defaultKeys",
            JSON.stringify(freshBindings)
        );
        window.localStorage.setItem("keyConfigVersion", KEY_CONFIG_VERSION);

        // Ticket #215: persistir a localStorage + server (canonical).
        this.persistMacrosBoth(freshMacros);
        // Ticket #281: action keybindings per-char + DB.
        const nameCh = this.state.user && this.state.user.nameCharacter;
        const perCharKey = this.getActionKeybindingsStorageKey(nameCh);
        if (perCharKey) {
            this.persistKeybindingsBoth("action", perCharKey, freshBindings);
        }

        await this.setState({
            keyCodeDefault: _.cloneDeep(freshBindings),
            keyCodeDefaultReset: _.cloneDeep(freshBindings),
            tmpKeyCodeDefault: _.cloneDeep(freshBindings),
            valueKeyMacro: freshMacros,
            keyCodeMacros: buildKeyCodeMacrosMap(freshMacros)
        });

        this.charKeyCodeDefault();
    };

    saveChangesKeys = () => {
        const { tmpKeyCodeDefault } = this.state;
        const next = _.cloneDeep(tmpKeyCodeDefault);

        // Global key (legacy / fallback para boot temprano sin nameCharacter).
        try {
            window.localStorage.setItem("defaultKeys", JSON.stringify(next));
        } catch (e) {}

        // Ticket #281: per-char + server. Si tenemos nameCharacter, persist
        // a la clave per-char + push al DB. El global queda como fallback.
        const nameCh = this.state.user && this.state.user.nameCharacter;
        const perCharKey = this.getActionKeybindingsStorageKey(nameCh);
        if (perCharKey) {
            this.persistKeybindingsBoth("action", perCharKey, next);
        }

        // Importante: actualizamos keyCodeDefault (que es lo que lee el
        // keypress handler en tiempo real) Y cerramos el modal para que
        // sea claro que se aplicó.
        this.setState(
            {
                keyCodeDefault: next,
                tmpKeyCodeDefault: _.cloneDeep(next),
                showModalControlPanel: false
            },
            () => {
                this.charKeyCodeDefault();
                // Ticket #273: cada tecla final de acción puede haber estado
                // en uso por macros / spells / items. Robamos esos slots
                // ahora (silencioso). El steal entre acciones se resolvió en
                // `handleKeyDefault` mientras editabas el modal.
                Object.keys(next).forEach(actionId => {
                    const kc = Number(next[actionId]);
                    if (!kc) return;
                    this.stealKeyFromAllSlots(kc, {
                        actionId: actionId
                    });
                });
            }
        );
    };

    restoreMacros = () => {
        // Limpia la key per-char (no más global "macros").
        const macroKey = this.getMacrosStorageKey(
            this.state.user && this.state.user.nameCharacter
        );
        if (macroKey) {
            try {
                window.localStorage.removeItem(macroKey);
            } catch (e) {}
        }

        this.setState({
            keyCodeMacros: {},
            valueKeyMacro: [
                {
                    idPosItem: "",
                    idSpell: "",
                    idPosSpell: "",
                    img: "",
                    key: "",
                    keyChar: ""
                },
                {
                    idPosItem: "",
                    idSpell: "",
                    idPosSpell: "",
                    img: "",
                    key: "",
                    keyChar: ""
                },
                {
                    idPosItem: "",
                    idSpell: "",
                    idPosSpell: "",
                    img: "",
                    key: "",
                    keyChar: ""
                },
                {
                    idPosItem: "",
                    idSpell: "",
                    idPosSpell: "",
                    img: "",
                    key: "",
                    keyChar: ""
                },
                {
                    idPosItem: "",
                    idSpell: "",
                    idPosSpell: "",
                    img: "",
                    key: "",
                    keyChar: ""
                },
                {
                    idPosItem: "",
                    idSpell: "",
                    idPosSpell: "",
                    img: "",
                    key: "",
                    keyChar: ""
                }
            ]
        });

        alert("Macros reseteados.");
    };

    render() {
        const {
            showInventary,
            showMacroConfig,
            loading,
            user,
            messagesConsole,
            crosshair,
            nameMap,
            showFullMap,
            musicEnabled,
            musicVolume,
            ambienceVolume,
            effectsVolume,
            showInputText,
            textDialog,
            showModalTrade,
            trade,
            cantTrade,
            mapasToLoad,
            mapasCargados,
            keyMacro,
            showModalControlPanel,
            keyCodeDefault,
            charKeyCodeDefault,
            lobbyMode,
            lobbyTypeGame,
            lobbyCharacters
        } = this.state;
        const { account, userAgent } = this.props;

        // ── Lobby (pre-game) ──────────────────────────────────────
        if (lobbyMode) {
            // Gate de Arena PvP: necesita al menos 1 char en Mundo Abierto
            // (PvE). Si no, force PvE como tab activo aunque el state diga
            // otra cosa (recovery de state stale post-delete del último char).
            const hasPveChars = (lobbyCharacters || []).length > 0;
            const effectiveTab =
                lobbyTypeGame === "PvP" && hasPveChars ? "PvP" : "PvE";
            const characters =
                effectiveTab === "PvE" ? lobbyCharacters : pvpChars;
            const loggedIn = !!(account && account.accountId);
            // "Mis personajes" solo se renderea cuando hay personajes para
            // mostrar. Cuenta nueva (0 PvE chars) → solo el form de crear.
            const showCharGrid = characters.length > 0;

            return (
                <React.Fragment>
                    <Header userAgent={userAgent} />
                    <div className={style.lobbyPage}>
                        <div className={style.lobbyCard}>
                            <div className={style.lobbyTabs}>
                                <button
                                    type="button"
                                    className={`${style.lobbyTab} ${
                                        effectiveTab === "PvE"
                                            ? style.lobbyTabActive
                                            : ""
                                    }`}
                                    onClick={() =>
                                        this.setState({
                                            lobbyTypeGame: "PvE"
                                        })
                                    }
                                >
                                    <FontAwesomeIcon icon={faShieldAlt} />
                                    <span>Mundo Abierto PvE/PvP</span>
                                    <small>Mundo + tus personajes</small>
                                </button>
                                <button
                                    type="button"
                                    disabled={!hasPveChars}
                                    title={
                                        !hasPveChars
                                            ? "Creá un personaje en Mundo Abierto primero para desbloquear la Arena"
                                            : "Arena PvP — preset chars"
                                    }
                                    className={`${style.lobbyTab} ${
                                        effectiveTab === "PvP"
                                            ? style.lobbyTabActive
                                            : ""
                                    } ${
                                        !hasPveChars
                                            ? style.lobbyTabDisabled
                                            : ""
                                    }`}
                                    onClick={() => {
                                        if (!hasPveChars) return;
                                        this.setState({
                                            lobbyTypeGame: "PvP"
                                        });
                                    }}
                                >
                                    <FontAwesomeIcon icon={faSkull} />
                                    <span>Arena PvP</span>
                                    <small>
                                        {!hasPveChars
                                            ? "🔒 requiere personaje en Mundo Abierto"
                                            : "Arena con preset chars"}
                                    </small>
                                </button>
                            </div>

                            {!loggedIn && (
                                <div className={style.lobbyHint}>
                                    Necesitás iniciar sesión para jugar.
                                </div>
                            )}

                            {showCharGrid && (
                                <div className={style.lobbyDivider}>
                                    <span className={style.lobbyDividerText}>
                                        Mis personajes
                                    </span>
                                </div>
                            )}

                            {showCharGrid && (
                            <div className={style.lobbyGrid}>
                                {Array.from({ length: 10 }).map((_unused, i) => {
                                    const character = characters[i];
                                    if (character && this.props.initsLoaded) {
                                        // Diferimos el dibujo hasta el siguiente tick
                                        setTimeout(
                                            () =>
                                                this.renderLobbyCanvas(
                                                    character,
                                                    i
                                                ),
                                            0
                                        );
                                    }
                                    // Ticket #213: drag & drop solo en PvE.
                                    // Los chars PvP son preset (sintéticos),
                                    // no tienen `_id` ni se reordenan.
                                    const draggableSlot =
                                        effectiveTab === "PvE" && !!character;
                                    const isDragging =
                                        this.state.lobbyDraggingFrom === i;
                                    const isDropTarget =
                                        this.state.lobbyDraggingOver === i &&
                                        this.state.lobbyDraggingFrom !== -1;
                                    return (
                                        <div
                                            key={i}
                                            className={`${
                                                style.lobbySlot
                                            } ${
                                                character
                                                    ? style.lobbySlotFilled
                                                    : ""
                                            } ${
                                                isDragging
                                                    ? style.lobbySlotDragging
                                                    : ""
                                            } ${
                                                isDropTarget
                                                    ? style.lobbySlotDropTarget
                                                    : ""
                                            }`}
                                            draggable={draggableSlot}
                                            onDragStart={
                                                draggableSlot
                                                    ? e =>
                                                          this.onLobbyDragStart(
                                                              e,
                                                              i
                                                          )
                                                    : undefined
                                            }
                                            onDragOver={
                                                lobbyTypeGame === "PvE"
                                                    ? e =>
                                                          this.onLobbyDragOver(
                                                              e,
                                                              i
                                                          )
                                                    : undefined
                                            }
                                            onDragLeave={
                                                lobbyTypeGame === "PvE"
                                                    ? this.onLobbyDragLeave
                                                    : undefined
                                            }
                                            onDragEnd={
                                                lobbyTypeGame === "PvE"
                                                    ? this.onLobbyDragEnd
                                                    : undefined
                                            }
                                            onDrop={
                                                lobbyTypeGame === "PvE"
                                                    ? e =>
                                                          this.onLobbyDrop(
                                                              e,
                                                              i
                                                          )
                                                    : undefined
                                            }
                                            onClick={() =>
                                                loggedIn &&
                                                this.startGameFromLobby(
                                                    character,
                                                    i
                                                )
                                            }
                                        >
                                            {character ? (
                                                <>
                                                    {lobbyTypeGame === "PvE" &&
                                                        character._id && (
                                                            <button
                                                                type="button"
                                                                className={
                                                                    style.lobbySlotDelete
                                                                }
                                                                title={`Eliminar ${character.name}`}
                                                                onClick={e =>
                                                                    this.openDeleteCharModal(
                                                                        character,
                                                                        e
                                                                    )
                                                                }
                                                            >
                                                                <FontAwesomeIcon
                                                                    icon={
                                                                        faTrash
                                                                    }
                                                                />
                                                            </button>
                                                        )}
                                                    {lobbyTypeGame === "PvE" &&
                                                        character.connected && (
                                                            <span
                                                                className={
                                                                    style.lobbySlotOnline
                                                                }
                                                                title="Este personaje está conectado en otra sesión. Si entrás vas a desconectar la otra."
                                                            >
                                                                <span
                                                                    className={
                                                                        style.lobbySlotOnlineDot
                                                                    }
                                                                />
                                                                Conectado
                                                            </span>
                                                        )}
                                                    <canvas
                                                        ref={ref => {
                                                            if (!this.lobbyCanvas)
                                                                this.lobbyCanvas = [];
                                                            this.lobbyCanvas[
                                                                i
                                                            ] = ref;
                                                        }}
                                                        className={
                                                            style.lobbySlotCanvas
                                                        }
                                                        width="80"
                                                        height="100"
                                                    />
                                                    <span
                                                        className={
                                                            style.lobbySlotName
                                                        }
                                                    >
                                                        {character.name}
                                                    </span>
                                                </>
                                            ) : (
                                                <div
                                                    className={
                                                        style.lobbySlotEmpty
                                                    }
                                                >
                                                    <FontAwesomeIcon
                                                        icon={faUserCircle}
                                                    />
                                                    <span>Vacío</span>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                            )}

                            {/* Crear personaje inline — antes vivía en /createCharacter.
                                Vive DENTRO del lobbyCard. Solo en PvE y logueado.
                                Al crear, refrescamos lobby y entramos directo.
                                Usa effectiveTab (no lobbyTypeGame) para que con
                                0 chars siempre aparezca aunque el tab state
                                diga PvP. */}
                            {loggedIn && effectiveTab === "PvE" && (
                                <>
                                    <div className={style.lobbyDivider}>
                                        <span
                                            className={style.lobbyDividerText}
                                        >
                                            Crear nuevo personaje
                                        </span>
                                    </div>
                                    <div className={style.createCharSection}>
                                        <CreateCharacterForm
                                            onCreated={
                                                this.handleCharacterCreated
                                            }
                                        />
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                    {this.state.deleteCharTarget &&
                        this.renderDeleteCharModal()}
                </React.Fragment>
            );
        }

        return (
            <React.Fragment>
                {/* Ticket #329: en fullscreen ocultamos el navbar para que el
                    HUD quede centrado en pantalla y no haya scroll vertical. */}
                {!this.state.isFullscreen && <Header userAgent={userAgent} />}
                <div
                    className={style.progressBar}
                    style={{ display: loading ? "flex" : "none" }}
                >
                    <div className={style.loadingLogo}>
                        <span className={style.loadingLogoMain}>
                            Argentum Online
                        </span>
                        <span className={style.loadingLogoAccent}>Web</span>
                    </div>

                    <div className={style.loadingProgressBox}>
                        <div className={style.loadingProgressLabel}>
                            <span className={style.loadingProgressCount}>
                                {mapasCargados}
                            </span>
                            <span className={style.loadingProgressSep}>
                                {" / "}
                            </span>
                            <span className={style.loadingProgressTotal}>
                                {mapasToLoad} mapas
                            </span>
                        </div>
                        <div className={style.loadingProgressTrack}>
                            <div
                                className={style.loadingProgressFill}
                                style={{
                                    width: `${Math.min(
                                        100,
                                        (mapasCargados / mapasToLoad) * 100
                                    )}%`
                                }}
                            />
                            <div className={style.loadingProgressShimmer} />
                        </div>
                    </div>

                    <div className={style.loadingCards}>
                        <div className={style.loadingCard}>
                            <div className={style.loadingCardTitle}>
                                Controles
                            </div>
                            <div className={style.loadingCardKeys}>
                                <div className={style.keyRow}>
                                    <span className={style.keyLabel}>
                                        Movimiento
                                    </span>
                                    <span className={style.keyValue}>
                                        <kbd>W</kbd>
                                        <kbd>A</kbd>
                                        <kbd>S</kbd>
                                        <kbd>D</kbd>
                                    </span>
                                </div>
                                <div className={style.keyRow}>
                                    <span className={style.keyLabel}>
                                        Atacar
                                    </span>
                                    <span className={style.keyValue}>
                                        <kbd>Ctrl</kbd>
                                    </span>
                                </div>
                                <div className={style.keyRow}>
                                    <span className={style.keyLabel}>
                                        Agarrar
                                    </span>
                                    <span className={style.keyValue}>
                                        <kbd>Q</kbd>
                                    </span>
                                </div>
                                <div className={style.keyRow}>
                                    <span className={style.keyLabel}>
                                        Usar / Equipar
                                    </span>
                                    <span className={style.keyValue}>
                                        <kbd>U</kbd>
                                        <kbd>E</kbd>
                                    </span>
                                </div>
                                <div className={style.keyRow}>
                                    <span className={style.keyLabel}>
                                        Tirar
                                    </span>
                                    <span className={style.keyValue}>
                                        <kbd>T</kbd>
                                    </span>
                                </div>
                                <div className={style.keyRow}>
                                    <span className={style.keyLabel}>
                                        Meditar
                                    </span>
                                    <span className={style.keyValue}>
                                        <kbd>C</kbd>
                                    </span>
                                </div>
                                <div className={style.keyRow}>
                                    <span className={style.keyLabel}>
                                        Seguro
                                    </span>
                                    <span className={style.keyValue}>
                                        <kbd>L</kbd>
                                    </span>
                                </div>
                                <div className={style.keyRow}>
                                    <span className={style.keyLabel}>
                                        Hablar
                                    </span>
                                    <span className={style.keyValue}>
                                        <kbd>Enter</kbd>
                                    </span>
                                </div>
                                <div className={style.keyRow}>
                                    <span className={style.keyLabel}>Mapa</span>
                                    <span className={style.keyValue}>
                                        <kbd>M</kbd>
                                    </span>
                                </div>
                                <div className={style.keyRow}>
                                    <span className={style.keyLabel}>
                                        Macros
                                    </span>
                                    <span className={style.keyValue}>
                                        <kbd>1</kbd>
                                        <span className={style.keyDash}>–</span>
                                        <kbd>6</kbd>
                                    </span>
                                </div>
                            </div>
                        </div>

                        <div className={style.loadingCard}>
                            <div className={style.loadingCardTitle}>
                                Nuestro compromiso
                            </div>
                            <div className={style.loadingCardText}>
                                <p>
                                    Nos comprometemos a que{" "}
                                    <strong>
                                        ningún personaje creado en este
                                        servidor sea borrado nunca
                                    </strong>
                                    . Sin wipes sorpresa, sin resets. Lo que
                                    conseguís jugando, queda.
                                </p>
                                <p>
                                    El <strong>nivel máximo</strong> actual es{" "}
                                    <strong>42</strong>. Cuando estabilicemos
                                    el endgame lo iremos ampliando con
                                    contenido balanceado a cada tramo.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                <ul className={style.modalInfo} />

                <div
                    className={style.modalControlPanel}
                    style={{
                        display: showModalControlPanel ? "block" : "none"
                    }}
                >
                    <div className={style.modalHeader}>
                        <span className={style.modalTitle}>
                            Configuración
                        </span>
                        <span
                            className={style.closeControlPanel}
                            onClick={() => {
                                this.setState({
                                    showModalControlPanel: false
                                });
                            }}
                            title="Cerrar"
                        >
                            <FontAwesomeIcon icon={faTimes} />
                        </span>
                    </div>
                    <div className={style.sound}>
                        {/* UI skin selector — toggle aoweb / Tierras del Sur */}
                        <div className={style.soundRow}>
                            <span className={style.soundLabel}>Estilo UI</span>
                            <select
                                value={this.state.uiSkin}
                                onChange={e => {
                                    const v = e.target.value;
                                    this.setState({ uiSkin: v });
                                    try {
                                        localStorage.setItem("uiSkin", v);
                                    } catch (err) {}
                                }}
                                style={{
                                    background: "rgba(0,0,0,0.55)",
                                    color: "#d8d3c5",
                                    border: "1px solid rgba(212,130,50,0.4)",
                                    padding: "4px 8px",
                                    borderRadius: 4,
                                    fontFamily: "inherit",
                                    cursor: "pointer"
                                }}
                            >
                                <option value="aoweb">aoweb (default)</option>
                                <option value="tierras-del-sur">Tierras del Sur</option>
                            </select>
                        </div>
                        <div className={style.soundRow}>
                            <span className={style.soundLabel}>Audio</span>
                            <span
                                className={`${style.soundToggle} ${
                                    musicEnabled ? style.soundToggleOn : ""
                                }`}
                                onClick={this.toggleMusic}
                                title={
                                    musicEnabled
                                        ? "Apagar todo el audio"
                                        : "Encender audio"
                                }
                            >
                                <FontAwesomeIcon
                                    icon={
                                        musicEnabled
                                            ? faVolumeUp
                                            : faVolumeMute
                                    }
                                />
                                <span className={style.soundStatus}>
                                    {musicEnabled ? "ON" : "OFF"}
                                </span>
                            </span>
                        </div>
                        {[
                            {
                                key: "musicVolume",
                                label: "Música",
                                value: musicVolume,
                                onChange: this.setMusicVolume
                            },
                            {
                                key: "ambienceVolume",
                                label: "Ambiente",
                                value: ambienceVolume,
                                onChange: this.setAmbienceVolume
                            },
                            {
                                key: "effectsVolume",
                                label: "Efectos",
                                value: effectsVolume,
                                onChange: this.setEffectsVolume
                            }
                        ].map(slider => (
                            <div
                                key={slider.key}
                                className={`${style.soundRow} ${
                                    !musicEnabled ? style.soundRowDisabled : ""
                                }`}
                            >
                                <span className={style.soundLabel}>
                                    {slider.label}
                                </span>
                                <div className={style.volumeControl}>
                                    <input
                                        type="range"
                                        min="0"
                                        max="100"
                                        value={slider.value}
                                        onChange={slider.onChange}
                                        disabled={!musicEnabled}
                                        className={style.volumeSlider}
                                    />
                                    <span className={style.volumeValue}>
                                        {slider.value}%
                                    </span>
                                </div>
                            </div>
                        ))}
                        {/* "Música actual ♪ Nombre" — label dinámico
                            que el music engine notifica cuando arranca/
                            cambia de track. Si no hay música sonando
                            (mapa sin entry o silencio entre loops), no
                            muestra nada. */}
                        {musicEnabled && this.state.currentMusicTrack && (
                            <div
                                className={`${style.soundRow} ${style.musicNowPlayingRow}`}
                            >
                                <span className={style.soundLabel}>
                                    Música actual
                                </span>
                                <span className={style.musicNowPlaying}>
                                    ♪ {this.state.currentMusicTrack}
                                </span>
                            </div>
                        )}
                    </div>

                    {/* Ticket #296: toggle del fix "bordes nítidos de
                        tiles". ON (default) usa floor en drawGrhCapa +
                        imageSmoothing:false — sin líneas negras al caminar.
                        OFF deja el render con coords float — render
                        "smooth" pero con halo / gaps visibles. */}
                    <div className={style.mapToggleSection}>
                        <div className={style.soundRow}>
                            <span className={style.soundLabel}>
                                Bordes nítidos de tiles
                            </span>
                            <span
                                className={`${style.soundToggle} ${
                                    this.state.pixelPerfect ? style.soundToggleOn : ""
                                }`}
                                onClick={this.togglePixelPerfect}
                                title={
                                    this.state.pixelPerfect
                                        ? "Click para desactivar (puede aparecer halo en tiles)"
                                        : "Click para activar (elimina líneas negras al caminar)"
                                }
                            >
                                <span className={style.soundStatus}>
                                    {this.state.pixelPerfect ? "ON" : "OFF"}
                                </span>
                            </span>
                        </div>
                        <div className={style.mapToggleHint}>
                            {this.state.pixelPerfect
                                ? "Tiles alineados al pixel exacto — sin líneas negras al caminar."
                                : "Render libre — más smooth pero pueden aparecer líneas negras entre tiles."}
                        </div>
                    </div>

                    {/* Ticket #218: sección Party en el mapa. Si on,
                        los miembros de tu party aparecen en el minimapa
                        + full map (con color por facción) y se suma una
                        tab Party al panel del full map. */}
                    <div className={style.mapToggleSection}>
                        <div className={style.soundRow}>
                            <span className={style.soundLabel}>
                                Mostrar party en el mapa
                            </span>
                            <select
                                className={style.mapToggleSelect}
                                value={this.state.showPartyOnMap}
                                onChange={e =>
                                    this.setShowPartyOnMap(e.target.value)
                                }
                            >
                                <option value="none">No mostrar</option>
                                <option value="show">Mostrar</option>
                            </select>
                        </div>
                        <div className={style.mapToggleHint}>
                            {this.state.showPartyOnMap === "none" &&
                                "No se muestran tus compañeros de party en el mapa."}
                            {this.state.showPartyOnMap === "show" &&
                                "Compañeros de party aparecen con su color de facción (azul ciudadano / rojo criminal)."}
                        </div>
                    </div>

                    {/* Sección Fuentes — round robin de tipografía del
                        header del char (CAJA 4). Cambio en tiempo real via
                        CSS variable, sin re-render del play. Persistido en
                        localStorage bajo "argentum:headerFontIndex". */}
                    <div className={style.fontSection}>
                        <div className={style.fontRow}>
                            <span className={style.fontLabel}>Fuente</span>
                            <div className={style.fontControl}>
                                <span
                                    className={style.fontCurrent}
                                    style={{
                                        fontFamily:
                                            '"' +
                                            HEADER_FONTS[
                                                this.state.headerFontIndex ||
                                                    0
                                            ].family +
                                            '", sans-serif'
                                    }}
                                >
                                    {
                                        HEADER_FONTS[
                                            this.state.headerFontIndex || 0
                                        ].name
                                    }
                                </span>
                                <button
                                    type="button"
                                    className={style.fontBtn}
                                    onClick={this.cycleHeaderFont}
                                    title="Probar siguiente fuente"
                                >
                                    Siguiente
                                </button>
                                <button
                                    type="button"
                                    className={`${style.fontBtn} ${style.fontBtnKeep}`}
                                    onClick={() =>
                                        this.setState({
                                            showModalControlPanel: false
                                        })
                                    }
                                    title="Mantener esta fuente y cerrar"
                                >
                                    Mantener
                                </button>
                            </div>
                        </div>
                        <div className={style.fontHint}>
                            {this.state.headerFontIndex + 1} de{" "}
                            {HEADER_FONTS.length} · Cambia el nombre, nivel y
                            barra de exp del char
                        </div>
                    </div>

                    <div className={style.teclas}>
                        <div className={style.keysGrid}>
                            {[
                                {
                                    code: this.nameKeyCode.flechaArriba,
                                    label: "Arriba"
                                },
                                {
                                    code: this.nameKeyCode.flechaAbajo,
                                    label: "Abajo"
                                },
                                {
                                    code: this.nameKeyCode.flechaIzquierda,
                                    label: "Izquierda"
                                },
                                {
                                    code: this.nameKeyCode.flechaDerecha,
                                    label: "Derecha"
                                },
                                {
                                    code: this.nameKeyCode.usar,
                                    label: "Usar"
                                },
                                {
                                    code: this.nameKeyCode.atacar,
                                    label: "Atacar"
                                },
                                {
                                    code: this.nameKeyCode.agarrar,
                                    label: "Agarrar"
                                },
                                {
                                    code: this.nameKeyCode.tirar,
                                    label: "Tirar"
                                },
                                {
                                    code: this.nameKeyCode.equipar,
                                    label: "Equipar"
                                },
                                {
                                    code: this.nameKeyCode.domar,
                                    label: "Domar"
                                },
                                {
                                    code: this.nameKeyCode.robar,
                                    label: "Robar"
                                },
                                {
                                    code: this.nameKeyCode.seguro,
                                    label: "Seguro"
                                },
                                {
                                    code: this.nameKeyCode.meditar,
                                    label: "Meditar"
                                },
                                {
                                    code: this.nameKeyCode.ocultar,
                                    label: "Ocultar"
                                },
                                {
                                    code: this.nameKeyCode.refreshPosition,
                                    label: "Actualizar posición"
                                }
                            ].map(({ code, label }) => (
                                <label key={code} className={style.keySlot}>
                                    <span className={style.keyLabel}>
                                        {label}
                                    </span>
                                    <input
                                        type="text"
                                        className={style.tecla}
                                        value={
                                            charKeyCodeDefault[code] || ""
                                        }
                                        // Usamos onKeyDown + preventDefault
                                        // para asegurar capture de TODAS las
                                        // teclas. Antes era onKeyUp y las
                                        // flechas izquierda/derecha no se
                                        // podían bindear: el browser ejecuta
                                        // su acción default (mover caret)
                                        // antes del keyup en algunos casos
                                        // y la re-render por el setState
                                        // del caret movement interfería.
                                        onKeyDown={e => {
                                            e.preventDefault();
                                            this.handleKeyDefault(e, code);
                                        }}
                                        onChange={() => {}}
                                        readOnly
                                    />
                                </label>
                            ))}
                        </div>

                        <div className={style.actionRow}>
                            <div
                                className={`${style.actionBtn} ${style.actionBtnSecondary}`}
                                onClick={this.restoreDefaultKeys}
                            >
                                Restaurar teclas
                            </div>
                            <div
                                className={`${style.actionBtn} ${style.actionBtnSecondary}`}
                                onClick={this.restoreMacros}
                            >
                                Reset macros
                            </div>
                            <div
                                className={`${style.actionBtn} ${style.actionBtnPrimary}`}
                                onClick={this.saveChangesKeys}
                            >
                                Guardar cambios
                            </div>
                        </div>
                    </div>
                </div>

                {this.state.showModalReconnect && (
                    <div className={style.modalReconnect}>
                        <div className={style.modalReconnectCard}>
                            <div className={style.modalReconnectSpinner} />
                            <div className={style.modalReconnectTitle}>
                                Reconectando al servidor
                            </div>
                            <div className={style.modalReconnectSubtitle}>
                                Esto suele ocurrir cuando se aplica una
                                actualización. Tu personaje queda guardado.
                                Volvé a entrar en unos segundos.
                            </div>
                        </div>
                    </div>
                )}

                {/* Modal /PERDON — el Conde Aldebrando ofrece amnistía.
                    Costo = 100k × ciudadanos muertos. Confirmar = sendConfirmPerdon. */}
                {/* Modal Morgolock's Soul — upgrade premium Magia.
                    Click sobre el NPC #90 abre este modal. Solo se confirma
                    si user tiene magia=100 + 1M oro. Al confirmar, magia→101. */}
                {this.state.magiaUpgradeModal && (() => {
                    const m = this.state.magiaUpgradeModal;
                    const userMagia = (this.state.savedSkills || {}).magia || 0;
                    const userGold = (this.state.user && this.state.user.gold) || 0;
                    const canBuy = userMagia >= 100 && userMagia < 101 && userGold >= m.cost;
                    return (
                        <div className={style.modalReconnect}>
                            <div style={{
                                position: "relative",
                                background: "linear-gradient(to bottom, rgba(10,11,14,0.95) 0%, rgba(4,5,7,0.97) 100%)",
                                border: "1px solid rgba(140, 95, 50, 0.5)", borderRadius: 8,
                                boxShadow: "0 16px 48px rgba(0,0,0,0.85), 0 0 32px rgba(140, 95, 50, 0.12), inset 0 1px 0 rgba(255, 220, 160, 0.06)",
                                padding: "20px 28px", maxWidth: 480, color: "#d8d3c5"
                            }}>
                                <div style={{ position: "absolute", top: -1, left: "30%", right: "30%", height: 1,
                                    background: "linear-gradient(to right, transparent, rgba(245, 175, 80, 0.85), transparent)" }} />
                                <h2 style={{ margin: "0 0 12px", color: "rgba(245, 175, 80, 0.95)", fontSize: 18,
                                    letterSpacing: 1.5, display: "flex", alignItems: "center", gap: 10 }}>
                                    <FontAwesomeIcon icon={faBolt} /> Alma de Morgolock
                                </h2>
                                <p style={{ color: "#c8b890", fontSize: 13, lineHeight: 1.5, margin: "0 0 16px" }}>
                                    «Vienes en busca del conocimiento prohibido... El último escalón de la Magia, donde
                                    despiertan los hechizos vetados a los mortales.»
                                </p>
                                <div style={{
                                    background: "rgba(0,0,0,0.4)", padding: "10px 14px", borderRadius: 4,
                                    border: "1px solid rgba(140, 95, 50, 0.25)", margin: "0 0 16px",
                                    fontSize: 12, lineHeight: 1.8
                                }}>
                                    <div>Magia actual: <strong style={{
                                        color: userMagia >= 100 ? "#a0d0ff" : "#ff8888"
                                    }}>{userMagia} / 100</strong></div>
                                    <div>Oro: <strong style={{
                                        color: userGold >= m.cost ? "#ffd700" : "#ff8888"
                                    }}>{userGold.toLocaleString()}</strong> / {m.cost.toLocaleString()}</div>
                                    <div style={{ marginTop: 6, paddingTop: 6, borderTop: "1px solid rgba(140, 95, 50, 0.18)" }}>
                                        Resultado: Magia <strong style={{ color: "#ffe09a", textShadow: "0 0 6px rgba(245,175,80,0.6)" }}>101</strong> (premium)
                                    </div>
                                </div>
                                {userMagia < 100 && (
                                    <div style={{ color: "#ff8888", fontSize: 11, fontStyle: "italic",
                                        textAlign: "center", marginBottom: 10 }}>
                                        Necesitas tener Magia 100 antes de buscar este conocimiento.
                                    </div>
                                )}
                                {userMagia >= 101 && (
                                    <div style={{ color: "#ffe09a", fontSize: 11, fontStyle: "italic",
                                        textAlign: "center", marginBottom: 10 }}>
                                        Ya posees el rango premium de Magia. Vuelve cuando puedas ofrecer algo más.
                                    </div>
                                )}
                                <div style={{ display: "flex", justifyContent: "center", gap: 12 }}>
                                    <button type="button"
                                        onClick={() => this.setState({ magiaUpgradeModal: null })}
                                        style={{
                                            background: "linear-gradient(to bottom, rgba(60,60,70,0.5), rgba(30,30,35,0.5))",
                                            border: "1px solid rgba(140,140,150,0.45)", color: "#d8d3c5",
                                            padding: "8px 28px", cursor: "pointer", borderRadius: 6,
                                            fontFamily: "inherit", fontSize: 12, fontWeight: "bold", letterSpacing: 1.5
                                        }}>
                                        Cancelar
                                    </button>
                                    <button type="button"
                                        disabled={!canBuy}
                                        onClick={() => {
                                            if (!canBuy) return;
                                            if (this.general && this.general.sendConfirmMagiaUpgrade) {
                                                this.general.sendConfirmMagiaUpgrade();
                                            }
                                            // Cliente actualiza skill localmente (server cobra y manda actGold).
                                            const newSaved = { ...(this.state.savedSkills || {}), magia: 101 };
                                            const newSkills = { ...(this.state.skills || {}), magia: 101 };
                                            this.setState({
                                                savedSkills: newSaved, skills: newSkills,
                                                magiaUpgradeModal: null
                                            });
                                            try {
                                                localStorage.setItem("savedSkills", JSON.stringify(newSaved));
                                                localStorage.setItem("skills", JSON.stringify(newSkills));
                                            } catch (e) {}
                                        }}
                                        style={{
                                            background: canBuy
                                                ? "linear-gradient(to bottom, rgba(212, 130, 50, 0.5), rgba(140, 70, 20, 0.5))"
                                                : "linear-gradient(to bottom, rgba(60,50,40,0.4), rgba(40,30,20,0.4))",
                                            border: "1px solid " + (canBuy ? "rgba(245, 175, 80, 0.85)" : "rgba(140, 95, 50, 0.3)"),
                                            color: canBuy ? "#fff8e8" : "rgba(170,150,127,0.45)",
                                            padding: "8px 28px",
                                            cursor: canBuy ? "pointer" : "not-allowed",
                                            borderRadius: 6, opacity: canBuy ? 1 : 0.6,
                                            fontFamily: "inherit", fontSize: 12, fontWeight: "bold", letterSpacing: 1.5,
                                            boxShadow: canBuy
                                                ? "0 4px 16px rgba(212, 130, 50, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.14)"
                                                : "none"
                                        }}>
                                        Pagar 1.000.000
                                    </button>
                                </div>
                            </div>
                        </div>
                    );
                })()}

                {/* Modal SKILLS — catálogo AO clásico de 21 habilidades.
                    Botón ⚡ al lado del ⚙ lo abre. Cada skill tiene < val >
                    para subir/bajar. PUNTOS DISPONIBLES gates el total.
                    Persiste en localStorage (no afecta gameplay todavía). */}
                {this.state.showSkillsModal && (() => {
                    const SKILL_CATALOG = [
                        { col: "combate", group: 0, key: "magia", label: "Magia" },
                        { col: "combate", group: 0, key: "meditar", label: "Meditar" },
                        { col: "combate", group: 0, key: "resMagica", label: "Resistencia Mágica" },
                        { col: "combate", group: 1, key: "combateArmas", label: "Combate con armas" },
                        { col: "combate", group: 1, key: "tacticasCombate", label: "Tacticas de combate" },
                        { col: "combate", group: 1, key: "defensaEscudo", label: "Defensa con escudo" },
                        { col: "combate", group: 1, key: "combateSinArmas", label: "Combate sin armas" },
                        { col: "combate", group: 1, key: "armasDistancia", label: "Armas a distancia" },
                        { col: "combate", group: 1, key: "apunalar", label: "Apuñalar" },
                        { col: "combate", group: 2, key: "robar", label: "Robar" },
                        { col: "combate", group: 2, key: "liderazgo", label: "Liderazgo" },
                        { col: "habilidades", group: 0, key: "talar", label: "Talar" },
                        { col: "habilidades", group: 0, key: "pescar", label: "Pescar" },
                        { col: "habilidades", group: 0, key: "minar", label: "Minar" },
                        { col: "habilidades", group: 0, key: "carpinteria", label: "Carpintería" },
                        { col: "habilidades", group: 0, key: "herreria", label: "Herrería" },
                        { col: "habilidades", group: 0, key: "sastreria", label: "Sastrería" },
                        { col: "habilidades", group: 0, key: "alquimia", label: "Alquimia" },
                        { col: "habilidades", group: 1, key: "supervivencia", label: "Supervivencia" },
                        { col: "habilidades", group: 1, key: "ocultar", label: "Ocultar" },
                        { col: "habilidades", group: 1, key: "comercio", label: "Comercio" },
                        { col: "habilidades", group: 1, key: "domar", label: "Domar animales" },
                        { col: "habilidades", group: 1, key: "navegar", label: "Navegar" }
                    ];
                    const sks = this.state.skills || {};
                    const savedSks = this.state.savedSkills || {};
                    const pts = this.state.skillPoints || 0;
                    // ‑ solo decrementa lo PENDIENTE (no locked). +  consume
                    // pt y sube. Si savedSkills[key] == skills[key] no podés
                    // decrementar — ya fue guardado.
                    const adjustSkill = (key, delta) => {
                        const cur = sks[key] || 0;
                        const next = cur + delta;
                        if (next < 0 || next > 100) return;
                        if (delta > 0 && pts <= 0) return;
                        if (delta < 0 && next < (savedSks[key] || 0)) return;
                        const newSkills = { ...sks, [key]: next };
                        const newPts = pts - delta;
                        this.setState({ skills: newSkills, skillPoints: newPts });
                        try {
                            localStorage.setItem("skills", JSON.stringify(newSkills));
                            localStorage.setItem("skillPoints", String(newPts));
                        } catch (e) {}
                    };
                    // GUARDAR — mueve los valores pendientes a savedSkills,
                    // lockeándolos. Después de esto el `-` ya no podrá
                    // revertir lo gastado en esta sesión.
                    const saveSkills = () => {
                        const newSaved = { ...sks };
                        this.setState({ savedSkills: newSaved });
                        try {
                            localStorage.setItem("savedSkills", JSON.stringify(newSaved));
                        } catch (e) {}
                    };
                    const hasPending = SKILL_CATALOG.some(s =>
                        (sks[s.key] || 0) > (savedSks[s.key] || 0)
                    );
                    // Estilos de los controles − N + alineados con Rogue Frame.
                    // El botón "−" se ve deshabilitado (alpha) si no podés
                    // refundar (porque el valor ya está lockeado).
                    const btnBase = {
                        background: "rgba(14, 16, 20, 0.85)",
                        color: "rgba(236, 154, 68, 0.95)",
                        border: "1px solid rgba(140, 95, 50, 0.45)",
                        cursor: "pointer", padding: "0 7px", borderRadius: 3,
                        fontFamily: "inherit", fontSize: 14, fontWeight: "bold",
                        lineHeight: "16px", minWidth: 20, textAlign: "center"
                    };
                    const btnDisabled = {
                        ...btnBase,
                        opacity: 0.35, cursor: "not-allowed", color: "rgba(140, 110, 70, 0.55)"
                    };
                    const renderSkillRow = (skill) => {
                        const curVal = sks[skill.key] || 0;
                        const lockedVal = savedSks[skill.key] || 0;
                        const canDec = curVal > lockedVal;
                        const canInc = pts > 0 && curVal < 100;
                        return (
                            <div key={skill.key} style={{
                                display: "flex", alignItems: "center", justifyContent: "space-between",
                                padding: "3px 0", fontSize: 12, color: "#d8d3c5"
                            }}>
                                <span>{skill.label}</span>
                                <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                                    <button type="button" onClick={() => adjustSkill(skill.key, -1)}
                                        style={canDec ? btnBase : btnDisabled}
                                        disabled={!canDec}
                                        title={canDec ? "Refundar 1 punto" : "Ya guardado — no se puede revertir"}>
                                        −
                                    </button>
                                    <span style={{
                                        background: curVal === 101
                                            ? "linear-gradient(180deg, rgba(60,40,5,0.85), rgba(40,25,2,0.85))"
                                            : curVal === 100
                                                ? "rgba(20, 40, 70, 0.85)"
                                                : "rgba(0, 0, 0, 0.55)",
                                        border: curVal === 101
                                            ? "1px solid rgba(245, 175, 80, 0.95)"
                                            : curVal === 100
                                                ? "1px solid rgba(120, 170, 230, 0.7)"
                                                : "1px solid rgba(140, 95, 50, 0.35)",
                                        color: curVal === 101
                                            ? "#ffe09a"   // dorado premium vivo
                                            : curVal === 100
                                                ? "#a0d0ff"  // azul cielo maxed
                                                : "#fff8e8",
                                        textShadow: curVal === 101
                                            ? "0 0 8px rgba(245, 175, 80, 0.6)"
                                            : curVal === 100
                                                ? "0 0 6px rgba(120, 170, 230, 0.5)"
                                                : "none",
                                        padding: "1px 14px", borderRadius: 2,
                                        fontSize: 12, fontWeight: "bold",
                                        minWidth: 28, textAlign: "center", display: "inline-block"
                                    }}>
                                        {curVal}
                                    </span>
                                    <button type="button" onClick={() => adjustSkill(skill.key, 1)}
                                        style={canInc ? btnBase : btnDisabled}
                                        disabled={!canInc}
                                        title={canInc ? "Asignar 1 punto" : (pts <= 0 ? "Sin puntos disponibles" : "Skill al máximo")}>
                                        +
                                    </button>
                                </div>
                            </div>
                        );
                    };
                    const renderCol = (colName) => {
                        const list = SKILL_CATALOG.filter(s => s.col === colName);
                        const out = [];
                        list.forEach((s, i) => {
                            if (i > 0 && s.group !== list[i - 1].group) {
                                out.push(<hr key={"sep-" + i} style={{
                                    border: "none",
                                    borderTop: "1px solid rgba(140, 95, 50, 0.18)",
                                    margin: "10px 0"
                                }} />);
                            }
                            out.push(renderSkillRow(s));
                        });
                        return out;
                    };
                    return (
                        <div className={style.modalReconnect}>
                            {/* Rogue Frame — chrome compartido del HUD principal.
                                Ver CLAUDE.md → "Rogue Frame". Gradient slate
                                oscuro + border dorado tenue + 4 sombras
                                layered + acento dorado superior. */}
                            <div style={{
                                position: "relative",
                                background: "linear-gradient(to bottom, rgba(10, 11, 14, 0.9) 0%, rgba(4, 5, 7, 0.94) 100%)",
                                border: "1px solid rgba(140, 95, 50, 0.45)",
                                borderRadius: 8,
                                boxShadow:
                                    "0 10px 28px rgba(0, 0, 0, 0.75),"
                                    + "0 0 0 1px rgba(0, 0, 0, 0.6),"
                                    + "0 0 22px rgba(140, 95, 50, 0.07),"
                                    + "inset 0 1px 0 rgba(255, 220, 160, 0.04)",
                                padding: "0 0 16px 0",
                                maxWidth: 700, width: "92vw",
                                color: "#d8d3c5"
                            }}>
                                {/* Acento dorado superior (sustituye al ::before del Rogue Frame
                                    porque inline styles no soportan pseudo-elements). */}
                                <div style={{
                                    position: "absolute", top: -1, left: "35%", right: "35%",
                                    height: 1, pointerEvents: "none", zIndex: 2,
                                    background:
                                        "linear-gradient(to right, transparent 0%, "
                                        + "rgba(212, 130, 50, 0.6) 50%, transparent 100%)"
                                }} />
                                <div style={{
                                    display: "flex", alignItems: "center", justifyContent: "space-between",
                                    padding: "12px 16px",
                                    borderBottom: "1px solid rgba(140, 95, 50, 0.25)"
                                }}>
                                    <h2 style={{
                                        margin: 0, color: "rgba(236, 154, 68, 0.95)",
                                        fontSize: 20, letterSpacing: 2,
                                        display: "flex", alignItems: "center", gap: 10
                                    }}>
                                        <FontAwesomeIcon icon={faBolt} /> SKILLS
                                    </h2>
                                    <span style={{ cursor: "pointer", color: "rgba(170, 150, 127, 0.85)", fontSize: 18 }}
                                        onClick={() => this.setState({ showSkillsModal: false })}>✕</span>
                                </div>
                                <div style={{
                                    textAlign: "center", padding: "12px 0 10px",
                                    borderBottom: "1px solid rgba(140, 95, 50, 0.18)",
                                    marginBottom: 14
                                }}>
                                    <span style={{ color: "rgba(236, 154, 68, 0.85)", fontWeight: "bold", letterSpacing: 1, fontSize: 13 }}>
                                        PUNTOS DISPONIBLES:
                                    </span>{" "}
                                    <span style={{ color: "#fff8e8", fontWeight: "bold", fontSize: 16 }}>{pts}</span>
                                </div>
                                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24, padding: "0 20px" }}>
                                    <div>{renderCol("combate")}</div>
                                    <div>{renderCol("habilidades")}</div>
                                </div>
                                <div style={{
                                    display: "flex", justifyContent: "center", gap: 12,
                                    marginTop: 18, padding: "0 20px"
                                }}>
                                    <button type="button" onClick={() => this.setState({ showSkillsModal: false })}
                                        style={{
                                            background: "linear-gradient(to bottom, rgba(60, 60, 70, 0.5) 0%, rgba(30, 30, 35, 0.5) 100%)",
                                            border: "1px solid rgba(140, 140, 150, 0.45)",
                                            color: "#d8d3c5",
                                            padding: "8px 36px", cursor: "pointer", borderRadius: 6,
                                            fontFamily: "inherit", fontSize: 13, fontWeight: "bold", letterSpacing: 2,
                                            boxShadow: "inset 0 1px 0 rgba(255, 255, 255, 0.06)"
                                        }}>
                                        ACEPTAR
                                    </button>
                                    <button type="button"
                                        onClick={() => { saveSkills(); this.setState({ showSkillsModal: false }); }}
                                        disabled={!hasPending}
                                        style={{
                                            background: hasPending
                                                ? "linear-gradient(to bottom, rgba(212, 130, 50, 0.5) 0%, rgba(140, 70, 20, 0.5) 100%)"
                                                : "linear-gradient(to bottom, rgba(80, 60, 40, 0.3) 0%, rgba(50, 35, 20, 0.3) 100%)",
                                            border: "1px solid " + (hasPending ? "rgba(212, 130, 50, 0.7)" : "rgba(140, 95, 50, 0.3)"),
                                            color: hasPending ? "#fff8e8" : "rgba(170, 150, 127, 0.5)",
                                            padding: "8px 36px",
                                            cursor: hasPending ? "pointer" : "not-allowed",
                                            borderRadius: 6,
                                            fontFamily: "inherit", fontSize: 13, fontWeight: "bold", letterSpacing: 2,
                                            boxShadow: hasPending
                                                ? "0 4px 16px rgba(212, 130, 50, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.14)"
                                                : "inset 0 1px 0 rgba(255, 255, 255, 0.04)",
                                            opacity: hasPending ? 1 : 0.6
                                        }}
                                        title={hasPending
                                            ? "Lockear los puntos asignados — no podrás revertirlos"
                                            : "Sin cambios pendientes"}>
                                        GUARDAR
                                    </button>
                                </div>
                            </div>
                        </div>
                    );
                })()}

                {this.state.perdonModal && (
                    <div className={style.modalReconnect}>
                        <div
                            className={style.modalReconnectCard}
                            style={{
                                maxWidth: 480,
                                background:
                                    "linear-gradient(135deg, rgba(40,30,15,0.95), rgba(20,15,10,0.95))",
                                border: "2px solid #d4a64a",
                                color: "#f5e7c6"
                            }}
                        >
                            <div
                                className={style.modalReconnectTitle}
                                style={{ color: "#e69500" }}
                            >
                                ¿Quieres ser perdonado?
                            </div>
                            <div
                                className={style.modalReconnectSubtitle}
                                style={{ color: "#d8c89a" }}
                            >
                                Aldebrando de Valois, Conde de Banderbill,
                                te ofrece la amnistía imperial.
                                <br />
                                <br />
                                Crímenes registrados:{" "}
                                <strong style={{ color: "#ff6b6b" }}>
                                    {this.state.perdonModal.ciudadanosKilled}
                                </strong>{" "}
                                ciudadanos asesinados.
                                <br />
                                Precio:{" "}
                                <strong style={{ color: "#ffd700" }}>
                                    {this.state.perdonModal.cost.toLocaleString()}
                                </strong>{" "}
                                de oro.
                                <br />
                                <br />
                                Si aceptas, vuelves a ser ciudadano del
                                Imperio.
                            </div>
                            <div
                                style={{
                                    display: "flex",
                                    gap: 12,
                                    justifyContent: "center",
                                    marginTop: 20
                                }}
                            >
                                <button
                                    type="button"
                                    style={{
                                        padding: "10px 24px",
                                        background:
                                            "linear-gradient(180deg, #2a5a2a, #1a3a1a)",
                                        border: "1px solid #4a8a4a",
                                        color: "#d8e8c0",
                                        cursor: "pointer",
                                        borderRadius: 4,
                                        fontFamily: "inherit",
                                        fontSize: 14
                                    }}
                                    onClick={() => {
                                        if (this.general && this.general.sendConfirmPerdon) {
                                            this.general.sendConfirmPerdon();
                                        }
                                        this.setState({ perdonModal: null });
                                    }}
                                >
                                    Aceptar
                                </button>
                                <button
                                    type="button"
                                    style={{
                                        padding: "10px 24px",
                                        background:
                                            "linear-gradient(180deg, #5a2a2a, #3a1a1a)",
                                        border: "1px solid #8a4a4a",
                                        color: "#e8c0c0",
                                        cursor: "pointer",
                                        borderRadius: 4,
                                        fontFamily: "inherit",
                                        fontSize: 14
                                    }}
                                    onClick={() =>
                                        this.setState({ perdonModal: null })
                                    }
                                >
                                    Rechazar
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Modal de confirmación /criminal (#328). Abrir = server
                    manda openCriminalConfirm. Confirmar = sendConfirmCriminal. */}
                {this.state.criminalModal && (
                    <div className={style.modalReconnect}>
                        <div
                            className={style.modalReconnectCard}
                            style={{
                                maxWidth: 480,
                                background:
                                    "linear-gradient(135deg, rgba(40,15,15,0.95), rgba(20,10,10,0.95))",
                                border: "2px solid #a83232",
                                color: "#f5d6d6"
                            }}
                        >
                            <div
                                className={style.modalReconnectTitle}
                                style={{ color: "#ff6b6b" }}
                            >
                                ¿Convertirte en Criminal?
                            </div>
                            <div
                                className={style.modalReconnectSubtitle}
                                style={{ color: "#d8b0b0" }}
                            >
                                Renuncias a tu ciudadanía y te vuelves un
                                Criminal del reino.
                                <br />
                                <br />
                                Tu nombre pasará a estar en{" "}
                                <strong style={{ color: "#ff6b6b" }}>
                                    rojo
                                </strong>{" "}
                                y el Imperio te perseguirá. Si perteneces a la
                                Armada Real, perderás tu alistamiento.
                                <br />
                                <br />
                                Para volver a ser ciudadano deberás pedir
                                amnistía con <strong>/perdon</strong>.
                            </div>
                            <div
                                style={{
                                    display: "flex",
                                    gap: 12,
                                    justifyContent: "center",
                                    marginTop: 20
                                }}
                            >
                                <button
                                    type="button"
                                    style={{
                                        padding: "10px 24px",
                                        background:
                                            "linear-gradient(180deg, #7a2a2a, #4a1a1a)",
                                        border: "1px solid #a83232",
                                        color: "#f5d6d6",
                                        cursor: "pointer",
                                        borderRadius: 4,
                                        fontFamily: "inherit",
                                        fontSize: 14
                                    }}
                                    onClick={() => {
                                        if (
                                            this.general &&
                                            this.general.sendConfirmCriminal
                                        ) {
                                            this.general.sendConfirmCriminal();
                                        }
                                        this.setState({ criminalModal: false });
                                    }}
                                >
                                    Convertirme
                                </button>
                                <button
                                    type="button"
                                    style={{
                                        padding: "10px 24px",
                                        background:
                                            "linear-gradient(180deg, #3a3a3a, #1a1a1a)",
                                        border: "1px solid #6a6a6a",
                                        color: "#d0d0d0",
                                        cursor: "pointer",
                                        borderRadius: 4,
                                        fontFamily: "inherit",
                                        fontSize: 14
                                    }}
                                    onClick={() =>
                                        this.setState({ criminalModal: false })
                                    }
                                >
                                    Cancelar
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Modal "nueva versión disponible". Dispara cuando el
                    engine versionWatch detecta cambio en buildId. No bloquea
                    el juego — el user decide cuándo recargar. */}
                {this.state.showVersionUpdateModal && (
                    <div className={style.modalVersionUpdate}>
                        <div className={style.modalVersionUpdateCard}>
                            <div className={style.modalVersionUpdateIcon}>
                                ✨
                            </div>
                            <div className={style.modalVersionUpdateTitle}>
                                Nueva versión disponible
                            </div>
                            <div className={style.modalVersionUpdateSubtitle}>
                                Hay una actualización del juego. Recargá
                                para aplicarla — algunos cambios pueden no
                                funcionar correctamente hasta que recargues.
                            </div>
                            <div className={style.modalVersionUpdateButtons}>
                                <button
                                    type="button"
                                    className={
                                        style.modalVersionUpdateBtnSecondary
                                    }
                                    onClick={this.handleVersionSnooze}
                                >
                                    Después
                                </button>
                                <button
                                    type="button"
                                    className={
                                        style.modalVersionUpdateBtnPrimary
                                    }
                                    onClick={this.handleVersionReload}
                                >
                                    Recargar ahora
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {this.state.showModalCloseForce && (
                    <div className={style.modalCloseForce}>
                        <div className={style.modalCloseForceCard}>
                            <div className={style.modalCloseForceIcon}>!</div>
                            <div className={style.modalCloseForceTitle}>
                                Conexión cerrada por el servidor
                            </div>
                            <div className={style.modalCloseForceSubtitle}>
                                Las causas más comunes son:
                                <ul className={style.modalCloseForceList}>
                                    <li>
                                        Entraste con este mismo personaje desde
                                        otra pestaña o dispositivo
                                    </li>
                                    <li>
                                        El servidor detectó un comportamiento
                                        sospechoso
                                    </li>
                                    <li>
                                        Un GM te expulsó de la sesión
                                    </li>
                                </ul>
                            </div>
                            <button
                                type="button"
                                className={style.modalCloseForceBtn}
                                onClick={() => {
                                    this.setState({
                                        showModalCloseForce: false
                                    });
                                    this.doLogout();
                                }}
                            >
                                Volver al lobby
                            </button>
                        </div>
                    </div>
                )}

                {/* Modal invitación recibida — botones envían /p aceptar
                    o /p rechazar via chat command. */}
                {this.state.partyInvite && (
                    <div className={style.modalPartyInvite}>
                        <div className={style.modalPartyInviteCard}>
                            <div className={style.modalPartyInviteTitle}>
                                {this.state.partyInvite.inviterName} te invitó a su party
                            </div>
                            <div className={style.modalPartyInviteSubtitle}>
                                En party la experiencia de matar mobs se reparte
                                entre todos los miembros en rango.
                            </div>
                            <div className={style.modalPartyInviteButtons}>
                                <button
                                    type="button"
                                    className={`${style.partyAcceptBtn}`}
                                    onClick={() => {
                                        this.general.sendDialog("/p aceptar");
                                        this.setState({ partyInvite: null });
                                    }}
                                >
                                    Aceptar
                                </button>
                                <button
                                    type="button"
                                    className={`${style.partyRejectBtn}`}
                                    onClick={() => {
                                        this.general.sendDialog("/p rechazar");
                                        this.setState({ partyInvite: null });
                                    }}
                                >
                                    Rechazar
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Ticket #313: modal único del clan. Muestra:
                    - Si NO tenés clan: tabs [Unirse | Fundar].
                    - Si SÍ tenés clan: info + members + solicitudes (líder).
                    Atrium Frame + draggable. */}
                {false && this.state.showClanFoundModal && (() => {
                    const modalPos = this.state.clanModalPos;
                    const modalStyle = modalPos
                        ? {
                              top: `calc(50% + ${modalPos.y}px)`,
                              left: `calc(50% + ${modalPos.x}px)`,
                              transform: "translate(-50%, -50%)"
                          }
                        : {};
                    return (
                        <div
                            className={style.clanModalOverlay}
                            onClick={this.closeClanModals}
                        >
                            <div
                                className={style.clanModalCard}
                                style={modalStyle}
                                onClick={e => e.stopPropagation()}
                                onMouseDown={this.onClanModalMouseDown}
                            >
                                <div
                                    className={style.clanModalHeader}
                                    data-clan-drag-handle="true"
                                >
                                    <span className={style.clanModalTitle}>
                                        Fundar Clan
                                    </span>
                                    <span
                                        className={style.clanModalClose}
                                        onClick={this.closeClanModals}
                                    >
                                        ×
                                    </span>
                                </div>
                                <div className={style.clanModalBody}>
                                    <div className={style.clanRequirements}>
                                        <div>Nivel requerido: <strong>35</strong></div>
                                        <div>Costo: <strong>10.000.000 oro</strong></div>
                                    </div>
                                    <label className={style.clanFormLabel}>
                                        Nombre del Clan
                                    </label>
                                    <input
                                        type="text"
                                        className={style.clanFormInput}
                                        placeholder="3-20 caracteres"
                                        value={this.state.clanFoundName}
                                        onChange={e =>
                                            this.setState({
                                                clanFoundName: e.target.value
                                            })
                                        }
                                        maxLength={20}
                                    />
                                    <label className={style.clanFormLabel}>
                                        Tipo de Clan
                                    </label>
                                    <select
                                        className={style.clanFormSelect}
                                        value={this.state.clanFoundType}
                                        onChange={e =>
                                            this.setState({
                                                clanFoundType: e.target.value
                                            })
                                        }
                                    >
                                        <option value="mixto">Mixto (ciudadanos + criminales)</option>
                                        <option value="solo_real">Solo Armada Real</option>
                                        <option value="solo_caos">Solo Legión del Caos</option>
                                        <option value="ciudadanos_real">Ciudadanos + Armada Real</option>
                                        <option value="criminales_caos">Criminales + Legión del Caos</option>
                                    </select>
                                    <div className={style.clanModalActions}>
                                        <button
                                            type="button"
                                            className={style.clanModalBtnPrimary}
                                            onClick={this.submitClanFound}
                                        >
                                            Fundar
                                        </button>
                                        <button
                                            type="button"
                                            className={style.clanModalBtn}
                                            onClick={this.closeClanModals}
                                        >
                                            Cancelar
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    );
                })()}

                {/* Old single-info modal — replaced by unified modal abajo. */}
                {false && this.state.showClanInfoModal && (() => {
                    const modalPos = this.state.clanModalPos;
                    const modalStyle = modalPos
                        ? {
                              top: `calc(50% + ${modalPos.y}px)`,
                              left: `calc(50% + ${modalPos.x}px)`,
                              transform: "translate(-50%, -50%)"
                          }
                        : {};
                    const c = this.state.clan || {};
                    const members = c.members || [];
                    return (
                        <div
                            className={style.clanModalOverlay}
                            onClick={this.closeClanModals}
                        >
                            <div
                                className={style.clanModalCard}
                                style={modalStyle}
                                onClick={e => e.stopPropagation()}
                                onMouseDown={this.onClanModalMouseDown}
                            >
                                <div
                                    className={style.clanModalHeader}
                                    data-clan-drag-handle="true"
                                >
                                    <span className={style.clanModalTitle}>
                                        {c.name || "Clan"}
                                    </span>
                                    <span
                                        className={style.clanModalClose}
                                        onClick={this.closeClanModals}
                                    >
                                        ×
                                    </span>
                                </div>
                                <div className={style.clanModalBody}>
                                    <div className={style.clanInfoGrid}>
                                        <div className={style.clanInfoCell}>
                                            <div className={style.clanInfoLabel}>Tipo</div>
                                            <div className={style.clanInfoValue}>{c.typeLabel || c.type || "—"}</div>
                                        </div>
                                        <div className={style.clanInfoCell}>
                                            <div className={style.clanInfoLabel}>Nivel</div>
                                            <div className={style.clanInfoValue}>{c.level || 1}</div>
                                        </div>
                                        <div className={style.clanInfoCell}>
                                            <div className={style.clanInfoLabel}>Miembros</div>
                                            <div className={style.clanInfoValue}>{members.length}/{c.memberCap || 6}</div>
                                        </div>
                                        <div className={style.clanInfoCell}>
                                            <div className={style.clanInfoLabel}>Muertes totales</div>
                                            <div className={style.clanInfoValue}>{c.totalKills || 0}</div>
                                        </div>
                                    </div>
                                    <div className={style.clanMembersHeader}>
                                        <span>Miembros</span>
                                        {c.isLeader && (
                                            <button
                                                type="button"
                                                className={style.clanModalBtnSmall}
                                                onClick={this.promptClanInvite}
                                            >
                                                + Invitar
                                            </button>
                                        )}
                                    </div>
                                    <div className={style.clanMembersList}>
                                        {members.map(m => (
                                            <div
                                                key={m.id}
                                                className={`${style.clanMemberRow} ${
                                                    m.online
                                                        ? style.clanMemberOnline
                                                        : style.clanMemberOffline
                                                }`}
                                            >
                                                <span className={style.clanMemberStatus}>●</span>
                                                <span className={style.clanMemberName}>
                                                    {m.name}
                                                    {m.isLeader && (
                                                        <span className={style.clanMemberLeader}>
                                                            {" "}♔ Líder
                                                        </span>
                                                    )}
                                                </span>
                                                <span className={style.clanMemberMeta}>
                                                    {m.online
                                                        ? `Mapa ${m.map}`
                                                        : "Desconectado"}
                                                </span>
                                                <span className={style.clanMemberKills}>
                                                    {m.kills || 0}k
                                                </span>
                                                {c.isLeader && !m.isLeader && (
                                                    <button
                                                        type="button"
                                                        className={style.clanMemberKick}
                                                        title={"Expulsar " + m.name}
                                                        onClick={() => this.promptClanKick(m.name)}
                                                    >
                                                        ×
                                                    </button>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                    <div className={style.clanModalActions}>
                                        <button
                                            type="button"
                                            className={style.clanModalBtn}
                                            onClick={this.submitClanLeave}
                                        >
                                            {c.isLeader && members.length === 1
                                                ? "Disolver Clan"
                                                : "Salir del Clan"}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    );
                })()}

                {/* Ticket #313: modal UNIFICADO del clan. */}
                {this.state.showClanModal && (() => {
                    const modalPos = this.state.clanModalPos;
                    const modalStyle = modalPos
                        ? {
                              top: `calc(50% + ${modalPos.y}px)`,
                              left: `calc(50% + ${modalPos.x}px)`,
                              transform: "translate(-50%, -50%)"
                          }
                        : {};
                    const c = this.state.clan || {};
                    const hasClan = !!c.hasClan;
                    const members = c.members || [];
                    const pendingReqs = c.pendingRequests || [];
                    const clanList = this.state.clanList || [];
                    const noClanTab = this.state.clanNoClanTab || "unirse";
                    const dissolving = this.state.showClanDissolveConfirm;
                    return (
                        <div
                            className={style.clanModalOverlay}
                            onClick={this.closeClanModals}
                        >
                            <div
                                className={style.clanModalCard}
                                style={modalStyle}
                                onClick={e => e.stopPropagation()}
                                onMouseDown={this.onClanModalMouseDown}
                            >
                                <div
                                    className={style.clanModalHeader}
                                    data-clan-drag-handle="true"
                                >
                                    <span className={style.clanModalTitle}>
                                        {hasClan ? c.name : "Clan"}
                                    </span>
                                    <span
                                        className={style.clanModalClose}
                                        onClick={this.closeClanModals}
                                    >
                                        ×
                                    </span>
                                </div>

                                {/* Caso 1: NO TENGO CLAN → tabs Unirse / Fundar */}
                                {!hasClan && !dissolving && (
                                    <div className={style.clanModalBody}>
                                        <div className={style.clanTabRow}>
                                            <button
                                                type="button"
                                                className={`${style.clanTab} ${
                                                    noClanTab === "unirse"
                                                        ? style.clanTabActive
                                                        : ""
                                                }`}
                                                onClick={() =>
                                                    this.setState({
                                                        clanNoClanTab: "unirse"
                                                    })
                                                }
                                            >
                                                Unirse a un Clan
                                            </button>
                                            <button
                                                type="button"
                                                className={`${style.clanTab} ${
                                                    noClanTab === "fundar"
                                                        ? style.clanTabActive
                                                        : ""
                                                }`}
                                                onClick={() =>
                                                    this.setState({
                                                        clanNoClanTab: "fundar"
                                                    })
                                                }
                                            >
                                                Fundar Clan
                                            </button>
                                        </div>

                                        {noClanTab === "unirse" && (
                                            <div className={style.clanListPanel}>
                                                {clanList.length === 0 && (
                                                    <div className={style.clanListEmpty}>
                                                        No hay clanes disponibles. Sé el primero en fundar uno.
                                                    </div>
                                                )}
                                                {clanList.map(cl => (
                                                    <div
                                                        key={cl._id}
                                                        className={`${style.clanListRow} ${
                                                            !cl.canJoin
                                                                ? style.clanListRowLocked
                                                                : ""
                                                        }`}
                                                    >
                                                        <div className={style.clanListMain}>
                                                            <div className={style.clanListName}>
                                                                {cl.name}
                                                                <span className={style.clanListLevel}>
                                                                    {" "}L{cl.level}
                                                                </span>
                                                            </div>
                                                            <div className={style.clanListType}>
                                                                {cl.typeLabel}
                                                            </div>
                                                        </div>
                                                        <div className={style.clanListMembers}>
                                                            {cl.memberCount}/{cl.memberCap}
                                                        </div>
                                                        {cl.requestPending ? (
                                                            <span className={style.clanListPending}>
                                                                Pendiente…
                                                            </span>
                                                        ) : cl.canJoin ? (
                                                            <button
                                                                type="button"
                                                                className={style.clanModalBtnSmall}
                                                                onClick={() =>
                                                                    this.requestJoinClan(cl._id)
                                                                }
                                                            >
                                                                Solicitar
                                                            </button>
                                                        ) : (
                                                            <span className={style.clanListLockedNote}>
                                                                {cl.memberCount >= cl.memberCap
                                                                    ? "Lleno"
                                                                    : "No cumple"}
                                                            </span>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        )}

                                        {noClanTab === "fundar" && (
                                            <div>
                                                <div className={style.clanRequirements}>
                                                    <div>Nivel requerido: <strong>35</strong></div>
                                                    <div>Costo: <strong>10.000.000 oro</strong></div>
                                                </div>
                                                <label className={style.clanFormLabel}>Nombre del Clan</label>
                                                <input
                                                    type="text"
                                                    className={style.clanFormInput}
                                                    placeholder="3-20 caracteres"
                                                    value={this.state.clanFoundName}
                                                    onChange={e =>
                                                        this.setState({
                                                            clanFoundName: e.target.value
                                                        })
                                                    }
                                                    maxLength={20}
                                                />
                                                <label className={style.clanFormLabel}>Tipo de Clan</label>
                                                <select
                                                    className={style.clanFormSelect}
                                                    value={this.state.clanFoundType}
                                                    onChange={e =>
                                                        this.setState({
                                                            clanFoundType: e.target.value
                                                        })
                                                    }
                                                >
                                                    <option value="mixto">Mixto (ciudadanos + criminales)</option>
                                                    <option value="solo_real">Solo Armada Real</option>
                                                    <option value="solo_caos">Solo Legión del Caos</option>
                                                    <option value="ciudadanos_real">Ciudadanos + Armada Real</option>
                                                    <option value="criminales_caos">Criminales + Legión del Caos</option>
                                                </select>
                                                <div className={style.clanModalActions}>
                                                    <button
                                                        type="button"
                                                        className={style.clanModalBtnPrimary}
                                                        onClick={this.submitClanFound}
                                                    >
                                                        Fundar
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* Caso 2: TENGO CLAN → info + members + (líder) solicitudes */}
                                {hasClan && !dissolving && (
                                    <div className={style.clanModalBody}>
                                        <div className={style.clanInfoGrid}>
                                            <div className={style.clanInfoCell}>
                                                <div className={style.clanInfoLabel}>Tipo</div>
                                                <div className={style.clanInfoValue}>{c.typeLabel || c.type || "—"}</div>
                                            </div>
                                            <div className={style.clanInfoCell}>
                                                <div className={style.clanInfoLabel}>Nivel</div>
                                                <div className={style.clanInfoValue}>{c.level || 1}</div>
                                            </div>
                                            <div className={style.clanInfoCell}>
                                                <div className={style.clanInfoLabel}>Miembros</div>
                                                <div className={style.clanInfoValue}>{members.length}/{c.memberCap || 6}</div>
                                            </div>
                                            <div className={style.clanInfoCell}>
                                                <div className={style.clanInfoLabel}>Muertes totales</div>
                                                <div className={style.clanInfoValue}>{c.totalKills || 0}</div>
                                            </div>
                                        </div>

                                        {/* Solicitudes pendientes (solo líder). */}
                                        {c.isLeader && pendingReqs.length > 0 && (
                                            <div>
                                                <div className={style.clanMembersHeader}>
                                                    <span>Solicitudes ({pendingReqs.length})</span>
                                                </div>
                                                <div className={style.clanRequestsList}>
                                                    {pendingReqs.map(r => (
                                                        <div
                                                            key={r.requesterId}
                                                            className={style.clanRequestRow}
                                                        >
                                                            <span className={style.clanRequestName}>
                                                                {r.name}
                                                                <span className={style.clanRequestMeta}>
                                                                    {" "}L{r.level}
                                                                </span>
                                                            </span>
                                                            <button
                                                                type="button"
                                                                className={style.clanRequestAccept}
                                                                onClick={() =>
                                                                    this.acceptJoinRequest(r.requesterId)
                                                                }
                                                            >
                                                                ✓
                                                            </button>
                                                            <button
                                                                type="button"
                                                                className={style.clanRequestReject}
                                                                onClick={() =>
                                                                    this.rejectJoinRequest(r.requesterId)
                                                                }
                                                            >
                                                                ✕
                                                            </button>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}

                                        <div className={style.clanMembersHeader}>
                                            <span>Miembros</span>
                                            {c.isLeader && (
                                                <button
                                                    type="button"
                                                    className={style.clanModalBtnSmall}
                                                    onClick={this.promptClanInvite}
                                                >
                                                    + Invitar
                                                </button>
                                            )}
                                        </div>
                                        <div className={style.clanMembersList}>
                                            {members.map(m => (
                                                <div
                                                    key={m.id}
                                                    className={`${style.clanMemberRow} ${
                                                        m.online ? style.clanMemberOnline : style.clanMemberOffline
                                                    }`}
                                                >
                                                    <span className={style.clanMemberStatus}>●</span>
                                                    <span className={style.clanMemberName}>
                                                        {m.name}
                                                        {m.isLeader && (
                                                            <span className={style.clanMemberLeader}>
                                                                {" "}♔ Líder
                                                            </span>
                                                        )}
                                                    </span>
                                                    <span className={style.clanMemberMeta}>
                                                        {m.online ? `Mapa ${m.map}` : "Desconectado"}
                                                    </span>
                                                    <span className={style.clanMemberKills}>
                                                        {m.kills || 0}k
                                                    </span>
                                                    {c.isLeader && !m.isLeader && (
                                                        <button
                                                            type="button"
                                                            className={style.clanMemberKick}
                                                            title={"Expulsar " + m.name}
                                                            onClick={() => this.promptClanKick(m.name)}
                                                        >
                                                            ×
                                                        </button>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                        <div className={style.clanModalActions}>
                                            <button
                                                type="button"
                                                className={style.clanModalBtn}
                                                onClick={this.submitClanLeave}
                                            >
                                                {c.isLeader && members.length === 1
                                                    ? "Disolver Clan"
                                                    : "Salir del Clan"}
                                            </button>
                                        </div>
                                    </div>
                                )}

                                {/* Caso 3: CONFIRMAR DISOLUCIÓN — tipear el nombre. */}
                                {dissolving && (
                                    <div className={style.clanModalBody}>
                                        <div className={style.clanDangerNote}>
                                            <strong>¡Atención!</strong> Estás por <strong>disolver</strong> este clan.
                                            Esta acción es <strong>permanente</strong> y no se puede deshacer.
                                            Para confirmar, tipeá el nombre exacto del clan:
                                        </div>
                                        <div className={style.clanDangerExpected}>
                                            {c.name}
                                        </div>
                                        <input
                                            type="text"
                                            className={style.clanFormInput}
                                            placeholder="Tipear nombre del clan"
                                            value={this.state.clanDissolveConfirm}
                                            onChange={e =>
                                                this.setState({
                                                    clanDissolveConfirm: e.target.value
                                                })
                                            }
                                            autoFocus
                                        />
                                        <div className={style.clanModalActions}>
                                            <button
                                                type="button"
                                                className={style.clanModalBtn}
                                                onClick={() =>
                                                    this.setState({
                                                        showClanDissolveConfirm: false,
                                                        clanDissolveConfirm: ""
                                                    })
                                                }
                                            >
                                                Cancelar
                                            </button>
                                            <button
                                                type="button"
                                                className={style.clanModalBtnDanger}
                                                disabled={
                                                    (this.state.clanDissolveConfirm || "").trim() !==
                                                    (c.name || "").trim()
                                                }
                                                onClick={this.submitClanDissolve}
                                            >
                                                Disolver Clan
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    );
                })()}

                {/* Ticket #336: el modal del party ya NO flota — vive
                    inline dentro de BOX 5 (ver InventoryBox.renderPartyPanel).
                    Mantengo el bloque deshabilitado para preservar el
                    state showPartyModal por compatibilidad con otros sites. */}
                {false && this.state.showPartyModal && (() => {
                    // Helper inline: lookup mapname si ya está loaded, sino
                    // fallback a "Mapa N". El lazy-load del worldmap ya
                    // populó state.mapNames si el user abrió el mapamundi.
                    const mapName = (idMap) => {
                        if (!idMap) return "—";
                        const n = this.state.mapNames && this.state.mapNames[String(idMap)];
                        return n || ("Mapa " + idMap);
                    };
                    // HP bar color por % restante.
                    const hpFillClass = (hp, maxHp) => {
                        if (!maxHp) return style.partyBarFillHp;
                        const pct = hp / maxHp;
                        if (pct < 0.3) return style.partyBarFillHpLow;
                        if (pct < 0.6) return style.partyBarFillHpMid;
                        return style.partyBarFillHp;
                    };
                    return (
                        <div
                            className={style.modalParty}
                            onClick={() => this.setState({ showPartyModal: false })}
                        >
                            <div
                                className={style.modalPartyCard}
                                onClick={e => e.stopPropagation()}
                            >
                                <div className={style.modalPartyHeader}>
                                    <span className={style.modalPartyTitle}>PARTY</span>
                                    {this.state.party && (
                                        <span className={style.modalPartySubtitle}>
                                            {this.state.party.members.length} miembros
                                        </span>
                                    )}
                                    <div
                                        className={style.modalPartyClose}
                                        onClick={() => this.setState({ showPartyModal: false })}
                                    >×</div>
                                </div>

                                {this.state.party ? (
                                    <>
                                        <div className={style.partyMembersList}>
                                            {this.state.party.members.map((m, idx) => {
                                                const cls = nameClases[m.idClase] || "—";
                                                const rz = nameRazas[m.idRaza] || "—";
                                                const dead = m.connected && m.hp <= 0 && m.maxHp > 0;
                                                const hpPct = m.maxHp > 0 ? Math.max(0, Math.min(100, (m.hp / m.maxHp) * 100)) : 0;
                                                const mpPct = m.maxMana > 0 ? Math.max(0, Math.min(100, (m.mana / m.maxMana) * 100)) : 0;
                                                // Nivel 1 (GM bajo) o 99 (GM MAX) → nombre en verde staff.
                                                const nameColor = (m.privileges === 1 || m.privileges === 99)
                                                    ? "#7CFC8B"
                                                    : m.criminal ? "#FF6464" : "#9FC7FF";
                                                return (
                                                    <div
                                                        key={idx}
                                                        className={`${style.partyMemberLine} ${
                                                            !m.connected ? style.partyMemberLineDisconnected : ""
                                                        } ${dead ? style.partyMemberLineDead : ""}`}
                                                    >
                                                        <span className={style.partyLineCrown}>
                                                            {m.isLeader ? "👑" : ""}
                                                        </span>
                                                        <span className={style.partyLineName} style={{ color: nameColor }}>
                                                            {m.name}
                                                        </span>
                                                        <span className={style.partyLineLevel}>
                                                            L{m.level || "?"}
                                                        </span>
                                                        <span className={style.partyLineClassRace}>
                                                            {cls}/{rz}
                                                        </span>
                                                        {m.connected && m.maxHp > 0 ? (
                                                            <>
                                                                <div className={style.partyLineBarHp}>
                                                                    <div className={style.partyLineBarFillHp}
                                                                         style={{ width: hpPct + "%" }} />
                                                                </div>
                                                                <span className={style.partyLineBarText}>
                                                                    {m.hp}/{m.maxHp}
                                                                </span>
                                                            </>
                                                        ) : (
                                                            <span className={style.partyLineDash}>—</span>
                                                        )}
                                                        {m.connected && m.maxMana > 0 ? (
                                                            <>
                                                                <div className={style.partyLineBarMp}>
                                                                    <div className={style.partyLineBarFillMp}
                                                                         style={{ width: mpPct + "%" }} />
                                                                </div>
                                                                <span className={style.partyLineBarText}>
                                                                    {m.mana}/{m.maxMana}
                                                                </span>
                                                            </>
                                                        ) : (
                                                            <span className={style.partyLineDash}>—</span>
                                                        )}
                                                        <span className={style.partyLineMap}>
                                                            {m.connected
                                                                ? `${mapName(m.map)} (${m.x},${m.y})`
                                                                : (dead ? "muerto" : "desconectado")}
                                                        </span>
                                                        {this.state.party.iAmLeader && !m.isLeader ? (
                                                            <span
                                                                className={style.partyLineKickX}
                                                                title={"Expulsar " + m.name}
                                                                onClick={() => {
                                                                    this.general.sendDialog(
                                                                        /\s/.test(m.name)
                                                                            ? '/p kick "' + m.name + '"'
                                                                            : "/p kick " + m.name
                                                                    );
                                                                }}
                                                            >×</span>
                                                        ) : (
                                                            <span className={style.partyLineKickX} style={{ visibility: "hidden" }}>×</span>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                        <button
                                            type="button"
                                            className={style.partyLeaveBtn}
                                            onClick={() => {
                                                this.general.sendDialog("/p salir");
                                                this.setState({ showPartyModal: false });
                                            }}
                                        >
                                            Salir de la party
                                        </button>
                                    </>
                                ) : (
                                    <div className={style.partyEmpty}>
                                        No estás en una party. Invitá a alguien para
                                        formar una.
                                    </div>
                                )}

                                {(!this.state.party || this.state.party.iAmLeader) && (
                                    <div className={style.partyInviteRow}>
                                        <input
                                            type="text"
                                            className={style.partyInviteInput}
                                            value={this.state.partyInviteName}
                                            onChange={e =>
                                                this.setState({ partyInviteName: e.target.value })
                                            }
                                            onKeyDown={e => {
                                                if (e.keyCode === 13) {
                                                    const name = (this.state.partyInviteName || "").trim();
                                                    if (!name) return;
                                                    const cmd = /\s/.test(name)
                                                        ? '/p invitar "' + name + '"'
                                                        : "/p invitar " + name;
                                                    this.general.sendDialog(cmd);
                                                    this.setState({ partyInviteName: "" });
                                                }
                                            }}
                                            placeholder="Nombre del jugador"
                                            maxLength={30}
                                        />
                                        <button
                                            type="button"
                                            className={style.partyInviteBtn}
                                            onClick={() => {
                                                const name = (this.state.partyInviteName || "").trim();
                                                if (!name) return;
                                                const cmd = /\s/.test(name)
                                                    ? '/p invitar "' + name + '"'
                                                    : "/p invitar " + name;
                                                this.general.sendDialog(cmd);
                                                this.setState({ partyInviteName: "" });
                                            }}
                                        >
                                            Invitar
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    );
                })()}

                {/* Ticket #321: modal del Marinero (ferry NPC). Click en el
                    NPC → server abre con packet openFerry. Cada destino es
                    un botón; click → general.sendUseFerry(idx). */}
                {this.state.showFerryModal && (
                    <div
                        className={style.modalFerry}
                        onClick={() => this.setState({ showFerryModal: false })}
                    >
                        <div
                            className={style.modalFerryCard}
                            onClick={e => e.stopPropagation()}
                        >
                            <div className={style.modalFerryHeader}>
                                <div className={style.modalFerryTitle}>
                                    {this.state.ferry.npcName || "Marinero"}
                                </div>
                                <div
                                    className={style.modalFerryClose}
                                    onClick={() =>
                                        this.setState({ showFerryModal: false })
                                    }
                                >
                                    ×
                                </div>
                            </div>
                            <div className={style.modalFerrySubtitle}>
                                ¿A qué puerto quieres viajar?
                            </div>
                            <div className={style.modalFerryRoutes}>
                                {(this.state.ferry.routes || []).map((r, i) => (
                                    <button
                                        key={i}
                                        type="button"
                                        className={style.modalFerryRouteBtn}
                                        onClick={() => {
                                            this.general.sendUseFerry(i);
                                            this.setState({
                                                showFerryModal: false
                                            });
                                        }}
                                    >
                                        <span className={style.modalFerryRouteLabel}>
                                            {r.label}
                                        </span>
                                        <span className={style.modalFerryRouteCost}>
                                            {r.cost.toLocaleString()} oro
                                        </span>
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                )}

                {this.state.showDropItemModal && (
                    <div
                        className={style.modalDropItem}
                        onClick={this.cancelDropItem}
                    >
                        <div
                            className={style.modalDropInner}
                            onClick={e => e.stopPropagation()}
                        >
                            <div className={style.modalDropHeader}>
                                <span>Tirar items</span>
                                <span
                                    className={style.modalDropClose}
                                    onClick={this.cancelDropItem}
                                    title="Cancelar"
                                >
                                    <FontAwesomeIcon icon={faTimes} />
                                </span>
                            </div>
                            <div className={style.modalDropBody}>
                                <label className={style.modalDropLabel}>
                                    ¿Cuántos querés tirar?
                                </label>
                                <div className={style.modalDropRow}>
                                    <button
                                        type="button"
                                        className={style.modalDropStep}
                                        onClick={() => {
                                            const v =
                                                Math.max(
                                                    1,
                                                    (parseInt(
                                                        this.state.dropItemValue,
                                                        10
                                                    ) || 1) - 1
                                                ) + "";
                                            this.setState({
                                                dropItemValue: v
                                            });
                                        }}
                                    >
                                        −
                                    </button>
                                    <input
                                        type="text"
                                        inputMode="numeric"
                                        className={style.modalDropInput}
                                        value={this.state.dropItemValue}
                                        onChange={this.onDropItemInput}
                                        onKeyUp={this.onDropItemKey}
                                        autoFocus
                                    />
                                    <button
                                        type="button"
                                        className={style.modalDropStep}
                                        onClick={() => {
                                            const v =
                                                Math.min(
                                                    this.state.dropItemMaxCant,
                                                    (parseInt(
                                                        this.state.dropItemValue,
                                                        10
                                                    ) || 0) + 1
                                                ) + "";
                                            this.setState({
                                                dropItemValue: v
                                            });
                                        }}
                                    >
                                        +
                                    </button>
                                </div>
                                <div className={style.modalDropHint}>
                                    Tenés {this.state.dropItemMaxCant} en el slot.{" "}
                                    <button
                                        type="button"
                                        className={style.modalDropAllBtn}
                                        onClick={() =>
                                            this.setState({
                                                dropItemValue: String(
                                                    this.state.dropItemMaxCant
                                                )
                                            })
                                        }
                                    >
                                        Tirar todos
                                    </button>
                                </div>
                            </div>
                            <div className={style.modalDropFooter}>
                                <button
                                    type="button"
                                    className={style.modalDropCancel}
                                    onClick={this.cancelDropItem}
                                >
                                    Cancelar
                                </button>
                                <button
                                    type="button"
                                    className={style.modalDropConfirm}
                                    onClick={this.confirmDropItem}
                                >
                                    Tirar
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {this.state.spellInfoTarget && this.renderSpellInfoModal()}

                {showMacroConfig && this.renderMacroConfigModal()}

                {/* Context menu del right-click sobre NPC. Position fixed
                    en clientX/Y del click — overlay sobre todo. */}
                {this.renderNpcContextMenu()}

                {/* Context menu del right-click sobre una row de hechizo
                    (ticket #207). Opciones: Asignar tecla / Quitar tecla. */}
                {this.renderSpellContextMenu()}

                {/* Context menu del right-click sobre una row del PartyPanel.
                    Solo el líder lo abre, opción "Promover a Líder". */}
                {this.renderPartyContextMenu()}

                {/* Context menu del right-click sobre una row del GlobalPanel.
                    Opciones: Susurrar (pre-fill /w) + Invitar a party. */}
                {this.renderGlobalContextMenu()}

                {/* Context menu del right-click sobre una row del ClanPanel.
                    Opción única (líder): Expulsar del Clan. */}
                {this.renderClanContextMenu()}

                {/* Ticket #280: mini menu del right-click sobre el buff de
                    una pet — pick modo defensivo / ofensivo. */}
                {this.renderPetModeMenu()}

                {/* Modal de captura de tecla — se abre tras click en
                    "Asignar tecla". Escucha el siguiente keydown, valida
                    colisión vía getKeyInUse y persiste. */}
                {this.renderSpellKeyCaptureModal()}

                {/* Context menu del right-click sobre un slot del
                    inventario (ticket #208). */}
                {this.renderInventoryContextMenu()}

                {/* Modal de captura de tecla para items (ticket #208). */}
                {this.renderItemKeyCaptureModal()}

                {/* Modal de confirmación al apretar ESC (ticket #214). */}
                {this.renderExitConfirmModal()}

                {/* Modal de inspección de NPC — abierto cuando llega el
                    packet npcInspect del server. */}
                {this.renderNpcInspectModal()}


                {/* Modales de Banco y Comerciante movidos dentro de
                    .content_left para que su overlay se posicione 1:1
                    sobre la CAJA 1 (.render) via position: absolute. */}

                <div
                    className={`${style.outer} ${this.state.isFullscreen ? style.outerFullscreen : ""} ${this.state.uiSkin === "tierras-del-sur" ? style.tdsSkin : ""}`}
                    style={{ display: loading ? "none" : "flex" }}
                >
                    <div className={style.middle}>
                        <div className={style.content}>
                            <div className={style.content_left}>
                                {/* CAJA 2 — chatBox: vive arriba del juego, sibling de .render */}
                                <div className={style.chatBox}>
                                    {/* Ticket #283: tabs minimalistas
                                        encima del console. General (todo) /
                                        Chat (personajes) / Combate (daño).
                                        Badge en inactivas cuando hay msgs
                                        nuevos para ellas. */}
                                    {this.renderChatTabs()}
                                    <div className={style.chatConsoleWrap}>
                                        <div
                                            id="console"
                                            ref="console"
                                            className={style.console}
                                            onScroll={this.onChatScroll}
                                        >
                                            {this.getFilteredConsoleMsgs()}
                                        </div>
                                        {/* Ticket #300: pill "↓ N mensajes
                                            nuevos" cuando el user scrolleó
                                            arriba y siguen llegando msgs a
                                            la tab activa. Click → scroll
                                            al fondo + reset counter. */}
                                        {this.state.chatScrollPending > 0 && (
                                            <button
                                                type="button"
                                                className={style.chatScrollPill}
                                                onClick={this.scrollChatToBottom}
                                                title="Ir al último mensaje"
                                            >
                                                ↓ {this.state.chatScrollPending} mensaje{this.state.chatScrollPending > 1 ? "s" : ""} nuevo{this.state.chatScrollPending > 1 ? "s" : ""}
                                            </button>
                                        )}
                                    </div>
                                </div>
                                <div className={style.render}>
                                    <input
                                        type="text"
                                        name="text"
                                        autoFocus
                                        ref={input => {
                                            if (!input) return;
                                            input.focus();
                                            // Ticket #305: cuando el input
                                            // se abre pre-cargado con un
                                            // /w prefix, el cursor cae al
                                            // FINAL para que el user tipe
                                            // de una. Sin esto, focus deja
                                            // el cursor al inicio o
                                            // selecciona-todo según browser.
                                            try {
                                                const len = input.value.length;
                                                input.setSelectionRange(len, len);
                                            } catch (e) {}
                                        }}
                                        className={style.text}
                                        // Marca para que el keyup handler global
                                        // SÍ procese teclas mientras este input
                                        // está focusado (Enter envía + cierra
                                        // chat). Otros inputs (party modal, etc)
                                        // sin esta marca son early-return.
                                        data-game-key="true"
                                        style={{
                                            display: showInputText
                                                ? "block"
                                                : "none"
                                        }}
                                        value={textDialog}
                                        onChange={e => {
                                            this.setState({
                                                textDialog: e.target.value,
                                                // Ticket #306: al tipear,
                                                // salimos del modo history.
                                                chatHistoryIndex: -1
                                            });
                                        }}
                                        onKeyDown={e => {
                                            // Ticket #306: navegar el history
                                            // de mensajes enviados con ↑ y ↓
                                            // (como shells / Discord).
                                            //   ↑ = más viejo (index +1).
                                            //   ↓ = más nuevo (index -1).
                                            //   ↓ con index=0 → input vacío.
                                            const h = this.state.chatHistory || [];
                                            if (!h.length) return;
                                            const idx = this.state.chatHistoryIndex;
                                            if (e.key === "ArrowUp") {
                                                e.preventDefault();
                                                const nextIdx = Math.min(idx + 1, h.length - 1);
                                                this.setState({
                                                    chatHistoryIndex: nextIdx,
                                                    textDialog: h[nextIdx] || ""
                                                });
                                            } else if (e.key === "ArrowDown") {
                                                e.preventDefault();
                                                if (idx <= 0) {
                                                    this.setState({
                                                        chatHistoryIndex: -1,
                                                        textDialog: ""
                                                    });
                                                } else {
                                                    const nextIdx = idx - 1;
                                                    this.setState({
                                                        chatHistoryIndex: nextIdx,
                                                        textDialog: h[nextIdx] || ""
                                                    });
                                                }
                                            }
                                        }}
                                        placeholder="Decí algo al mundo de Argentum…"
                                        autoComplete="off"
                                        spellCheck={false}
                                    />
                                    <canvas
                                        width="672"
                                        height="672"
                                        id="background"
                                        className={style.background}
                                        ref={ref => {
                                            this.canvas.background = ref;
                                        }}
                                    />
                                    <canvas
                                        width="672"
                                        height="672"
                                        id="foreground"
                                        className={style.foreground}
                                        ref={ref => {
                                            this.canvas.foreground = ref;
                                        }}
                                    />
                                    <canvas
                                        width="672"
                                        height="672"
                                        id="items"
                                        className={style.items}
                                        ref={ref => {
                                            this.canvas.items = ref;
                                        }}
                                    />
                                    <canvas
                                        width="672"
                                        height="672"
                                        id="techos"
                                        className={style.techos}
                                        ref={ref => {
                                            this.canvas.techos = ref;
                                        }}
                                    />
                                    <canvas
                                        width="672"
                                        height="672"
                                        id="textos"
                                        className={style.textos}
                                        ref={ref => {
                                            this.canvas.textos = ref;
                                        }}
                                    />
                                    <canvas
                                        width="672"
                                        height="672"
                                        id="mouseEvent"
                                        className={style.mouseEvent}
                                        onClick={this.clickCanvas}
                                        onContextMenu={this.contextMenuCanvas}
                                        style={{
                                            cursor: crosshair
                                                ? "crosshair"
                                                : "default"
                                        }}
                                    />
                                    {this.renderBuffs()}

                                    {/* Stack de inspect en la esquina INFERIOR
                                        derecha de la CAJA 1. Apilado vertical:
                                        el modal de OTRO jugador va arriba, el de
                                        AUTO-inspección abajo (anclado al fondo).
                                        Solo se monta si hay al menos uno. */}
                                    {(this.state.userInspect || this.state.selfInspect) && (
                                        <div className={style.inspectStack}>
                                            {this.renderUserInspectModal()}
                                            {this.renderSelfInspectModal()}
                                        </div>
                                    )}

                                    {/* Mapas dentro de .render — overlay 21×21
                                        de la CAJA 1 (position absolute inset:0).
                                        Mutuamente excluyentes (M cicla estados). */}
                                    {this.state.showWorldMap &&
                                        this.renderWorldMapModal()}
                                    {this.state.showFullMap &&
                                        this.renderFullMapModal()}
                                </div>

                                <div className={style.macros}>
                                    {this.renderBoxMacros()}
                                </div>

                                {/* Modal de banco — overlay position: absolute
                                    referencial al .content_left (que es
                                    position: relative). Las coords del
                                    overlay matchean exactamente el .render. */}
                                {this.renderModalBank()}

                                {/* Herrería (#329) — modal de crafteo de
                                    lingotes. Argentum Frame. Se abre con
                                    doble-click sobre el Martillo equipado. */}
                                {this.renderModalHerreria()}

                                {/* Ticket #235: TradeModal — Argentum Frame,
                                    componente memoizado con
                                    shouldComponentUpdate. Mismo overlay
                                    position pattern que el banco. */}
                                {(() => {
                                    const t = trade || {};
                                    const tItems = t.itemsTrade || {};
                                    const uItems = t.itemsUser || {};
                                    // Perf 2026-07-07: los hashes (49 iter de
                                    // string-building) solo se calculan si el
                                    // modal está abierto. Antes se computaban
                                    // en CADA render del HUD aunque el trade
                                    // estuviera cerrado — puro peaje pagado por
                                    // cada tick de HP/mana/buff. Con el modal
                                    // cerrado el TradeModal bail-outea por
                                    // show=false igual, así que los hashes no
                                    // importan.
                                    let tHash = "";
                                    let uHash = "";
                                    if (showModalTrade) {
                                        for (let i = 0; i < 25; i++) {
                                            const it = tItems[i];
                                            tHash += it
                                                ? `${i}:${it.name || ""}:${it.cant || 0}|`
                                                : `${i}:0|`;
                                        }
                                        for (let i = 1; i < 25; i++) {
                                            const it = uItems[i];
                                            uHash += it
                                                ? `${i}:${it.name || ""}:${it.cant || 0}:${
                                                      it.equipped ? 1 : 0
                                                  }|`
                                                : `${i}:0|`;
                                        }
                                    }
                                    return (
                                        <TradeModal
                                            style={style}
                                            show={!!showModalTrade}
                                            tradeItems={tItems}
                                            userItems={uItems}
                                            tradeItemsHash={tHash}
                                            userItemsHash={uHash}
                                            npcName={t.npcName || ""}
                                            idPosTrade={
                                                t.idPosTrade != null ? t.idPosTrade : -1
                                            }
                                            idPosInv={t.idPosInv || 0}
                                            cantTrade={cantTrade}
                                            gold={(user && user.gold) || 0}
                                            onSelectTradeSlot={this.selectItemTrade}
                                            onSelectInvSlot={this.selectItemUserTrade}
                                            onBuy={this.buyTrade}
                                            onSell={this.sellTrade}
                                            onQtyChange={this.setCantTrade}
                                            onClose={this.closeModalTrade}
                                        />
                                    );
                                })()}
                            </div>
                            <div className={style.content_right}>
                                {/* ── CAJA 4 — Character Identity Card v3 ──
                                    Minimalismo rogue: nombre central como
                                    foco, abajo línea fina de progresión.
                                    Numero del nivel pegado a la barra,
                                    % y valores absolutos OUTSIDE la barra
                                    a la derecha. */}
                                {(() => {
                                    const isMaxLevel = user.level >= 42;
                                    const pct =
                                        user.expNextLevel > 0 && !isMaxLevel
                                            ? Math.max(
                                                  0,
                                                  Math.min(
                                                      100,
                                                      Math.floor(
                                                          (user.exp * 100) /
                                                              user.expNextLevel
                                                      )
                                                  )
                                              )
                                            : 0;
                                    const nearLevelUp = pct >= 90;
                                    const fmt = n =>
                                        (n || 0).toLocaleString("es-AR");
                                    // Fuente actual del header — inyectada
                                    // como CSS variable para que los hijos
                                    // (.name, .levelInline, .expPctBig,
                                    // .expValuesSmall) la lean sin re-render.
                                    const headerFontFamily =
                                        headerFontFamilyFromIndex(
                                            this.state.headerFontIndex || 0
                                        );
                                    return (
                                        <div
                                            className={style.header}
                                            style={{
                                                "--header-font": headerFontFamily
                                            }}
                                        >
                                            <div
                                                className={style.headerTopRow}
                                            >
                                                <div
                                                    className={
                                                        style.headerActions
                                                    }
                                                >
                                                    <div
                                                        className={style.configuration}
                                                        title="Skills"
                                                        onClick={() =>
                                                            this.setState({
                                                                showSkillsModal: true
                                                            })
                                                        }
                                                    >
                                                        <FontAwesomeIcon
                                                            icon={faBolt}
                                                        />
                                                    </div>
                                                    <div
                                                        className={
                                                            style.configuration
                                                        }
                                                        title="Configuración"
                                                        onClick={async () => {
                                                            // Reconcile: chars viejos pueden no
                                                            // tener todas las action ids en su
                                                            // keyCodeDefault. Mergeamos DEFAULTS
                                                            // como base + override con lo que
                                                            // tenga el state.
                                                            //
                                                            // Ticket #288: distinguir undefined
                                                            // ("nunca seteado" → usar default)
                                                            // de 0 ("limpiado intencionalmente
                                                            // por #273 steal" → preservar 0).
                                                            // Antes con `!reconciled[k]` el 0
                                                            // se trataba como "vacío" y se
                                                            // re-asignaba el default — bug:
                                                            // bindear C en Celeridad limpiaba
                                                            // Meditar a 0 pero al abrir el
                                                            // modal volvía a C automatico.
                                                            const reconciled = Object.assign(
                                                                {},
                                                                DEFAULT_KEY_BINDINGS,
                                                                keyCodeDefault
                                                            );
                                                            await this.setState(
                                                                {
                                                                    showModalControlPanel: true,
                                                                    keyCodeDefault: reconciled,
                                                                    tmpKeyCodeDefault: _.cloneDeep(
                                                                        reconciled
                                                                    )
                                                                }
                                                            );
                                                            this.charKeyCodeDefault();
                                                        }}
                                                    >
                                                        <FontAwesomeIcon
                                                            icon={faCog}
                                                        />
                                                    </div>
                                                    <div
                                                        className={
                                                            style.fullscreenBtn
                                                        }
                                                        title={
                                                            this.state
                                                                .isFullscreen
                                                                ? "Salir de pantalla completa"
                                                                : "Pantalla completa"
                                                        }
                                                        onClick={
                                                            this
                                                                .toggleFullscreen
                                                        }
                                                    >
                                                        <FontAwesomeIcon
                                                            icon={
                                                                this.state
                                                                    .isFullscreen
                                                                    ? faCompress
                                                                    : faExpand
                                                            }
                                                        />
                                                    </div>
                                                    <div
                                                        className={
                                                            style.exitBtn
                                                        }
                                                        title="Salir al lobby (ESC o /salir)"
                                                        onClick={() => {
                                                            if (
                                                                window.confirm(
                                                                    "¿Salir del juego?"
                                                                )
                                                            ) {
                                                                this.doLogout();
                                                            }
                                                        }}
                                                    >
                                                        <FontAwesomeIcon
                                                            icon={faSignOutAlt}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                            {/* Ticket #283: name en su
                                                propia row, alineado a la
                                                izquierda — los iconos
                                                quedan arriba a la derecha. */}
                                            <div className={style.name}>
                                                {user.nameCharacter}
                                            </div>

                                            <div
                                                className={
                                                    style.progressionRow
                                                }
                                            >
                                                <span
                                                    className={
                                                        style.levelInline
                                                    }
                                                >
                                                    {user.level}
                                                </span>
                                                {isMaxLevel ? (
                                                    <div
                                                        className={`${style.exp} ${style.expMaxLevel}`}
                                                    >
                                                        <span>
                                                            Nivel Máximo
                                                        </span>
                                                    </div>
                                                ) : (
                                                    <>
                                                        <div
                                                            className={`${style.exp} ${nearLevelUp ? style.expNearLevelUp : ""}`}
                                                        >
                                                            <div
                                                                className={
                                                                    style.progress_bar
                                                                }
                                                                style={{
                                                                    width: `${pct}%`
                                                                }}
                                                            />
                                                        </div>
                                                        <div
                                                            className={
                                                                style.expStatsCol
                                                            }
                                                        >
                                                            <span
                                                                className={
                                                                    style.expPctBig
                                                                }
                                                            >
                                                                {pct}%
                                                            </span>
                                                            <span
                                                                className={
                                                                    style.expValuesSmall
                                                                }
                                                            >
                                                                {fmt(user.exp)}
                                                                {" / "}
                                                                {fmt(
                                                                    user.expNextLevel
                                                                )}
                                                            </span>
                                                        </div>
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })()}
                                {/* CAJA 5 — inventorySpellsBox. Ticket #224
                                    fase 3: extraído a InventoryBox con
                                    shouldComponentUpdate basado en hashes
                                    primitivos. La lógica de renderBoxItems/
                                    Spells/SpellActionRow vive todavía en el
                                    padre — InventoryBox sólo provee el
                                    shell y delega vía callback props. */}
                                {(() => {
                                    const items =
                                        (user && user.items) || {};
                                    const spells =
                                        (user && user.spells) || {};
                                    // itemsHash: snapshot de los 24 slots
                                    // que captura cambios reales (idItem,
                                    // cantidad, equipped).
                                    let itemsHash = "";
                                    for (let i = 1; i < 25; i++) {
                                        const it = items[i];
                                        itemsHash += it
                                            ? `${i}:${it.idItem}:${it.cant}:${it.equipped ? 1 : 0}|`
                                            : `${i}:0|`;
                                    }
                                    // spellsHash: snapshot de los slots
                                    // ocupados (idSpell).
                                    let spellsHash = "";
                                    for (const k in spells) {
                                        const sp = spells[k];
                                        if (sp && sp.idSpell) {
                                            spellsHash += `${k}:${sp.idSpell}|`;
                                        }
                                    }
                                    const partyMembersCount =
                                        this.state.party &&
                                        Array.isArray(
                                            this.state.party.members
                                        )
                                            ? this.state.party.members.length
                                            : 0;
                                    return (
                                        <InventoryBox
                                            style={style}
                                            showInventary={showInventary}
                                            itemsHash={itemsHash}
                                            spellsHash={spellsHash}
                                            selectItem={
                                                this.state.selectItem
                                            }
                                            selectedSpellSlot={
                                                this.state.selectedSpellSlot
                                            }
                                            invDraggingFrom={
                                                this.state.invDraggingFrom
                                            }
                                            invDraggingOver={
                                                this.state.invDraggingOver
                                            }
                                            sortMenuOpen={
                                                this.state.sortMenuOpen
                                            }
                                            inventoryKeybindingsRef={
                                                this.state
                                                    .inventoryKeybindings
                                            }
                                            spellKeybindingsRef={
                                                this.state.spellKeybindings
                                            }
                                            spellOrderRef={
                                                this.state.spellOrder
                                            }
                                            partyMembersCount={
                                                partyMembersCount
                                            }
                                            onShowInv={this.showInventary}
                                            onShowSpells={this.showSpells}
                                            onShowPartyModal={this.togglePartyPanel}
                                            showPartyPanel={this.state.showPartyModal}
                                            partyPanelEl={
                                                this.state.showPartyModal
                                                    ? this._getCachedPartyPanelEl()
                                                    : null
                                            }
                                            onShowGlobalPanel={this.toggleGlobalPanel}
                                            showGlobalPanel={this.state.showGlobalPanel}
                                            globalPanelEl={
                                                this.state.showGlobalPanel
                                                    ? this._getCachedGlobalPanelEl()
                                                    : null
                                            }
                                            globalListCount={
                                                Array.isArray(this.state.globalList)
                                                    ? this.state.globalList.length
                                                    : 0
                                            }
                                            onShowClanModal={this.toggleClanPanel}
                                            showClanPanel={this.state.showClanPanel}
                                            clanPanelEl={
                                                this.state.showClanPanel
                                                    ? this._getCachedClanPanelEl()
                                                    : null
                                            }
                                            hasClan={!!(this.state.clan && this.state.clan.hasClan)}
                                            clanMemberCount={
                                                (this.state.clan && this.state.clan.members)
                                                    ? this.state.clan.members.length
                                                    : 0
                                            }
                                            clanMemberCap={
                                                (this.state.clan && this.state.clan.memberCap) || 6
                                            }
                                            renderBoxItems={
                                                this.renderBoxItems
                                            }
                                            renderBoxSpells={
                                                this.renderBoxSpells
                                            }
                                            renderSpellActionRow={
                                                this.renderSpellActionRow
                                            }
                                        />
                                    );
                                })()}
                                {/* CAJA 6 (stats) + CAJA 7 (mapa) — tabla 1 fila × 2 columnas */}
                                <div className={style.statsMapRow}>
                                    {/* CAJA 6 — stats. Ticket #224 fase 1:
                                        extraído como PureComponent. Recibe
                                        solo primitivos para que la shallow
                                        comparison salte el render cuando
                                        un setState({user}) llega pero estos
                                        valores no cambiaron. */}
                                    {(() => {
                                        const c = this.computeCombatStats();
                                        return (
                                            <StatsBox
                                                style={style}
                                                hp={user.hp}
                                                maxHp={user.maxHp}
                                                mana={user.mana}
                                                maxMana={user.maxMana}
                                                gold={user.gold}
                                                attrFuerza={user.attrFuerza}
                                                attrAgilidad={
                                                    user.attrAgilidad
                                                }
                                                furiaActive={
                                                    !!this.state.furiaActive
                                                }
                                                dmgMin={
                                                    c.dmg ? c.dmg.min : null
                                                }
                                                dmgMax={
                                                    c.dmg ? c.dmg.max : null
                                                }
                                                defMin={
                                                    c.def ? c.def.min : null
                                                }
                                                defMax={
                                                    c.def ? c.def.max : null
                                                }
                                            />
                                        );
                                    })()}
                                    {/* CAJA 7 — mapa. Ticket #224 fase 2:
                                        extraído como subcomponente con
                                        shouldComponentUpdate custom. Los
                                        arrays partyMembers llegan con nuevo
                                        ref cada render del padre, pero los
                                        ignoramos en la comparación — el
                                        partyHash (cambia con movimientos del
                                        party) cubre el disparador real. */}
                                    {(() => {
                                        const mapNumber = this.config.mapNumber;
                                        const partyMembers =
                                            this.state.showPartyOnMap === "show"
                                                ? this.getPartyMembersForMap(
                                                      mapNumber
                                                  )
                                                : [];
                                        // Hash primitivo de party members
                                        // para comparación shallow. Cambia
                                        // cuando alguno se mueve o se une/sale.
                                        const partyHash = partyMembers
                                            .map(m => `${m.name}:${m.x}:${m.y}`)
                                            .join("|");
                                        const userFactionClass = this.factionClassSuffix(
                                            user
                                        );
                                        // Ticket #313: filtrar miembros del clan al
                                        // mismo mapa. Server ya solo manda los online.
                                        const clanMembersMini = (
                                            this.state.clanMembersPos || []
                                        ).filter(m => m.map === mapNumber);
                                        const clanHash = clanMembersMini
                                            .map(m => `${m.name}:${m.x}:${m.y}`)
                                            .join("|");
                                        return (
                                            <MapBox
                                                style={style}
                                                mapNumber={mapNumber}
                                                nameMap={nameMap}
                                                userPosX={
                                                    user.pos ? user.pos.x : null
                                                }
                                                userPosY={
                                                    user.pos ? user.pos.y : null
                                                }
                                                userFactionClass={
                                                    userFactionClass
                                                }
                                                showPartyOnMap={
                                                    this.state.showPartyOnMap
                                                }
                                                partyMembers={partyMembers}
                                                partyHash={partyHash}
                                                clanMembers={clanMembersMini}
                                                clanHash={clanHash}
                                                getFactionClassSuffix={
                                                    this.factionClassSuffix
                                                }
                                            />
                                        );
                                    })()}
                                </div>
                            </div>
                            {/* CAJA 8 — Discord box (ticket #286). Vive
                                como sibling absolute de content_left/
                                content_right, posicionada a la derecha
                                de la macroBar (CAJA 3), debajo de stats
                                + mapa (CAJA 6/7). */}
                            <div className={style.discordBox}>
                                <a
                                    href="https://discord.gg/YsKZjtVuz8"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className={style.discordButton}
                                    title="Unite a nuestra comunidad de Discord"
                                >
                                    <svg
                                        className={style.discordIcon}
                                        viewBox="0 0 24 24"
                                        aria-hidden="true"
                                    >
                                        <path
                                            fill="currentColor"
                                            d="M20.317 4.37a19.79 19.79 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.249a18.27 18.27 0 0 0-5.487 0a12.64 12.64 0 0 0-.617-1.249a.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057a19.9 19.9 0 0 0 5.993 3.03a.078.078 0 0 0 .084-.028a14.09 14.09 0 0 0 1.226-1.994a.076.076 0 0 0-.041-.106a13.107 13.107 0 0 1-1.872-.892a.077.077 0 0 1-.008-.128a10.2 10.2 0 0 0 .372-.292a.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127a12.299 12.299 0 0 1-1.873.892a.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028a19.839 19.839 0 0 0 6.002-3.03a.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419c0-1.333.956-2.419 2.157-2.419c1.21 0 2.176 1.096 2.157 2.42c0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419c0-1.333.955-2.419 2.157-2.419c1.21 0 2.176 1.096 2.157 2.42c0 1.333-.946 2.418-2.157 2.418z"
                                        />
                                    </svg>
                                    <span className={style.discordLabel}>
                                        Unirse al Discord
                                    </span>
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            </React.Fragment>
        );
    }
}

function mapStateToProps(state) {
    return {
        account: state.account || {},
        initsLoaded: state.initsLoaded
    };
}

export default connect(mapStateToProps)(Home);
